import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { createClient } from "@supabase/supabase-js";

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700;800&family=Barlow+Condensed:wght@600;700;800&display=swap";
document.head.appendChild(fontLink);

// Load SheetJS for client-side .xlsx export (exposes window.XLSX)
if (!window.XLSX) {
  const xlsxScript = document.createElement("script");
  xlsxScript.src = "https://unpkg.com/xlsx/dist/xlsx.full.min.js";
  document.head.appendChild(xlsxScript);
}

// Load ExcelJS lazily — only fetched when an installation report needs to embed
// photo thumbnails inline (SheetJS doesn't support image embedding). Exposes
// window.ExcelJS. The loader is kicked off the first time loadExcelJs() is called.
let _exceljsLoadPromise = null;
function loadExcelJs() {
  if (window.ExcelJS) return Promise.resolve(window.ExcelJS);
  if (_exceljsLoadPromise) return _exceljsLoadPromise;
  _exceljsLoadPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js";
    s.onload  = () => window.ExcelJS ? resolve(window.ExcelJS) : reject(new Error("ExcelJS loaded but global missing"));
    s.onerror = () => reject(new Error("ExcelJS failed to load from CDN"));
    document.head.appendChild(s);
  });
  return _exceljsLoadPromise;
}

// Load Recharts for performance report charts (exposes window.Recharts)
// Recharts UMD requires React + ReactDOM on window.
if (!window.React)    window.React    = React;
if (!window.ReactDOM) window.ReactDOM = ReactDOM;
if (!window.Recharts) {
  const rechartsScript = document.createElement("script");
  rechartsScript.src = "https://unpkg.com/recharts@2.12.7/umd/Recharts.js";
  document.head.appendChild(rechartsScript);
}

// Load canvas-confetti for approval celebration
if (!window.confetti) {
  const confettiScript = document.createElement("script");
  confettiScript.src = "https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.2/dist/confetti.browser.min.js";
  document.head.appendChild(confettiScript);
}

// Load pdf.js for client-side SRO PDF parsing (exposes window.pdfjsLib)
// Pinned to 3.11.174 — the last version that ships as UMD with a matching worker.
if (!window.pdfjsLib) {
  const pdfjsScript = document.createElement("script");
  pdfjsScript.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
  pdfjsScript.onload = () => {
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
    }
  };
  document.head.appendChild(pdfjsScript);
}

// ── Global UI styles: micro-interactions, animations, reduced-motion guard ────
const _style = document.createElement("style");
_style.textContent = `
  /* ── Universal button baseline ───────────────────────────────────────────── */
  /* Applies to every <button> with no extra markup required */
  button { cursor: pointer !important; transition: filter 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease !important; }
  button:not(:disabled):hover  { filter: brightness(1.1); }
  button:not(:disabled):active { transform: scale(0.97) !important; }
  button:disabled              { cursor: not-allowed !important; }

  /* ── Enhanced sm-btn — richer transitions on explicitly-classed buttons ─── */
  .sm-btn { transition: filter 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease, background-color 0.15s ease, color 0.15s ease !important; cursor: pointer !important; }
  .sm-btn:hover:not(:disabled) { filter: brightness(1.12); }
  .sm-btn:active:not(:disabled) { transform: scale(0.97) !important; }

  /* ── Primary action glow (blue) — Clock In, Submit, Approve, Create etc. ── */
  .sm-btn-primary:hover:not(:disabled) {
    box-shadow: 0 0 0 3px rgba(37,80,212,0.22), 0 6px 24px rgba(17,52,166,0.45) !important;
  }

  /* ── Danger action glow (red) — Reject, Delete, Deactivate etc. ─────────── */
  .sm-btn-danger:hover:not(:disabled) {
    box-shadow: 0 0 0 3px rgba(192,57,43,0.22), 0 6px 20px rgba(192,57,43,0.35) !important;
    filter: brightness(1.12) !important;
  }

  /* ── Tab button — smooth bg/border/colour highlight on switch ───────────── */
  .sm-tab { transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, border-bottom-color 0.18s ease, box-shadow 0.18s ease !important; cursor: pointer !important; }
  .sm-tab:not(:disabled):active { transform: scale(0.97) !important; }

  /* ── Card hover lift ─────────────────────────────────────────────────────── */
  .sm-card { transition: box-shadow 0.22s ease, transform 0.22s ease !important; }
  .sm-card:hover { box-shadow: 0 10px 40px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.3) !important; transform: translateY(-2px) !important; }

  /* ── List row hover ──────────────────────────────────────────────────────── */
  .sm-row { cursor: pointer !important; transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease !important; }
  .sm-row:hover { background: rgba(255,255,255,0.04) !important; box-shadow: 0 4px 20px rgba(0,0,0,0.35) !important; transform: translateY(-1px) !important; }

  /* ── Clickable non-button elements (type selector tiles, history items) ─── */
  .sm-clickable { cursor: pointer !important; transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease !important; }
  .sm-clickable:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.35) !important; transform: translateY(-1px) !important; }

  /* ── Input / textarea focus glow ─────────────────────────────────────────── */
  .sm-input { transition: border-color 0.18s ease, box-shadow 0.18s ease !important; }
  .sm-input:focus { outline: none !important; box-shadow: 0 0 0 3px rgba(26,69,196,0.25) !important; }
  .sm-input.sm-err:focus { box-shadow: 0 0 0 3px rgba(192,57,43,0.2) !important; }

  /* Status badge pulses */
  @keyframes sm-pulse-blue   { 0%,100%{box-shadow:0 0 0 0 rgba(37,80,212,0.55)}  55%{box-shadow:0 0 0 5px rgba(37,80,212,0)}  }
  @keyframes sm-pulse-amber  { 0%,100%{box-shadow:0 0 0 0 rgba(230,126,34,0.6)}  55%{box-shadow:0 0 0 5px rgba(230,126,34,0)} }
  @keyframes sm-pulse-green  { 0%,100%{box-shadow:0 0 0 0 rgba(39,174,96,0.55)}  55%{box-shadow:0 0 0 6px rgba(39,174,96,0)}  }
  .sm-badge-pulse        { animation: sm-pulse-blue  2.6s ease-in-out infinite; }
  .sm-badge-pulse-amber  { animation: sm-pulse-amber 2.8s ease-in-out infinite; }
  .sm-badge-pulse-green  { animation: sm-pulse-green 2.8s ease-in-out infinite; }

  /* Skeleton shimmer */
  @keyframes sm-shimmer { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
  .sm-skel { background:linear-gradient(90deg,#242424 25%,#303030 50%,#242424 75%); background-size:1200px 100%; animation:sm-shimmer 1.6s ease-in-out infinite; border-radius:4px; }

  /* Login background gradient */
  @keyframes sm-grad { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  .sm-login-bg { background:linear-gradient(135deg,#080810 0%,#0c0c18 20%,#080f10 40%,#100808 60%,#0a0a10 80%,#080810 100%); background-size:600% 600%; animation:sm-grad 14s ease infinite; }

  /* Welcome screen premium gradient */
  @keyframes sm-welcome-grad {
    0%   { background-position: 0% 0%;   }
    25%  { background-position: 60% 40%; }
    50%  { background-position: 100% 100%; }
    75%  { background-position: 40% 60%; }
    100% { background-position: 0% 0%;   }
  }
  .sm-welcome-bg {
    background: radial-gradient(ellipse at 20% 30%, rgba(17,52,166,0.28) 0%, transparent 55%),
                radial-gradient(ellipse at 80% 70%, rgba(37,80,212,0.18) 0%, transparent 50%),
                radial-gradient(ellipse at 55% 10%, rgba(26,69,196,0.12) 0%, transparent 45%),
                linear-gradient(160deg, #09090f 0%, #0d0d1a 30%, #080c14 55%, #0a0a10 80%, #09090f 100%);
    background-size: 300% 300%;
    animation: sm-welcome-grad 9s ease-in-out infinite;
  }

  /* Animated SVG checkmark */
  @keyframes sm-ring { from{stroke-dashoffset:220} to{stroke-dashoffset:0} }
  @keyframes sm-tick { from{stroke-dashoffset:80} to{stroke-dashoffset:0} }
  .sm-ring { stroke-dasharray:220; stroke-dashoffset:220; animation:sm-ring 0.55s cubic-bezier(0.65,0,0.45,1) forwards; }
  .sm-tick { stroke-dasharray:80; stroke-dashoffset:80; animation:sm-tick 0.4s cubic-bezier(0.65,0,0.45,1) 0.4s forwards; }

  /* Toast notification */
  @keyframes sm-toast-in  { from{transform:translateY(14px);opacity:0} to{transform:translateY(0);opacity:1} }
  @keyframes sm-toast-out { from{opacity:1;transform:translateY(0)} to{opacity:0;transform:translateY(4px)} }
  .sm-toast-enter { animation:sm-toast-in 0.28s ease forwards; }
  .sm-toast-exit  { animation:sm-toast-out 0.35s ease forwards; }

  /* Spinner */
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Splash screen fog / cloud animations ────────────────────────────────── */
  @keyframes sm-fog1 {
    0%   { transform: translate(0px,   0px)  scale(1)    rotate(0deg);  }
    25%  { transform: translate(45px, -30px) scale(1.07) rotate(2deg);  }
    55%  { transform: translate(-20px, 35px) scale(0.95) rotate(-1deg); }
    80%  { transform: translate(15px,  10px) scale(1.03) rotate(1deg);  }
    100% { transform: translate(0px,   0px)  scale(1)    rotate(0deg);  }
  }
  @keyframes sm-fog2 {
    0%   { transform: translate(0px,   0px)  scale(1);    }
    35%  { transform: translate(-50px, 22px) scale(1.08); }
    70%  { transform: translate(28px, -18px) scale(0.96); }
    100% { transform: translate(0px,   0px)  scale(1);    }
  }
  @keyframes sm-fog3 {
    0%   { transform: translate(0px,  0px)  scale(1);    }
    45%  { transform: translate(22px, 38px) scale(1.05); }
    100% { transform: translate(0px,  0px)  scale(1);    }
  }
  @keyframes sm-fog4 {
    0%   { transform: translate(0px,   0px)  scale(1);    opacity: 0.6; }
    50%  { transform: translate(-30px,-22px) scale(1.1);  opacity: 0.9; }
    100% { transform: translate(0px,   0px)  scale(1);    opacity: 0.6; }
  }
  .sm-fog1 { animation: sm-fog1 8s ease-in-out infinite; }
  .sm-fog2 { animation: sm-fog2 11s ease-in-out infinite; }
  .sm-fog3 { animation: sm-fog3 14s ease-in-out infinite; }
  .sm-fog4 { animation: sm-fog4 7s ease-in-out infinite; }

  /* Splash entry animations */
  @keyframes sm-splash-logo { from { opacity:0; transform: scale(0.86) translateY(14px); } to { opacity:1; transform: scale(1) translateY(0); } }
  @keyframes sm-splash-sub  { from { opacity:0; transform: translateY(8px); } to { opacity:1; transform: translateY(0); } }
  @keyframes sm-splash-btn  { from { opacity:0; transform: translateY(10px); } to { opacity:1; transform: translateY(0); } }
  @keyframes sm-splash-rule { from { width:0; opacity:0; } to { width:80px; opacity:1; } }

  /* ── Blur-dissolve page transition ───────────────────────────────────────── */
  @keyframes sm-blur-out { from { opacity:1; filter:blur(0px);   } to { opacity:0; filter:blur(12px); } }
  @keyframes sm-blur-in  { from { opacity:0; filter:blur(12px);  } to { opacity:1; filter:blur(0px);  } }
  .sm-blur-out { animation: sm-blur-out 340ms ease forwards; pointer-events:none; }
  .sm-blur-in  { animation: sm-blur-in  380ms ease forwards; }

  /* Respect prefers-reduced-motion */
  @media (prefers-reduced-motion: reduce) {
    button, .sm-btn, .sm-tab, .sm-card, .sm-row, .sm-clickable, .sm-input { transition: none !important; }
    .sm-badge-pulse, .sm-badge-pulse-amber, .sm-badge-pulse-green,
    .sm-skel, .sm-ring, .sm-tick, .sm-toast-enter, .sm-toast-exit,
    .sm-login-bg, .sm-welcome-bg,
    .sm-fog1, .sm-fog2, .sm-fog3, .sm-fog4 { animation: none !important; }
    .sm-blur-out, .sm-blur-in { animation: none !important; }
    button:not(:disabled):active, .sm-btn:active:not(:disabled), .sm-tab:not(:disabled):active { transform: none !important; }
    .sm-card:hover, .sm-row:hover, .sm-clickable:hover { transform: none !important; }
    .sm-btn-primary:hover:not(:disabled), .sm-btn-danger:hover:not(:disabled) { box-shadow: none !important; }
  }
`;
document.head.appendChild(_style);

// ── THEME PALETTES ────────────────────────────────────────────────────────────
// Two named themes that fill the same property keys. Components read colors via
// the `B` Proxy (below), so swapping the active theme at runtime makes every
// re-rendered subtree pick up the new values.
//
// Semantic naming (don't be thrown off by "white" / "black"):
//   black, dark, card, surface, border  →  background stack, lightest to darkest
//   mid, muted, light, white            →  text stack, dimmest to strongest
//   blue/red/green/orange/gold (+ Lt/Dim) → brand & status accents
//
// Brand accents (blue, red, green, orange, gold) stay close in both themes so
// status pills, charts and gradients keep their identity.
const THEMES = {
  dark: {
    black:"#080810",  dark:"#111118",  card:"#1c1c26",  border:"#2a2a38",
    surface:"#252535",
    mid:"#4a4a5e",   muted:"#7a7a96", light:"#c8c8d8", white:"#f0f0f8",
    blue:"#1134A6",  blueLt:"#2550d4", blueDim:"rgba(37,80,212,0.14)",
    red:"#c0392b",   green:"#27ae60",
    orange:"#e67e22",orangeDim:"rgba(230,126,34,0.12)",
    gold:"#C9A84C",  goldDim:"rgba(201,168,76,0.15)",
    shadow:"0 4px 24px rgba(0,0,0,0.45), 0 1px 6px rgba(0,0,0,0.3)",
    shadowSm:"0 2px 10px rgba(0,0,0,0.35)",
    // Theme-aware overlays — `glass` = translucent floating surface (toasts,
    // popovers, dropdowns); `scrim` = full-screen modal backdrop. Second stops
    // for gradients use `navGradStop` so the nav bar stays cohesive.
    glass:"rgba(28,28,38,0.92)",
    scrim:"rgba(0,0,0,0.75)",
    navGradStop:"rgba(17,17,24,0.98)",
    // `onAccent` = text color that always sits on saturated brand-color
    // backgrounds (blue/green/red buttons, avatar circles, primary CTAs).
    // Stays near-white in BOTH themes — unlike `white` which flips to ink-dark.
    onAccent:"#f5f5f8",
  },
  light: {
    // Background stack — lightest "page" is white-ish; cards/surfaces a touch
    // grayer so they have depth. "black" semantically = page background here.
    black:"#f5f6fa",  dark:"#ffffff",  card:"#ffffff",  border:"#d8dae6",
    surface:"#eef0f5",
    // Text stack — INVERTED. "white" is the darkest ink, "mid"/"muted" the dimmest.
    mid:"#a8a8b8",   muted:"#5a5a72", light:"#26263a", white:"#0d0d18",
    // Same brand accents — blue is slightly darker for AA contrast on white.
    blue:"#0d2c8a",  blueLt:"#1c45c4", blueDim:"rgba(13,44,138,0.10)",
    red:"#b03020",   green:"#1f8c4e",
    orange:"#cf6a10",orangeDim:"rgba(207,106,16,0.10)",
    gold:"#a8893a",  goldDim:"rgba(168,137,58,0.12)",
    shadow:"0 4px 16px rgba(13,13,24,0.10), 0 1px 4px rgba(13,13,24,0.06)",
    shadowSm:"0 2px 8px rgba(13,13,24,0.08)",
    // Glass: very light translucent panel; scrim: dim wash; nav second stop
    // is a soft gray so the sticky bar reads as a paper strip on white.
    glass:"rgba(255,255,255,0.94)",
    scrim:"rgba(13,13,24,0.45)",
    navGradStop:"#f5f6fa",
    // Same near-white "on accent" color — saturated brand bgs need light text
    // even when the rest of the page is light.
    onAccent:"#ffffff",
  },
};

// Active theme — mutable, read on every B.* access via the Proxy below.
// Initialised from localStorage so the user's choice survives reloads.
let _activeTheme = (typeof localStorage !== "undefined" && localStorage.getItem("fieldops-theme") === "light")
  ? THEMES.light : THEMES.dark;
let _activeThemeName = _activeTheme === THEMES.light ? "light" : "dark";

// All components read colors through this Proxy. Each access looks up the
// current theme, so switching theme at runtime instantly affects every render.
const B = new Proxy({}, {
  get(_t, key)      { return _activeTheme[key]; },
  has(_t, key)      { return key in _activeTheme; },
  ownKeys()         { return Object.keys(_activeTheme); },
  getOwnPropertyDescriptor(_t, key) {
    return { enumerable: true, configurable: true, value: _activeTheme[key] };
  },
});

function setActiveTheme(name) {
  const t = THEMES[name] || THEMES.dark;
  _activeTheme = t;
  _activeThemeName = (t === THEMES.light) ? "light" : "dark";
  try { localStorage.setItem("fieldops-theme", _activeThemeName); } catch (_) { /* SSR / private mode */ }
  // Body + html background follow the theme so the area outside React renders
  // matches. Done via direct style mutation since it's outside the render tree.
  if (typeof document !== "undefined") {
    document.documentElement.style.background = t.black;
    document.body.style.background            = t.black;
    document.body.style.color                 = t.light;
    document.documentElement.dataset.theme    = _activeThemeName;
  }
}
function getActiveTheme() { return _activeThemeName; }

// Apply at module load so the initial paint matches the saved preference.
if (typeof document !== "undefined") {
  // Defer to next tick so document.body exists.
  Promise.resolve().then(() => setActiveTheme(_activeThemeName));
}

const STATUS_CONFIG = {
  pending:          { label:"Pending",           color:B.blueLt,   bg:B.blueDim,                    border:B.blue,   icon:""  },
  approved:         { label:"Approved",           color:"#52d9a0",  bg:"rgba(82,217,160,0.12)",       border:"#52d9a0",icon:"✓" },
  completed:        { label:"Completed",          color:B.green,    bg:"rgba(39,174,96,0.15)",        border:B.green,  icon:"✓" },
  rejected:         { label:"Rejected",           color:B.red,      bg:"rgba(192,57,43,0.15)",        border:B.red,    icon:"✗" },
  requires_revisit: { label:"Requires Revisit",   color:B.orange,   bg:B.orangeDim,                  border:B.orange, icon:"⚠" },
  aborted:          { label:"Aborted",             color:"#95a5a6",  bg:"rgba(149,165,166,0.12)",      border:"#7f8c8d", icon:"⊘" },
  incomplete:       { label:"Incomplete",           color:"#f1c40f",  bg:"rgba(241,196,15,0.10)",       border:"#d4ac0d", icon:"◑" },
};

const ACTION_CONFIG = {
  job_created:          { label:"Job Created",                icon:"⊕", color:"#888" },
  job_submitted:        { label:"Job Submitted",              icon:"↑", color:"#1a45c4" },
  job_resubmitted:      { label:"Job Resubmitted",            icon:"↺", color:"#1a45c4" },
  job_approved:         { label:"Job Approved",               icon:"✓", color:"#52d9a0" },
  job_rejected:         { label:"Job Rejected",               icon:"✗", color:"#c0392b" },
  job_completed:        { label:"Job Completed",              icon:"✓", color:"#27ae60" },
  job_requires_revisit: { label:"Flagged: Requires Revisit",  icon:"⚠", color:"#e67e22" },
  job_aborted:          { label:"Job Aborted",                icon:"⊘", color:"#95a5a6" },
  job_incomplete:       { label:"Job Marked Incomplete",      icon:"◑", color:"#f1c40f" },
};

const FORTY_EIGHT_HOURS = 48 * 3600 * 1000;

// NOTE: Passwords are stored in plaintext for prototype purposes.
// MUST be replaced with bcrypt or equivalent hashing before production deployment.
// Users are loaded exclusively from Supabase. This array is intentionally empty
// — it only serves as a fallback if the network is completely unavailable.
const INITIAL_USERS = [];

// ── EMAIL NOTIFICATION SYSTEM ─────────────────────────────────────────────────
//
// sendEmail() is the single integration point for outbound email.
// Replace the console.log body with a real provider call (SendGrid, Postmark,
// AWS SES, etc.) when ready. The envelope shape stays the same.
//
// envelope: {
//   to:       string | string[]   — recipient address(es)
//   subject:  string
//   body:     string              — plain-text body (swap for html: when using templates)
//   metadata: object             — extra context for logging / audit trail
// }
//
function sendEmail(envelope) {
  const recipients = Array.isArray(envelope.to) ? envelope.to : [envelope.to];
  // TODO: replace with real provider (e.g. SendGrid, Postmark, AWS SES)
  // Example SendGrid swap:
  //   await sgMail.send({ to: recipients, from: "noreply@somerville.com",
  //                       subject: envelope.subject, text: envelope.body });
  console.group(`📧 EMAIL — ${envelope.subject}`);
  console.log("To:     ", recipients.join(", "));
  console.log("Subject:", envelope.subject);
  console.log("─────────────────────────────────────────");
  console.log(envelope.body);
  if (Object.keys(envelope.metadata || {}).length) {
    console.log("─────────────────────────────────────────");
    console.log("Metadata:", envelope.metadata);
  }
  console.groupEnd();
}

// Convenience: returns email addresses of all active admin users.
function adminEmails(users) {
  return users.filter(u => u.role === "admin" && u.active !== false).map(u => u.email);
}

// ── Trigger 1: Job submitted → notify all Head Office admins ──
function notifyJobSubmitted(job, users) {
  const to = adminEmails(users);
  if (!to.length) return;
  sendEmail({
    to,
    subject: `New installation submitted — SRO ${job.sroNumber}`,
    body: [
      `A new installation job has been submitted and is awaiting review.`,
      ``,
      `  Installer:    ${job.installerName} <${job.installerEmail}>`,
      `  Site:         ${job.storeName}`,
      `  Address:      ${job.storeAddress}`,
      `  SRO Number:   ${job.sroNumber}`,
      `  RL Number:    ${job.rlNumber || "—"}`,
      `  Type:         ${job.typeSnapshot?.label || "—"}`,
      `  Submitted:    ${new Date(job.submittedAt).toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" })}`,
      ``,
      `  View job:     /jobs/${job.id}`,
      ``,
      `Log in to the Field Operations Platform to review and approve or reject this submission.`,
    ].join("\n"),
    metadata: { jobId: job.id, sroNumber: job.sroNumber, trigger: "job_submitted" },
  });
}

// ── Trigger 2: Job rejected → notify the submitting installer ──
function notifyJobRejected(job, reason, rejectedByName, users) {
  const installer = users.find(u => u.id === job.installerUserId && u.active !== false);
  const to = installer?.email ?? job.installerEmail;
  if (!to) return;
  sendEmail({
    to,
    subject: `Your installation submission has been rejected — SRO ${job.sroNumber}`,
    body: [
      `Hi ${job.installerName},`,
      ``,
      `Your installation submission for the following job has been rejected by Head Office.`,
      ``,
      `  SRO Number:   ${job.sroNumber}`,
      `  RL Number:    ${job.rlNumber || "—"}`,
      `  Site:         ${job.storeName}`,
      `  Address:      ${job.storeAddress}`,
      `  Rejected by:  ${rejectedByName}`,
      reason ? `  Reason:       ${reason}` : `  Reason:       No reason provided.`,
      ``,
      `ACTION REQUIRED`,
      `Please review the rejection reason, correct any outstanding issues, and re-submit`,
      `within 48 hours of the rejection time. If the job is not re-submitted and approved`,
      `within 48 hours it will be automatically flagged as a Warranty Revisit.`,
      ``,
      `  View job:     /jobs/${job.id}`,
      ``,
      `If you have questions, please contact your Head Office coordinator directly.`,
    ].join("\n"),
    metadata: { jobId: job.id, sroNumber: job.sroNumber, installerEmail: to, trigger: "job_rejected" },
  });
}

// ── Trigger 3: Job auto-escalated to requires_revisit → notify all admins ──
function notifyJobRevisit(job, users) {
  const to = adminEmails(users);
  if (!to.length) return;
  sendEmail({
    to,
    subject: `SRO ${job.sroNumber} requires a revisit — warranty visit needed`,
    body: [
      `A previously rejected job has not been resolved within 48 hours and has been`,
      `automatically flagged as requiring a warranty revisit.`,
      ``,
      `  SRO Number:       ${job.sroNumber}`,
      `  RL Number:        ${job.rlNumber || "—"}`,
      `  Site:             ${job.storeName}`,
      `  Address:          ${job.storeAddress}`,
      `  Installer:        ${job.installerName} <${job.installerEmail}>`,
      `  Originally submitted: ${new Date(job.submittedAt).toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" })}`,
      `  Rejected at:      ${job.rejectedAt ? new Date(job.rejectedAt).toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" }) : "—"}`,
      job.rejectionReason ? `  Rejection reason: ${job.rejectionReason}` : "",
      `  Flagged at:       ${new Date().toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" })}`,
      ``,
      `A warranty revisit must be scheduled. Please contact the installer and arrange`,
      `a return visit to complete the installation to the required standard.`,
      ``,
      `  View job:         /jobs/${job.id}`,
    ].filter(l => l !== undefined).join("\n"),
    metadata: { jobId: job.id, sroNumber: job.sroNumber, trigger: "job_requires_revisit" },
  });
}

function notifyJobOutcome(job, users) {
  const admins = users.filter(u => u.role === "admin" && u.active !== false);
  const isAborted = job.status === "aborted";
  const label = isAborted ? "Aborted" : "Incomplete";
  const icon  = isAborted ? "⊘" : "◑";
  admins.forEach(admin => sendEmail({
    // TODO (backend): replace with SendGrid / Postmark call
    // POST /api/notifications/job-outcome
    to: admin.email,
    subject: `${icon} Job ${label} — ${job.sroNumber} · ${job.storeName}`,
    body: [
      `A job has been marked as ${label.toUpperCase()} by ${job.installerName}.`,
      ``,
      `  SRO Number:   ${job.sroNumber}`,
      `  Site Name:    ${job.storeName}`,
      `  Installer:    ${job.installerName}`,
      `  Submitted At: ${new Date(job.submittedAt).toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" })}`,
      `  Reason:       ${job.outcomeReason || "No reason provided."}`,
      ``,
      `  View job:     /jobs/${job.id}`,
    ].join("\n"),
    metadata: { jobId: job.id, sroNumber: job.sroNumber, trigger: `job_${job.status}` },
  }));
}

// ── DATA SERVICE ──────────────────────────────────────────────────────────────
//
// ── SUPABASE CLIENT ───────────────────────────────────────────────────────────
const supabase = createClient(
  "https://yvzbwvcdkbkclbmonrkx.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl2emJ3dmNka2JrY2xibW9ucmt4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzMwNjcyODQsImV4cCI6MjA4ODY0MzI4NH0._j4vx9J2_kqO1S8hiUBiUcVhzHdG0lMXJiAvmjEHCoI"
);

// ── PHOTO COMPRESSION ─────────────────────────────────────────────────────────
// Resizes and re-encodes a photo client-side using Canvas before upload.
// Caps the long edge at 1600 px and encodes as JPEG at 82% quality.
// Typical result: a 6–10 MB phone photo shrinks to ~300–600 KB.
async function compressPhoto(file, maxDimension = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width >= height) {
          height = Math.round(height * maxDimension / width);
          width  = maxDimension;
        } else {
          width  = Math.round(width  * maxDimension / height);
          height = maxDimension;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width  = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error("Canvas compression failed")); return; }
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg", lastModified: Date.now() }));
      }, "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Image load failed")); };
    img.src = url;
  });
}

// ── PHOTO UPLOAD ──────────────────────────────────────────────────────────────
// Compresses then uploads a File to the job-photos Storage bucket.
// Returns {name, url}. Throws on error so callers can show feedback.
async function uploadPhoto(file, folder = "general") {
  // Compress before upload — fails gracefully (uses original if compression errors)
  let toUpload = file;
  if (file.type.startsWith("image/")) {
    try { toUpload = await compressPhoto(file); }
    catch (e) { console.warn("[compress] skipped:", e.message); }
  }
  const path = `jobs/${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`;
  const { error } = await supabase.storage
    .from("job-photos")
    .upload(path, toUpload, { cacheControl: "3600", upsert: false, contentType: "image/jpeg" });
  if (error) {
    console.error("[Supabase] uploadPhoto:", error.message);
    throw new Error(error.message);
  }
  const { data: urlData } = supabase.storage.from("job-photos").getPublicUrl(path);
  return { name: file.name, url: urlData.publicUrl };
}

// ── SRO PDF: UPLOAD + PARSE ──────────────────────────────────────────────────
// Pre-loaded SROs are exported from the Somerville ERP as fixed-template PDFs
// (Service Order Work Order Report). Head office drops them into the SROs tab;
// we parse them client-side with pdf.js and also archive the raw PDF to the
// sro-pdfs Supabase Storage bucket so the installer can view it on-site if
// the parser mangles anything (observed: unicode × encoding artifacts).

// Upload the raw PDF to Storage. Returns { path, filename, sizeBytes }.
async function uploadSroPdf(file) {
  if (!file || file.type !== "application/pdf") {
    throw new Error("Expected a .pdf file");
  }
  const safeName = (file.name || "sro.pdf").replace(/[^A-Za-z0-9._-]+/g, "_");
  const path = `uploads/${Date.now()}-${Math.random().toString(36).slice(2)}-${safeName}`;
  const { error } = await supabase.storage
    .from("sro-pdfs")
    .upload(path, file, { cacheControl: "3600", upsert: false, contentType: "application/pdf" });
  if (error) {
    console.error("[Supabase] uploadSroPdf:", error.message);
    throw new Error(error.message);
  }
  return { path, filename: file.name, sizeBytes: file.size };
}

// ── INSTALLATION INSTRUCTIONS DOC UPLOAD ─────────────────────────────────────
// Admins attach a PDF or Word doc per installation type. The file goes to the
// public-read "installation-docs" bucket; the resulting public URL is stored
// in `installation_types.instructions_url` (same column that previously held
// pasted external URLs — old records keep working unchanged).
const INSTALLATION_DOC_MIME_OK = new Set([
  "application/pdf",
  "application/msword",                                                       // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",  // .docx
]);
const INSTALLATION_DOC_EXT_OK  = [".pdf", ".doc", ".docx"];
const INSTALLATION_DOC_MAX_BYTES = 25 * 1024 * 1024;

async function uploadInstallationDoc(file, installationTypeName) {
  if (!file) throw new Error("No file provided");
  // Validate by extension AND MIME — Safari/older browsers report odd MIMEs for .doc.
  const lowerName = (file.name || "").toLowerCase();
  const ext = INSTALLATION_DOC_EXT_OK.find(e => lowerName.endsWith(e));
  const mimeOk = INSTALLATION_DOC_MIME_OK.has(file.type) || file.type === "" || file.type === "application/octet-stream";
  if (!ext || !mimeOk) {
    throw new Error("Only PDF or Word documents (.pdf, .doc, .docx) are allowed.");
  }
  if (file.size > INSTALLATION_DOC_MAX_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    throw new Error(`File is ${mb} MB — limit is 25 MB.`);
  }
  // Sanitize the type name: lowercase, non-alphanumeric → "-", collapse runs, trim.
  const slug = (installationTypeName || "installation-type")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || "installation-type";
  const path = `${slug}-${Date.now()}${ext}`;
  const contentType = file.type || (
    ext === ".pdf"  ? "application/pdf" :
    ext === ".docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" :
                      "application/msword"
  );
  const { error } = await supabase.storage
    .from("installation-docs")
    .upload(path, file, { cacheControl: "3600", upsert: false, contentType });
  if (error) {
    console.error("[Supabase] uploadInstallationDoc:", error.message);
    throw new Error(error.message);
  }
  const { data: urlData } = supabase.storage.from("installation-docs").getPublicUrl(path);
  return { url: urlData.publicUrl, path, filename: file.name };
}

// Get a short-lived signed URL (1 hour) to display a stored SRO PDF in an iframe.
async function getSroPdfSignedUrl(path, expiresInSeconds = 3600) {
  if (!path) return null;
  const { data, error } = await supabase.storage.from("sro-pdfs").createSignedUrl(path, expiresInSeconds);
  if (error) {
    console.error("[Supabase] getSroPdfSignedUrl:", error.message);
    throw new Error(error.message);
  }
  return data?.signedUrl || null;
}

// Wait for pdf.js to finish loading from CDN (it's injected async at boot).
async function waitForPdfJs(timeoutMs = 10000) {
  const start = Date.now();
  while (!window.pdfjsLib) {
    if (Date.now() - start > timeoutMs) {
      throw new Error("pdf.js failed to load from CDN — check network");
    }
    await new Promise(r => setTimeout(r, 50));
  }
  return window.pdfjsLib;
}

// Pull raw text out of a PDF using pdf.js. Inserts a newline whenever the
// y-coordinate of the next text item changes (approximate reading order).
async function readPdfText(file) {
  const pdfjsLib = await waitForPdfJs();
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  let out = "";
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    // Items are in content.items. Each has .str and .transform (matrix).
    // .transform[5] is y. Group items by y (rounded) to approximate rows.
    const rows = {};
    for (const it of content.items) {
      const y = Math.round(it.transform[5]);
      (rows[y] = rows[y] || []).push({ x: it.transform[4], s: it.str });
    }
    const ys = Object.keys(rows).map(n => parseInt(n, 10)).sort((a, b) => b - a); // top→bottom
    for (const y of ys) {
      const row = rows[y].sort((a, b) => a.x - b.x).map(r => r.s).join(" ");
      out += row.replace(/\s+/g, " ").trim() + "\n";
    }
    out += "\n";
  }
  return out;
}

// Pull structured header fields from the raw text.
function parseSroHeader(text) {
  const pick = re => { const m = re.exec(text); return m ? m[1].trim() : ""; };
  return {
    sroNumber:            pick(/SRO:\s*(OI\d+)/i),
    sroDescription:       pick(/Description:\s*([\s\S]+?)\s+SRO\s+Type:/i),
    sroType:              pick(/SRO\s+Type:\s*([A-Za-z0-9_-]+)/i),
    startDate:            pick(/Start\s+Date:\s*([\d/:\sAMP]+?)(?=\s{2,}|\n|$)/i),
    endDate:              pick(/End\s+Date:\s*([\d/:\sAMP]+?)(?=\s{2,}|\n|$)/i),
    customerCode:         pick(/Customer:\s*(\S+)/i),
    customerName:         pick(/Bill\s+To:\s*\S+\s+(.+?)\s+End\s+Date:/i),
    shipTo:               pick(/Ship\s+To:\s*([\s\S]+?)\s+Start\s+Date:/i),
    billTo:               pick(/Bill\s+To:\s*(\S+)/i),
    custPo:               pick(/Cust\s+PO:\s*([^\n]*?)\s*OLG\s+Sales\s+Specialist:/i),
    olgSalesSpecialist:   pick(/OLG\s+Sales\s+Specialist:\s*(\S+)/i),
    olgDistrictManager:   pick(/OLG\s+District\s+Manager:\s*(\S+)/i),
    salesperson:          pick(/Salesperson\s*:?\s*(\S+)/i),
    shipVia:              pick(/Ship\s+Via:\s*(\S+)/i),
  };
}

// Pull the store/site block between "Contact:…Phone:" and "STORE CONTACT:".
// RL# is identified as the standalone all-numeric line — robust to the
// country line being optional (missing in RL 131175).
function parseSroSite(text, warnings) {
  const m = /Contact:[^\n]*Phone:[^\n]*\n([\s\S]*?)STORE\s+CONTACT:\s*([^\n]*)/i.exec(text);
  if (!m) {
    warnings.push("Site block not found (Contact…STORE CONTACT: missing)");
    return {};
  }
  const block = m[1].trim();
  const phoneLine = m[2].trim();
  const phone = (phoneLine.match(/\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/) || [])[0] || phoneLine;
  const lines = block.split(/\n/).map(s => s.trim()).filter(Boolean);

  // Find the RL# — a line that is purely digits, length 4-8
  const rlIdx = lines.findIndex(l => /^\d{4,8}$/.test(l));
  if (rlIdx < 0) warnings.push("RL# not found in site block");

  const storeName   = rlIdx > 1 ? lines.slice(0, rlIdx - 1).join(" ") : (lines[0] || "");
  const street      = rlIdx > 0 ? lines[rlIdx - 1] : "";
  const rlNumber    = rlIdx >= 0 ? lines[rlIdx] : "";
  const cityLine    = rlIdx >= 0 ? (lines[rlIdx + 1] || "") : "";
  const nextLine    = rlIdx >= 0 ? (lines[rlIdx + 2] || "") : "";

  // Parse "CITY PROV POSTAL" — postal can be "L1T 1W2" or "N5P1E2" (no space)
  const cm = /^(.+?)\s+([A-Z]{2})\s+([A-Z]\d[A-Z]\s*\d[A-Z]\d)$/i.exec(cityLine);
  const storeCity       = cm ? cm[1].trim() : cityLine;
  const storeProvince   = cm ? cm[2].toUpperCase() : "";
  const storePostalCode = cm ? cm[3].replace(/\s+/g, " ").toUpperCase() : "";
  if (!cm) warnings.push(`City/Prov/Postal not parseable from: "${cityLine}"`);

  // Country is optional — present if the line is a 2-3 letter all-caps token
  const storeCountry = /^[A-Z]{2,3}$/.test(nextLine) ? nextLine : "";

  return { storeName, storeStreet: street, storeCity, storeProvince, storePostalCode, storeCountry, storePhone: phone, rlNumber };
}

// Notes = any free-text between the STORE CONTACT line and the first "Line:" row.
function parseSroNotes(text) {
  const m = /STORE\s+CONTACT:[^\n]*\n([\s\S]*?)\n\s*Line:\s*\d+/i.exec(text);
  if (!m) return "";
  return m[1].split(/\n/).map(s => s.trim()).filter(Boolean).join(" ");
}

// Extract line items from the Material/Trans table. Robust to item codes that
// wrap onto a second line (DELIVER-L POSTAL / CODE) and multi-line descriptions.
function parseSroLineItems(text, servicesMap, warnings) {
  const m = /Trans\s+Num\s+Trans\s+Date[\s\S]*?Description\s*\n([\s\S]*?)(?:SMI\d+|TSABANIS|Page\s+\d)/i.exec(text);
  if (!m) {
    warnings.push("Line-item table not found");
    return [];
  }
  const body = m[1];
  const lines = body.split(/\n/).map(s => s.trim()).filter(Boolean);
  const rowAnchor = /^(\d+)\s+(\d{1,2}\/\d{1,2}\/\d{4})\s+(.+?)\s+(\d+\.\d+)\s+([A-Za-z]+)$/;
  const items = [];
  let cur = null;
  for (const line of lines) {
    const rm = rowAnchor.exec(line);
    if (rm) {
      if (cur) items.push(finalizeItem(cur, servicesMap));
      cur = {
        lineNo:        parseInt(rm[1], 10),
        transDate:     rm[2],
        itemCodeRaw:   rm[3].trim(),
        qty:           parseFloat(rm[4]),
        uom:           rm[5].toUpperCase(),
        descParts:     [],
      };
    } else if (cur) {
      cur.descParts.push(line);
    }
  }
  if (cur) items.push(finalizeItem(cur, servicesMap));
  return items;
}

function finalizeItem(raw, servicesMap) {
  // First whitespace token of the item column is the primary item code.
  // Anything after is description overflow, but if the NEXT description line
  // looks like a code-continuation (all-caps, short, no spaces), merge it.
  const codeParts = raw.itemCodeRaw.split(/\s+/);
  let itemCode = codeParts[0];
  const descOverflow = codeParts.slice(1).join(" ");
  const descLines = raw.descParts.slice();

  // Heuristic: for delivery charges the item code is exactly DELIVER-L / DELIVER-N.
  // We don't try to concatenate "POSTAL CODE" into the code — the canonical
  // match set is the services library and these aren't in it anyway.

  const description = [descOverflow, ...descLines].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  const key = itemCode.toUpperCase();
  // servicesMap is { codeUpper -> { estimatedMinutes } }. Falsy when caller didn't supply one.
  const svcEntry = servicesMap ? servicesMap.get(key) : null;
  const isService = !!svcEntry;
  // Estimated minutes from the services library (null if not a service or no time set).
  const estimatedMinutes = svcEntry?.estimatedMinutes ?? null;
  return {
    lineNo:      raw.lineNo,
    transDate:   raw.transDate,
    itemCode,
    description,
    qty:         raw.qty,
    uom:         raw.uom,
    isService,
    estimatedMinutes,
  };
}

// Resolve estimated minutes for a single line item. Older SROs stored in the
// database don't have estimatedMinutes baked in (the field was added later);
// for those, fall back to a live lookup against the services list.
function lineItemMinutes(li, services) {
  if (Number.isFinite(li?.estimatedMinutes) && li.estimatedMinutes > 0) return li.estimatedMinutes;
  const code = String(li?.itemCode || "").toUpperCase();
  if (!code) return null;
  const svc = (services || []).find(s => String(s.service_id || "").toUpperCase() === code);
  return Number.isFinite(svc?.estimated_minutes) ? svc.estimated_minutes : null;
}

// Format minutes as "1h 25m" / "45m" / "" for nice display.
function formatMinutes(mins) {
  if (!Number.isFinite(mins) || mins <= 0) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// Sum estimated minutes across line items, multiplying by qty (rounded). Returns
// { total, byCount } where byCount = number of items that contributed.
function totalSroMinutes(lineItems, services) {
  let total = 0, byCount = 0;
  (lineItems || []).forEach(li => {
    if (!li?.isService) return;
    const m = lineItemMinutes(li, services);
    if (!Number.isFinite(m) || m <= 0) return;
    const q = Number.isFinite(li.qty) && li.qty > 0 ? Math.round(li.qty) : 1;
    total += m * q;
    byCount++;
  });
  return { total, byCount };
}

// Top-level parser: takes a File + services library, returns a structured SRO.
// Caller should pass an array of known service IDs so is_service can be resolved
// inline. Returns { header, site, notes, lineItems, rawText, warnings }.
async function parseSroPdf(file, servicesLibrary = []) {
  const warnings = [];
  // Build { CODE_UPPER -> { estimatedMinutes } } so the parser can stash time
  // info on each matched line item at parse time. Falsy estimated_minutes → null.
  const servicesMap = new Map();
  (servicesLibrary || []).forEach(s => {
    const key = String(s.service_id || s.serviceId || "").toUpperCase();
    if (!key) return;
    const min = Number.isFinite(s.estimated_minutes) ? s.estimated_minutes
              : Number.isFinite(s.estimatedMinutes)  ? s.estimatedMinutes
              : null;
    servicesMap.set(key, { estimatedMinutes: min });
  });
  const rawText = await readPdfText(file);
  const header = parseSroHeader(rawText);
  const site = parseSroSite(rawText, warnings);
  const notes = parseSroNotes(rawText);
  const lineItems = parseSroLineItems(rawText, servicesMap, warnings);
  if (!header.sroNumber) warnings.push("SRO number not found");
  return { header, site, notes, lineItems, rawText, warnings };
}

// ── ROW MAPPERS ───────────────────────────────────────────────────────────────
// Convert between the app's camelCase in-memory shape and the Supabase
// snake_case column names.  All nullable columns are handled gracefully.

// Map a job object → Supabase row for INSERT / UPDATE
const jobToRow = job => ({
  sro_number:           job.sroNumber           ?? null,
  rl_number:            job.rlNumber             ?? null,
  store_name:           job.storeName            ?? null,
  store_address:        job.storeAddress         ?? null,
  installation_type_id: job.installationTypeId   ?? null,
  type_snapshot:        job.typeSnapshot         ?? null,
  installer_name:       job.installerName        ?? null,
  installer_email:      job.installerEmail       ?? null,
  installer_user_id:    job.installerUserId      ?? null,
  second_installer:     job.secondInstaller      ?? null,
  status:               job.status               ?? null,
  submitted_at:         job.submittedAt          ?? null,
  start_time:           job.startTime            ?? null,
  end_time:             job.endTime              ?? null,
  clock_in_location:    job.clockInLocation      ?? null,
  submit_location:      job.submitLocation       ?? null,
  answers:              job.answers              ?? null,
  photos:               job.photos?.map(p => ({ name: p.name || "", url: p.url || p.data })) ?? null,
  outcome_photos:       job.outcomePhotos?.map(p => ({ name: p.name || "", url: p.url || p.data })) ?? null,
  signature_data:       job.signatureData        ?? null,
  site_contact_name:    job.siteContactName      ?? null,
  site_contact_title:   job.siteContactTitle     ?? null,
  site_contact_phone:   job.siteContactPhone     ?? null,
  approved_by:          job.approvedBy           ?? null,
  approved_at:          job.approvedAt           ?? null,
  rejected_by:          job.rejectedBy           ?? null,
  rejected_at:          job.rejectedAt           ?? null,
  rejection_reason:     job.rejectionReason      ?? null,
  completed_at:         job.completedAt          ?? null,
  completed_by:         job.completedBy          ?? null,
  outcome_reason:       job.outcomeReason        ?? null,
  outcome_mode:         job.outcomeMode          ?? null,
  status_history:       job.statusHistory        ?? null,
  audit_log:            job.auditLog             ?? null,
  revisit_flagged_at:   job.revisitFlaggedAt     ?? null,
  excluded_from_reports: job.excludedFromReports   ?? false,
});

// Map a Supabase row → in-memory job object
const rowToJob = row => ({
  id:                  row.id,
  sroNumber:           row.sro_number,
  rlNumber:            row.rl_number,
  storeName:           row.store_name,
  storeAddress:        row.store_address,
  installationTypeId:  row.installation_type_id,
  typeSnapshot:        row.type_snapshot,
  installerName:       row.installer_name,
  installerEmail:      row.installer_email,
  installerUserId:     row.installer_user_id,
  secondInstaller:     row.second_installer,
  status:              row.status,
  submittedAt:         row.submitted_at,
  startTime:           row.start_time,
  endTime:             row.end_time,
  clockInLocation:     row.clock_in_location,
  submitLocation:      row.submit_location,
  answers:             row.answers,
  photos:              row.photos?.map(p => typeof p === "string" ? { url: p } : p) ?? [],
  outcomePhotos:       row.outcome_photos?.map(p => typeof p === "string" ? { url: p } : p) ?? [],
  signatureData:       row.signature_data,
  siteContactName:     row.site_contact_name,
  siteContactTitle:    row.site_contact_title,
  siteContactPhone:    row.site_contact_phone,
  approvedBy:          row.approved_by,
  approvedAt:          row.approved_at,
  rejectedBy:          row.rejected_by,
  rejectedAt:          row.rejected_at,
  rejectionReason:     row.rejection_reason,
  completedAt:         row.completed_at,
  completedBy:         row.completed_by,
  outcomeReason:       row.outcome_reason,
  outcomeMode:         row.outcome_mode,
  statusHistory:       row.status_history,
  auditLog:            row.audit_log,
  revisitFlaggedAt:    row.revisit_flagged_at,
  excludedFromReports: row.excluded_from_reports ?? false,
});

// Map a Supabase users row → in-memory user object
const rowToUser = row => ({
  id:        row.id,
  name:      row.name,
  email:     row.email,
  password:  row.password,
  role:      row.role,
  active:    row.active,
  createdAt: row.created_at,
});

// All data operations go through this object.  Every method is async and
// returns a Promise so that calling components are already written in the
// pattern required by a real backend.
//
// HOW TO SWAP IN A REAL BACKEND (e.g. Express + PostgreSQL, Supabase, etc.):
//   1. Replace each method body with the equivalent fetch() / axios / SDK call.
//   2. Remove in-memory id generation (Date.now()) — let the server assign IDs.
//   3. After each mutation, either use the server's response directly (as shown)
//      or trigger a full refetch of the relevant collection.
//   4. Move authentication tokens / headers into a shared request utility.
//
// Each method is annotated with the HTTP verb + endpoint it will map to.
//
const dataService = {

  // ── JOBS ──────────────────────────────────────────────────────────────────

  async deleteJob(jobId) {
    const { error } = await supabase.from("jobs").delete().eq("id", jobId);
    if (error) throw new Error(error.message);
  },

  async toggleExcludeFromReports(job) {
    const newVal = !job.excludedFromReports;
    const { error } = await supabase.from("jobs").update({ excluded_from_reports: newVal }).eq("id", job.id);
    if (error) throw new Error(error.message);
    return { ...job, excludedFromReports: newVal };
  },

  async submitJob(payload) {
    const { data, error } = await supabase
      .from("jobs")
      .insert(jobToRow(payload))
      .select()
      .single();
    if (error) console.error("[Supabase] submitJob:", error.message);
    // Use the UUID Supabase auto-generated as the job's id
    return { ...payload, id: data?.id ?? crypto.randomUUID() };
  },

  async approveJob(job, actorName) {
    const ts = new Date().toISOString();
    const updated = {
      ...job,
      status:        "approved",
      approvedBy:    actorName,
      approvedAt:    ts,
      statusHistory: [...(job.statusHistory || []), { status:"approved", timestamp:ts, actor:actorName }],
      auditLog:      [...(job.auditLog      || []), { action:"job_approved", actor:actorName, timestamp:ts, note:null }],
    };
    const { error } = await supabase.from("jobs").update({
      status:         updated.status,
      approved_by:    updated.approvedBy,
      approved_at:    updated.approvedAt,
      status_history: updated.statusHistory,
      audit_log:      updated.auditLog,
    }).eq("id", job.id);
    if (error) console.error("[Supabase] approveJob:", error.message);
    return updated;
  },

  async completeJob(job, actorName) {
    const ts = new Date().toISOString();
    const updated = {
      ...job,
      status:        "completed",
      completedAt:   ts,
      completedBy:   actorName,
      statusHistory: [...(job.statusHistory || []), { status:"completed", timestamp:ts, actor:actorName }],
      auditLog:      [...(job.auditLog      || []), { action:"job_completed", actor:actorName, timestamp:ts, note:null }],
    };
    const { error } = await supabase.from("jobs").update({
      status:         updated.status,
      completed_at:   updated.completedAt,
      completed_by:   updated.completedBy,
      status_history: updated.statusHistory,
      audit_log:      updated.auditLog,
    }).eq("id", job.id);
    if (error) console.error("[Supabase] completeJob:", error.message);
    return updated;
  },

  async rejectJob(job, reason, actorName) {
    const ts = new Date().toISOString();
    const updated = {
      ...job,
      status:          "rejected",
      rejectedBy:      actorName,
      rejectedAt:      ts,
      rejectionReason: reason,
      statusHistory: [...(job.statusHistory || []), { status:"rejected", timestamp:ts, actor:actorName, note:reason }],
      auditLog:      [...(job.auditLog      || []), { action:"job_rejected", actor:actorName, timestamp:ts, note:reason }],
    };
    const { error } = await supabase.from("jobs").update({
      status:           updated.status,
      rejected_by:      updated.rejectedBy,
      rejected_at:      updated.rejectedAt,
      rejection_reason: updated.rejectionReason,
      status_history:   updated.statusHistory,
      audit_log:        updated.auditLog,
    }).eq("id", job.id);
    if (error) console.error("[Supabase] rejectJob:", error.message);
    return updated;
  },

  async escalateJob(job) {
    const ts = new Date().toISOString();
    const updated = {
      ...job,
      status:           "requires_revisit",
      revisitFlaggedAt: ts,
      statusHistory: [...(job.statusHistory || []), { status:"requires_revisit", timestamp:ts, actor:"System (auto-escalated after 48h)" }],
    };
    const { error } = await supabase.from("jobs").update({
      status:             updated.status,
      revisit_flagged_at: updated.revisitFlaggedAt,
      status_history:     updated.statusHistory,
    }).eq("id", job.id);
    if (error) console.error("[Supabase] escalateJob:", error.message);
    return updated;
  },

  // ── INSTALLATION TYPES ────────────────────────────────────────────────────

  // TODO: POST /api/installation-types
  // In production: const res = await fetch("/api/installation-types", { method:"POST",
  //   body:JSON.stringify(data) });  return res.json(); // server assigns id
  async createInstallationType(data) {
    const docs = Array.isArray(data.instructionsDocs) ? data.instructionsDocs : [];
    const { data: row, error } = await supabase
      .from("installation_types")
      .insert({
        label:             data.label,
        icon:              data.icon || "◈",
        questions:         data.questions || [],
        instructions_docs: docs,
        instructions_url:  docs[0]?.url || data.instructionsUrl || null,  // mirror first doc for legacy readers
        created_by:        data.createdBy || null,
        updated_by:        data.createdBy || null,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return rowToInstallationType(row);
  },

  async updateInstallationType(type) {
    const docs = Array.isArray(type.instructionsDocs) ? type.instructionsDocs : [];
    const { data: row, error } = await supabase
      .from("installation_types")
      .update({
        label:             type.label,
        icon:              type.icon,
        questions:         type.questions || [],
        instructions_docs: docs,
        instructions_url:  docs[0]?.url || null,  // mirror first doc for legacy readers
        updated_by:        type.updatedBy || null,
        updated_at:        new Date().toISOString(),
      })
      .eq("id", type.id)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return rowToInstallationType(row);
  },

  async deleteInstallationType(id) {
    const { error } = await supabase.from("installation_types").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },

  // ── USERS ─────────────────────────────────────────────────────────────────

  // TODO: POST /api/users
  // NOTE: password must be hashed server-side; never send plaintext over the wire
  //       in production (use HTTPS + let the server hash with bcrypt/argon2).
  // In production: const res = await fetch("/api/users", { method:"POST",
  //   body:JSON.stringify(data) });  return res.json(); // server hashes pw, assigns id/createdAt
  async createUser(data) {
    const { data: res, error } = await supabase.functions.invoke("create-user", {
      body: data,
    });
    if (error) {
      console.error("[Supabase] createUser edge function:", error.message);
      throw new Error(error.message);
    }
    if (res?.error) {
      console.error("[Supabase] createUser:", res.error);
      throw new Error(res.error);
    }
    return res.user;
  },

  async updateUser(existingUser, updates) {
    const { name, email, role, password } = updates;
    const { data: res, error } = await supabase.functions.invoke("update-user", {
      body: {
        id: existingUser.id,
        ...(name     && { name }),
        ...(email    && { email }),
        ...(role     && { role }),
        ...(password && { password }),
      },
    });
    if (error) {
      console.error("[Supabase] updateUser edge function:", error.message);
      throw new Error(error.message);
    }
    if (res?.error) {
      console.error("[Supabase] updateUser:", res.error);
      throw new Error(res.error);
    }
    return res.user;
  },

  // TODO: PATCH /api/users/:id  →  { active }
  // In production: const res = await fetch(`/api/users/${existingUser.id}`,
  //   { method:"PATCH", body:JSON.stringify({ active }) });  return res.json();
  async setUserActive(existingUser, active) {
    const { error } = await supabase.from("users").update({ active }).eq("id", existingUser.id);
    if (error) throw new Error(error.message);
    return { ...existingUser, active };
  },

  // ── SERVICES LIBRARY ──────────────────────────────────────────────────────
  async loadServices() {
    const { data, error } = await supabase.from("services").select("*").order("service_id", { ascending: true });
    if (error) { console.error("[Supabase] loadServices:", error.message); throw new Error(error.message); }
    return data;
  },
  async createService({ serviceId, description }) {
    const { data: row, error } = await supabase.from("services")
      .insert({ service_id: serviceId.trim().toUpperCase(), description: description.trim(), active: true, obsolete: false })
      .select().single();
    if (error) { console.error("[Supabase] createService:", error.message); throw new Error(error.message); }
    return row;
  },
  async updateService(id, updates) {
    const { error } = await supabase.from("services").update(updates).eq("id", id);
    if (error) { console.error("[Supabase] updateService:", error.message); throw new Error(error.message); }
  },

  // ── PRE-LOADED SROs ───────────────────────────────────────────────────────
  // Head-office-curated SROs that installers can claim on-site. See
  // sro_preload_schema.sql for the table definition.

  async loadPreloadedSros({ status = null } = {}) {
    let q = supabase.from("pre_loaded_sros").select("*").order("uploaded_at", { ascending: false });
    if (status) q = q.eq("status", status);
    const { data, error } = await q;
    if (error) { console.error("[Supabase] loadPreloadedSros:", error.message); throw new Error(error.message); }
    return (data || []).map(sroRowToObj);
  },

  // Save a parsed SRO plus the archived PDF path. Returns the saved row.
  async savePreloadedSro({ parsed, pdf, uploadedBy }) {
    const row = {
      sro_number:           parsed.header.sroNumber,
      rl_number:            parsed.site.rlNumber || null,
      sro_description:      parsed.header.sroDescription || null,
      sro_type:             parsed.header.sroType || null,
      start_date:           parseDateOrNull(parsed.header.startDate),
      end_date:             parseDateOrNull(parsed.header.endDate),
      customer_code:        parsed.header.customerCode || null,
      customer_name:        parsed.header.customerName || null,
      ship_to:              parsed.header.shipTo || null,
      bill_to:              parsed.header.billTo || null,
      cust_po:              parsed.header.custPo || null,
      olg_sales_specialist: parsed.header.olgSalesSpecialist || null,
      olg_district_manager: parsed.header.olgDistrictManager || null,
      salesperson:          parsed.header.salesperson || null,
      ship_via:             parsed.header.shipVia || null,
      store_name:           parsed.site.storeName || null,
      store_street:         parsed.site.storeStreet || null,
      store_city:           parsed.site.storeCity || null,
      store_province:       parsed.site.storeProvince || null,
      store_postal_code:    parsed.site.storePostalCode || null,
      store_country:        parsed.site.storeCountry || null,
      store_phone:          parsed.site.storePhone || null,
      site_notes:           parsed.notes || null,
      line_items:           parsed.lineItems || [],
      raw_text:             parsed.rawText || null,
      parse_warnings:       parsed.warnings || [],
      pdf_path:             pdf?.path || null,
      pdf_filename:         pdf?.filename || null,
      pdf_size_bytes:       pdf?.sizeBytes || null,
      uploaded_by:          uploadedBy || null,
      status:               "active",
    };
    const { data, error } = await supabase
      .from("pre_loaded_sros")
      .insert(row)
      .select()
      .single();
    if (error) { console.error("[Supabase] savePreloadedSro:", error.message); throw new Error(error.message); }
    return sroRowToObj(data);
  },

  // Atomically claim an SRO for an installer. Will only succeed if the SRO
  // is still in 'active' status (prevents two installers grabbing the same
  // job). Returns null if someone already claimed it first.
  async claimPreloadedSro(sroId, userId) {
    const ts = new Date().toISOString();
    const { data, error } = await supabase
      .from("pre_loaded_sros")
      .update({ status: "claimed", claimed_by_user_id: userId, claimed_at: ts })
      .eq("id", sroId)
      .eq("status", "active")  // optimistic lock
      .select()
      .single();
    if (error) {
      // PGRST116 = "returned 0 rows" — someone else won the race
      if (error.code === "PGRST116") return null;
      console.error("[Supabase] claimPreloadedSro:", error.message);
      throw new Error(error.message);
    }
    return sroRowToObj(data);
  },

  async releasePreloadedSro(sroId) {
    const { error } = await supabase
      .from("pre_loaded_sros")
      .update({ status: "active", claimed_by_user_id: null, claimed_at: null })
      .eq("id", sroId);
    if (error) { console.error("[Supabase] releasePreloadedSro:", error.message); throw new Error(error.message); }
  },

  async completePreloadedSro(sroId, jobId) {
    const { error } = await supabase
      .from("pre_loaded_sros")
      .update({ status: "completed", job_id: jobId })
      .eq("id", sroId);
    if (error) { console.error("[Supabase] completePreloadedSro:", error.message); throw new Error(error.message); }
  },

  async archivePreloadedSro(sroId) {
    const { error } = await supabase.from("pre_loaded_sros").update({ status: "archived" }).eq("id", sroId);
    if (error) throw new Error(error.message);
  },

  async deletePreloadedSro(sroId, pdfPath) {
    if (pdfPath) {
      const { error: storageErr } = await supabase.storage.from("sro-pdfs").remove([pdfPath]);
      if (storageErr) console.warn("[Supabase] deletePreloadedSro: storage cleanup:", storageErr.message);
    }
    const { error } = await supabase.from("pre_loaded_sros").delete().eq("id", sroId);
    if (error) throw new Error(error.message);
  },
};

// ── SRO row helpers ──────────────────────────────────────────────────────────
function parseDateOrNull(s) {
  if (!s) return null;
  // Accept "4/16/2026 7:50:55 AM" or plain "4/16/2026"
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s.trim());
  if (!m) return null;
  return `${m[3]}-${String(m[1]).padStart(2,"0")}-${String(m[2]).padStart(2,"0")}`;
}

// Convert an installation_types row into the camelCase shape the React code expects.
// Handles the schema transition from a single instructions_url string to an
// instructions_docs jsonb array. Legacy rows with only instructions_url get a
// synthetic single-entry array so they keep displaying correctly.
function rowToInstallationType(row) {
  const arr  = Array.isArray(row.instructions_docs) ? row.instructions_docs : [];
  const docs = arr.length > 0
    ? arr
    : (row.instructions_url
        ? [{ url: row.instructions_url, path: null, filename: "Installation Instructions",
             uploaded_at: null, uploaded_by: null, _legacy: true }]
        : []);
  return {
    id:               row.id,
    label:            row.label,
    icon:             row.icon,
    questions:        row.questions || [],
    instructionsDocs: docs,
    instructionsUrl:  docs[0]?.url || null,         // legacy convenience for code paths still reading .instructionsUrl
    createdBy:        row.created_by,
    createdAt:        row.created_at,
    updatedBy:        row.updated_by,
    updatedAt:        row.updated_at,
  };
}

function sroRowToObj(r) {
  if (!r) return null;
  return {
    id:                   r.id,
    sroNumber:            r.sro_number,
    rlNumber:             r.rl_number,
    sroDescription:       r.sro_description,
    sroType:              r.sro_type,
    startDate:            r.start_date,
    endDate:              r.end_date,
    customerCode:         r.customer_code,
    customerName:         r.customer_name,
    shipTo:               r.ship_to,
    billTo:               r.bill_to,
    custPo:               r.cust_po,
    olgSalesSpecialist:   r.olg_sales_specialist,
    olgDistrictManager:   r.olg_district_manager,
    salesperson:          r.salesperson,
    shipVia:              r.ship_via,
    storeName:            r.store_name,
    storeStreet:          r.store_street,
    storeCity:            r.store_city,
    storeProvince:        r.store_province,
    storePostalCode:      r.store_postal_code,
    storeCountry:         r.store_country,
    storePhone:           r.store_phone,
    siteNotes:            r.site_notes,
    lineItems:            r.line_items || [],
    rawText:              r.raw_text,
    parseWarnings:        r.parse_warnings || [],
    pdfPath:              r.pdf_path,
    pdfFilename:          r.pdf_filename,
    pdfSizeBytes:         r.pdf_size_bytes,
    uploadedBy:           r.uploaded_by,
    uploadedAt:           r.uploaded_at,
    status:               r.status,
    claimedByUserId:      r.claimed_by_user_id,
    claimedAt:            r.claimed_at,
    jobId:                r.job_id,
    createdAt:            r.created_at,
    updatedAt:            r.updated_at,
  };
}

// Unified installation type + question schema.
// answerType:   "confirm" (Yes/No/N/A) | "text" (free text) | "both" (Y/N/NA + text) | "none" (instruction only)
// photoMode:    "none" | "optional" | "required"
// commentMode:  "optional" (show comment box) | "hidden" (no comment box) | "barcode" (camera scan, required)
const INITIAL_INSTALLATION_TYPES = [
  // Installation types are loaded from Supabase. Only WJS Sign is kept here
  // as a fallback in case the network is unavailable on first load.
  { id:"wjs", label:"WJS Sign", icon:"◼", instructionsUrl:"https://example.com/wjs.pdf", questions:[
    {id:"wjs1", text:"Wall surface inspected and confirmed suitable for mounting?",       answerType:"confirm", photoMode:"none"},
    {id:"wjs2", text:"Mounting template positioned and level confirmed before drilling?", answerType:"confirm", photoMode:"none"},
    {id:"wjs3", text:"Anchor type — describe wall construction and anchor used:",         answerType:"text",    photoMode:"none",     placeholder:"e.g. Concrete anchors into CMU block"},
    {id:"wjs4", text:"Sign face confirmed undamaged prior to installation?",              answerType:"confirm", photoMode:"optional"},
    {id:"wjs5", text:"Sign installed level — horizontal tolerance within spec?",          answerType:"confirm", photoMode:"none"},
    {id:"wjs6", text:"All mounting hardware torqued and secure?",                         answerType:"confirm", photoMode:"none"},
    {id:"wjs7", text:"Gap between sign and wall surface acceptable / per spec?",          answerType:"confirm", photoMode:"none"},
    {id:"wjs8", text:"Final installed position photo captured?",                          answerType:"confirm", photoMode:"required"},
  ]},
];

const initialForm = () => ({
  // entryPath: null = gateway not yet answered, "template" = project template path,
  // "preloaded_sro" = pre-loaded SRO path (Regular Installs, PDF-based)
  entryPath:null,
  sroNumber:"", rlNumber:"", storeName:"", storeAddress:"",
  secondInstaller:"", installationTypeId:"",
  startTime:null, endTime:null,
  answers:{}, photos:[],
  siteContactName:"", siteContactTitle:"", siteContactPhone:"",
  signatureData:null,
  clockInLocation:null,
  // Pre-loaded SRO fields (populated when the installer claims a preloaded SRO):
  preloadedSroId:null,          // uuid of the pre_loaded_sros row — also used as the atomic claim lock
  preloadedLineItems:null,      // array of parsed line items (with is_service flag)
  preloadedSiteNotes:"",        // free-text notes from the SRO PDF
  preloadedPdfPath:null,        // storage path for the source PDF (for the in-app viewer)
  preloadedPdfFilename:null,
});

const fmt     = iso => iso ? new Date(iso).toLocaleString("en-CA",{dateStyle:"medium",timeStyle:"short"}) : "—";
const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString("en-CA",{hour:"2-digit",minute:"2-digit",second:"2-digit"}) : "—";
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString("en-CA",{dateStyle:"medium"}) : "—";
const elapsed = (start, end) => {
  if (!start) return null;
  const ms = (end ? new Date(end) : new Date()) - new Date(start);
  const h = Math.floor(ms/3600000), m = Math.floor((ms%3600000)/60000);
  return `${h}h ${m}m`;
};

// ── GEOLOCATION ───────────────────────────────────────────────────────────────
// Resolves to { lat, lng } on success, or null if unavailable / denied / timed out.
// Behaviour:
//   • enableHighAccuracy: true by default (uses GPS where available — meaningfully
//     more accurate indoors than network/wifi positioning).
//   • 8s timeout, no cached fix unless opts.maximumAge is supplied.
//   • Retries ONCE on a TIMEOUT (code 3) or POSITION_UNAVAILABLE (code 2). A
//     PERMISSION_DENIED (code 1) returns immediately — retrying that won't help.
// opts mirrors PositionOptions — pass { maximumAge: 60000 } to allow a cached fix.
function captureLocation(opts = {}) {
  const merged = { enableHighAccuracy: true, timeout: 8000, maximumAge: 0, ...opts };
  return new Promise(resolve => {
    if (!navigator?.geolocation) { resolve(null); return; }
    const ok   = pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    const fail = err => {
      // err.code: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
      if (err && err.code === 1) { resolve(null); return; }
      // Retry once for transient failures (no signal, timed out indoors, etc.).
      navigator.geolocation.getCurrentPosition(ok, () => resolve(null), merged);
    };
    navigator.geolocation.getCurrentPosition(ok, fail, merged);
  });
}

// ── EXCEL EXPORT ──────────────────────────────────────────────────────────────
// filterKey: "all"|"pending"|"approved"|"completed"|"rejected"|"requires_revisit"
// datePreset: "all"|"thisMonth"|"lastMonth"|"nav"|"custom"
// Produces: [prefix]-jobs-[month]-[year].xlsx  or  [prefix]-jobs-export-[date].xlsx
function deriveExportFilename(filterKey, datePreset, navYear, navMonth) {
  const MONTH_ABBR = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
  const prefix = (filterKey === "requires_revisit" ? "requires-revisit"
                : filterKey === "all"              ? "all"
                : filterKey)                       + "-jobs";
  const today = new Date();
  if (datePreset === "thisMonth") {
    return `${prefix}-${MONTH_ABBR[today.getMonth()]}-${today.getFullYear()}.xlsx`;
  }
  if (datePreset === "lastMonth") {
    const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return `${prefix}-${MONTH_ABBR[lm.getMonth()]}-${lm.getFullYear()}.xlsx`;
  }
  if (datePreset === "nav") {
    return `${prefix}-${MONTH_ABBR[navMonth]}-${navYear}.xlsx`;
  }
  // custom range or no date filter — use today's date
  const iso = today.toISOString().slice(0, 10);
  return `${prefix}-export-${iso}.xlsx`;
}

function exportToExcel(jobs, filterKey, { datePreset, navYear, navMonth, dateFrom, dateTo }) {
  const XLSX = window.XLSX;
  if (!XLSX) {
    console.error("[Export] SheetJS (window.XLSX) is not loaded yet. Try again in a moment.");
    return;
  }
  if (jobs.length === 0) {
    console.warn("[Export] No jobs to export for the current filter.");
    return;
  }

  const fmtCell = iso =>
    iso ? new Date(iso).toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" }) : "";

  const FILTER_LABELS = {
    all:"All Jobs", pending:"Pending Jobs", approved:"Approved Jobs",
    completed:"Completed Jobs", rejected:"Rejected Jobs", requires_revisit:"Requires Revisit",
    aborted:"Aborted Jobs", incomplete:"Incomplete Jobs",
  };

  // Build row data — one object per job, keys become column headers
  const rows = jobs.filter(j=>!j.excludedFromReports).map(j => ({
    "SRO Number":               j.sroNumber             || "",
    "Site Name":                j.storeName             || "",
    "Site Address":             j.storeAddress          || "",
    "Installation Type":        j.typeSnapshot?.label   || "",
    "Installer Name":           j.installerName         || "",
    "Clock-In Time":            fmtCell(j.startTime),
    "Submission Time":          fmtCell(j.submittedAt),
    "Approval / Rejection Time":fmtCell(j.approvedAt || j.rejectedAt),
    "Site Contact Name":        j.siteContactName       || "",
    "Status":                   j.status ? j.status.replace(/_/g, " ") : "",
    "Clock-In Location":        j.clockInLocation ? `https://www.google.com/maps?q=${j.clockInLocation.lat},${j.clockInLocation.lng}` : "",
    "Submission Location":      j.submitLocation  ? `https://www.google.com/maps?q=${j.submitLocation.lat},${j.submitLocation.lng}`  : "",
    "Outcome Reason":           j.outcomeReason   || "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook  = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, FILTER_LABELS[filterKey] || "Jobs");

  // Auto-width: set each column to the max character width of its content
  const colWidths = Object.keys(rows[0] || {}).map(key => ({
    wch: Math.max(key.length, ...rows.map(r => String(r[key] || "").length)) + 2,
  }));
  worksheet["!cols"] = colWidths;

  const filename = deriveExportFilename(filterKey, datePreset, navYear, navMonth);
  XLSX.writeFile(workbook, filename);
  console.log(`[Export] Wrote ${rows.length} rows → ${filename}`);
}

// ── SITE COMPLETION REPORT PDF ────────────────────────────────────────────────
//
// Static map images are generated using the Google Maps Static API.
// A free API key is required — obtain one at:
//   https://developers.google.com/maps/documentation/maps-static/get-api-key
// Replace the placeholder value below with your actual key.
// Without a key the map images will not load; GPS coordinates are always shown as text.
//
const GOOGLE_MAPS_STATIC_API_KEY = "YOUR_GOOGLE_MAPS_STATIC_API_KEY";

function generateSiteCompletionReport(job) {
  const snap    = job.typeSnapshot || null;
  const sroSafe = (job.sroNumber || "unknown").replace(/[^a-z0-9]/gi, "-").replace(/-+/g, "-").toLowerCase();
  const dateSafe = new Date().toISOString().slice(0, 10);
  const docTitle = `site-completion-report-${sroSafe}-${dateSafe}`;

  const fmtDt = iso =>
    iso ? new Date(iso).toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" }) : "—";

  const staticMapUrl = loc =>
    loc
      ? `https://maps.googleapis.com/maps/api/staticmap?center=${loc.lat},${loc.lng}&zoom=15&size=400x200&markers=color:red%7C${loc.lat},${loc.lng}&key=${GOOGLE_MAPS_STATIC_API_KEY}`
      : null;

  const sc   = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;

  // ── Checklist HTML ──
  const checklistHtml = (snap?.questions || []).map((q, i) => {
    const ans    = job.answers?.[q.id] || {};
    const photos = (ans.photos || []).map(p => `<img src="${p.url || p.data}" alt="" class="ans-photo"/>`).join("");
    const confirmColor = ans.confirmed === "Yes" ? "#27ae60" : ans.confirmed === "No" ? "#c0392b" : "#555";
    return `
      <div class="cl-item">
        <div class="cl-num">Question ${i + 1}</div>
        <div class="cl-text">${q.text}</div>
        ${ans.confirmed ? `<div class="cl-confirm" style="color:${confirmColor}">${ans.confirmed}</div>` : ""}
        ${ans.value     ? `<div class="cl-value">${ans.value}</div>` : ""}
        ${!ans.confirmed && !ans.value ? `<div class="cl-none">No answer recorded</div>` : ""}
        ${ans.notes     ? `<div class="cl-notes">Note: ${ans.notes}</div>` : ""}
        ${photos        ? `<div class="photo-row">${photos}</div>` : ""}
      </div>`;
  }).join("");

  // ── Location block HTML ──
  const locHtml = (label, loc) => {
    if (!loc) return `<div class="loc-block"><div class="loc-label">${label}</div><div class="loc-none">Not recorded</div></div>`;
    const url    = staticMapUrl(loc);
    const coords = `${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}`;
    return `
      <div class="loc-block">
        <div class="loc-label">${label}</div>
        ${url
          ? `<img src="${url}" alt="${label} map" class="map-img"
               onerror="this.style.display='none';this.nextSibling.style.display='block'"/>
             <div class="map-err" style="display:none">Map image unavailable — add a Google Maps Static API key</div>`
          : ""}
        <div class="loc-coords">${coords}</div>
      </div>`;
  };

  // ── General photos ──
  const generalPhotosHtml = job.photos?.length
    ? `<div class="section">
         <div class="section-title">General Job Photos</div>
         <div class="photo-row">${job.photos.map(p => `<img src="${p.url || p.data}" alt="" class="gen-photo"/>`).join("")}</div>
       </div>`
    : "";

  // ── Approval row ──
  const completedRow = job.status === "completed"
    ? `<div class="dr"><span class="dl">Completed</span><span class="dv">${fmtDt(job.completedAt)}</span></div>
       <div class="dr"><span class="dl">Marked Complete By</span><span class="dv">${job.completedBy || job.approvedBy || "—"}</span></div>`
    : "";

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>${docTitle}</title>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700;800&family=Barlow+Condensed:wght@600;700;800&display=swap"/>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    @page{margin:0;size:A4}
    body{font-family:'Barlow',sans-serif;color:#1a1a1a;background:#fff;font-size:13px;line-height:1.5}

    /* ── Branded header band ── */
    .hdr-band{background:#1134A6;padding:0;print-color-adjust:exact;-webkit-print-color-adjust:exact}
    .hdr-band-inner{display:flex;align-items:center;justify-content:space-between;padding:18px 24px;gap:16px}
    .hdr-logo-block{display:flex;align-items:center;gap:12px;flex-shrink:0}
    .logo-img{height:28px;display:block;filter:brightness(0) invert(1);print-color-adjust:exact;-webkit-print-color-adjust:exact}
    .logo-fallback{font-family:'Barlow Condensed',sans-serif;font-weight:800;font-size:22px;color:#fff;letter-spacing:1px;display:none}
    .hdr-divider{width:1px;height:28px;background:rgba(255,255,255,0.3);margin:0 4px}
    .hdr-field-ops{font-family:'Barlow',sans-serif;font-size:9px;color:rgba(255,255,255,0.65);letter-spacing:3px;text-transform:uppercase}
    .hdr-center{flex:1;padding:0 20px}
    .rpt-title{font-family:'Barlow Condensed',sans-serif;font-weight:800;font-size:26px;color:#fff;letter-spacing:1px;line-height:1}
    .rpt-sub{font-size:11px;color:rgba(255,255,255,0.6);letter-spacing:1px;margin-top:3px}
    .status-pill{display:inline-block;margin-top:6px;padding:3px 10px;border-radius:2px;font-family:'Barlow Condensed',sans-serif;font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;background:rgba(255,255,255,0.15);color:#fff;border:1px solid rgba(255,255,255,0.35)}
    .hdr-right{text-align:right;flex-shrink:0}
    .gen-label{font-size:9px;color:rgba(255,255,255,0.55);letter-spacing:2px;text-transform:uppercase}
    .gen-date{font-size:12px;color:#fff;font-weight:600;margin-top:3px}
    .gen-sro{font-size:10px;color:rgba(255,255,255,0.55);margin-top:4px}

    /* ── Page body padding ── */
    .page-body{padding:20px 24px 24px}

    /* ── Save-as-PDF toolbar (hidden when printing) ── */
    .pdf-toolbar{background:#f0f3fb;border-bottom:1px solid #d0d8f0;padding:10px 24px;display:flex;align-items:center;gap:14px}
    .pdf-btn{background:#1134A6;color:#fff;border:none;border-radius:4px;padding:8px 20px;font-family:'Barlow Condensed',sans-serif;font-weight:800;font-size:12px;letter-spacing:1.5px;text-transform:uppercase;cursor:pointer;display:flex;align-items:center;gap:7px}
    .pdf-btn:hover{background:#0d2a80}
    .pdf-hint{font-family:'Barlow',sans-serif;font-size:12px;color:#555}
    .pdf-hint strong{color:#1134A6}

    /* ── Sections ── */
    .section{margin-bottom:22px;break-inside:avoid}
    .section-title{font-family:'Barlow Condensed',sans-serif;font-weight:800;font-size:12px;letter-spacing:2.5px;text-transform:uppercase;color:#1134A6;border-bottom:2px solid #e8eaf6;padding-bottom:7px;margin-bottom:12px;display:flex;align-items:center;gap:7px}
    .section-title::before{content:'';display:inline-block;width:3px;height:13px;background:#1134A6;border-radius:2px;flex-shrink:0;print-color-adjust:exact;-webkit-print-color-adjust:exact}

    /* ── Detail rows ── */
    .detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 28px}
    .dr{display:flex;flex-direction:column;gap:2px;padding:8px 0;border-bottom:1px solid #f2f2f2}
    .dl{font-size:10px;font-weight:700;color:#888;letter-spacing:1.5px;text-transform:uppercase}
    .dv{font-size:13px;color:#1a1a1a;font-weight:500}

    /* ── Checklist ── */
    .cl-item{padding:12px 0;border-bottom:1px solid #f2f2f2}
    .cl-num{font-family:'Barlow Condensed',sans-serif;font-weight:700;font-size:10px;color:#1134A6;letter-spacing:2px;text-transform:uppercase;margin-bottom:3px}
    .cl-text{font-weight:500;font-size:13px;color:#1a1a1a;margin-bottom:5px;line-height:1.4}
    .cl-confirm{font-weight:700;font-size:14px;margin-bottom:3px}
    .cl-value{font-size:13px;color:#333;margin-bottom:3px}
    .cl-none{font-size:12px;color:#aaa;font-style:italic}
    .cl-notes{font-size:12px;color:#666;font-style:italic;margin-top:5px;padding:6px 10px;background:#f8f8f8;border-left:2px solid #d0d8f0;border-radius:2px}

    /* ── Photos ── */
    .photo-row{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}
    .ans-photo{width:88px;height:88px;object-fit:cover;border-radius:4px;border:1px solid #e0e0e0}
    .gen-photo{width:120px;height:120px;object-fit:cover;border-radius:4px;border:1px solid #e0e0e0}

    /* ── Location ── */
    .loc-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:8px}
    .loc-block{}
    .loc-label{font-size:10px;font-weight:700;color:#888;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:7px}
    .map-img{width:100%;max-width:320px;height:150px;object-fit:cover;border-radius:4px;border:1px solid #e0e0e0;display:block;margin-bottom:5px}
    .map-err{font-size:11px;color:#aaa;font-style:italic;padding:10px;background:#f8f8f8;border-radius:4px;margin-bottom:5px}
    .loc-coords{font-size:11px;color:#555;font-family:monospace}
    .loc-none{font-size:13px;color:#aaa;font-style:italic}

    /* ── Signature ── */
    .sig-wrap{display:inline-block;margin-top:10px}
    .sig-top{background:#111111;border-radius:4px 4px 0 0;border:1px solid #27ae60;border-bottom:none;padding:10px 14px;print-color-adjust:exact;-webkit-print-color-adjust:exact}
    .sig-img{height:80px;max-width:280px;display:block}
    .sig-bot{background:#fff;border-radius:0 0 4px 4px;border:1px solid #27ae60;border-top:1px solid #eee;padding:7px 14px;min-width:200px}
    .sig-name{font-weight:600;font-size:13px;color:#1a1a1a}
    .sig-role{font-size:11px;color:#888;margin-top:2px}
    .sig-none{font-size:13px;color:#aaa;font-style:italic;margin-top:8px}

    /* ── Footer ── */
    .footer{margin-top:32px;padding-top:10px;border-top:1px solid #e8e8e8;display:flex;justify-content:space-between;font-size:10px;color:#bbb;letter-spacing:1px;text-transform:uppercase}

    @media print{
      body{print-color-adjust:exact;-webkit-print-color-adjust:exact}
      .section{break-inside:avoid}
      .pdf-toolbar{display:none!important}
      .hdr-band{print-color-adjust:exact;-webkit-print-color-adjust:exact}
      @page{margin:0;size:A4}
    }
  </style>
</head>
<body>

  <!-- ── Save-as-PDF toolbar (hidden on print) ── -->
  <div class="pdf-toolbar">
    <button class="pdf-btn" onclick="window.print()">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor"><path d="M8 12l-4-4h2.5V2h3v6H12L8 12z"/><path d="M2 13h12v1.5H2z"/></svg>
      Save as PDF
    </button>
    <span class="pdf-hint">In the print dialog, set <strong>Destination → Save as PDF</strong></span>
  </div>

  <!-- ── Branded header band ── -->
  <div class="hdr-band">
    <div class="hdr-band-inner">
      <div class="hdr-logo-block">
        <img src="data:image/png;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAUDBAQEAwUEBAQFBQUGBwwIBwcHBw8LCwkMEQ8SEhEPERETFhwXExQaFRERGCEYGh0dHx8fExciJCIeJBweHx7/2wBDAQUFBQcGBw4ICA4eFBEUHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh7/wAARCADAAz4DASIAAhEBAxEB/8QAHQABAAMBAQEBAQEAAAAAAAAAAAcICQYFBAMCAf/EAFwQAAEDAwICAwgKDAoIBQUBAAEAAgMEBQYHEQghEjFBExQiUWFxdYEJMjY3QlJikbGzFRYYVnKCkpOVocHRIzM0VXaUtMLS0yRDU1dzg6KyFzU4VHQlJmOj8MP/xAAUAQEAAAAAAAAAAAAAAAAAAAAA/8QAFBEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AuWiIgIiICIiAiIgIigDiM4irXgrajHMUfBc8l2LJZPbQUJ+V8aQfE7PheIhP6LN3A9c9RcWy92QPv9bd21D96yjrp3SQztJ5gA8mEdhbtt1dXI3q0g1QxfU6wC42Kp7nVRAd+UEpAmpnHxjtaexw5HyHcAO4REQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQF/FRNDTU8lRUSxwwxNL5JJHBrWNA3JJPIADtXmZdklkxOw1F9yG4w0Fvpxu+WQ9Z7GtHW5x7ANyVRDiG19vepNRLZ7T3a1Yu13g0wdtLV7Hk6Yjs7Qwch27kAoJC4juJqSt75xXTaqfFTc46q8s5Pk7C2Dta35fWfg7dZrbhuL3/NMigsePW+a4XCoO/Rb1NHa97jya0b8yV0mjGlGT6oXzvOzw972+Fw78uMzT3GAeL5T9upo5+PYc1f7SbTXGNNMfFqx+k/hZADV1soBnqXDtcfF4mjkPWSQqnqHwoZJYcJprvYLiL7dYIi+5UMUfRJPXvB2v2HLY7E7bjr6KgbFMhv2HZFDebDXVFtuVK8gPZyPla5p5EHqLSNitWFA/EVw9WjUCOfIMbEFrycAuedujDXHxSbe1f4nj178iA+7h619sepNPFZ7t3G05QxvhUxdtFVbDm6Ent7Sw8x2bgEqallJfLTfcTyKW23Slq7VdqGUdJjt2SRuHMOaR6iHA7HkQVa/hx4mYq3vbFdSapkNTyjpby/YMk7A2fsa75fUfhbdZC1iL/GkOaHNIII3BHUV/qAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgLhtYNUcX0xsJuF9qO6VcoPedBER3apcPEOxo7XHkPKdgeN4htfrJpvBLZrR3G7ZQ5vKnDt4qTccnTEdvaGDme3YEE0SynIL9mGRTXm+11RcrnVvAL38yfE1rRyAHUGgbDsQdDrBqhlGp1+NxvtT3OliJ7zoIiRDTNPiHa49rjzPkGwHdcO3D5eNQ5Yb7fxPasXDtxJttNW7dkQPU3xvPLxb89pD4ceGUu72yrUqk2bykpbK8dfaHVH+X+V2tVuIo44omRRMbHGxoa1rRsGgdQA7Ag8/GLBZ8YsdNZLDb4Lfb6ZvRihibsB4yT1knrJO5J5lekiICIiCPdatJMY1Rsve11i71ucLCKO5QsHdYT4j8dm/W0+rY81QHVbTjJ9NshdaMio+i1+5pauPcwVLB8Jjvm3aeY35jqWoC8POMTsGa49PYckt0VdRTc+i7k6N3Y9jutrh2EfQgpXw58RV0wV1PjmWPnueNbhkUntp6EfJ+NGPidY+D4jeKw3e1360U13s1dBX0FUzpwzwv6TXj9/YR1g8is/OIDQq/wCmNY+4U3dbrjMj9oa5rfCh3PJkwHtT2B3tXeQ8h5Gh+sOTaW3fp2+Q1tnneDWWyV5Ecnjcw/Aft8IdfLcEINKEXKaYag4zqLjrL1jdaJWjYVFO/Zs1M8/Bkb2HxHqPYSurQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREEL6vcQ+N6bZlJjFzsV2rKhkEcxlpzH0NnjcDwnA78lzdk4tsPut6obXDjF+jkrKiOnY5xi2aXuDQT4XVzUH8cfv8VPo6m+gqKNPfd9j3pSm+tag1TUfa3aq2nSm0W+5Xe211dHXVBgY2l6O7SG9Lc9IjkpBVYfZCvcNjPpOT6ooP2+7Fwv71cg+eH/Gp9wPI6bL8OteTUdPNT09xpxPHFLt02g9h25brKpaXcNvvE4d6Mj/ag6jOsipsSw+6ZLWQTVFPbad1RJFFt03BvYN+W6gH7sXC/vVyD54f8alniM94vMfRcv0LM5BpdojqtaNVrVcbhaLbXUDKGdsL21XQ3cS3pbjok8lISq/7Hp7jMo9IxfVq0CAiIgIiICIiAiIgIiICIiAiIgIi+W7XCitNrqrpcqmOloqSF008zzs2NjRu5x8wCD6XOa1pc4hrQNySeQCqnxHcTUdH3zium1UyWo5x1V5ZzZH2FsHxj8vqHwd+REd8RnETdM7dUY5ijp7ZjO5ZK/2s9cPl/FjPxO34XiEAIPVsVovuW5FFbbVSVV1u1dKeixu75JHHmXOJ9ZLidhzJKvLw68PVo0+jgv8AkYguuUEBzXbdKGiPij363+N59W3MmAdFtdcR0usve1p01kqrlM0CsuU12b3Wc+IfwPgM36mj17nmpB+7Pg/3dSfpgf5KC2aKpn3Z8H+7qT9MD/JT7s+D/d1J+mB/koLZoqmfdnwf7upP0wP8lPuz4P8Ad1J+mB/koLZoq24Bxb4tfcghtmRWCfHIJz0WVrqsTxMcerungNLW/K5gduw3IsfDLFPCyaGRkkUjQ5j2OBa4HmCCOsIP7REQfjXUtLXUc1FW08VTTTsMcsMrA5kjSNi0g8iD4lTPiO4aKmx985Tp5Ty1drG8lTa27vlph1l0Xa9nyfbDyjqukiDK/AcyyLBcjhvuNXCSjq4+Th1smZ2se3qc0+I+cbEAq/Gget+O6o0DaRxjtmRxM3qLe9/t9ut8RPtm+Trb28tieK4juG+gy7vnJ8Iigt+QHeSej5MgrT1kjsZIfH1OPXsSXKl0rL3i2RFj21tovFun6vCimgkafUQUGryKt/DPxGRZfPSYhmpZT5A/aOkrWt2irTt7VwHJkh/Jd2bHYGyCAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiCG9WuHnFNSMwfk13vN6pKp8DITHSviDNmDYHwmE78/GucsvCXg1qvNFdIMhyN8tHUR1DGvkh6JcxwcAdo+rkrDogKsPshXuGxn0nJ9UVZ5Vh9kK9w2M+k5PqigpatLuG33icO9GR/tWaK0u4bfeJw70ZH+1B/fEZ7xeY+i5foWZy0x4jPeLzH0XL9CzOQXQ9j09xmUekYvq1aBVf9j09xmUekYvq1aBAREQEREBERAREQEREBERAREQF52T2WgyPHbhYbpG6SiuFO+nna12x6Dhsdj2HnyK9FEGdGvuh+RaX17qxgkueOSv2p7gxnOPfqZMB7V3l6ndnPcCJlrRcaKjuVBPQXClhq6SoYY5oZmB7JGnrBB5EKl3Edw11mN985RgEE1bZRvJU24bvmpB1lzO18Y/Kb5RuQHI6bcPF81Cx2O+Y1mOLVEJ2bNC6Wds1O/4kje5eCf1HrBI5rp/uPNQfvixf87P/AJShPTvN8kwHI4r7jVe6lqW8pGHnFOztZI34TT846wQeav1oPrVjeqVtEMTm27IIWb1VtkfuT43xH4bP1jtHUSFcfuPNQfvixf8AOz/5Sfceag/fFi/52f8AyleBQXxFcQdo08imsOPmC6ZQW7OZv0oaLftl263eJg59p25bhVbV/RW5aYW+KfIMrxyarn/k9BSyTPqJBvsXdExgBo8ZIHLYbnkosXq32733LciluV1q6q63aulHSe7d75HHkGtA9QDQNhyACthw38M8VD3tlepFIyaq5SUlneN2RdodP2Od8jqHwtzyAVSvGL5FZrRbrvdbLXUVBc2F9FUTQlrJgPik/P5Rseo7qWuHbiBvGnU0NivpnuuLOdt3Lfeai3PN0RPW3xsPLtGx33vRleN2PKrBUWG/22Cut1Q3ovhkbyHiLSObXDsI2I7FRDiH0Cvem1RLebT3a64u9/g1PR3lpNzybMB2dgeOR7diQCF88Yv1nyax017sNwgr7fUt6UU0TtwfGD2gjqIOxB5Feksz9F9WMn0uvnfdom75t0zga22yuPcpx4x8V+3U4evccloBpRqPjGpOOtu+PVe72bCqpJNhNTPPwXt8XXs4cj2HrQdgiEgAknYBVa4juJiC1984tpxVR1Fdzjqru3Z0cHYWw9jnfL6h2bnmAkDiD15sWmdLJarf3G65O9n8HRh28dNuOT5iOrxhg5nyA7qhuXZHfMxySpvt+rZa+5Vbx03keoNa0cgB1ABflardfMryKOht9PWXa718x2a3eSWV5O5cSfWS4+Ukq7/Dpw7WvA20+RZS2C6ZNsHxt26UFCfkb+2f8vs+D4yHDcLPDvcaK6W7Pc4ZLRS0z21FutZ5SB45tkm+LtyIZ177b7bbG2yIgIiIC4nVrU/E9M7M2vyOsd3eYHvWigHSnqCOvojsA7XEgDx77Be9m2Q0OJ4jdMkuW/etupn1D2tOxf0RyaPK47AeUhZkaiZfec6y2tyS+VDpamqeS1nSJZBHv4MbB2NaOQ+c8yUEu6gcVeol+mfFjopcZotz0RAwTTuHypHgj8lrVFNz1Fz+5yukr81yKcu6w65S9H1DpbD1L1tF9Kcl1TvslDZWx09HTdF1ZXz79ygB6hy5ucdjs0eLmQOatTjvCLp5RUzRebre7tU7eG4SsgjJ8jWtJHrcUFOaPOc1oyDSZhkFOR1dyuUzfocu/wAO4kdWMdqIzLfxeqVvtqe5RCXpD/iDaTf8ZWPvPCTplWQObQVV+ts3wXsqmyNB8oe07j1hVr110IyfS4NuT5mXewyPDG18MZYY3HqbKzc9AnsO5B8e/JBa7QziExbUiWO0VkYsWQu5Mo5ZOlHUbf7J+w3PySAfFvsSpnWSdLPPS1MVTTTSQzxPD45I3FrmOB3BBHMEHtWinC3qW/UnTeOe4zB98tjxS3HkB3Q7bsl2Hxm9fymu7EEsIi4nWrUO2aaYJV5DXdCWp/iqGlLtjUTkHot8w6yewA9uwQfvqhqTiWm9obcMnuPcXS7970sTenPUEdYY39p2A3G5G6qXqDxa5vdamaHEaOjx+h32jkfGKipI8ZLvAHmDTt4yoOzjK79muR1F/wAjr5KyunPWeTY29jGN6mtHYB9O6/3B8RyPNb7HZcZtc9wrH8y1g2bG3fbpPceTW+UkIPTumqOpFzke+tzvJJOmdyxtxlYz1Na4NHqC+CjznNqJ/To8wyGndvvvFcpmHf1OVmMP4OGmlbLl2XubO4DeC2Qgtb/zJOv8kL37jwc4ZJCRbsqv9PL0eTp2wzN38ewazl5N0EAYfxE6s429obk0l2gB3MN0YKgO87ztJ8zgrK6OcUOK5bLT2nK4W45eJSGNkc7pUkzjy5PPOMnxO5fKKrnrFw85vp5SSXVojvtljBMlZRsd0oQO2WM82jyguaO0hQ6g1wRVC4ONb6t1wp9OsvrzNFKBHZquZ3hMcOqnc49YI9pv1EdHnu0C3qDzMtrZ7bil3uNKWieloZp4i4bjpNjc4bjt5hUX+6r1a/8Ac2f+oD96u/qF7gci9F1P1TllWgvvwjap5TqVQZLUZXNRO+xslOITBAIgA8SF2/Pn7ULmta+Ky32SrnsmntLT3aqjJZJc59zTNPb3NoIMn4W4by5dIKquPZzfLBhF8xW1TGmpr5LC6tmYSJHxxh47kD2Nd0+fjA26id/V0j0mzHU6vfFj1GyOihd0ai4VJLKeI7b9HcAlzurwWgnmN9hzQfvkGuGrF7ndLVZzeKfc7hlDN3q0eQCLor47Zq9qjbpmy0+f5G4t6hPXyTN/JkJH6lYq18GluFK37KZzVPqC3n3tQtaxp/GcSR8y4zU3hNyrH7dNc8UuseSQwtL30vce41PRA59Fu5a/zAgnsBKD0NK+La/0NXDQ6g0MV1onENdX0kYiqI/lOYNmPHkAafP1K4ON3u05HY6W92OuhrrfVs6cM8R3Dh9IIPIg8wQQVlC9rmPcx7S1zTsQRsQfEpz4SNX26fZNNZcgrTHjFxa58hduRTTtaS2QD5W3RIHXu09iC7Gomb43gOOyX3Jq9tLTNPRjYB0pJ37cmMb1ucfmHWSBzVPNSOLHN7zUy0+H09PjlBvsyRzGz1Th4y5wLG7+IN3Hxiow1v1Ju2p2bVF7rnPioYyY7dR9LwaeHfkNurpHrce0+QADudI+GbNc3t8F4uk8WN2qcB0T6mIvqJWHmHNiBHgnsLi3fkQCOaDgK7VnU+smMs2oGTBxO+0Vylib+SxwA+ZenYNddWbLUtmp83ulTt1srXipa4eIiQH5xzVgH8GllNN0WZzcBPt7c0LCzf8AB6W/61CetegeYaZ0xusz4bxY+kGmupWkdyJ5DurDzZueW+5HUN9yAgnbRLiqob7XU1i1ApKe1VkzgyO5wHo0znHqEjSd49/jblu559EKzwIcAQQQeYIWR6u/wO6l1OSYvU4Rd5u611jia+jkc7d0lKT0eif+Gdhv4nNHYgsiiL8qyop6Okmq6qaOCngY6SWWRwa1jWjcuJPUABvug/C93W22S1VN1u9bBQ0NMwvmnmeGsY3xkn/+KqxqvxctgqpLfpxa4qhjSWuuVwjd0XeWOIEHbyuP4qiLiU1muepmRzUFDUSQYtRzEUVMN293I5d2kHa489gfag7de5MaYpjt6yq+01jx+3TXC4VLto4Yh85J6mtHaTsB2oOsyPWrVW/VL56zObzD0v8AV0VQaVgHi6MXRHzrnBmmYiczjLL8Jj1yfZGXpfP0t1aDT/g9pe9YqnOsknM7ubqO1gNazyGV4O/l2aPIT1ru38KOk7oO5iO+Nd/tBXeF+tu36kFTsX1z1Xx17TR5pcqmMHnFXvFU0jxfwocQPMQrF6QcWNovNVDac/oIbJUP2a2405JpXO+W07ujHl3cPHsFy2pfCFXUVHLXYFfH3EsBd3hcA1krh4mSN2aXeQho8qq7c6Gttlwnt9xpZqSrp3mOaCZhY+Nw6wQeYKDWSmnhqaeOoppo5oZWB8ckbg5r2kbggjkQR2r9FR7hC1sqcYvVLg2T1xfj9Y/udFNK7+QzOPIbnqjceRHUCQeQ6SvCgKg8/FTqyyeRgqbPs1xA/wBAHj86vwslKr+VS/hn6UF4uEbV7MdS71f6TKJaJ8dDTRSQ9704jILnOB358+oLstbNd8P0yLqCoL7tfS0ObbqZ4BYDzBlfzEYPi2LuYO23NU10Q1Qk0ysuW1Nub0r1c6WGmt5czdsTuk4ulPZ4IPIdpI5bbqN6+rqq+tnra2olqameQyTTSuLnyOJ3LiTzJJQTnlPFdqjdJ3/Yh9rsMHwG09K2Z4HldL0gT5gPMufp+I/WaGcS/bk+TnzZJQUxafV3P6F4+HaKao5XTsqrRh9f3q9ocyeq6NMx7T1FplLekPNuuql4XNYGQd0bZ7fI7/ZtuMXS/WQP1oLZcTWUZjhembspw+SmbNRVMffrZ4BIDC/wdwOwh5Z6iVVb7qvVr/3Nn/qA/erzZZY6TJcVuePV43prjSSU0nLctD2kbjyjfceULLK/2ursl8r7NXx9zq6Gpkpp2+J7HFrv1hBoDwp6o3HU7CK6pvr6c3m3VhinEMfQa6NzQ6N23Zv4Y/FUwqhHA9ln2A1hFlnl6NJfqZ1MQTsO7M8OM+fk9o/DV90BERAUEcW+sF40yoLHRYy+lF2uEskshni7oGQMG3Vv1uc4bH5JU7rOfizy37bdbrzJFL06O1kW2m57jaIkPI88hkPm2Qe791Xq1/7mz/1AfvVv9Ar1lOSaWWnIcvfA643JrqljYYe5tZCT/B8vK0B2/wApZ1adY1UZhnVmxil6QfcatkLnNHNjCd3v/FaHO9S1Kt9JT0FBT0NJE2GmpomxQxt6mMaAGgeYAIKtcTeu2faf6pTY7j09uZQso4ZgJqUSO6Tgd+e64PEuJ7VO5ZVaLdVVNoMFVXQwShtCAei6QNOx35civL44/f4qfR1N9BUUae+77HvSlN9a1Bqmqw+yFe4bGfScn1RVnlWH2Qr3DYz6Tk+qKClq0u4bfeJw70ZH+1ZorS7ht94nDvRkf7UH98RnvF5j6Ll+hZnLTHiM94vMfRcv0LM5BdD2PT3GZR6Ri+rVoFV/2PT3GZR6Ri+rVoEBRhrNrfhmmTHUlwnfcb2WB0dspSDJz6jI7qjHn57cwCvI4ptYBpli8dDZ5In5Nc2kUjXAOFPGOTpnN/U0HkTv1hpCz+uddWXO4VFwuNVNV1dRIZJppnlz5HE7kknrKCZ844odT8hdJFbaylx2kcSAygi3l6Plkfud/K3oqNbjqFntx37/AM1yOpB7JbnM4fMXbLvtE+HvL9SKeO7yvZY7A8+DW1EZc+Ydpij5dIfKJA8ROxCsTZOEjTSjp2C41t9uU3w3uqWxNJ8jWt3A9ZQU7tmouf2yVklBm2RQFh3Abcpej629LY+sKV9PuKzUKxTsjyRtLk1FyDhKxsE7R8mRg2/Ka7zqZMj4Q9P6ymd9hbve7VUfBc+RlRH62loJ/KCrJrPoxl+l9S2S6wx1tpmf0YLlSgmInsa8Hmx23YeR57E7FBe7STVTENTLUarHq7o1kTQ6pt8+zaiDzt7W7/CG49fJdyspcRyO84nkNJfrBXSUVwpX9KOVh+dpHU5pHIg8iFo5oRqVb9T8Ep73TiOG4Q7Q3Glaf4mYDnt29B3W0+Ll1goO+REQEREBERAREQEREFa+I7hsosm75yjAoIaG9neSpt42ZDWHrJb2MkP5Lj17Hcmmsb73i2RBzDW2i8W6f5UU0EjT6iCtTsivVpx2zVN5vdfBQW+mZ05p5nbNaP2k9QA5k8gs+OJrU+0anZnHW2WwwUVLRtMLK17NqqsHYZNuQaNvBbzI3PPnsA6q+8Vmc3HTyKwwU8FFfHAx1V5hOz3x7cixm2zJDz3cOrraATyhPGrFfsvyKG0WWiqbndKyQ9FjPCc4nmXOJ6h2lxOw6yV5Cn/hG1fxvTy41NmyO001PTXKQb3qOMmaLqAZJ1kxdvg9R3Ox35BYTh30Bsum8EV6vPcbrlLm85+jvFSbjm2IHt7C88z2bDfebV+VHU01bSRVdHPFUU8zBJFLE8OY9pG4cCORBHav1QF+dTBDU08lNUwxzQysLJI5GhzXtI2IIPIgjsX6IgpvxIcM81u75yvTilfPRc5Kqzs3c+HtLoe1zfkdY7NxyFdMHyzIMJyOC+45cJaGuhOxI5tkb2se3qc09oP0gLS7UfOcb0/xyW+5LXtpoG7tiibzlqH7cmRt+E79Q6yQOazh1dy+mzrPbhktLYqOyxVTtxT049t8t56i89ZIAB8XaQkbWLiTyzPcdgsNDTMx+jkhDbl3tKXPqn/CAd1tjPxeZPUSRyUc6Xae5NqNkTLNjdEZCNjUVMm7YaZh+E93Z27Abk7cgVySt5wfa1YlbrNS6f3uhobBVdP/AEevjHRirXn/AGzj1SnkOkTseQ8HYAhN2iOkGM6W2fuVujFZd52AVlzlYBJL8lo+Azf4I8m5J5qRkRAREQEREEFcctbNS6ETwRFwbWXKnhk2+KC5/PybsH6lQRaNcWeOVGS6FX6Cjb06iiayvY3bfcRO6T/X0Oms5UGgfBLQ0VJoFbammY0TVtXUzVRA5l4ldGN/xGMU2qgvDFrz/wCGLJ8fv1HPW49VTd2DoNjNSyEAOc0HYOadhu3cbbbjtBuRheqWn2Ysj+1/K7ZUzP6qZ8vcp/zb9nfqQdkvOyey2/I8dr7DdYRNQ19O+CZh+K4bbjxEdYPYQCvRRBk7kFtms1+uFnqCDNQ1UtNIR1FzHlp/WFYHgBvM9JqldrL0/wDR7ha3SOb45IpG9E/kvk+dWZu+hWk92utXdbhh1NPWVk76iolNRMOnI9xc52weANySeS9DDdI9O8Ovbb1jWMwW+4MjdG2Zk8riGu5EbOcR+pB3Cz+4zc6OW6tz2mlmL7bj4NFEAfBdNv8AwzvP0gGf8sK+WRXOGyY/crzU/wARQUktVJ+DGwuP6gspbjVz19wqK6qf056mV0srvG5xJJ+coP6tdDV3S50ttoIHVFXVzMggib1ve4hrWjykkBaUaFaaWrTHCKe00sUT7nMxslzqwPCnm25jf4jdyGjxc+snen/BLjcN+1up62pj6cNmpJK4Ajl3TdsbPWDJ0h5Wq/6AiIg/mRjJY3RyMa9jwWua4bgg9YIVAuLzSiDT3M4rtY6YQ49eC58Mbfa00w5viHibzDm+QkfBV/1E3FvjUOSaFX3pR9KotbG3Gndt7QxHwz+bMg9aDOuGSSGZk0Mjo5I3BzHtOxaRzBB7CtNdCc2ZqBpdZ8jL2msfF3GuaPg1DPBfy7Nz4QHicFmOrh+x6X4yWjKMYkf/ABE8VfC3x9NpY/6uP50FkdQvcDkXoup+qcsq1qpqF7gci9F1P1TllWg9zAcarcxzO04xbyG1FxqWwh5G4jaebnkeJrQXHyBad4TjNow/F6HHbHStp6GjjDGADm8/Ce49rnHck+MqmHAPZmV2rlfdpWNcLZa5HRkjm2SR7WAj8Xug9avQgIiIKL8dGCUuOag0eUW2BsNLkEb3zsaOQqYyOm7ydIOYfKekVXdXf9kEpmP0rsdYWjpxXtkQPaA+CYn/ALAqQIJ34NNNKXOM+mvV5p2z2ewhkr4ZG7snndv3NhHa0dFziPI0HkVflQTwNWeO3aGQ14aO6XS4VFQ523PZpEQH/wCs/OVOyAvlu9vorta6q13GnZU0dXC6GeF43a9jhs4Hzgr6kQZZ6n4zJhuoV8xiQucLfWPijc7rfHvvG4+UsLT611vCpfn4/rzjM3dSyKsqDQSjfYPEzSxoP45YfOAvc426RlNr9cZmMDTVUdNM4+MiMM3+ZgUY6b1HeuomNVQO3cbtSyfNM0oNUVXHjszs2PAqTDqGcsrL6/pVHRPNtLGQSPJ0n9EeUNcFY5Z88ad+N614uVM1xdDaaaGhj8XJvdHf9Ujh6kEKrQ/hV0tptPcAp66tpQMiu8TZ6+R48KJp5sgHiDQRv43b+IbUj0PsMWTau4vZKiPulPUXGIzs+NGw9N49bWlaeoCIiAq08b+l1NeMWdqHaaUNutsDW3DoDbu9N1dIjtcwkc/i779Q2ssvjvltpbzZa60VzOnS11PJTTN8bHtLXD5iUGTa0f4Xc4lzzR+2XCtl7pcqEmgrXE7l8kYGzz5XMLHHykrOi5UktBcamgnG0tNM+F4+U0kH9YVpPY873Iy+ZTjbpCY5qaKujYT7UscWOI8/dGfMEFxFkpVfyqX8M/Sta1kpVfyqX8M/Sg/JXc4U9BLZYrJRZpmNvZV3yrYJqSkqYw5lEw82uLT/AK0jY7n2u4HI7qsXDxi0OY6y45Y6uPulG6p7vUtI5OjiaZHNPkd0ej+MtMUBERAVDuOXEPsBqyzIKeLo0mQU4mJA2Hd49mSD5u5u87yr4qE+M/EPtn0Yq7hBF062xSCvjIHMxjwZR5ugekfwAgoTj90q7HfqC9UD+hV0FTHUwu8T2ODh+sLVHGLxSZDjdtvtC7pUtwpY6mLn8F7Q4A+UbrKBXx4F8s+zmkklgnl6VVYKp0IBO57hJu+M/P3RvmaEE/oiIOX1ZymPC9N79k73ND6Gje6AO6nTHwYm+t7mj1rLqaSSaV80r3Pke4ue5x3LieZJVyfZAct71xux4XTy7SV8xrqoA8+5R+CwHyFzifPGqZoLPcAWId/Zfd80qYt4bXAKWlcR/rpfbEeVrAR/zFdJRlwwYh9pmi9koJou51tbH9kKwEbHukoDgD5Ws6DfxVJqCgfHH7/FT6OpvoKijT33fY96UpvrWqV+OP3+Kn0dTfQVFGnvu+x70pTfWtQapqsPshXuGxn0nJ9UVZ5Vh9kK9w2M+k5PqigpatLuG33icO9GR/tWaK0u4bfeJw70ZH+1B/fEZ7xeY+i5foWZy0x4jPeLzH0XL9CzOQXQ9j09xmUekYvq1ZurqIKSkmq6qVsUEMbpJZHHYMa0bknyABVk9j09xmUekYvq1KPFRfjj2g+TVMbi2aqpxQx7dvdnCN3/AEOcfUgoZrFmVRn2o94yeZz+5VM5bSxuP8XA3wY27dnggE+Uk9q6zhZ0zj1J1GZFconPsVrYKq4bHYSc9o4t+zpEHf5LXdR2USq9fAZYYrdpDVXox7VF2uMji/xxxAMaPU7unzoLBQQxU8EcEETIoo2hkbGNAa1oGwAA6gAv7REBefkdmtmRWKssl5pI6u31sRinheOTmn6COsEcwQCF6CIMwNZcHqtPNRbpi85fJDBJ06SZ3XLA7nG7z7cj5QV2/B1nEuI6v0Vull2tt/LaCoaTyEhP8C/zh56Pme5SX7IZYom1eK5NHHtLIyehnf4w0tfGP+qVVRo6iejq4aumkdFPBI2SN7etrmncEeYhBrWi87F7m29Y1a7ywAMr6OGpaB1bSMDv2r0UBERAREQEREBcjqpqLjGm+OuvGR1gYXbimpY9jNUvHwWN+bcnYDfmVyGv+ueP6YUT6CDud0yWVm8FA1/gxbjk+Yj2re0N63dmw5ihWc5dkOb5FNfcjuEtdWzHYb8mxt7GMb1NaPEPpJKDptbNXcn1SvPd7pL3paoXk0dtiee5Qj4zvjv263Hy7ADkug4fNBr9qZVR3Sv7rasYjf8AwlYW+HUbHmyEHrPYXHkPKRspA4ceGee697ZTqNTSU1BykpbQ7dsk/aHTdrGfJ6z27Dkbj0lNT0dLFSUkEVPTwsDIoomBrGNA2DQByAA7EEf33RXTu66eRYQbDDS2+nBdSzQDaeGUjnKJDuS47DffcHYA7gBUa1u0hybS289xuUZrLTO8ijuUTCI5fkuHwH7fBPl2JHNaVLz8istqyKzVNmvdBBX2+qZ0JoJm7tcP2EdYI5g8wgoHw+a8X3TKrjtdf3W64xI/eSjLvDp9zzfCT1HtLTyPkJ3V9MNyexZhj9PfsduMVfQTjwZGHm09rXDra4doPNUg4jOHq66fyT5DjTZ7pi5Jc/l0pqHySbe2Z4n+o7ciY80i1NyfTLIBc7DU9KnkIFXQykmGpaOxw7HDscOY8xIIadKMtddZcb0ttO1U5tffJ2b0ltjfs53ifIfgM37es9gPPaJtQ+Laz/aRTOwmhn+2GtiPdW1cf8Hbj1Ensld2t25bbE/FVRLtcbrkN7muFyqqm43Ktl6Uksri+SV55D9gA9SD19SM6yTUHI5L5ktc6ondu2KJvKKnZvyZG34I/Wesknmpu4b+G2syfvbKc8gmorGdpKagO7JqwdYLu1kZ/KcOrYbE9Vw36KYfj/e2Vag3uxVd3G0lLbHVsT4qU9YdJ4Wz5PJ7VvlOxFm/tpxj747P/XY/3oOA1j0Lw7UDHIaKCjp7LcqGARW6spYQ0RMHtY3NGwdH5OsdhHPehmpOCZLp9kcljyWgdTzDd0MrecVQz48bvhD9Y6iAeS0y+2nGPvjs/wDXY/3rmtRaDTXP8clsWS3Oy1VO7nFIK2ISwP25Pjdv4Lh8x6iCOSCrnDjxJV2K97Yvnc09fYhtHT1x3fPRDqAd2vjHi9s0dW42Cuta6+hutuguNtq4KyjqGCSGeF4eyRp6iCORCzZ1p0yqdPL30Ke70N7s07j3pXUszHH8CRrSeg/b1HsPWB6mgutmR6XXEU7S+5Y9M/ept0j+Td+t8R+A/wDU7t7CA0bRc7p7muOZ5jkV9xq4Mq6V/J7Tykgf2skb1tcP19Y3BBXRICIiD/Hta9pa5oc0jYgjcEKlPEVw1Xm0XSqyTT6hfcrRO90sttgbvNRk8yGN63s8QHhDq2IG6uuiDJKaOSGV8U0bo5GOLXMcNi0jrBHYV/C1FzfTfBc1BOTYxbrhKRt3d0fQmA8QkZs8D1qF8s4QMKr+nJjt+u1mkd1MmDaqJvmB6LvncUFUMU1M1AxboCw5fd6OJntYe+C+Ef8ALfuz9SmHDOLrOba6OLJrTbL9APbSMBppz627s/6Avgy/hO1KtIfLZZrVkEI6mwT9xmI8rZNm/M4qFsoxnIcXr+8cistfaqg79FlVA6PpAdrSRs4eUbhBoFpXr9p7n80dDTXB9ou0hDW0Nx2jdI7xRuBLX+QA9LyKV1kgCQQQSCOohXL4Nda7hkFQNPstrZKu4NjL7XWSnd8rGN3dE89ZcAC4OPMgEE8huE18QNT3pojmcu+3Ss1RF+Wws/vLMhac6+U3feieZxbb7WWpk/IjL/7qzGQWw9jtpg65ZpWFo3jho4gfwnTE/wDYFcBU89juq2svGZUJeA6WnpJQ3xhjpQT/ANY+dXDQEREBeLntMK3Br/RuaHCe2VMRB7elE4ftXtLwtQ6ttBgGRVz3hjae11MpcewNicd/1IMrFZH2Pyp6Gqt7pCdhNZHv85bPEP7xVblZP2Pul6eqN9rNv4qyuj3/AApoj/cQXB1C9wORei6n6pyyrWqmoXuByL0XU/VOWVaC1fsd7Gm/ZhIR4TaWlaPMXSb/AEBXFVPPY7v/ADnMv/j0n/dKrhoCIiCu3sgHvNWn+kMP9nqFRhXn9kA95q0/0hh/s9QqMINGeENrW8OuKho2BZUn1mqmUrqKeEX/ANO2Kf8ADqP7TKpWQEREFCeOv38z6Kp/peocwr3ZWT0hB9Y1THx1+/mfRVP9L1DmFe7KyekIPrGoNWVmTxA1JqtbszlO+7bzUx8/kPLf7q02WaHEfTd6a65jFtt0rpJL+Xs/+8g4SjqqmjqWVNHUTU87N+hJE8sc3cbHYjmORK9L7acm++K7/wBdk/euk4f8fsmVav2DHsjgfPbK6SWOZjJTG4nuLyzZw5jwg1XK+5e0e/mSu/SM3+JBRH7acm++K7/12T96fbTk33xXf+uyfvV7vuXtHv5krv0jN/iT7l7R7+ZK79Izf4kFEftpyb74rv8A12T96fbTk33xXf8Arsn71e77l7R7+ZK79Izf4k+5e0e/mSu/SM3+JBn3LI+WV0sr3PkeS5znHcuJ6yT2lT5wHVDodb5YwdhPZ6iM+XZ8bv7qsR9y9o9/Mld+kZv8S6LT3RHT3A8ibf8AG7bVU9e2J8QfJWSSDou6xs47diCSFkpVfyqX8M/Sta1kpVfyqX8M/Sgn3gJphPrXWSkA972SeQHxEywt/vFXuVG/Y/ffhvH9H5f7RTq8iAiIgL8a+kp6+hqKGribLT1ETopY3dT2OBBB84JX7Igyv1Gxqow7O71jFT0i+3Vb4WucOb2b7sf+M0td61KnBJln2vayxWmeXo0l+p3UbgTyEo8OM+fdpaPw11XH7iHeOXWfNKaLaG5wGkqnAcu7Re1J8rmHb/lqttjuVVZr1Q3ehk7nV0NRHUQO+K9jg5p+cBBrIi8zE71S5HjFrv8AQnemuNJHUx899g9odsfKN9j5l42sWUtwvTHIMl6YbLR0b+99+2Z3gRD8tzUFCuKPLPtv1sv1ZFL3SkopfsfS89x0Id2kjyF/Td+MvJ0HxA5xqxYcffH06WSpE1Zy5dwj8OQHxbhvR87guHe5z3l73FznHcknckq3nsfeIdCmv2dVMXOQi20biPgjZ8pHnPcxv8koLZgAAADYBERBQPjj9/ip9HU30FRRp77vse9KU31rVK/HH7/FT6OpvoKijT33fY96UpvrWoNU1WH2Qr3DYz6Tk+qKs8qw+yFe4bGfScn1RQUtWl3Db7xOHejI/wBqzRWl3Db7xOHejI/2oP74jPeLzH0XL9CzOWmPEZ7xeY+i5foWZyC6HsenuMyj0jF9Wuh486kwaI08Q32qLzBGfVHK7+6ue9j09xmUekYvq11HHTTd30LdLtv3tdKeXzbh7P76Cg69Kiv18oqZtNRXq400DN+jHFVPY1u53OwB26yvNVv+G3Q7TPOtILTkN8tVVPc5ZJ46l8dbIxpLZnhvgg7Dweigqx9tOTffFd/67J+9Ptpyb74rv/XZP3q933L2j38yV36Rm/xJ9y9o9/Mld+kZv8SCiP205N98V3/rsn70+2nJvviu/wDXZP3q933L2j38yV36Rm/xJ9y9o9/Mld+kZv8AEgoPcbxdrjG2K4XSurGNPSa2eodIAfGAT1r4VoN9y9o9/Mld+kZv8SfcvaPfzJXfpGb/ABIOw4fqh1TohhkjjuRZ6eP8lgb+xd0vMxWxW7GMdobBaInxUFDEIYGOeXlrR2EnmV6aAiIgIiIC8TPW35+E3pmLOY2+GhlFAXbbCboHodfLffbbflvtvyXtogyfySO8Q3+ujyFtY27Cd3fgrOl3bum/hdPpc99/Gps4M6jTCDN//vJnRv5kb9h5asjvRrvX1S7+1LuXVts7be0GvOieOao24zvDLbkMLNqa5Rs5nbqZKPhs/WOztBoNqHhOR4FkctiyW3vpalnON45xzs7Hxu6nNP6uo7HcINTUVKOHHiVq8d72xfUCeats42jprkd3zUg6g1/a+MeP2zfKNgLoW+spLjQwV1BVQ1VLOwSQzQvD2SNPMEEciEH7oiIP5kYySN0cjGvY4FrmuG4IPWCFUriQ4Zv5TlemtJ45Kuyxj1l1OP8A/P8AJ7Gq26IMkZY5IpXxSsdHIwlrmuGxaR1gjsK/lazy2+gmkMktDTSPd1udE0k+vZfx9i7Z/N1H+Yb+5Bk2i1k+xds/m6j/ADDf3J9i7Z/N1H+Yb+5Bk2i1k+xds/m6j/MN/cuV1OyvBtOsdfeskjooWHdsFOyBjpql/wASNvafL1DtIQZiopB1p1UvOpV77tPTwWy0wOJo7dTtAZGPjPIA6b9u09XPYBe7oBoXkGp9ayvqO62vGon7TV7meFNsebIQfbHsLvat8p5EPq4PIM3fq/b5cV76bb2SN+zThv3DvbnuJOwuPPojr35jqK0IXhYNiWP4TjsFhxu3RUVFDz2bzdI7te93W5x8Z+gBe6gIiIIc4g9cW6SXW1UUmLvu7LjA+Vsja7uHQLXAFu3Qdv1g7qL/ALs+n/3dy/pgf5K9X2QTHJKzDMfyeFpP2NrJKaYDsZM0EOPkDogPx1S1BrHYbgy7WOgusbOgyspo6hrd9+iHtDgN+3rX2qFeD7UGiy/SqhsslQz7MWGJtHUQl3hOhbyikA7WloDfO0+MKakBeVlWOWPKbLNZshtlNcaGYbPimZvt5Qetrh2EbEdi9VEGZOvGCjTrU+6YzDK+ajjLZqOR/tnQvHSaD5RzaT2lu/avG01vM+P6g4/eqaUxPo7jDIXD4vTHSB8hbuD5Cu/4wMmt+T64XOW2SsnprfDHQd2Yd2yPZuX7Ht2c5zd/kqOMGtM19zOy2aniMslbXwwBo7ek8A/qKDUy9W+nu1mrbVVDenraeSnlHjY9paf1FZSXigqLVdqy2VTejUUc74JR4nscWn9YWsyoZxt4G7GdTzktHTlttyBpnLmt8FlS3lK3zu5P8pc7xIPK4MsnhxvXGggqX9CC8wPtriTyD3lro/nexrfxloQslKSonpKuGrpZnwzwvbJFIw7OY5p3BB7CCN1o3w6asW3U/DopHzxx5DRRNZc6XkD0ururR2sd1+Q8vFuEoIiICh3jEyeHHNDbtB09qq8OZbqdoPM9M7yerubX+sjxqXK6qpqGjmra2oip6aCMySyyuDWRsA3LiTyAA7VnrxTarN1MzhrLVJJ9r1rDoqEOBb3Zx9vMR2dLYAb/AAQOokhBD6uV7HtYO443k2TyM51VXHRROI6hE3pu28hMrfyVTygpKmvroKGigkqKmokbFDFG3dz3uOzWgdpJIC070cw2DAdN7NjEQYZaWAOqnt/1k7vCkd5R0idvIAOxB6WoXuByL0XU/VOWVa1U1C9wORei6n6pyyrQWt9ju/8AOcy/+PSf90quGqeex3f+c5l/8ek/7pVcNAREQV29kA95q0/0hh/s9QqMK8/sgHvNWn+kMP8AZ6hUYQaNcIv/AKdsU/4dR/aZVKyinhF/9O2Kf8Oo/tMqlZAREQUJ46/fzPoqn+l6hzCvdlZPSEH1jVMfHX7+Z9FU/wBL1DmFe7KyekIPrGoNWVQvjqsJtetQurI9orxb4Zy4DkZGbxOHn2Yw+tX0UGcaOByZdpUbvb6YzXOwSGqjDG7ufARtM0eoNf8A8tBSLTi/OxfPrDkQJDbfcIZ37drGvBePW3cetamwSxTwRzwSNkikaHse07hzSNwQfEskleXgx1ZpMkxKnwS8VTI73aIhHR90fsaumaPB6O/W5g8Ej4oB8ewWLREQF+VZUwUdJNV1UrIaeCN0ksjzs1jWjcknxABfqq/cZ2qFHi+B1GG2+qa++XyIxSMYQTT0p5Pc7xdMbtA7d3HsQSB/416T/f7ZPz69zEM9w3L6qemxnIqC6zU7BJKymk6RY0nYE+tZaK9HAphU1g03rMorY+hUZBM10II5ini6TWH8ZznnyjolBYhZKVX8ql/DP0rWtZKVX8ql/DP0oLD+x++/DeP6Py/2inV5FRv2P334bx/R+X+0U6vIgIiICIiCM+J7EPtz0XvlBFF3Sto4+/6MAbnukW7iB5XM6bfxlm2tcCARsRuCsyNesQOD6s37H44+50jKkzUY25dwk8NgHj2Duj52lBbbgVyv7N6STWCeXpVNhq3RAE7nuEu72H8rug8zQue9kCyrvXF7Fh0Em0lfUOrakA8+5xDosB8hc4nzxqKeB7KvsDrI2zzSdGlvtK+lIJ5d1Z/CRnz+C5o/DXgcWuU/bTrle3xydOltjhbafnuAItw//wDYZD60EURsfJI2ONrnvcQGtaNySewLT/RnEmYPphYca6DWzUtK01JHbO/w5D5fDc7bybKi3CdiH23612eOaLulFayblVbjcbREdAHzyFg28W60YQEREFA+OP3+Kn0dTfQVFGnvu+x70pTfWtUr8cfv8VPo6m+gqKNPfd9j3pSm+tag1TVYfZCvcNjPpOT6oqzyrD7IV7hsZ9JyfVFBS1aXcNvvE4d6Mj/as0Vpdw2+8Th3oyP9qD++Iz3i8x9Fy/QszlpjxGe8XmPouX6Fmcguh7Hp7jMo9IxfVqY+IWwnJdFcrtTY+6Sm3vqImgbkyQ7StA8pLAPWoc9j09xmUekYvq1aAgEEEbg9YQZHq6vsfuSMq8HvmLyygz2+tFVEwnn3KVoB28gcw/lBVv4i8El0+1Vutpjp3RW2okNXbXbeC6B53DR+Ad2fir5tB9QqjTTUaiyJrHzUTgaevgaeckDiOlt5QQHDytCDTRF8Ngu9sv8AZqW8WathraCrjEkE8Tt2vaf29hHWCCCvuQERfNdrhQ2m21FyuVXDSUdNGZZ55XBrI2gbkklB4eX5/hmI1cNHk2SW61VE8fdIo6iXouc3fbcDxbgheH/416T/AH+2T8+qIcQWoB1J1Or8hiD2W9gFLb2PGzmwM36JI7C4lziOzpbdi+HRbDKjPtS7NjUTCYZ5xJVvA5R07PCkP5IIHlIHag06pZ4qqmiqad4khlYJI3jqc0jcH5l+i/xjWsaGMaGtaNgANgAv9QEREBERAREQFy+peBY1qHjklkyWhE8XN0MzPBmp3/Hjd2H9R6iCF1CIM29c9Gsl0tuv+mMNfZJ37UlziYQx3iY8fAft2Hkewnnt9+gWuOQ6X1zaKTul0xuV+89ve/nFv1vhJ9q7xjqd27HmNCb3arbe7VU2q70UFdQ1LDHNBMwOY9vlB/8A4KkHEbw5XLCjUZLh0c9yxwbvmg5vnoR279r4x8brA9t1dIhc3A8wx7OMdhv2NXCOto5eTtuT4n9rHt62uHiPnG4IK95ZeaW6h5NpxkTbzjlaYy7YVNNJu6GpYPgvb29uxGxG/IhX90R1exnVKzd1tsgo7tAwGstkrx3SL5TT8Nm/wh5NwDyQSMiIgIiICIq18R3EnRYx3zi+BTQ117G8dRXjZ8NGeohvY+QfktPXudwA73XvW7HdLre6l3Zc8imZvT25j/ab9T5SPaN8nW7s7SKD6gZnkWd5HNfcluD6yrk5Mb1Rws7GRt6mtHi9Z3JJXwMbe8pyINaK273i4z/KlmnkcfWSVdDhx4bqHE+9snzmKC4X8bSU9FyfBRHrBPY+QeP2rT1bkByCOuHHhpqr93tlOoVPNR2o7SU1rduyaqHWHSdrGeT2x8g67m0NJS0FFDRUNNDTUsDBHFDEwMZG0DYNAHIAeJfsiAiIgIiIPHzbHLbl2J3LGrvH3SiuEDoZNutp62vHymkBw8oCzS1TwK/adZbUY9foNns8OnqGg9zqYieUjD4j2jrB3B6lqKuc1BwfGM8sbrPk9rirYOZiefBlgd8aN45tPm6+o7jkgzIxLJL5id8gveO3Oe3V8B8CWJ3WO1rh1Oae1pBBVksP4xbvTU0cGVYlTXCRo2dU0NQYCfKWODhv5iB5F+Wf8H99pZnT4RkFNcacncU1w/gZmjxB7QWv85DFFN20D1etkzopsIr5tvhUz452nzFjigsDPxk442HeDC7tJLt7V9VG1u/nAP0KKtUuKHOcvoJrVZqeDGbfM0tk72kMlS9p62mUgbD8FrT5VwMOjuqkshY3AMhBHx6J7R85AC6rGuGbVy8vYZrFT2iFx/ja+rY3bztYXP8A+lBDSthwSaRVv2Uj1LyGkkggiYRZopBsZXOBa6fb4oaSG+MknsBPb6TcKuKY1VRXTLqsZNWs2cymdF0KRjvK0kmT8bYeNqsRGxkcbY42tYxoAa1o2AA7Ag/1ctqpg9p1Dwmuxi7t2ZO3pQThu7qeYe0kb5QeztBI7V1KIMudTsCyPTvJpbFkdGYpBu6Cdm5iqY9+T43do8nWOogFeNjt8u+O3eC72K41Nur4DvHPA8tcPJ5Qe0Hke1agZ1hmM5xZjaMotFPcaXclnTGz4nbbdJjx4TT5QVVnUHg/ukVTNU4NkNNU0x8JlJct45W/JEjQWu9Yag8/D+MLKqClbBk2M2+9PaABPTzGke7yuHRe0nzBoXv3HjNZ3Ei3afu7qW8nT3TwWnzCPmPWFCd10B1et0j2TYRXTBp26VNJHMD5R0HFfBR6Lar1b+hFgN9ad9v4WmMQ+d2wQfTqxrXnmpLO9b1cI6W2B3SFvoWmOAnsLtyXPP4RIHYAo4aC4gAEk8gB2qdsP4VtUL09r7rDb8fg35mrqBJIR5GRdLn5CWqymjvDthGAPguVVGb/AH2IhzayrYAyJw7YouYafET0nDsIQcDwgaF1mP1EefZnRCG4Oj/+l0Mo8OnDhzlkHY8jkG9YBJPPba0aIg8PUL3A5F6LqfqnLKtav5RQS3XGbpa4HsZLWUc1Oxz9+iHPYWgnbs5qmX3HmfffJjP5yf8Ay0Ht+x3f+c5l/wDHpP8AulVw1BPC3ovkOlFffqi93O11rblFCyIUbpCWlheT0uk1vxh1KdkBERBXb2QD3mrT/SGH+z1CowtHeJrTa76pYFRY/Za6ho6inukdY59WXhhY2KVhA6LSd95B2dhVdPuPM+++TGfzk/8AloLEcIv/AKdsU/4dR/aZVKy4vQ/Ea/BNLbLidzqKapq6Bsoklpy4xu6Uz3jbpAHqcOxdogIiIKE8dfv5n0VT/S9Q5hXuysnpCD6xquLxHcPmU6l6i/bLZ7xZqSm7yip+51T5Q/pNLtz4LCNufjXB4/wkZ1br9b7hLkWOPjpaqOZ7WyTbkNcCQP4Pr5ILoIQCCCAQesFEQUQ4qtDK3CrxV5djdIZsXqpenJHE3nb3uPNpH+zJPgu6hv0T2EwLb6yrt1dBXUFTNS1VO8SQzRPLXxuB3DgRzBC1lqYIKqnkpqmGOeCVhZJHI0Oa9pGxBB5EEdirnqzwo41kFS+5YVWtxyreS59I9hkpHn5IB6Ufq3HiaEEb6dcXOT2mkiocwssF/Yzwe/IZBT1BHjcNixx8wb5VIbeMTBu4bnGMjEvxQ2Ho/P3T9igbJeGrV2zVLo4seiu0I6p6CqY9rvxXFr/nauZdo3qoJ+4nAMg6XjFG4t/K6v1oJiz/AIvshuNLJSYdj9PZemC3vyql74mA8bW7BrT5+mFWy8XO4Xi51F0utbPW1tS8vmnneXve7xklSljvDfq9eZg12Mi2xds1fUxxtH4oJf8AM1Trpdwj2S1zQ3DPLsbzOwh3eFJvHTb+Jzz4bx5gz1oIQ4a9FblqZfo7lcIZKbFaOUGrqDu01JHMwx+MntcPag+PYHQijpqeio4aOkgjgp4I2xxRRt6LWMaNg0AdQAGy/m3UVHbqGGht9LBSUkDAyKGGMMZG0dQa0cgF+6AslKr+VS/hn6VrWqSTcH2evme8ZJjWznEj+En/AMtB83sfvvw3j+j8v9op1eRV24ZNBsm0tzquv96u1orKeotj6NrKR0heHOlieCekwDbaM9vaFYlAREQEREBVN9kExDp0lhzmmi8KJxttY4D4J3fET5Ae6Df5QVslymrmHxZ5pzesVkfHHJW05FPI8eDHM0h0bjtz2Dmt327N0GZGP3WtsV9oL1bZe5VtBUR1MDyNwHscHN3HaNx1L5ameapqZamokdJNK8vke483OJ3JPrVjvuPM+++TGfzk/wDlp9x5n33yYz+cn/y0El8BGIfYvT+45fUxbVF6qe5U7iP9RCSNx53l+/4AVk14+E2ClxXELTjlFt3C3UkdO1223TLWgFx8pO5PlK9hAREQUD44/f4qfR1N9BUUae+77HvSlN9a1W74ieHnKtSNSZcmtF5stJSvpYoRHVPlD92AgnwWEbc/GuLxfhLzm1ZLa7pPkOOPio6yGoe1kk3SLWPDiBvH18kFzFWH2Qr3DYz6Tk+qKs8og4oNK71qrjtottkuFvopaKrdPI6sc8NcCzo7DotPNBnctLuG33icO9GR/tVZPuPM9++TGfzk/wDlq2uk+OVeI6cWLGa+eCept1I2CWSEkscRvzG4B284QeRxGe8XmPouX6FmctStVcdq8t05v2NUM0MFTcaN9PFJMSGNc7tOwJ28wVSPuPM9++TGfzk/+Wg732PT3GZR6Ri+rVoFD3C9pVe9KrBeLfe7hb62SuqmTRuo3PIaAzbY9Jo5qYUEZ8RGlVHqnhfeDHxU15onGa21Txya4+2jdtz6DgAD4iAee2xzvymwXjF79VWK/UEtDcKV/QlhkHMeIg9RBHMEciOYWri4rVLS7DdSLeKfJbW19QxnRgroT0KmD8F/aPku3b5EFBdItYM00yqS2w1rJrdI/pz26qBfBIe0gdbHeVpHUN99tlYqw8ZFgkp2fZ7DbnTT9Tu8qhkzT5R0+gR5v1rhs54RMztr5Z8Uu9vvtOCSyGY97VG3YOe7D5+kPMoyuGhurdBv3fA7u/b/AGDGzf8AYSgsTe+MjHI4HfYTDbtVTfBFZURwNHlPR6Z9Sr1q/rVm+ph72vFXHR2pruky20YLIdx1F+5JefOdh2AL8aDRHVmtc0Q4HeWlx2HdohD/AN5GyknCeEfPLnURSZPcbbYaQ85Gsf3zUDyBrfA9fT9RQV9tNurrtc6e2WykmrK2pkEcEELC58jj1AALQPhe0dj0wxqSsuohmyW5NBq5GeEKePrEDT27Hm4jrO3WGgrotItH8L0zpT9gqF09xkb0ZrjVkPqHjtAOwDG+RoHl3UgoCIiAiIgIiICIiAiIgIQCCCNwesIiCrPEdwzwXTvnKtOKWOnruclVaG7Njn7S6Hsa75HUezY8jUm03G+YpkUddb6istN3oJjs5u8csTwdi0g+sFp8oIWrihriD0FsWplLJdbd3G1ZQxn8HVhu0dTsOTJgOvxB45jygbIPH4dOIi1Z42nx3KXQWvJtgyN2/Rgrj8jf2r/kdvwfEJ9WVOYYzf8ADMinsmQUE9uuNM7ctd2jsexw5OaduThyVleHHiafS97YrqTVukg5R0t5ed3M7A2fxj/8nWPhb83ALgr8LjW0duoJ6+4VUNLSU7DJNNM8MZG0dZJPIBeJm2bYxh2LPyW/XWCC3dEOie1weagkbtbGB7ckdW3Zz6tyqF6+64ZDqhXuo2d0tmNxP3p7ex/OTbqfMR7Z3k6m9nPckO94juJSsyPvnF8AnmorMd46m4jdk1WOotZ2sjP5TvINwYM07wjI8+yOKxY1QPqql/OR55RQM35vkd1NaPnPUATsF1OhejWS6pXXeka6gscD+jV3OVm7G+NkY+G/bsHIdpHLe/mmuB41p7jkdjxqhbTwjZ00zvCmqH/Hkd2n9Q6gAEHJ6D6KY5pbbRPGGXHIJmbVVykZsRv1siHwGfrPaeoCU0RAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERBxeremeMamY+bZf6XaeME0lbEAJ6Zx7WntB7WnkfOARQDWXSrJ9L773leoO70Ezj3ncYmnuNQP7r9utp5jyjYnTNeZlOP2bKLFU2S/wBvguFvqW9GSGUbjyEHrDh1gjYg9SDK6tulzraKjoay4VdRS0TXMpYZZnOZA1x3IY0nZoJ58lPfDlw5XHNDT5LmUc9txw7Php+bJ64dm3ayM/G6yPa9fSE3YHwtYHjOXuvtXVVd8gif06KhrGN7lEd9wX7fxpHZuAPGCp7AAGwGwCD47La7dZbVT2q00UFFQ0zBHDBCwNYxo7AAvsREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERB/9k=" alt="Somerville" style="height:28px;display:block;filter:brightness(0) invert(1);print-color-adjust:exact;-webkit-print-color-adjust:exact"/>
        <div class="hdr-divider"></div>
        <span class="hdr-field-ops">Field Operations</span>
      </div>
      <div class="hdr-center">
        <div class="rpt-title">Site Completion Report</div>
        <div class="rpt-sub">${job.storeName || ""}</div>
        <div class="status-pill">${sc.icon ? sc.icon + " " : ""}${sc.label}</div>
      </div>
      <div class="hdr-right">
        <div class="gen-label">Generated</div>
        <div class="gen-date">${new Date().toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" })}</div>
        <div class="gen-sro">SRO# ${job.sroNumber || "—"}</div>
      </div>
    </div>
  </div>

  <div class="page-body">

  <!-- ── Job Details ── -->
  <div class="section">
    <div class="section-title">Job Details</div>
    <div class="detail-grid">
      <div class="dr"><span class="dl">SRO Number</span><span class="dv">${job.sroNumber || "—"}</span></div>
      <div class="dr"><span class="dl">RL Number</span><span class="dv">${job.rlNumber || "—"}</span></div>
      <div class="dr"><span class="dl">Site Name</span><span class="dv">${job.storeName || "—"}</span></div>
      <div class="dr"><span class="dl">Site Address</span><span class="dv">${job.storeAddress || "—"}</span></div>
      <div class="dr"><span class="dl">Installation Type</span><span class="dv">${snap?.label || "—"}</span></div>
      <div class="dr"><span class="dl">Status</span><span class="dv">${sc.label}</span></div>
    </div>
  </div>

  <!-- ── Installer Details ── -->
  <div class="section">
    <div class="section-title">Installer Details</div>
    <div class="detail-grid" style="margin-bottom:18px">
      <div class="dr"><span class="dl">Lead Installer</span><span class="dv">${job.installerName || "—"}</span></div>
      <div class="dr"><span class="dl">2nd Installer</span><span class="dv">${job.secondInstaller || "N/A"}</span></div>
      <div class="dr"><span class="dl">Clock-In Time</span><span class="dv">${fmtDt(job.startTime)}</span></div>
      <div class="dr"><span class="dl">Submission Time</span><span class="dv">${fmtDt(job.submittedAt)}</span></div>
      <div class="dr"><span class="dl">Time on Site</span><span class="dv">${
        job.startTime && job.endTime
          ? (() => {
              const ms = new Date(job.endTime) - new Date(job.startTime);
              const h = Math.floor(ms / 3600000);
              const m = Math.round((ms % 3600000) / 60000);
              return h > 0 ? `${h}h ${m}m` : `${m}m`;
            })()
          : "—"
      }</span></div>
    </div>
    <div class="loc-grid">
      ${locHtml("Clock-In Location", job.clockInLocation)}
      ${locHtml("Submission Location", job.submitLocation)}
    </div>
  </div>

  <!-- ── Checklist ── -->
  ${snap && snap.questions.length > 0 ? `
  <div class="section">
    <div class="section-title">${snap.label} — Site Checklist</div>
    ${checklistHtml}
  </div>` : ""}

  <!-- ── General Photos ── -->
  ${generalPhotosHtml}

  <!-- ── Site Sign-Off ── -->
  <div class="section">
    <div class="section-title">Site Sign-Off</div>
    <div class="detail-grid" style="margin-bottom:12px">
      <div class="dr"><span class="dl">Site Contact</span><span class="dv">${job.siteContactName || "—"}</span></div>
      <div class="dr"><span class="dl">Title / Role</span><span class="dv">${job.siteContactTitle || "—"}</span></div>
      <div class="dr"><span class="dl">Phone / Email</span><span class="dv">${job.siteContactPhone || "—"}</span></div>
    </div>
    ${job.signatureData
      ? `<div class="sig-wrap">
           <div class="sig-top"><img src="${job.signatureData}" alt="Signature" class="sig-img"/></div>
           <div class="sig-bot">
             <div class="sig-name">${job.siteContactName || "Name not recorded"}</div>
             ${job.siteContactTitle ? `<div class="sig-role">${job.siteContactTitle}</div>` : ""}
           </div>
         </div>`
      : `<div class="sig-none">No signature captured.</div>`
    }
  </div>

  <!-- ── Approval Details ── -->
  <div class="section">
    <div class="section-title">Approval Details</div>
    <div class="detail-grid">
      <div class="dr"><span class="dl">Approved By</span><span class="dv">${job.approvedBy || "—"}</span></div>
      <div class="dr"><span class="dl">Approval Date / Time</span><span class="dv">${fmtDt(job.approvedAt)}</span></div>
      ${completedRow}
    </div>
  </div>

  <!-- ── Activity Log ── -->
  ${(job.auditLog?.length>0)?`
  <div class="section" style="page-break-inside:avoid">
    <div class="section-title">Activity Log</div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;font-family:'Barlow',sans-serif;">
      <thead>
        <tr style="background:#f0f0f0;">
          <th style="text-align:left;padding:6px 10px;font-weight:700;color:#333;border-bottom:1px solid #ddd;width:38%">Action</th>
          <th style="text-align:left;padding:6px 10px;font-weight:700;color:#333;border-bottom:1px solid #ddd;width:22%">Performed By</th>
          <th style="text-align:left;padding:6px 10px;font-weight:700;color:#333;border-bottom:1px solid #ddd;width:20%">Date / Time</th>
          <th style="text-align:left;padding:6px 10px;font-weight:700;color:#333;border-bottom:1px solid #ddd;">Note</th>
        </tr>
      </thead>
      <tbody>
        ${job.auditLog.map((entry,i)=>{
          const actionLabels={job_created:"Job Created",job_submitted:"Job Submitted",job_resubmitted:"Job Resubmitted",job_approved:"Job Approved",job_rejected:"Job Rejected",job_completed:"Job Completed",job_requires_revisit:"Flagged: Requires Revisit",job_aborted:"Job Aborted",job_incomplete:"Job Marked Incomplete"};
          const label=actionLabels[entry.action]||entry.action;
          const bg=i%2===0?"#fff":"#f9f9f9";
          return `<tr style="background:${bg};"><td style="padding:6px 10px;border-bottom:1px solid #eee;font-weight:600;">${label}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;">${entry.actor||"—"}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;">${fmtDt(entry.timestamp)}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;color:#555;">${entry.note?`"${entry.note}"`:"—"}</td></tr>`;
        }).join("")}
      </tbody>
    </table>
  </div>`:""}

  <!-- ── Footer ── -->
  <div class="footer">
    <span>Somerville Merchandising · Field Operations</span>
    <span>SRO# ${job.sroNumber || "—"}</span>
    <span>${new Date().toLocaleDateString("en-CA")}</span>
  </div>

  </div><!-- /page-body -->

  <script>
    window.onload = function() {
      document.title = "${docTitle}";
    };
  </script>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (!win) {
    alert("Please allow pop-ups to export this report.\nYou may need to click the pop-up blocked icon in your browser's address bar.");
    return;
  }
  win.document.write(html);
  win.document.close();
}

// ── UI PRIMITIVES ─────────────────────────────────────────────────────────────
function LogoDisplay({ height=24 }) {
  const [failed,setFailed] = useState(false);
  // The Somerville logo SVG ships with dark-toned vector fills. In dark mode we
  // invert it to white so it reads on the navy nav; in light mode we just force
  // it dark (`brightness(0)`) so it reads on the white nav. Both filters
  // produce a flat single-color silhouette of the logo — no soft greys.
  const isLight = getActiveTheme() === "light";
  const logoFilter = isLight ? "brightness(0)" : "brightness(0) invert(1)";
  return (
    <div style={{display:"flex",alignItems:"center",gap:12}}>
      {!failed
        ? <img src="https://experiencesomerville.com/wp-content/themes/candybox/assets/images/Somerville.svg"
            alt="Somerville" onError={()=>setFailed(true)}
            style={{height,filter:logoFilter,objectFit:"contain"}}/>
        : <span style={{fontFamily:"'Barlow',sans-serif",fontWeight:700,fontSize:height,color:B.white,letterSpacing:1}}>somerville</span>
      }
      <div style={{borderLeft:`1px solid ${B.border}`,paddingLeft:12,fontFamily:"'Barlow',sans-serif",fontSize:10,color:B.muted,letterSpacing:3,textTransform:"uppercase"}}>
        Field Operations
      </div>
    </div>
  );
}

function Card({children,style={},hoverable=false}) {
  return (
    <div className={hoverable?"sm-card":undefined}
      style={{background:B.card,borderRadius:10,padding:20,border:`1px solid ${B.border}`,boxShadow:B.shadowSm,...style}}>
      {children}
    </div>
  );
}
function SectionTitle({children}) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
      <div style={{width:3,height:18,background:B.blueLt,borderRadius:2,flexShrink:0,boxShadow:`0 0 8px ${B.blueLt}`}}/>
      <h3 style={{margin:0,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,color:B.white,letterSpacing:1.5,textTransform:"uppercase"}}>{children}</h3>
    </div>
  );
}
function FieldLabel({children,required}) {
  return <label style={{fontSize:11,fontWeight:700,color:B.muted,display:"block",marginBottom:6,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow',sans-serif"}}>{children}{required&&<span style={{color:B.blueLt,marginLeft:3}}>*</span>}</label>;
}
function ErrMsg({msg}) { return msg ? <div style={{color:B.red,fontSize:12,marginTop:5,fontFamily:"'Barlow',sans-serif"}}>⚠ {msg}</div> : null; }
const BADGE_GLOW = {
  pending:          "0 0 8px rgba(37,80,212,0.28)",
  approved:         "0 0 8px rgba(82,217,160,0.22)",
  completed:        "0 0 8px rgba(39,174,96,0.25)",
  rejected:         "0 0 8px rgba(192,57,43,0.28)",
  requires_revisit: "0 0 8px rgba(230,126,34,0.3)",
  aborted:          "0 0 6px rgba(149,165,166,0.18)",
  incomplete:       "0 0 8px rgba(241,196,15,0.22)",
};
const BADGE_PULSE_CLASS = {
  pending:          "sm-badge-pulse",
  requires_revisit: "sm-badge-pulse-amber",
};
function StatusBadge({ status="pending" }) {
  const cfg       = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const pulseClass = BADGE_PULSE_CLASS[status] || "";
  const glow       = BADGE_GLOW[status] || "";
  return (
    <span className={pulseClass||undefined}
      style={{
        display:"inline-block", whiteSpace:"nowrap",
        padding:"4px 11px", borderRadius:4,
        fontSize:10, fontWeight:800, letterSpacing:1.8, textTransform:"uppercase",
        fontFamily:"'Barlow Condensed',sans-serif",
        background:cfg.bg, color:cfg.color,
        border:`1px solid ${cfg.border}`,
        boxShadow: glow,
      }}>
      {cfg.icon ? `${cfg.icon} ${cfg.label}` : cfg.label}
    </span>
  );
}
function SInput({value,onChange,placeholder,error,type="text",onKeyDown}) {
  const [focus,setFocus]=useState(false);
  return <input type={type} value={value} onChange={onChange} placeholder={placeholder} onKeyDown={onKeyDown}
    onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
    className={`sm-input${error?" sm-err":""}`}
    style={{width:"100%",padding:"12px 14px",borderRadius:6,border:`1px solid ${error?B.red:focus?B.blueLt:B.border}`,background:B.surface,color:B.white,fontSize:14,fontFamily:"'Barlow',sans-serif",boxSizing:"border-box",outline:"none"}}/>;
}
function STextarea({value,onChange,placeholder}) {
  return <textarea value={value} onChange={onChange} placeholder={placeholder}
    className="sm-input"
    style={{width:"100%",padding:"10px 14px",borderRadius:6,border:`1px solid ${B.border}`,background:B.surface,color:B.light,fontSize:13,fontFamily:"'Barlow',sans-serif",boxSizing:"border-box",outline:"none",resize:"vertical",minHeight:52,lineHeight:1.5}}/>;
}

// ── STEP INDICATOR ────────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  return (
    <div style={{display:"flex",alignItems:"center"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
        <div style={{width:24,height:24,borderRadius:"50%",background:step>1?B.green:B.blue,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:B.onAccent,flexShrink:0}}>
          {step>1?"✓":"1"}
        </div>
        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,color:step>1?B.muted:B.white,letterSpacing:1,textTransform:"uppercase"}}>Job Information</span>
      </div>
      <div style={{height:1,flex:1,background:step>1?B.blue:B.border,margin:"0 8px"}}/>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <div style={{width:24,height:24,borderRadius:"50%",background:step===2?B.blue:B.surface,border:`1px solid ${step===2?B.blue:B.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:step===2?B.onAccent:B.mid,flexShrink:0}}>2</div>
        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,color:step===2?B.white:B.mid,letterSpacing:1,textTransform:"uppercase"}}>Installation Work</span>
      </div>
    </div>
  );
}

// ── INSTALLER PILL ────────────────────────────────────────────────────────────
function InstallerPill({ user }) {
  const initials = user.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  return (
    <div style={{background:B.blueDim,border:`1px solid ${B.blue}`,borderRadius:6,padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <div style={{width:36,height:36,borderRadius:"50%",background:B.blue,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <span style={{color:B.onAccent,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14}}>{initials}</span>
        </div>
        <div>
          <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:700,fontSize:14,color:B.white}}>{user.name}</div>
          <div style={{fontFamily:"'Barlow',sans-serif",fontSize:11,color:B.muted}}>{user.email}</div>
        </div>
      </div>
      <div style={{textAlign:"right"}}>
        <div style={{fontSize:10,color:B.muted,fontFamily:"'Barlow',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Signed in</div>
        <div style={{fontSize:11,color:B.blueLt,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>Installer</div>
      </div>
    </div>
  );
}

// ── SIGNATURE PAD ─────────────────────────────────────────────────────────────
function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x:(src.clientX-rect.left)*(canvas.width/rect.width), y:(src.clientY-rect.top)*(canvas.height/rect.height) };
  };
  const startDraw = e => { e.preventDefault(); drawing.current=true; lastPos.current=getPos(e,canvasRef.current); };
  const draw = e => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas=canvasRef.current, ctx=canvas.getContext("2d"), pos=getPos(e,canvas);
    ctx.beginPath(); ctx.moveTo(lastPos.current.x,lastPos.current.y); ctx.lineTo(pos.x,pos.y);
    ctx.strokeStyle=B.white; ctx.lineWidth=2.5; ctx.lineCap="round"; ctx.lineJoin="round"; ctx.stroke();
    lastPos.current=pos;
  };
  const endDraw = e => { e.preventDefault(); drawing.current=false; onChange(canvasRef.current.toDataURL()); };
  const clear   = () => { canvasRef.current.getContext("2d").clearRect(0,0,canvasRef.current.width,canvasRef.current.height); onChange(null); };

  useEffect(() => {
    if (!value && canvasRef.current) canvasRef.current.getContext("2d").clearRect(0,0,canvasRef.current.width,canvasRef.current.height);
  }, [value]);

  return (
    <div>
      <div style={{position:"relative",borderRadius:6,overflow:"hidden",border:`1px solid ${value?B.green:B.border}`,background:"#111",touchAction:"none"}}>
        <canvas ref={canvasRef} width={560} height={160}
          style={{width:"100%",height:160,display:"block",cursor:"crosshair",touchAction:"none"}}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}/>
        {!value&&<div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
          <span style={{fontSize:13,color:B.mid,fontFamily:"'Barlow',sans-serif"}}>Sign here with finger or mouse</span>
        </div>}
        <div style={{position:"absolute",bottom:0,left:16,right:16,height:1,background:B.border}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
        {value
          ? <span style={{fontSize:12,color:B.green,fontFamily:"'Barlow',sans-serif"}}>✓ Signature captured</span>
          : <span style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>No signature yet</span>}
        <button onClick={clear} style={{background:"transparent",border:`1px solid ${B.border}`,color:B.muted,borderRadius:3,padding:"4px 12px",fontSize:11,cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>Clear</button>
      </div>
    </div>
  );
}

// ── ANIMATED CHECKMARK ────────────────────────────────────────────────────────
function AnimatedCheck({ color="#52d9a0", size=72 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="36" cy="36" r="32" stroke={color} strokeWidth="3" fill={`${color}18`}
        className="sm-ring" strokeLinecap="round"/>
      <polyline points="20,37 31,48 52,26" stroke={color} strokeWidth="3.5"
        fill="none" strokeLinecap="round" strokeLinejoin="round"
        className="sm-tick"/>
    </svg>
  );
}

// ── LOADING SPINNER ────────────────────────────────────────────────────────────
function Spinner({ size=14, color="rgba(255,255,255,0.8)" }) {
  return (
    <span style={{width:size,height:size,border:`2px solid rgba(255,255,255,0.2)`,borderTopColor:color,borderRadius:"50%",display:"inline-block",animation:"spin 0.65s linear infinite",flexShrink:0}}/>
  );
}

// Pull the docs array from an installation type or a snapshot of one. Handles
// both the new instructionsDocs array and the legacy instructionsUrl single
// string. Use this for any display surface that lists instructions.
function getInstructionDocs(typeOrSnap) {
  const arr = typeOrSnap?.instructionsDocs;
  if (Array.isArray(arr) && arr.length > 0) return arr;
  const url = typeOrSnap?.instructionsUrl;
  if (url) return [{ url, filename: "Installation Instructions", _legacy: true }];
  return [];
}

// ── INSTRUCTIONS LINK ─────────────────────────────────────────────────────────
// PDFs render natively in a browser tab; Word docs (.doc/.docx) trigger a
// download by default. Wrap Word URLs in the Microsoft Office Online viewer
// so they render inline. The bucket is public-read, which is what the viewer
// needs to fetch them.
function getViewableUrl(doc) {
  if (!doc?.url) return null;
  const fname = (doc.filename || "").toLowerCase();
  // Also fall back to URL ext detection in case filename is "Installation Instructions" (synthesised legacy entry).
  const urlLower = doc.url.toLowerCase();
  const isWord = /\.(doc|docx)(\?|$)/.test(fname) || /\.(doc|docx)(\?|$)/.test(urlLower);
  if (isWord) {
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(doc.url)}`;
  }
  return doc.url;
}

// Used in records views (installer "My Records" + HO records detail) where the
// link is a subtle inline reference. Renders one anchor per document. The
// installer Phase 2 view uses a more prominent pill defined inline.
function InstructionsLink({ docs, label, compact=false, stack=false }) {
  const list = Array.isArray(docs) ? docs.filter(d => d?.url) : [];
  if (list.length === 0) return null;
  const linkStyle = {
    fontSize: compact ? 11 : 12,
    color: B.blueLt,
    textDecoration: "none",
    fontFamily: "'Barlow',sans-serif",
    whiteSpace: "nowrap",
  };
  // Multi-doc render: stacked links so each filename is visible.
  if (list.length > 1 || stack) {
    return (
      <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end"}}>
        {list.map((doc, i) => (
          <a key={i} href={getViewableUrl(doc)} target="_blank" rel="noopener noreferrer"
            title={doc.filename || "Installation Instructions"}
            style={{...linkStyle, maxWidth: 240, overflow:"hidden", textOverflow:"ellipsis"}}
            onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
            onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
            📄 {doc.filename || "Installation Instructions"}
          </a>
        ))}
      </div>
    );
  }
  // Single-doc render: one inline link with the supplied label (or filename).
  const doc = list[0];
  return (
    <a href={getViewableUrl(doc)} target="_blank" rel="noopener noreferrer"
      style={linkStyle}
      onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
      onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
      {label || `📄 ${doc.filename || "Instructions"}`}
    </a>
  );
}

// ── PER-QUESTION PHOTO ────────────────────────────────────────────────────────
function QuestionPhoto({ qId, photos=[], onUpdate }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(0);
  const handleFile = e => Array.from(e.target.files).forEach(async file => {
    setUploading(c => c + 1);
    try {
      const photo = await uploadPhoto(file, "questions");
      onUpdate(qId, [...photos, photo]);
    } catch (err) {
      alert(`Photo upload failed: ${err.message}`);
    } finally {
      setUploading(c => c - 1);
    }
  });
  return (
    <div style={{marginTop:10}}>
      {photos.length>0&&<div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
        {photos.map((p,i)=>(
          <div key={i} style={{position:"relative",width:56,height:56,borderRadius:4,overflow:"hidden",border:`1px solid ${B.blue}`}}>
            <img src={p.url||p.data} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            <button onClick={()=>onUpdate(qId,photos.filter((_,j)=>j!==i))}
              style={{position:"absolute",top:1,right:1,background:"rgba(0,0,0,0.75)",color:B.white,border:"none",borderRadius:2,width:16,height:16,fontSize:9,cursor:"pointer",lineHeight:"16px",padding:0}}>✕</button>
          </div>
        ))}
      </div>}
      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
        {/* Take a Photo — forces the rear camera on iOS/Android. capture="environment"
            tells the browser to launch the camera app instead of the file picker. On
            desktop browsers that ignore capture, this falls back to the file picker. */}
        <label style={{display:"inline-flex",alignItems:"center",gap:6,border:`1px solid ${B.blue}`,background:B.blueDim,borderRadius:4,padding:"6px 12px",cursor:uploading?"not-allowed":"pointer",opacity:uploading?0.6:1,fontSize:11,color:B.blueLt,fontWeight:700,letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>
          📷 Take Photo
          <input ref={ref} type="file" accept="image/*" capture="environment" onChange={handleFile} disabled={uploading>0} style={{display:"none"}}/>
        </label>
        {/* Choose from library — no capture attribute, so the OS file picker / photo
            library opens. Useful when the installer already has a photo or is testing
            from desktop. */}
        <label style={{display:"inline-flex",alignItems:"center",gap:6,border:`1px solid ${B.border}`,borderRadius:4,padding:"6px 12px",cursor:uploading?"not-allowed":"pointer",opacity:uploading?0.6:1,fontSize:11,color:B.muted,fontWeight:600,letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow',sans-serif"}}>
          🖼 Choose
          <input type="file" accept="image/*" multiple onChange={handleFile} disabled={uploading>0} style={{display:"none"}}/>
        </label>
        {uploading>0&&<span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif",display:"flex",alignItems:"center",gap:4}}><Spinner size={12} color={B.muted}/>Uploading…</span>}
      </div>
    </div>
  );
}

// ── SKELETON COMPONENTS ───────────────────────────────────────────────────────
// Skel: a single shimmer block. Use className sm-skel which already carries the animation.
function Skel({ w="100%", h=16, r=4, style={} }) {
  return <div className="sm-skel" style={{width:w,height:h,borderRadius:r,flexShrink:0,...style}}/>;
}
// Stats grid skeleton — mirrors the 8-card layout
function SkeletonStatCards() {
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(75px,1fr))",gap:6}}>
      {Array.from({length:8}).map((_,i)=>(
        <div key={i} style={{background:B.card,border:`1px solid ${B.border}`,borderRadius:6,padding:"12px 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:7}}>
          <Skel h={24} w="52%"/>
          <Skel h={9}  w="68%"/>
        </div>
      ))}
    </div>
  );
}
// Job row skeleton — mirrors the card in the admin job list
function SkeletonJobRow() {
  return (
    <div style={{background:B.card,borderRadius:10,padding:18,border:`1px solid ${B.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
        <div style={{flex:1,marginRight:16,display:"flex",flexDirection:"column",gap:7}}>
          <Skel h={18} w="44%"/>
          <Skel h={13} w="62%"/>
        </div>
        <Skel h={22} w={74} r={4}/>
      </div>
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        <Skel h={12} w={86}/><Skel h={12} w={66}/><Skel h={12} w={78}/>
      </div>
    </div>
  );
}
// History row skeleton — mirrors installer job history cards
function SkeletonHistRow() {
  return (
    <div style={{background:B.card,borderRadius:6,padding:16,border:`1px solid ${B.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:10}}>
        <div style={{flex:1,display:"flex",flexDirection:"column",gap:6}}>
          <Skel h={17} w="40%"/>
          <Skel h={13} w="54%"/>
        </div>
        <Skel h={22} w={66} r={4}/>
      </div>
      <div style={{display:"flex",gap:12}}>
        <Skel h={12} w={82}/><Skel h={12} w={92}/>
      </div>
    </div>
  );
}

// ── LOGIN SCREEN ──────────────────────────────────────────────────────────────
// ── BLUR-DISSOLVE PAGE TRANSITION WRAPPER ─────────────────────────────────────
// Applies .sm-blur-out (exit) or .sm-blur-in (enter) CSS animation.
// Only used on the Splash → Login and Login → Welcome transitions.
function BlurPage({ isExiting, isEntering, children }) {
  const cls = isExiting ? "sm-blur-out" : isEntering ? "sm-blur-in" : undefined;
  return <div className={cls}>{children}</div>;
}

// ── SPLASH SCREEN ─────────────────────────────────────────────────────────────
// Shown on first app load, before login. Animated fog/cloud background with
// prominent branding. Click "Enter Platform" to proceed to the login screen.
function SplashScreen({ onEnter }) {
  const [vis, setVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVis(true), 60); return () => clearTimeout(t); }, []);

  const fog = (cls, top, left, w, h, color, blur, delay) => (
    <div className={cls} style={{
      position:"absolute", width:w, height:h, top, left,
      background:`radial-gradient(ellipse, ${color} 0%, transparent 70%)`,
      filter:`blur(${blur}px)`, animationDelay:delay, willChange:"transform",
    }}/>
  );

  // See WelcomeScreen for the same pattern — light theme gets a clean page with
  // a couple of soft tints; dark theme keeps the dramatic deep-navy hero.
  const isLight = getActiveTheme() === "light";
  return (
    <div style={{
      position:"fixed", inset:0, overflow:"hidden",
      background: isLight ? B.black : "#05050d",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
    }}>
      {/* ── Fog layer ── */}
      <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
        {isLight ? (
          <>
            {fog("sm-fog1",  "2%",   "-5%",  "70vw", "65vh", "rgba(37,80,212,0.10)",  60, "0s"  )}
            {fog("sm-fog2",  "55%",  "30%",  "60vw", "55vh", "rgba(17,52,166,0.08)",  72, "-4s" )}
          </>
        ) : (
          <>
            {fog("sm-fog1",  "2%",   "-5%",  "80vw", "72vh", "rgba(17,52,166,0.55)",  32, "0s"  )}
            {fog("sm-fog2",  "30%",  "52%",  "72vw", "62vh", "rgba(37,80,212,0.45)",  40, "-3s" )}
            {fog("sm-fog3",  "58%",  "5%",   "65vw", "58vh", "rgba(13,37,128,0.50)",  36, "-6s" )}
            {fog("sm-fog4",  "-5%",  "42%",  "58vw", "52vh", "rgba(26,69,196,0.40)",  28, "-2s" )}
            {fog("sm-fog1",  "22%",  "18%",  "60vw", "64vh", "rgba(8,16,60,0.55)",    50, "-9s" )}
            {fog("sm-fog2",  "68%",  "58%",  "52vw", "46vh", "rgba(28,72,200,0.38)",  30, "-5s" )}
          </>
        )}
      </div>

      {/* ── Edge vignette — dark only ── */}
      {!isLight && (
        <div style={{
          position:"absolute", inset:0, pointerEvents:"none",
          background:"radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(3,3,10,0.85) 100%)",
        }}/>
      )}

      {/* ── Content ── */}
      <div style={{
        position:"relative", zIndex:10,
        display:"flex", flexDirection:"column", alignItems:"center",
        gap:0,
      }}>

        {/* Logo */}
        <div style={{
          opacity: vis ? 1 : 0,
          animation: vis ? "sm-splash-logo 1.0s cubic-bezier(0.22,1,0.36,1) forwards" : "none",
          display:"flex", flexDirection:"column", alignItems:"center",
        }}>
          {/* Large logo */}
          <div style={{marginBottom:24}}>
            <LogoDisplay height={56}/>
          </div>

          {/* Divider rule */}
          <div style={{
            height:1, background:`linear-gradient(90deg, transparent, ${B.blueLt}, transparent)`,
            width:80, animation: vis ? "sm-splash-rule 0.9s 0.35s ease forwards" : "none",
            opacity:0, marginBottom:20,
          }}/>

          {/* Tagline */}
          <div style={{
            fontFamily:"'Barlow',sans-serif", fontSize:11,
            color:"rgba(200,200,220,0.45)", letterSpacing:6, textTransform:"uppercase",
            animation: vis ? "sm-splash-sub 0.8s 0.5s ease forwards" : "none",
            opacity:0,
          }}>
            Installation Tracker
          </div>
        </div>

        {/* Enter button */}
        <div style={{
          marginTop:44,
          animation: vis ? "sm-splash-btn 0.7s 0.75s ease forwards" : "none",
          opacity:0,
        }}>
          <button
            onClick={onEnter}
            className="sm-btn sm-btn-primary"
            style={{
              background:`linear-gradient(135deg, ${B.blueLt} 0%, ${B.blue} 100%)`,
              color:B.white, border:"none", borderRadius:6,
              padding:"13px 52px", fontSize:11, fontWeight:800,
              letterSpacing:3.5, textTransform:"uppercase",
              fontFamily:"'Barlow Condensed',sans-serif",
              boxShadow:`0 4px 28px rgba(26,69,196,0.55), 0 0 0 1px rgba(37,80,212,0.35)`,
              cursor:"pointer",
            }}>
            Enter Platform
          </button>
        </div>

        {/* Version / env indicator */}
        <div style={{
          marginTop:32,
          fontFamily:"'Barlow',sans-serif", fontSize:10,
          color:"rgba(200,200,220,0.18)", letterSpacing:2.5, textTransform:"uppercase",
          animation: vis ? "sm-splash-sub 0.8s 1.0s ease forwards" : "none",
          opacity:0,
        }}>
          Field Operations Platform · Prototype
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin, users }) {
  const [email,setEmail]=useState(""); const [password,setPassword]=useState("");
  const [error,setError]=useState(""); const [loading,setLoading]=useState(false);
  const go = async () => {
    setError(""); setLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (authError) { setError("Invalid email or password."); setLoading(false); return; }
      const { data: userData, error: userError } = await supabase
        .from("users").select("*").eq("id", authData.user.id).single();
      if (userError || !userData) { setError("Account not found. Contact your administrator."); setLoading(false); return; }
      if (userData.active === false) {
        setError("This account has been deactivated. Contact your administrator.");
        await supabase.auth.signOut(); setLoading(false); return;
      }
      onLogin({ id:userData.id, name:userData.name, email:userData.email, role:userData.role, active:userData.active, createdAt:userData.created_at });
    } catch(e) { setError("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <div className="sm-login-bg" style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{marginBottom:40}}><LogoDisplay height={30}/></div>
      <div style={{width:"100%",maxWidth:400}}>
        <div style={{background:B.glass,backdropFilter:"blur(12px)",borderRadius:14,border:`1px solid ${B.border}`,overflow:"hidden",boxShadow:B.shadow}}>
          <div style={{background:`linear-gradient(135deg, ${B.blue} 0%, #0d2580 100%)`,padding:"24px 28px",borderBottom:`1px solid rgba(255,255,255,0.08)`}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.white,letterSpacing:0.5}}>Sign In</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.55)",marginTop:3,fontFamily:"'Barlow',sans-serif"}}>Field Operations Platform</div>
          </div>
          <div style={{padding:"28px 28px 24px",display:"flex",flexDirection:"column",gap:18}}>
            <div><FieldLabel>Email Address</FieldLabel><SInput value={email} onChange={e=>setEmail(e.target.value)} placeholder="yourname@somerville.com" type="email"/></div>
            <div><FieldLabel>Password</FieldLabel><SInput value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" type="password" onKeyDown={e=>e.key==="Enter"&&go()}/></div>
            {error&&<div style={{background:"rgba(192,57,43,0.12)",border:`1px solid rgba(192,57,43,0.4)`,borderRadius:6,padding:"10px 14px",fontSize:13,color:B.red,fontFamily:"'Barlow',sans-serif"}}>⚠ {error}</div>}
            <button onClick={go} disabled={loading} className="sm-btn"
              style={{background:loading?B.mid:`linear-gradient(135deg, ${B.blueLt} 0%, ${B.blue} 100%)`,color:B.onAccent,border:"none",borderRadius:6,padding:"14px",fontSize:13,fontWeight:800,cursor:loading?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",marginTop:2,boxShadow:loading?"none":"0 4px 16px rgba(26,69,196,0.4)"}}>
              {loading
                ? <span style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    <span style={{width:14,height:14,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block",animation:"spin 0.7s linear infinite"}}/>
                    Signing in...
                  </span>
                : "Sign In →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PRE-LOADED SRO PICKER (installer) ────────────────────────────────────────
// Lists all currently active pre-loaded SROs. When an installer taps one,
// we atomically claim it (status active → claimed) — if someone beat us to
// it the list refreshes and we show a message. On success, the onClaim
// callback fires with the claimed SRO and the parent jumps to the review
// screen.
function PreloadedSroPicker({ user, onClaim, onBack }) {
  const [sros,       setSros]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [claimBusy,  setClaimBusy]  = useState(null); // id of SRO being claimed
  const [claimError, setClaimError] = useState("");
  const [viewPdfFor, setViewPdfFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await dataService.loadPreloadedSros({ status: "active" });
      setSros(list);
    } catch(e) {
      console.warn("loadPreloadedSros:", e.message);
      setClaimError(e.message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const claim = async (sro) => {
    setClaimBusy(sro.id);
    setClaimError("");
    try {
      const claimed = await dataService.claimPreloadedSro(sro.id, user.id);
      if (!claimed) {
        setClaimError(`${sro.sroNumber} was just claimed by another installer. Refreshing…`);
        await load();
        return;
      }
      onClaim(claimed);
    } catch(e) {
      setClaimError(e.message || "Claim failed");
    } finally { setClaimBusy(null); }
  };

  const q = search.trim().toLowerCase();
  const filtered = sros.filter(s => !q
    || (s.sroNumber || "").toLowerCase().includes(q)
    || (s.rlNumber  || "").toLowerCase().includes(q)
    || (s.storeName || "").toLowerCase().includes(q)
    || (s.storeCity || "").toLowerCase().includes(q));

  return (
    <>
      <Card>
        <SectionTitle>Look Up Pre-loaded SRO</SectionTitle>
        <p style={{margin:"0 0 14px", fontSize:13, color:B.muted, fontFamily:"'Barlow',sans-serif", lineHeight:1.5}}>
          Tap an SRO to claim it and start work. Once you clock in it's locked to you.
        </p>
        <SInput
          value={search}
          onChange={e=>setSearch(e.target.value)}
          placeholder="Search SRO#, RL#, store, or city…"
          style={{marginBottom:14}}
        />

        {claimError && (
          <div style={{background:"rgba(192,57,43,0.12)", border:`1px solid ${B.red}`, borderRadius:4, padding:"9px 12px", color:B.red, fontFamily:"'Barlow',sans-serif", fontSize:12, marginBottom:12}}>
            {claimError}
          </div>
        )}

        {loading ? (
          <div style={{padding:20, textAlign:"center", color:B.muted, fontFamily:"'Barlow',sans-serif", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", gap:8}}>
            <Spinner size={14}/> Loading available SROs…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{padding:24, textAlign:"center", color:B.muted, fontFamily:"'Barlow',sans-serif", fontSize:13, background:B.surface, border:`1px dashed ${B.border}`, borderRadius:4}}>
            {sros.length === 0
              ? "No pre-loaded SROs available right now. Check with head office."
              : `No SROs match "${search}".`}
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {filtered.map(s => {
              const busy = claimBusy === s.id;
              const matchCount = (s.lineItems || []).filter(li => li.isService).length;
              const itemCount = (s.lineItems || []).length;
              return (
                <div key={s.id}
                  onClick={()=>{ if (!busy) claim(s); }}
                  className="sm-clickable"
                  style={{
                    background: busy ? B.blueDim : B.surface,
                    border: `1px solid ${busy ? B.blue : B.border}`,
                    borderRadius: 6, padding: "12px 14px",
                    cursor: busy ? "wait" : "pointer",
                  }}>
                  <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap"}}>
                    <span style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:B.white, letterSpacing:0.5}}>{s.sroNumber}</span>
                    {s.rlNumber && <span style={{fontSize:11, color:B.muted, fontFamily:"'Barlow',sans-serif"}}>RL {s.rlNumber}</span>}
                    {busy && <span style={{fontSize:11, color:B.blueLt, fontFamily:"'Barlow',sans-serif", display:"flex", alignItems:"center", gap:5}}><Spinner size={10} color={B.blueLt}/>Claiming…</span>}
                    <span style={{marginLeft:"auto", color:B.mid}}>→</span>
                  </div>
                  <div style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", marginBottom:3}}>
                    {s.storeName || "—"}
                  </div>
                  <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", marginBottom:6}}>
                    {[s.storeStreet, s.storeCity, s.storeProvince, s.storePostalCode].filter(Boolean).join(", ")}
                  </div>
                  <div style={{fontSize:11, color:B.muted, fontFamily:"'Barlow',sans-serif"}}>
                    {itemCount} line item{itemCount===1?"":"s"} · <span style={{color:matchCount?B.green:B.muted}}>{matchCount} service match{matchCount===1?"":"es"}</span>
                    {s.pdfPath && <>{" · "}<span onClick={e=>{ e.stopPropagation(); setViewPdfFor(s); }} style={{color:B.blueLt, cursor:"pointer", textDecoration:"underline"}}>View PDF</span></>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <button onClick={onBack}
        style={{background:"transparent", color:B.muted, border:`1px solid ${B.border}`, borderRadius:4, padding:"12px 16px", fontSize:13, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase"}}>
        ← Back to Start
      </button>

      {viewPdfFor && <SROPdfViewer pdfPath={viewPdfFor.pdfPath} filename={viewPdfFor.pdfFilename} onClose={()=>setViewPdfFor(null)} />}
    </>
  );
}

// ── INSTALLER VIEW ────────────────────────────────────────────────────────────
function InstallerView({ jobs, setJobs, user, installationTypes, users, services, setActiveSessions, defaultHistView, initialSlots, initialActiveSlot, onSaveDraft, onRegisterNav }) {
  const [slots,      setSlots]      = useState(()=> initialSlots || [{ form: initialForm(), errors: {}, done: null, sessionId: null }]);
  const [activeSlot, setActiveSlot] = useState(()=> initialActiveSlot || 0);
  const [tick,       setTick]       = useState(0); // drives live timer re-renders
  const [histView,   setHistView]   = useState(defaultHistView ?? null); // null | "list" | jobObject
  const [histLoading,setHistLoading]= useState(defaultHistView === "list"); // 400ms skeleton on list open
  const [dupWarning, setDupWarning] = useState(null); // null | matching job
  const [submitting, setSubmitting] = useState(false);
  const [locNotice,  setLocNotice]  = useState(null); // null | "clock_in" | "submit"
  const [outcomeMode,   setOutcomeMode]   = useState(null);   // null | "aborted" | "incomplete"
  const [outcomeReason, setOutcomeReason] = useState("");
  const [outcomePhotos, setOutcomePhotos] = useState([]);
  const [outcomeErr,    setOutcomeErr]    = useState("");
  const [viewSroPdf,    setViewSroPdf]    = useState(null);   // null | { path, filename } — mounts the SROPdfViewer modal

  // Re-render every second so elapsed() stays live
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-dismiss the location notice after 5 seconds
  useEffect(() => {
    if (!locNotice) return;
    const id = setTimeout(() => setLocNotice(null), 5000);
    return () => clearTimeout(id);
  }, [locNotice]);

  // Reset outcome state when the active slot changes
  useEffect(() => {
    setOutcomeMode(null); setOutcomeReason(""); setOutcomePhotos([]); setOutcomeErr("");
  }, [activeSlot]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show 400ms skeleton whenever the history list view is opened
  useEffect(() => {
    if (histView === "list") {
      setHistLoading(true);
      const id = setTimeout(() => setHistLoading(false), 400);
      return () => clearTimeout(id);
    }
  }, [histView]);

  // Photo upload state — tracks in-progress uploads so we can show spinners
  const [uploadingPhotos,  setUploadingPhotos]  = useState(0); // general job photos
  const [uploadingOutcome, setUploadingOutcome] = useState(0); // outcome photos
  const [scannerFor,       setScannerFor]       = useState(null); // qId | null — barcode scanner modal

  // Draft toast state
  const [draftToast,     setDraftToast]     = useState(false);
  const [draftToastExit, setDraftToastExit] = useState(false);
  const draftToastTimerRef                  = useRef(null);

  // Refs for latest slots/activeSlot — safe to read inside closures and cleanup
  const slotsRef      = useRef(slots);
  const activeSlotRef = useRef(activeSlot);
  slotsRef.current      = slots;      // keep in sync on every render
  activeSlotRef.current = activeSlot;

  const showDraftToastFn = () => {
    clearTimeout(draftToastTimerRef.current);
    setDraftToast(true); setDraftToastExit(false);
    draftToastTimerRef.current = setTimeout(() => {
      setDraftToastExit(true);
      setTimeout(() => setDraftToast(false), 350);
    }, 2200);
  };

  // Save draft when navigating away from the form to history view
  useEffect(() => {
    if (histView !== null) {
      const hasData = slotsRef.current.some(s => (s.form.sroNumber.trim() || s.form.startTime || s.form.installationTypeId) && !s.done);
      if (hasData && onSaveDraft) onSaveDraft(user.id, slotsRef.current, activeSlotRef.current);
    }
  }, [histView]); // eslint-disable-line

  // Auto-save draft every 60 seconds
  useEffect(() => {
    const id = setInterval(() => {
      const hasData = slotsRef.current.some(s => (s.form.sroNumber.trim() || s.form.startTime || s.form.installationTypeId) && !s.done);
      if (hasData && onSaveDraft) {
        onSaveDraft(user.id, slotsRef.current, activeSlotRef.current);
        showDraftToastFn();
      }
    }, 60000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  // Save draft on unmount (e.g. user navigates to welcome screen)
  useEffect(() => {
    return () => {
      const hasData = slotsRef.current.some(s => (s.form.sroNumber.trim() || s.form.startTime || s.form.installationTypeId) && !s.done);
      if (hasData && onSaveDraft) onSaveDraft(user.id, slotsRef.current, activeSlotRef.current);
    };
  }, []); // eslint-disable-line

  // Register nav Back state based on current view within InstallerView.
  // Back-button progression when inside the form flow (pre-clock-in, no draft submitted):
  //   Phase 1a (template picker)   → clears entryPath, returns to Phase 0 gateway
  //   Phase 1b (SRO header)        → clears installationTypeId, returns to Phase 1a
  //   Pre-loaded SRO stub          → clears entryPath, returns to Phase 0 gateway
  // Once startTime or done is set, Back is suppressed (can't unwind a clock-in).
  useEffect(() => {
    if (!onRegisterNav) return;
    if (histView === "list") { onRegisterNav(true, () => setHistView(null)); return; }
    if (histView && typeof histView === "object") { onRegisterNav(true, () => setHistView("list")); return; }
    const curSlot = slots[activeSlot];
    const f = curSlot?.form;
    // After clock-in or on success screen, no internal back
    if (!f || f.startTime || curSlot.done) { onRegisterNav(false, null); return; }
    // Pre-loaded SRO review screen → release claim, return to picker
    if (f.entryPath === "preloaded_sro" && f.preloadedSroId) {
      onRegisterNav(true, async () => {
        const sroId = f.preloadedSroId;
        try { await dataService.releasePreloadedSro(sroId); }
        catch(e) { console.warn("releasePreloadedSro:", e.message); }
        setSlots(p => p.map((s, i) => i === activeSlot ? {
          ...s,
          form: {
            ...s.form,
            preloadedSroId: null, preloadedLineItems: null, preloadedSiteNotes: "",
            preloadedPdfPath: null, preloadedPdfFilename: null,
            sroNumber: "", rlNumber: "", storeName: "", storeAddress: "",
            siteContactPhone: "",
          },
          errors: {},
        } : s));
      });
      return;
    }
    // Pre-loaded SRO picker → back to gateway
    if (f.entryPath === "preloaded_sro") {
      onRegisterNav(true, () => setSlots(p => p.map((s, i) => i === activeSlot
        ? {...s, form: {...s.form, entryPath: null}, errors: {}} : s)));
      return;
    }
    // Phase 1b (SRO header, template path w/ type picked) → back to Phase 1a
    if (f.entryPath === "template" && f.installationTypeId) {
      onRegisterNav(true, () => setSlots(p => p.map((s, i) => i === activeSlot
        ? {...s, form: {...s.form, installationTypeId: "", answers: {}}, errors: {}} : s)));
      return;
    }
    // Phase 1a (template picker, no type yet) → back to gateway
    if (f.entryPath === "template") {
      onRegisterNav(true, () => setSlots(p => p.map((s, i) => i === activeSlot
        ? {...s, form: {...s.form, entryPath: null}, errors: {}} : s)));
      return;
    }
    // Phase 0 gateway itself — no internal back
    onRegisterNav(false, null);
  }, [histView, onRegisterNav, slots, activeSlot]);

  // ── Active-slot accessors ──
  const slot = slots[activeSlot];
  const { form, errors, done } = slot;

  const setForm = fn =>
    setSlots(p => p.map((s, i) => i === activeSlot ? {...s, form: fn(s.form)} : s));
  const setErrors = fn =>
    setSlots(p => p.map((s, i) => i === activeSlot
      ? {...s, errors: typeof fn === "function" ? fn(s.errors) : fn} : s));
  const setDone = val =>
    setSlots(p => p.map((s, i) => i === activeSlot ? {...s, done: val} : s));

  const selectedType = installationTypes.find(t => t.id === form.installationTypeId) || null;

  // Jobs this installer submitted in the last 30 days, newest first
  const THIRTY_DAYS_MS = 30 * 24 * 3600 * 1000;
  const myRecentJobs = jobs
    .filter(j => j.installerUserId === user.id && (Date.now() - new Date(j.submittedAt).getTime()) <= THIRTY_DAYS_MS)
    .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));

  // ── Validation ──
  const validateJobInfo = () => {
    const e = {};
    if (!form.sroNumber.trim())    e.sroNumber    = "1";
    if (!form.rlNumber.trim())     e.rlNumber     = "1";
    if (!form.storeName.trim())    e.storeName    = "1";
    if (!form.storeAddress.trim()) e.storeAddress = "1";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateFull = () => {
    const e = {};
    const isPreloaded = form.entryPath === "preloaded_sro";
    // Template flow: installation type + template questions drive the checklist.
    // Preloaded-SRO flow: the line items that matched the services library drive it (line_no keyed).
    if (!isPreloaded && !form.installationTypeId) e.installationTypeId = "1";
    if (!isPreloaded) {
      selectedType?.questions.forEach(q => {
        const ans = form.answers[q.id] || {};
        if (q.answerType === "confirm" && !ans.confirmed)     e[`ans_${q.id}`] = "1";
        if (q.answerType === "text"    && !ans.value?.trim()) e[`ans_${q.id}`] = "1";
        if (q.answerType === "both" && (!ans.confirmed || !ans.value?.trim())) e[`ans_${q.id}`] = "1";
        if (q.photoMode === "required" && !(ans.photos?.length > 0)) e[`photo_${q.id}`] = "1";
        if (q.commentMode === "barcode" && !ans.notes?.trim())       e[`comment_${q.id}`] = "1";
      });
    } else {
      // Preloaded SRO: each service-matched line item is a required confirm +
      // required Before photo + required After photo.
      const items = Array.isArray(form.preloadedLineItems) ? form.preloadedLineItems : [];
      items.filter(li => li && li.isService).forEach(li => {
        const qId = `svc_${li.lineNo}`;
        const ans = form.answers[qId] || {};
        if (!ans.confirmed)                       e[`ans_${qId}`]         = "1";
        if (!(ans.photosBefore?.length > 0))      e[`photoBefore_${qId}`] = "1";
        if (!(ans.photosAfter?.length  > 0))      e[`photoAfter_${qId}`]  = "1";
      });
    }
    if (!form.siteContactName.trim()) e.siteContactName = "1";
    if (!form.signatureData)          e.signature       = "1";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Actions ──
  const clockIn = async () => {
    if (!validateJobInfo()) return;
    const sessionId = `session_${user.id}_${Date.now()}`;
    const now = new Date().toISOString();
    // Transition to Phase 2 immediately — don't block the installer waiting for GPS
    setForm(f => ({...f, startTime: now}));
    setErrors({});
    // Store sessionId in slot so submit() can remove it later
    setSlots(p => p.map((s, i) => i === activeSlot ? {...s, sessionId} : s));
    // Register with live tracking panel in HeadOfficeView
    setActiveSessions(p => [...p, {
      sessionId,
      installerUserId: user.id,
      installerName:   user.name,
      sroNumber:       form.sroNumber,
      storeName:       form.storeName,
      typeLabel:       selectedType?.label || "—",
      startTime:       now,
    }]);
    const loc = await captureLocation();
    if (loc) setForm(f => ({...f, clockInLocation: loc}));
    else setLocNotice("clock_in");
  };
  const submit = async () => {
    const currentSessionId = slot.sessionId; // snapshot before any awaits
    if (!validateFull()) return;
    setSubmitting(true);
    // Capture submission timestamp immediately at the moment the installer taps Submit,
    // before any async operations (GPS, network). This also freezes the elapsed timer.
    const now = new Date().toISOString();
    setForm(f => ({...f, endTime: now}));
    // Allow a 60-second-old cached fix to avoid making the installer wait
    const loc = await captureLocation({ maximumAge: 60000 });
    if (!loc) setLocNotice("submit");
    const priorRejected = jobs.find(j =>
      j.installerUserId === user.id &&
      j.sroNumber?.trim().toLowerCase() === form.sroNumber?.trim().toLowerCase() &&
      (j.status === "rejected" || j.status === "requires_revisit")
    );
    const auditLog = [
      { action:"job_created",   actor:user.name, timestamp:form.startTime || now, note:null },
      { action:"job_submitted", actor:user.name, timestamp:now, note:null },
      ...(priorRejected ? [{ action:"job_resubmitted", actor:user.name, timestamp:now,
          note:`Resubmission of previously ${priorRejected.status === "rejected" ? "rejected" : "flagged"} job` }] : []),
    ];
    const payload = {
      ...form,
      endTime:         now,
      submittedAt:     now,
      installerName:   user.name,
      installerEmail:  user.email,
      installerUserId: user.id,
      status:          "pending",
      statusHistory:   [{ status:"pending", timestamp:now, actor:user.name }],
      auditLog,
      typeSnapshot:    selectedType,
      submitLocation:  loc,
    };
    const job = await dataService.submitJob(payload);
    // If this job came from a pre-loaded SRO, flip it from claimed → completed and link the job id.
    // Don't let a failure here block the installer — the job is already saved.
    if (form.preloadedSroId && job?.id) {
      try { await dataService.completePreloadedSro(form.preloadedSroId, job.id); }
      catch(err) { console.warn("completePreloadedSro:", err.message); }
    }
    setJobs(p => [job, ...p]);
    setActiveSessions(p => p.filter(s => s.sessionId !== currentSessionId));
    setDone(job);
    // Immediately update the App-level draft so the logout warning check reflects
    // the submitted state. setDone() is async (batched React state), so we construct
    // the updated slots manually here rather than waiting for slotsRef to update.
    // This ensures drafts[userId].slots shows done=job for this slot, so
    // the logout check's `slots.some(s => s.form.startTime && !s.done)` returns false.
    if (onSaveDraft) {
      const updatedSlots = slots.map((s, i) => i === activeSlot ? {...s, done: job} : s);
      onSaveDraft(user.id, updatedSlots, activeSlot);
    }
    notifyJobSubmitted(job, users);
    setSubmitting(false);
  };
  const setAns = (qId, field, value) => {
    setForm(f => ({...f, answers: {...f.answers, [qId]: {...f.answers[qId], [field]: value}}}));
    // Clear matching error keys when the value changes.
    if (field === "confirmed" || field === "value") {
      setErrors(e => { const n={...e}; delete n[`ans_${qId}`]; return n; });
    }
    if (field === "photos")       setErrors(e => { const n={...e}; delete n[`photo_${qId}`]; return n; });
    if (field === "photosBefore") setErrors(e => { const n={...e}; delete n[`photoBefore_${qId}`]; return n; });
    if (field === "photosAfter")  setErrors(e => { const n={...e}; delete n[`photoAfter_${qId}`]; return n; });
  };
  const addSlot = () => {
    setSlots(p => [...p, { form: initialForm(), errors: {}, done: null, sessionId: null }]);
    setActiveSlot(1);
  };
  const resetAll = () => {
    setActiveSessions(p => p.filter(s => s.installerUserId !== user.id));
    setSlots([{ form: initialForm(), errors: {}, done: null, sessionId: null }]);
    setActiveSlot(0);
  };

  const checkDuplicate = () => {
    const today = new Date().toDateString();
    return jobs.find(j =>
      j.installerUserId === user.id &&
      j.sroNumber.trim().toLowerCase() === form.sroNumber.trim().toLowerCase() &&
      j.installationTypeId === form.installationTypeId &&
      new Date(j.submittedAt).toDateString() === today
    ) || null;
  };
  const handleSubmitClick = () => {
    const dup = checkDuplicate();
    if (dup) { setDupWarning(dup); return; }
    submit();
  };

  const submitOutcome = async () => {
    if (!outcomeReason.trim()) { setOutcomeErr("Please enter a reason before submitting."); return; }
    setOutcomeErr("");
    const currentSessionId = slot.sessionId;
    setSubmitting(true);
    // Capture submission timestamp immediately at the moment the installer taps Submit,
    // before any async operations (GPS, network). This also freezes the elapsed timer.
    const now = new Date().toISOString();
    setForm(f => ({...f, endTime: now}));
    const loc = await captureLocation({ maximumAge: 60000 });
    if (!loc) setLocNotice("submit");
    const outcomeAction = outcomeMode === "aborted" ? "job_aborted" : "job_incomplete";
    const auditLog = [
      { action:"job_created",  actor:user.name, timestamp:form.startTime || now, note:null },
      { action:outcomeAction,  actor:user.name, timestamp:now, note:outcomeReason.trim() },
    ];
    const payload = {
      ...form,
      endTime:         now,
      submittedAt:     now,
      installerName:   user.name,
      installerEmail:  user.email,
      installerUserId: user.id,
      status:          outcomeMode,  // "aborted" | "incomplete"
      outcomeReason:   outcomeReason.trim(),
      outcomePhotos:   outcomePhotos,
      statusHistory:   [{ status: outcomeMode, timestamp: now, actor: user.name }],
      auditLog,
      typeSnapshot:    selectedType,
      submitLocation:  loc,
    };
    const job = await dataService.submitJob(payload);
    // Aborted/incomplete outcome on a pre-loaded SRO — release the claim so another installer
    // can pick it up. The outcome is still captured in the job record for HO review.
    if (form.preloadedSroId) {
      try { await dataService.releasePreloadedSro(form.preloadedSroId); }
      catch(err) { console.warn("releasePreloadedSro:", err.message); }
    }
    setJobs(p => [job, ...p]);
    setActiveSessions(p => p.filter(s => s.sessionId !== currentSessionId));
    setDone(job);
    // Immediately update the App-level draft so the logout warning check reflects
    // the submitted state (mirrors the same fix in submit()).
    if (onSaveDraft) {
      const updatedSlots = slots.map((s, i) => i === activeSlot ? {...s, done: job} : s);
      onSaveDraft(user.id, updatedSlots, activeSlot);
    }
    notifyJobOutcome(job, users);
    setSubmitting(false);
  };

  // ── History: job list ─────────────────────────────────────────────────────
  if (histView === "list") {
    return (
      <div style={{maxWidth:600, margin:"0 auto", paddingBottom:48}}>
        <div style={{background:B.dark, borderBottom:`1px solid ${B.border}`, padding:"20px 20px 16px", marginBottom:20}}>
          <button onClick={()=>setHistView(null)}
            style={{background:"transparent", color:B.muted, border:`1px solid ${B.border}`, borderRadius:3, padding:"5px 14px", cursor:"pointer", fontSize:11, marginBottom:14, fontFamily:"'Barlow',sans-serif"}}>
            ← Back
          </button>
          <div style={{fontSize:10, color:B.muted, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif", marginBottom:4}}>My Submissions</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:26, color:B.white}}>Job History</div>
          <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:4}}>Showing jobs from the last 30 days</div>
        </div>
        <div style={{padding:"0 16px", display:"flex", flexDirection:"column", gap:10}}>
          {histLoading
            ? Array.from({length:4}).map((_,i) => <SkeletonHistRow key={i}/>)
            : myRecentJobs.length === 0
            ? <div style={{textAlign:"center", padding:"64px 24px"}}>
                <div style={{fontSize:40, marginBottom:14, opacity:0.25, color:B.mid}}>◫</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:18, letterSpacing:1, color:B.muted, marginBottom:8}}>No jobs in the last 30 days</div>
                <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.mid, lineHeight:1.6}}>Jobs you submit will appear here once they've been processed.</div>
              </div>
            : myRecentJobs.map(job => {
                const hsc = STATUS_CONFIG[job.status];
                const hAccent = hsc ? hsc.border : B.border;
                const hHasAccent = ["requires_revisit","aborted","incomplete"].includes(job.status);
                return (
                <div key={job.id} onClick={()=>setHistView(job)} className="sm-clickable"
                  style={{background:B.card, borderRadius:6, padding:16, border:`1px solid ${hHasAccent?hAccent:B.border}`}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=hHasAccent?hAccent:B.blue}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=hHasAccent?hAccent:B.border}>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, marginBottom:8}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:17, color:B.white}}>
                        {job.sroNumber}
                        {job.rlNumber && <span style={{fontSize:13, fontWeight:400, color:B.muted}}> · RL# {job.rlNumber}</span>}
                      </div>
                      <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.light, marginTop:2}}>{job.storeName}</div>
                    </div>
                    <StatusBadge status={job.status}/>
                  </div>
                  <div style={{display:"flex", gap:12, fontSize:12, color:B.muted, flexWrap:"wrap", fontFamily:"'Barlow',sans-serif"}}>
                    <span>{job.typeSnapshot?.label || "—"}</span>
                    <span>{fmt(job.submittedAt)}</span>
                  </div>
                  {(job.status === "rejected" || job.status === "requires_revisit") && (
                    <div style={{marginTop:10, background:"rgba(192,57,43,0.08)", border:`1px solid rgba(192,57,43,0.3)`, borderRadius:4, padding:"8px 12px"}}>
                      <div style={{fontSize:11, fontWeight:700, color:job.status==="requires_revisit"?B.orange:B.red, letterSpacing:1, textTransform:"uppercase", fontFamily:"'Barlow Condensed',sans-serif", marginBottom:3}}>
                        {job.status === "requires_revisit" ? "⚠ Warranty Revisit Required" : "✗ Rejected"}
                      </div>
                      {job.rejectionReason
                        ? <div style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", lineHeight:1.5}}>{job.rejectionReason}</div>
                        : <div style={{fontSize:13, color:B.muted, fontFamily:"'Barlow',sans-serif", fontStyle:"italic"}}>No reason provided.</div>
                      }
                      {job.rejectedBy && <div style={{fontSize:11, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:4}}>by {job.rejectedBy} · {fmt(job.rejectedAt)}</div>}
                    </div>
                  )}
                  {(job.status === "aborted" || job.status === "incomplete") && hsc && (
                    <div style={{marginTop:10, background:hsc.bg, border:`1px solid ${hsc.border}`, borderRadius:4, padding:"8px 12px"}}>
                      <div style={{fontSize:11, fontWeight:700, color:hsc.color, letterSpacing:1, textTransform:"uppercase", fontFamily:"'Barlow Condensed',sans-serif", marginBottom:3}}>
                        {hsc.icon} {job.status === "aborted" ? "Aborted" : "Incomplete"}
                      </div>
                      {job.outcomeReason
                        ? <div style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", lineHeight:1.5}}>{job.outcomeReason}</div>
                        : <div style={{fontSize:13, color:B.muted, fontFamily:"'Barlow',sans-serif", fontStyle:"italic"}}>No reason provided.</div>
                      }
                    </div>
                  )}
                </div>
                );
              })
          }
        </div>
      </div>
    );
  }

  // ── History: job detail (read-only) ───────────────────────────────────────
  if (histView && typeof histView === "object") {
    const hj = histView;
    const snap = hj.typeSnapshot || null;
    const allHistPhotos = [
      ...(hj.photos || []).map(p => ({...p, label:"General"})),
      ...(snap ? snap.questions.flatMap((q, qi) => (hj.answers?.[q.id]?.photos || []).map(p => ({...p, label:`Q${qi+1}`}))) : []),
      ...(hj.outcomePhotos || []).map(p => ({...p, label:"Outcome"})),
    ];
    const hdRow = (label, value) => (
      <div style={{display:"flex", gap:12, padding:"10px 0", borderBottom:`1px solid ${B.border}`}}>
        <span style={{width:130, flexShrink:0, fontSize:11, fontWeight:600, color:B.muted, textTransform:"uppercase", letterSpacing:1, fontFamily:"'Barlow',sans-serif"}}>{label}</span>
        <span style={{fontSize:14, color:B.light, fontFamily:"'Barlow',sans-serif"}}>{value}</span>
      </div>
    );
    return (
      <div style={{maxWidth:600, margin:"0 auto", paddingBottom:40}}>
        <div style={{background:B.dark, borderBottom:`1px solid ${B.border}`, padding:"16px 20px", marginBottom:20}}>
          <button onClick={()=>setHistView("list")}
            style={{background:"transparent", color:B.muted, border:`1px solid ${B.border}`, borderRadius:3, padding:"5px 14px", cursor:"pointer", fontSize:11, marginBottom:14, fontFamily:"'Barlow',sans-serif"}}>
            ← Back to History
          </button>
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8}}>
            <div>
              <div style={{fontSize:10, color:B.muted, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif", marginBottom:4}}>Job Report</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:24, color:B.white}}>{hj.sroNumber}</div>
            </div>
            <StatusBadge status={hj.status}/>
          </div>
        </div>
        <div style={{padding:"0 16px", display:"flex", flexDirection:"column", gap:14}}>
          <Card>
            <SectionTitle>Job Details</SectionTitle>
            {hdRow("Store", hj.storeName)}
            {hdRow("RL #", hj.rlNumber || "—")}
            {hdRow("Address", hj.storeAddress)}
            {hdRow("Type", snap?.label || "—")}
            {hdRow("2nd Installer", hj.secondInstaller || "N/A")}
            {hdRow("Clocked In", `${fmtTime(hj.startTime)} · ${fmtDate(hj.startTime)}`)}
            {hdRow("Clocked Out", `${fmtTime(hj.endTime)} · ${fmtDate(hj.endTime)}`)}
            {hdRow("Time on Site", elapsed(hj.startTime, hj.endTime) || "—")}
            {hdRow("Submitted", fmt(hj.submittedAt))}
          </Card>

          {snap && snap.questions.length > 0 && <Card>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8, marginBottom:6}}>
              <SectionTitle>{snap.label} — Checklist</SectionTitle>
              <InstructionsLink docs={getInstructionDocs(snap)} label="📄 Instructions" compact/>
            </div>
            {snap.questions.map((q, i) => {
              const ans = hj.answers?.[q.id] || {};
              const confirm = ans.confirmed;
              const text = ans.value;
              return (
                <div key={q.id} style={{padding:"12px 0", borderBottom:`1px solid ${B.border}`}}>
                  <div style={{fontSize:10, color:q.answerType==="none"?B.muted:B.blueLt, marginBottom:3, fontWeight:700, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow Condensed',sans-serif"}}>{q.answerType==="none"?`Step ${i+1}`:`Q${i+1}`}</div>
                  <div style={{fontFamily:"'Barlow',sans-serif", fontWeight:500, fontSize:13, color:B.light, marginBottom:6}}>{q.text}</div>
                  {confirm && <div style={{fontWeight:700, fontSize:14, color:confirm==="Yes"?B.green:confirm==="No"?B.red:B.light, fontFamily:"'Barlow',sans-serif", marginBottom:text?6:0}}>{confirm}</div>}
                  {text && <div style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", marginBottom:4}}>{text}</div>}
                  {!confirm && !text && q.answerType!=="none" && <span style={{color:B.mid, fontWeight:400, fontStyle:"italic", fontSize:13, fontFamily:"'Barlow',sans-serif"}}>No answer recorded</span>}
                  {ans.notes && <div style={{fontSize:12, color:B.muted, marginTop:4, fontStyle:"italic", fontFamily:"'Barlow',sans-serif"}}>Note: {ans.notes}</div>}
                  {ans.photos?.length > 0 && <div style={{display:"flex", gap:6, marginTop:8, flexWrap:"wrap"}}>
                    {ans.photos.map((p, pi) => <img key={pi} src={p.url||p.data} alt="" style={{width:66, height:66, borderRadius:4, objectFit:"cover", border:`1px solid ${B.blue}`}}/>)}
                  </div>}
                </div>
              );
            })}
          </Card>}

          <Card>
            <SectionTitle>Site Sign-Off</SectionTitle>
            {hdRow("Title / Role", hj.siteContactTitle || "—")}
            {hdRow("Phone / Email", hj.siteContactPhone || "—")}
            <div style={{marginTop:16}}>
              <FieldLabel>Signature</FieldLabel>
              {hj.signatureData
                ? <div style={{marginTop:8, display:"inline-block"}}>
                    <div style={{background:"#111", borderRadius:"4px 4px 0 0", border:`1px solid ${B.green}`, borderBottom:"none", padding:"10px 14px"}}>
                      <img src={hj.signatureData} alt="Signature" style={{maxWidth:"100%", height:100, display:"block"}}/>
                    </div>
                    <div style={{background:B.surface, borderRadius:"0 0 4px 4px", border:`1px solid ${B.green}`, borderTop:`1px solid ${B.border}`, padding:"8px 14px", minWidth:200}}>
                      <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.white, fontWeight:600}}>
                        {hj.siteContactName || <span style={{color:B.mid, fontStyle:"italic", fontWeight:400}}>Name not recorded</span>}
                      </div>
                      {hj.siteContactTitle && <div style={{fontSize:11, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:2}}>{hj.siteContactTitle}</div>}
                    </div>
                  </div>
                : <div style={{marginTop:8, fontSize:13, color:B.mid, fontFamily:"'Barlow',sans-serif"}}>
                    No signature captured.
                    {hj.siteContactName && <span style={{marginLeft:6, color:B.light}}>{hj.siteContactName}</span>}
                  </div>
              }
            </div>
          </Card>

          {allHistPhotos.length > 0 && <Card>
            <SectionTitle>Photo Evidence ({allHistPhotos.length})</SectionTitle>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))", gap:8}}>
              {allHistPhotos.map((p, i) => (
                <div key={i}>
                  <img src={p.url||p.data} alt="" style={{width:"100%", borderRadius:4, border:`1px solid ${B.border}`, objectFit:"cover", aspectRatio:"1", display:"block"}}/>
                  <div style={{fontSize:10, color:B.mid, marginTop:3, fontFamily:"'Barlow',sans-serif"}}>{p.label}</div>
                </div>
              ))}
            </div>
          </Card>}

          {hj.statusHistory?.length > 0 && <Card>
            <SectionTitle>Status History</SectionTitle>
            <div style={{display:"flex", flexDirection:"column"}}>
              {hj.statusHistory.map((h, i) => {
                const cfg = STATUS_CONFIG[h.status] || STATUS_CONFIG.pending;
                return (
                  <div key={i} style={{display:"flex", gap:14, padding:"10px 0", borderBottom:i<hj.statusHistory.length-1?`1px solid ${B.border}`:"none"}}>
                    <div style={{display:"flex", flexDirection:"column", alignItems:"center", width:16, flexShrink:0}}>
                      <div style={{width:10, height:10, borderRadius:"50%", background:cfg.color, flexShrink:0, marginTop:3}}/>
                      {i < hj.statusHistory.length-1 && <div style={{width:1, flex:1, background:B.border, marginTop:4}}/>}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
                        <span style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, color:cfg.color}}>{cfg.label}</span>
                        <span style={{fontSize:11, color:B.muted, fontFamily:"'Barlow',sans-serif"}}>{fmt(h.timestamp)}</span>
                      </div>
                      <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:1}}>by {h.actor}</div>
                      {h.note && <div style={{fontSize:12, color:B.light, fontFamily:"'Barlow',sans-serif", marginTop:4, padding:"6px 10px", background:B.surface, borderRadius:3, borderLeft:`2px solid ${B.red}`}}>"{h.note}"</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>}

          {(hj.status === "rejected" || hj.status === "requires_revisit") && (
            <div style={{background:hj.status==="requires_revisit"?B.orangeDim:"rgba(192,57,43,0.08)", border:`1px solid ${hj.status==="requires_revisit"?B.orange:B.red}`, borderRadius:6, padding:16}}>
              <div style={{fontWeight:700, color:hj.status==="requires_revisit"?B.orange:B.red, fontFamily:"'Barlow Condensed',sans-serif", fontSize:15}}>
                {hj.status==="requires_revisit" ? "⚠ Warranty Revisit Required" : `✗ Rejected by ${hj.rejectedBy}`}
              </div>
              {hj.rejectionReason && <div style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", marginTop:6}}>Reason: {hj.rejectionReason}</div>}
              {hj.status === "rejected" && <>
                <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:4}}>{fmt(hj.rejectedAt)}</div>
                <div style={{fontSize:11, color:B.orange, fontFamily:"'Barlow',sans-serif", marginTop:8}}>⚠ Will auto-escalate to Requires Revisit if not resolved within 48 hours.</div>
              </>}
              {hj.status === "requires_revisit" && <div style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", marginTop:6}}>This job was rejected and not resolved within 48 hours. A warranty revisit must be scheduled.</div>}
            </div>
          )}

          {(hj.status === "aborted" || hj.status === "incomplete") && (() => {
            const sc = STATUS_CONFIG[hj.status];
            return (
              <div style={{background:sc.bg, border:`1px solid ${sc.border}`, borderRadius:6, padding:16}}>
                <div style={{fontWeight:700, color:sc.color, fontFamily:"'Barlow Condensed',sans-serif", fontSize:15, marginBottom:hj.outcomeReason?8:0}}>
                  {sc.icon} {hj.status === "aborted" ? "Job Aborted" : "Job Incomplete"}
                </div>
                {hj.outcomeReason && <div style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", lineHeight:1.5}}>Reason: {hj.outcomeReason}</div>}
                {hj.outcomePhotos?.length > 0 && <div style={{display:"flex", gap:6, marginTop:10, flexWrap:"wrap"}}>
                  {hj.outcomePhotos.map((p,i)=><img key={i} src={p.data} alt="" style={{width:64,height:64,borderRadius:4,objectFit:"cover",border:`1px solid ${sc.border}`}}/>)}
                </div>}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  const allDone = slots.every(s => !!s.done);

  // ── SRO Tab Bar ──
  // Visible once the first SRO is clocked in (enabling "Add SRO") or when 2 are active
  const showTabBar = slots.length > 1 || !!slots[0].form.startTime;
  const tabBar = showTabBar && (
    <div style={{display:"flex", borderRadius:6, overflow:"hidden", border:`1px solid ${B.border}`, background:B.surface}}>
      {slots.map((s, i) => {
        const isActive    = i === activeSlot;
        const isSubmitted = !!s.done;
        const isClockedIn = !!s.form.startTime && !isSubmitted;
        const label       = s.form.sroNumber.trim() || `SRO ${i + 1}`;
        const dotColor    = isSubmitted ? B.green : isClockedIn ? B.blueLt : B.mid;
        return (
          <button key={i} onClick={() => setActiveSlot(i)} className="sm-tab" style={{
            flex:1, padding:"10px 14px", border:"none",
            borderRight: i < slots.length - 1 ? `1px solid ${B.border}` : "none",
            background: isActive ? B.blueDim : "transparent", cursor:"pointer", textAlign:"left",
            borderBottom: isActive ? `2px solid ${B.blue}` : "2px solid transparent",
          }}>
            <div style={{display:"flex", alignItems:"center", gap:7}}>
              <div style={{width:7, height:7, borderRadius:"50%", background:dotColor, flexShrink:0}}/>
              <span style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14,
                color: isActive ? B.white : B.light, letterSpacing:0.5,
                overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:130}}>
                {label}
              </span>
              {isSubmitted && <span style={{color:B.green, fontSize:11}}>✓</span>}
            </div>
            <div style={{fontSize:10, fontFamily:"'Barlow',sans-serif", marginTop:2, paddingLeft:14,
              color: isSubmitted ? B.green : isClockedIn ? B.muted : B.mid}}>
              {isSubmitted ? "Submitted" : isClockedIn ? elapsed(s.form.startTime, s.form.endTime || null) : "Not started"}
            </div>
          </button>
        );
      })}
      {slots.length < 2 && slots[0].form.startTime && (
        <button onClick={addSlot} style={{
          padding:"10px 16px", border:"none", borderLeft:`1px solid ${B.border}`,
          background:"transparent", cursor:"pointer", color:B.blue,
          fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:12,
          letterSpacing:1, textTransform:"uppercase", whiteSpace:"nowrap",
          display:"flex", alignItems:"center", gap:6,
        }}>
          + Add SRO
        </button>
      )}
    </div>
  );

  // ── Banners ──
  const myRejectedJobs = jobs.filter(j => j.installerUserId === user.id && j.status === "rejected");
  const rejectionBanner = myRejectedJobs.length > 0 && (
    <div style={{background:"rgba(192,57,43,0.1)", border:`1px solid ${B.red}`, borderRadius:6, padding:"14px 16px"}}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:14, color:B.red, letterSpacing:0.5, marginBottom:8}}>
        ✗ {myRejectedJobs.length} Job{myRejectedJobs.length > 1 ? "s" : ""} Rejected by Head Office
      </div>
      {myRejectedJobs.map(j => (
        <div key={j.id} style={{marginBottom:6, paddingLeft:8, borderLeft:`2px solid ${B.red}`}}>
          <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.light, fontWeight:600}}>{j.sroNumber} · {j.storeName}</div>
          {j.rejectionReason && <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:2}}>Reason: {j.rejectionReason}</div>}
          <div style={{fontSize:11, color:B.mid, fontFamily:"'Barlow',sans-serif", marginTop:1}}>Rejected by {j.rejectedBy} · {fmt(j.rejectedAt)}</div>
        </div>
      ))}
      <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:8}}>Please contact head office to discuss next steps.</div>
    </div>
  );

  const pageHeader = (
    <div style={{background:B.dark, borderBottom:`1px solid ${B.border}`, padding:"20px 20px 16px", marginBottom:20}}>
      <div style={{fontSize:10, color:B.muted, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif", marginBottom:4}}>New Job Report</div>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:26, color:B.white}}>Installation Form</div>
    </div>
  );

  // ── Single unified return ──
  return (
    <div style={{maxWidth:600, margin:"0 auto", paddingBottom:48}}>
      {pageHeader}
      <div style={{padding:"0 16px", display:"flex", flexDirection:"column", gap:14}}>
        {tabBar}
        <InstallerPill user={user}/>
        {rejectionBanner}

        {done ? (
          // ── SUCCESS ──
          (() => {
            const sc = STATUS_CONFIG[done.status] || STATUS_CONFIG.pending;
            const isOutcome = done.status === "aborted" || done.status === "incomplete";
            const headline = isOutcome
              ? (done.status === "aborted" ? "Job Logged as Aborted" : "Job Logged as Incomplete")
              : "Job Submitted";
            return (
          <div style={{display:"flex", flexDirection:"column", alignItems:"center", padding:"36px 0", textAlign:"center"}}>
            {isOutcome
              ? <div style={{width:72, height:72, borderRadius:"50%", background:sc.bg, border:`2px solid ${sc.border}`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:20, fontSize:28, color:sc.color}}>{sc.icon}</div>
              : <div style={{marginBottom:20}}><AnimatedCheck color={sc.color} size={72}/></div>
            }
            <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:26, color:sc.color, letterSpacing:1, marginBottom:6}}>{headline}</div>
            <div style={{fontFamily:"'Barlow',sans-serif", fontSize:14, color:B.muted, marginBottom:2}}>SRO# <span style={{color:B.blueLt}}>{done.sroNumber}</span></div>
            <div style={{fontFamily:"'Barlow',sans-serif", fontSize:14, color:B.muted, marginBottom:2}}>{done.storeName}</div>
            <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted, marginBottom:isOutcome?8:28}}>Time on site: <span style={{color:B.white}}>{elapsed(done.startTime, done.endTime)}</span></div>
            {isOutcome && done.outcomeReason && <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.light, marginBottom:28, maxWidth:340, lineHeight:1.5, padding:"10px 14px", background:sc.bg, border:`1px solid ${sc.border}`, borderRadius:4}}>"{done.outcomeReason}"</div>}
            {!allDone && slots.length > 1 && (
              <button onClick={() => setActiveSlot(slots.findIndex(s => !s.done))}
                style={{background:B.blue, color:B.onAccent, border:"none", borderRadius:4, padding:"11px 28px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textTransform:"uppercase"}}>
                Switch to Active SRO →
              </button>
            )}
            {allDone && (
              <button onClick={resetAll}
                style={{background:B.blue, color:B.onAccent, border:"none", borderRadius:4, padding:"12px 32px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textTransform:"uppercase"}}>
                Start New Job
              </button>
            )}
            <button onClick={()=>setHistView("list")}
              style={{background:"transparent", border:`1px solid ${B.border}`, borderRadius:4, padding:"10px 24px", fontSize:12, color:B.muted, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginTop:4}}>
              ◫ My Job History
            </button>
          </div>
            );
          })()

        ) : !form.entryPath ? (
          // ── PHASE 0: GATEWAY — how is the installer starting this job? ──
          <>
            <button onClick={()=>setHistView("list")}
              style={{background:B.surface, border:`1px solid ${B.border}`, borderRadius:4, padding:"11px 16px", fontSize:13, color:B.light, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", boxSizing:"border-box"}}>
              <span>◫ My Job History</span>
              {myRecentJobs.length > 0
                ? <span style={{background:B.blueDim, border:`1px solid ${B.blue}`, borderRadius:10, padding:"1px 9px", fontSize:11, color:B.blueLt, fontFamily:"'Barlow',sans-serif", fontWeight:600}}>{myRecentJobs.length}</span>
                : <span style={{color:B.mid, fontSize:14}}>→</span>
              }
            </button>

            <div style={{padding:"8px 0 4px"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:20, color:B.white, marginBottom:6, letterSpacing:0.5}}>How would you like to start?</div>
              <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted, lineHeight:1.5}}>
                Choose the path that matches the work order in your hand.
              </div>
            </div>

            {/* Option 1 — Project Template */}
            <div onClick={()=>setForm(f=>({...f, entryPath:"template"}))}
              style={{background:B.card, border:`1px solid ${B.border}`, borderRadius:8, padding:"18px 20px", cursor:"pointer", display:"flex", alignItems:"flex-start", gap:16, transition:"all 0.15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=B.blue;e.currentTarget.style.background=B.blueDim;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=B.border;e.currentTarget.style.background=B.card;}}>
              <div style={{width:44, height:44, borderRadius:6, background:B.blueDim, border:`1px solid ${B.blue}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:B.blueLt, flexShrink:0}}>◫</div>
              <div style={{flex:1}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, color:B.white, letterSpacing:0.5, marginBottom:4}}>Project Template</div>
                <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted, lineHeight:1.5}}>
                  Pick an installation type and enter the SRO details from your paper work order. Use this for WJS Sign jobs and other standard templates.
                </div>
              </div>
              <span style={{color:B.mid, fontSize:18, alignSelf:"center"}}>→</span>
            </div>

            {/* Option 2 — Pre-loaded SRO */}
            <div onClick={()=>setForm(f=>({...f, entryPath:"preloaded_sro"}))}
              style={{background:B.card, border:`1px solid ${B.border}`, borderRadius:8, padding:"18px 20px", cursor:"pointer", display:"flex", alignItems:"flex-start", gap:16, transition:"all 0.15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=B.blue;e.currentTarget.style.background=B.blueDim;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=B.border;e.currentTarget.style.background=B.card;}}>
              <div style={{width:44, height:44, borderRadius:6, background:B.blueDim, border:`1px solid ${B.blue}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:B.blueLt, flexShrink:0}}>◈</div>
              <div style={{flex:1}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, color:B.white, letterSpacing:0.5, marginBottom:4}}>Pre-loaded SRO</div>
                <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted, lineHeight:1.5}}>
                  Look up an SRO that head office has uploaded in advance. Currently used for Regular Installs.
                </div>
              </div>
              <span style={{color:B.mid, fontSize:18, alignSelf:"center"}}>→</span>
            </div>
          </>

        ) : form.entryPath === "preloaded_sro" && !form.preloadedSroId && !form.startTime ? (
          // ── PRE-LOADED SRO PICKER — list head-office-uploaded SROs, claim on tap ──
          <>
            <StepIndicator step={1}/>
            <PreloadedSroPicker
              user={user}
              onBack={()=>setForm(f=>({...f, entryPath:null, sroNumber:""}))}
              onClaim={(claimed)=>{
                // Populate the job form from the claimed SRO and move to the review screen.
                setForm(f => ({
                  ...f,
                  preloadedSroId:        claimed.id,
                  preloadedLineItems:    claimed.lineItems || [],
                  preloadedSiteNotes:    claimed.siteNotes || "",
                  preloadedPdfPath:      claimed.pdfPath || null,
                  preloadedPdfFilename:  claimed.pdfFilename || null,
                  sroNumber:             claimed.sroNumber || "",
                  rlNumber:              claimed.rlNumber || "",
                  storeName:             claimed.storeName || "",
                  storeAddress:          [claimed.storeStreet, claimed.storeCity, claimed.storeProvince, claimed.storePostalCode].filter(Boolean).join(", "),
                  siteContactPhone:      claimed.storePhone || "",
                }));
              }}
            />
          </>

        ) : form.entryPath === "preloaded_sro" && form.preloadedSroId && !form.startTime ? (
          // ── PRE-LOADED SRO REVIEW — site + line items from the parsed PDF + clock in ──
          (() => {
            const matchedItems   = (form.preloadedLineItems || []).filter(li => li.isService);
            const informational  = (form.preloadedLineItems || []).filter(li => !li.isService);
            const timeAgg        = totalSroMinutes(form.preloadedLineItems || [], services);
            const totalLabel     = formatMinutes(timeAgg.total);
            return (
              <>
                <StepIndicator step={1}/>

                <Card>
                  <div style={{display:"flex", alignItems:"flex-start", gap:10, marginBottom:10}}>
                    <div style={{flex:1}}>
                      <SectionTitle>Confirm & Clock In</SectionTitle>
                      <div style={{fontFamily:"'Barlow',sans-serif", fontSize:12, color:B.muted, marginBottom:8}}>
                        This SRO is now claim-locked to you. Back releases it.
                      </div>
                    </div>
                    {form.preloadedPdfPath && (
                      <button onClick={()=>setViewSroPdf({ path: form.preloadedPdfPath, filename: form.preloadedPdfFilename })}
                        className="sm-btn"
                        style={{background:"transparent", border:`1px solid ${B.border}`, borderRadius:3, color:B.blueLt, fontSize:11, padding:"5px 12px", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1, textTransform:"uppercase"}}>
                        View PDF
                      </button>
                    )}
                  </div>

                  <div style={{background:B.blueDim, border:`1px solid ${B.blue}`, borderRadius:6, padding:"10px 14px", marginBottom:12}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:14, color:B.white, letterSpacing:0.5}}>{form.sroNumber}{form.rlNumber ? `  ·  RL ${form.rlNumber}` : ""}</div>
                    <div style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", marginTop:3}}>{form.storeName}</div>
                    <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:2}}>{form.storeAddress}</div>
                    {form.siteContactPhone && <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:2}}>☎ {form.siteContactPhone}</div>}
                  </div>

                  {form.preloadedSiteNotes && (
                    <div style={{background:"rgba(230,126,34,0.08)", border:`1px solid rgba(230,126,34,0.45)`, borderRadius:4, padding:"9px 12px", marginBottom:12, fontFamily:"'Barlow',sans-serif", fontSize:12, color:B.light, lineHeight:1.5}}>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:11, color:B.orange, letterSpacing:1, textTransform:"uppercase", marginBottom:4}}>Site Notes</div>
                      {form.preloadedSiteNotes}
                    </div>
                  )}

                  {/* Total estimated time on site — sum of (estimated_minutes × qty) across all
                      service-matched line items. Helps the installer plan the visit. */}
                  {timeAgg.total > 0 && (
                    <div style={{background:"rgba(82,217,160,0.08)", border:`1px solid rgba(82,217,160,0.4)`, borderRadius:6, padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:10}}>
                      <span style={{fontSize:20}}>⏱</span>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:11, color:"#52d9a0", letterSpacing:1.5, textTransform:"uppercase"}}>Total Estimated Time on Site</div>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:18, color:B.white, marginTop:2}}>{totalLabel}</div>
                        <div style={{fontSize:11, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:1}}>
                          {timeAgg.byCount} service{timeAgg.byCount===1?"":"s"} estimated · times come from the OLG Master Pricing list
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{marginBottom:10}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:11, color:B.green, letterSpacing:1.5, textTransform:"uppercase", marginBottom:6}}>Required Services ({matchedItems.length})</div>
                    {matchedItems.length === 0 ? (
                      <div style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", fontStyle:"italic"}}>
                        No items on this SRO match the services library — nothing to confirm with photos. (Head office may still need to add these services.)
                      </div>
                    ) : (
                      <div style={{display:"flex", flexDirection:"column", gap:5}}>
                        {matchedItems.map((li, i) => {
                          const m = lineItemMinutes(li, services);
                          const q = Number.isFinite(li.qty) && li.qty > 0 ? Math.round(li.qty) : 1;
                          const itemMins = Number.isFinite(m) ? m * q : null;
                          return (
                            <div key={i} style={{fontSize:13, color:B.light, fontFamily:"'Barlow',sans-serif", padding:"4px 0", borderBottom: i < matchedItems.length - 1 ? `1px solid ${B.border}` : "none", display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap"}}>
                              <span style={{color:B.green, fontWeight:700, flexShrink:0}}>{li.itemCode}</span>
                              {li.description && <span style={{color:B.muted, flex:1, minWidth:0}}>— {li.description}</span>}
                              {itemMins > 0 && (
                                <span title={q>1?`${m} min × qty ${q}`:""}
                                  style={{fontSize:10,background:"rgba(82,217,160,0.15)",color:"#52d9a0",border:`1px solid rgba(82,217,160,0.4)`,borderRadius:3,padding:"1px 6px",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",whiteSpace:"nowrap",flexShrink:0}}>
                                  ⏱ {formatMinutes(itemMins)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {informational.length > 0 && (
                    <div style={{marginBottom:6}}>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:11, color:B.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:6}}>Also Included ({informational.length})</div>
                      <div style={{display:"flex", flexDirection:"column", gap:5}}>
                        {informational.map((li, i) => (
                          <div key={i} style={{fontSize:12, color:B.muted, fontFamily:"'Barlow',sans-serif", padding:"3px 0"}}>
                            <span style={{color:B.light}}>{li.itemCode}</span>
                            {li.description && <span> — {li.description}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>

                <button
                  onClick={clockIn}
                  className="sm-btn sm-btn-primary"
                  style={{background:B.blue, color:B.onAccent, border:"none", borderRadius:4, padding:"14px 20px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1, textTransform:"uppercase"}}>
                  ▶ Clock In & Start Checklist
                </button>
              </>
            );
          })()

        ) : form.entryPath === "template" && !form.installationTypeId ? (
          // ── PHASE 1a: TEMPLATE PICKER — pick the installation type up front ──
          <>
            <StepIndicator step={1}/>
            <Card>
              <SectionTitle>Choose Installation Type</SectionTitle>
              <p style={{margin:"0 0 14px", fontSize:13, color:B.muted, fontFamily:"'Barlow',sans-serif", lineHeight:1.5}}>
                Select the type of installation you're documenting. The checklist you see on site depends on this.
              </p>
              <div style={{display:"flex", flexDirection:"column", gap:8}}>
                {installationTypes.length === 0 && (
                  <div style={{padding:"18px", textAlign:"center", color:B.muted, fontFamily:"'Barlow',sans-serif", fontSize:13, background:B.surface, border:`1px dashed ${B.border}`, borderRadius:4}}>
                    No installation types have been configured. Please contact head office.
                  </div>
                )}
                {installationTypes.map(t => (
                  <div key={t.id}
                    onClick={()=>setForm(f=>({...f, installationTypeId:t.id, answers:{}}))}
                    style={{border:`1px solid ${B.border}`, borderRadius:4, padding:"14px 16px", cursor:"pointer", background:B.surface, display:"flex", alignItems:"center", gap:14, transition:"all 0.15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=B.blue;e.currentTarget.style.background=B.blueDim;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=B.border;e.currentTarget.style.background=B.surface;}}>
                    <span style={{color:B.blueLt, fontSize:16}}>{t.icon}</span>
                    <span style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:15, color:B.light, flex:1}}>{t.label}</span>
                    <span style={{color:B.mid, fontSize:16}}>→</span>
                  </div>
                ))}
              </div>
            </Card>
          </>

        ) : !form.startTime ? (
          // ── PHASE 1b: JOB INFO (SRO header) — template path, type already picked ──
          <>
            <button onClick={()=>setHistView("list")}
              style={{background:B.surface, border:`1px solid ${B.border}`, borderRadius:4, padding:"11px 16px", fontSize:13, color:B.light, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase", display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%", boxSizing:"border-box"}}>
              <span>◫ My Job History</span>
              {myRecentJobs.length > 0
                ? <span style={{background:B.blueDim, border:`1px solid ${B.blue}`, borderRadius:10, padding:"1px 9px", fontSize:11, color:B.blueLt, fontFamily:"'Barlow',sans-serif", fontWeight:600}}>{myRecentJobs.length}</span>
                : <span style={{color:B.mid, fontSize:14}}>→</span>
              }
            </button>
            <StepIndicator step={1}/>

            {/* Selected template summary */}
            {selectedType && (
              <div style={{background:B.blueDim, border:`1px solid ${B.blue}`, borderRadius:6, padding:"12px 16px", display:"flex", alignItems:"center", gap:12}}>
                <span style={{color:B.blueLt, fontSize:18}}>{selectedType.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:10, color:B.blueLt, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif", marginBottom:2}}>Installation Type</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, color:B.white}}>{selectedType.label}</div>
                </div>
                <button onClick={()=>setSlots(p=>p.map((s,i)=>i===activeSlot?{...s, form:{...s.form, installationTypeId:"", answers:{}}, errors:{}}:s))}
                  style={{background:"transparent", color:B.blueLt, border:`1px solid ${B.blue}`, borderRadius:3, padding:"5px 10px", fontSize:11, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase"}}>
                  Change
                </button>
              </div>
            )}

            <Card>
              <SectionTitle>Job Information</SectionTitle>
              <div style={{display:"flex", flexDirection:"column", gap:14}}>
                <div>
                  <FieldLabel required>SRO Number</FieldLabel>
                  <SInput value={form.sroNumber} onChange={e=>{setForm(f=>({...f,sroNumber:e.target.value}));setErrors(er=>({...er,sroNumber:""}));}} placeholder="e.g. SRO-20240312-001" error={errors.sroNumber}/>
                  <ErrMsg msg={errors.sroNumber?"Required":""}/>
                </div>
                <div>
                  <FieldLabel required>Store / Location Name</FieldLabel>
                  <SInput value={form.storeName} onChange={e=>{setForm(f=>({...f,storeName:e.target.value}));setErrors(er=>({...er,storeName:""}));}} placeholder="e.g. Walmart — Newmarket North" error={errors.storeName}/>
                  <ErrMsg msg={errors.storeName?"Required":""}/>
                </div>
                <div>
                  <FieldLabel required>RL #</FieldLabel>
                  <SInput value={form.rlNumber} onChange={e=>{setForm(f=>({...f,rlNumber:e.target.value}));setErrors(er=>({...er,rlNumber:""}));}} placeholder="e.g. RL-4821" error={errors.rlNumber}/>
                  <ErrMsg msg={errors.rlNumber?"Required":""}/>
                </div>
                <div>
                  <FieldLabel required>Store Address</FieldLabel>
                  <SInput value={form.storeAddress} onChange={e=>{setForm(f=>({...f,storeAddress:e.target.value}));setErrors(er=>({...er,storeAddress:""}));}} placeholder="123 Main St, City, Province" error={errors.storeAddress}/>
                  <ErrMsg msg={errors.storeAddress?"Required":""}/>
                </div>
                <div>
                  <FieldLabel>2nd Installer (if applicable)</FieldLabel>
                  <SInput value={form.secondInstaller} onChange={e=>setForm(f=>({...f,secondInstaller:e.target.value}))} placeholder="Leave blank if solo"/>
                </div>
              </div>
            </Card>

            {Object.keys(errors).length > 0 && (
              <div style={{background:"rgba(192,57,43,0.1)", border:`1px solid ${B.red}`, borderRadius:4, padding:"12px 16px"}}>
                <div style={{fontWeight:700, color:B.red, fontSize:13, fontFamily:"'Barlow',sans-serif"}}>⚠ Please complete all required fields above before clocking in.</div>
              </div>
            )}

            <div style={{background:B.card, border:`1px solid ${B.border}`, borderRadius:8, padding:"24px 20px", textAlign:"center"}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:18, color:B.white, marginBottom:6, letterSpacing:0.5}}>Ready to Clock In?</div>
              <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted, marginBottom:20, lineHeight:1.6}}>
                Once you clock in, your arrival time is recorded<br/>and the installation checklist will unlock.
              </div>
              <button onClick={clockIn} className="sm-btn sm-btn-primary"
                style={{background:`linear-gradient(135deg, ${B.blueLt} 0%, ${B.blue} 100%)`, color:B.onAccent, border:"none", borderRadius:6, padding:"15px 36px", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textTransform:"uppercase", boxShadow:`0 6px 24px rgba(26,69,196,0.45)`}}>
                ▶ Clock In — Arrive on Site
              </button>
            </div>
          </>

        ) : (
          // ── PHASE 2: CHECKLIST + SIGN-OFF ──
          <>
            <StepIndicator step={2}/>

            {/* Job summary */}
            <div style={{background:B.surface, border:`1px solid ${B.border}`, borderRadius:6, padding:"12px 16px"}}>
              <div style={{fontSize:10, color:B.muted, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif", marginBottom:6}}>Job Summary</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, color:B.white}}>{form.sroNumber} <span style={{color:B.muted, fontWeight:400, fontSize:13}}>· RL# {form.rlNumber}</span></div>
              <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.light, marginTop:2}}>{form.storeName}</div>
              <div style={{fontFamily:"'Barlow',sans-serif", fontSize:12, color:B.muted}}>{form.storeAddress}</div>
            </div>

            {/* Live clock — computed from tick */}
            <div style={{background:"rgba(39,174,96,0.06)", border:`1px solid rgba(39,174,96,0.4)`, borderRadius:6, padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12}}>
              <div>
                <div style={{fontSize:10, color:B.muted, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif", marginBottom:3}}>Clocked In</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, color:B.green}}>{fmtTime(form.startTime)}</div>
                <div style={{fontFamily:"'Barlow',sans-serif", fontSize:11, color:B.muted}}>{fmtDate(form.startTime)}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:10, color:B.muted, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif", marginBottom:3}}>Time on Site</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:22, color:B.white}}>{elapsed(form.startTime, null)}</div>
              </div>
            </div>

            {/* Location notice — non-blocking, auto-dismisses */}
            {locNotice && (
              <div style={{background:"rgba(230,126,34,0.08)", border:`1px solid rgba(230,126,34,0.35)`, borderRadius:4, padding:"10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10}}>
                <span style={{fontSize:12, color:B.orange, fontFamily:"'Barlow',sans-serif", lineHeight:1.5}}>
                  ⚠ Location could not be captured — your {locNotice === "clock_in" ? "clock-in" : "submission"} coordinates will not be recorded. You can continue normally.
                </span>
                <button onClick={()=>setLocNotice(null)} style={{background:"none", border:"none", color:B.orange, cursor:"pointer", fontSize:16, padding:"0 4px", lineHeight:1, flexShrink:0}}>×</button>
              </div>
            )}

            {/* Installation type is chosen in Phase 1a now; picker removed from Phase 2. */}

            {/* Pre-loaded SRO checklist — two sections: Required Services (service-library matches) + Also Included (informational). */}
            {form.entryPath === "preloaded_sro" && Array.isArray(form.preloadedLineItems) && (() => {
              const items = form.preloadedLineItems;
              const serviceItems = items.filter(li => li && li.isService);
              const otherItems   = items.filter(li => li && !li.isService);
              return (
                <>
                  {serviceItems.length > 0 && (
                    <Card>
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:10,marginBottom:6}}>
                        <SectionTitle>Required Services — SRO Checklist</SectionTitle>
                        {form.preloadedPdfPath && (
                          <button onClick={()=>setViewSroPdf({ path: form.preloadedPdfPath, filename: form.preloadedPdfFilename })}
                            style={{display:"inline-flex",alignItems:"center",gap:6,background:B.blueDim,border:`1px solid ${B.blue}`,borderRadius:4,padding:"6px 14px",fontSize:11,fontWeight:700,color:B.blueLt,cursor:"pointer",letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow',sans-serif",whiteSpace:"nowrap"}}>
                            ↗ View SRO PDF
                          </button>
                        )}
                      </div>
                      <p style={{margin:"0 0 20px",fontSize:13,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>For each service: confirm Yes/No/N/A, upload a Before photo and an After photo. Comment is optional.</p>
                      <div style={{display:"flex",flexDirection:"column",gap:14}}>
                        {serviceItems.map((li, i) => {
                          const qId = `svc_${li.lineNo}`;
                          const ans = form.answers[qId] || {};
                          const answered = !!ans.confirmed;
                          const hasAnsErr      = errors[`ans_${qId}`];
                          const hasPhotoBefore = errors[`photoBefore_${qId}`];
                          const hasPhotoAfter  = errors[`photoAfter_${qId}`];
                          const m   = lineItemMinutes(li, services);
                          const qty = Number.isFinite(li.qty) && li.qty > 0 ? Math.round(li.qty) : 1;
                          const itemMins = Number.isFinite(m) ? m * qty : null;
                          // Per-service photo handlers — kept separate from ans.photos used by the
                          // general/template flow. setAns stores them at ans.photosBefore / .photosAfter.
                          const onBefore = (id, photos) => setAns(id, "photosBefore", photos);
                          const onAfter  = (id, photos) => setAns(id, "photosAfter",  photos);
                          return (
                            <div key={qId} style={{borderRadius:4,border:`1px solid ${(hasAnsErr||hasPhotoBefore||hasPhotoAfter)?B.red:answered?"rgba(39,174,96,0.4)":B.border}`,padding:16,background:answered?"rgba(39,174,96,0.04)":B.black}}>
                              <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8,flexWrap:"wrap",marginBottom:4}}>
                                <div style={{fontSize:10,color:B.blue,fontWeight:700,letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>
                                  Service {i+1} · Line {li.lineNo}
                                </div>
                                {itemMins > 0 && (
                                  <span title={qty>1?`${m} min × qty ${qty}`:""}
                                    style={{fontSize:10,background:"rgba(82,217,160,0.15)",color:"#52d9a0",border:`1px solid rgba(82,217,160,0.4)`,borderRadius:3,padding:"1px 6px",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:0.8,textTransform:"uppercase"}}>
                                    ⏱ Est. {formatMinutes(itemMins)}
                                  </span>
                                )}
                              </div>
                              <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:14,color:B.white,marginBottom:4,lineHeight:1.5}}>
                                {li.itemCode}<span style={{color:B.blue}}> *</span>
                              </div>
                              {li.description && (
                                <div style={{fontSize:13,color:B.light,marginBottom:8,fontFamily:"'Barlow',sans-serif",lineHeight:1.45}}>
                                  {li.description}
                                </div>
                              )}
                              <div style={{fontSize:11,color:B.muted,marginBottom:12,fontFamily:"'Barlow',sans-serif"}}>
                                Qty: <span style={{color:B.light,fontWeight:600}}>{li.qty}</span>{li.uom?` ${li.uom}`:""}
                              </div>

                              {/* Yes / No / N/A */}
                              <div style={{fontSize:10,color:B.muted,marginBottom:6,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>Was the service performed?</div>
                              <div style={{display:"flex",gap:8,marginBottom:12}}>
                                {["Yes","No","N/A"].map(opt=>{
                                  const sel=ans.confirmed===opt;
                                  const accent=opt==="Yes"?B.green:opt==="No"?B.red:B.mid;
                                  return <button key={opt} onClick={()=>setAns(qId,"confirmed",opt)}
                                    style={{padding:"8px 20px",borderRadius:4,border:`1px solid ${sel?accent:B.border}`,background:sel?`${accent}22`:"transparent",fontWeight:700,fontSize:13,cursor:"pointer",color:sel?accent:B.muted,fontFamily:"'Barlow',sans-serif",transition:"all 0.15s"}}>{opt}</button>;
                                })}
                              </div>

                              {/* Comment */}
                              <STextarea value={ans.notes||""} onChange={e=>setAns(qId,"notes",e.target.value)} placeholder="Comment (optional)..."/>

                              {/* Before / After photo slots */}
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:6}}>
                                <div style={{border:`1px solid ${hasPhotoBefore?B.red:B.border}`,borderRadius:4,padding:"10px 12px",background:"rgba(0,0,0,0.25)"}}>
                                  <div style={{fontSize:10,color:hasPhotoBefore?B.red:B.muted,marginBottom:6,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>
                                    📷 Before Photo <span style={{color:B.blue}}>*</span>
                                  </div>
                                  <QuestionPhoto qId={qId} photos={ans.photosBefore||[]} onUpdate={onBefore}/>
                                </div>
                                <div style={{border:`1px solid ${hasPhotoAfter?B.red:B.border}`,borderRadius:4,padding:"10px 12px",background:"rgba(0,0,0,0.25)"}}>
                                  <div style={{fontSize:10,color:hasPhotoAfter?B.red:B.muted,marginBottom:6,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>
                                    📷 After Photo <span style={{color:B.blue}}>*</span>
                                  </div>
                                  <QuestionPhoto qId={qId} photos={ans.photosAfter||[]} onUpdate={onAfter}/>
                                </div>
                              </div>
                              <ErrMsg msg={hasAnsErr?"Confirm whether the service was performed":hasPhotoBefore?"Before photo required":hasPhotoAfter?"After photo required":""}/>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  )}
                  {otherItems.length > 0 && (
                    <Card>
                      <SectionTitle>Also Included on this SRO</SectionTitle>
                      <p style={{margin:"0 0 14px",fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",lineHeight:1.5}}>
                        Informational — these line items are on the SRO but don't require separate photo evidence.
                      </p>
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {otherItems.map(li => (
                          <div key={`other_${li.lineNo}`} style={{borderRadius:4,border:`1px solid ${B.border}`,padding:"10px 14px",background:B.black,display:"flex",flexDirection:"column",gap:3}}>
                            <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                              <span style={{fontFamily:"monospace",fontSize:12,color:B.light,fontWeight:700}}>{li.itemCode}</span>
                              <span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>Line {li.lineNo} · Qty {li.qty}{li.uom?` ${li.uom}`:""}</span>
                            </div>
                            {li.description && (
                              <div style={{fontSize:12,color:B.mid,fontFamily:"'Barlow',sans-serif",lineHeight:1.4}}>{li.description}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </>
              );
            })()}

            {selectedType && <Card>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:10,marginBottom:6}}>
                <SectionTitle>{selectedType.label} — Site Checklist</SectionTitle>
                {(() => {
                  const docs = getInstructionDocs(selectedType);
                  if (docs.length === 0) return null;
                  // Prominent on-site call-to-action. Single doc → one pill button.
                  // Multiple docs → stacked pill buttons, each labelled with its filename.
                  const pillStyle = {display:"inline-flex",alignItems:"center",gap:6,background:`linear-gradient(135deg, ${B.blueLt} 0%, ${B.blue} 100%)`,border:`1px solid ${B.blueLt}`,borderRadius:6,padding:"8px 14px",fontSize:12,fontWeight:800,color:B.onAccent,textDecoration:"none",letterSpacing:1.2,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",whiteSpace:"nowrap",boxShadow:"0 2px 8px rgba(26,69,196,0.35)",maxWidth:240,overflow:"hidden",textOverflow:"ellipsis"};
                  if (docs.length === 1) {
                    return (
                      <a href={getViewableUrl(docs[0])} target="_blank" rel="noopener noreferrer" style={pillStyle}
                        title={docs[0].filename || "Installation Instructions"}>
                        📄 View Installation Instructions
                      </a>
                    );
                  }
                  return (
                    <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
                      {docs.map((doc, i) => (
                        <a key={i} href={getViewableUrl(doc)} target="_blank" rel="noopener noreferrer" style={pillStyle}
                          title={doc.filename || "Installation Instructions"}>
                          📄 {doc.filename || `Document ${i+1}`}
                        </a>
                      ))}
                    </div>
                  );
                })()}
              </div>
              <p style={{margin:"0 0 20px",fontSize:13,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>All questions mandatory. Confirm each item and upload photo evidence where applicable.</p>
              {scannerFor && (
                <BarcodeScanner
                  onDetect={val => { setAns(scannerFor,"notes",val); setScannerFor(null); setErrors(e=>{const n={...e};delete n[`comment_${scannerFor}`];return n;}); }}
                  onClose={() => setScannerFor(null)}
                />
              )}
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {selectedType.questions.map((q,i) => {
                  const ans = form.answers[q.id] || {};
                  const isInstruction = q.answerType === "none";
                  const answered = isInstruction ? true : q.answerType==="confirm"?!!ans.confirmed:q.answerType==="text"?!!ans.value?.trim():(!!ans.confirmed&&!!ans.value?.trim());
                  const hasAnsErr = errors[`ans_${q.id}`];
                  const hasPhotoErr = errors[`photo_${q.id}`];
                  const commentMode = q.commentMode || "optional";
                  const showComment = commentMode === "optional";
                  const isBarcodeMode = commentMode === "barcode";
                  const hasBarcodeErr = errors[`comment_${q.id}`];
                  return (
                    <div key={q.id} style={{borderRadius:4,border:`1px solid ${(hasAnsErr||hasPhotoErr||hasBarcodeErr)?B.red:answered?"rgba(39,174,96,0.4)":B.border}`,padding:16,background:answered?"rgba(39,174,96,0.04)":B.black}}>
                      <div style={{fontSize:10,color:isInstruction?B.muted:B.blue,marginBottom:4,fontWeight:700,letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>
                        {isInstruction ? `Step ${i+1}` : `Question ${i+1}`}
                      </div>
                      <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:500,fontSize:14,color:B.white,marginBottom:12,lineHeight:1.5}}>
                        {q.text}{!isInstruction && <span style={{color:B.blue}}> *</span>}
                      </div>
                      {(q.answerType==="confirm"||q.answerType==="both") && (
                        <div style={{display:"flex",gap:8,marginBottom:12}}>
                          {["Yes","No","N/A"].map(opt=>{
                            const sel=ans.confirmed===opt;
                            const accent=opt==="Yes"?B.green:opt==="No"?B.red:B.mid;
                            return <button key={opt} onClick={()=>setAns(q.id,"confirmed",opt)}
                              style={{padding:"8px 20px",borderRadius:4,border:`1px solid ${sel?accent:B.border}`,background:sel?`${accent}22`:"transparent",fontWeight:700,fontSize:13,cursor:"pointer",color:sel?accent:B.muted,fontFamily:"'Barlow',sans-serif",transition:"all 0.15s"}}>{opt}</button>;
                          })}
                        </div>
                      )}
                      {(q.answerType==="text"||q.answerType==="both") && (
                        <div style={{marginBottom:12}}>
                          <SInput value={ans.value||""} onChange={e=>setAns(q.id,"value",e.target.value)} placeholder={q.placeholder||"Enter response..."}/>
                        </div>
                      )}
                      {showComment && <STextarea value={ans.notes||""} onChange={e=>setAns(q.id,"notes",e.target.value)} placeholder="Comment (optional)..."/>}
                      {isBarcodeMode && (
                        <div style={{marginTop:4}}>
                          {ans.notes
                            ? <div style={{display:"flex",alignItems:"center",gap:8,background:"rgba(39,174,96,0.08)",border:`1px solid rgba(39,174,96,0.35)`,borderRadius:4,padding:"8px 12px"}}>
                                <span style={{fontSize:11,color:B.green,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase",flexShrink:0}}>Barcode</span>
                                <span style={{fontFamily:"monospace",fontSize:13,color:B.white,flex:1,wordBreak:"break-all"}}>{ans.notes}</span>
                                <button onClick={()=>setScannerFor(q.id)} style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"3px 8px",cursor:"pointer",fontFamily:"'Barlow',sans-serif",flexShrink:0}}>Rescan</button>
                              </div>
                            : <button onClick={()=>setScannerFor(q.id)} style={{width:"100%",background:hasBarcodeErr?"rgba(192,57,43,0.1)":"rgba(17,52,166,0.08)",border:`1px solid ${hasBarcodeErr?B.red:B.blue}`,borderRadius:4,padding:"12px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,color:hasBarcodeErr?B.red:B.blueLt,letterSpacing:1,textTransform:"uppercase"}}>
                                📷 Scan Barcode
                              </button>
                          }
                          <ErrMsg msg={hasBarcodeErr?"Barcode scan required for this item":""}/>
                        </div>
                      )}
                      {q.photoMode!=="none" && (
                        <QuestionPhoto qId={q.id} photos={ans.photos||[]} onUpdate={(id,p)=>setAns(id,"photos",p)}/>
                      )}
                      {q.photoMode==="required" && <div style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif",marginTop:6}}>Photo required for this item.</div>}
                      <ErrMsg msg={hasAnsErr?"This item requires an answer":hasPhotoErr?"A photo is required for this item":""}/>
                    </div>
                  );
                })}
              </div>
            </Card>}

            {/* General Photos */}
            <Card>
              <SectionTitle>General Job Photos</SectionTitle>
              {(() => {
                // Shared handler — both buttons (camera + library) push through the same upload pipeline.
                const handleGeneralPhotos = e => {
                  Array.from(e.target.files).forEach(async file=>{
                    setUploadingPhotos(c=>c+1);
                    try { const photo=await uploadPhoto(file,"general"); setForm(f=>({...f,photos:[...f.photos,photo]})); }
                    catch(err){ alert(`Photo upload failed: ${err.message}`); }
                    finally { setUploadingPhotos(c=>c-1); }
                  });
                  e.target.value = ""; // allow reselecting same file
                };
                return (
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <label style={{display:"flex",flexDirection:"column",alignItems:"center",border:`1px solid ${B.blue}`,background:B.blueDim,borderRadius:6,padding:"18px 12px",cursor:uploadingPhotos>0?"not-allowed":"pointer",gap:6,opacity:uploadingPhotos>0?0.6:1}}>
                      <span style={{fontSize:24}}>📷</span>
                      <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,color:B.blueLt,fontSize:13,letterSpacing:1.5,textTransform:"uppercase"}}>Take Photo</span>
                      <input type="file" accept="image/*" capture="environment" disabled={uploadingPhotos>0} onChange={handleGeneralPhotos} style={{display:"none"}}/>
                    </label>
                    <label style={{display:"flex",flexDirection:"column",alignItems:"center",border:`1px dashed ${B.border}`,borderRadius:6,padding:"18px 12px",cursor:uploadingPhotos>0?"not-allowed":"pointer",background:B.black,gap:6,opacity:uploadingPhotos>0?0.6:1}}>
                      <span style={{fontSize:24}}>🖼</span>
                      <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:B.blue,fontSize:13,letterSpacing:1.5,textTransform:"uppercase"}}>Choose Photos</span>
                      <input type="file" accept="image/*" multiple disabled={uploadingPhotos>0} onChange={handleGeneralPhotos} style={{display:"none"}}/>
                    </label>
                  </div>
                );
              })()}
              {uploadingPhotos>0&&<div style={{display:"flex",alignItems:"center",gap:6,marginTop:8,fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif"}}><Spinner size={13} color={B.muted}/>Uploading {uploadingPhotos} photo{uploadingPhotos>1?"s":""}…</div>}
              {form.photos.length > 0 && <div style={{display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6, marginTop:12}}>
                {form.photos.map((p, i) => (
                  <div key={i} style={{position:"relative", borderRadius:4, overflow:"hidden", aspectRatio:"1", border:`1px solid ${B.border}`}}>
                    <img src={p.url||p.data} alt="" style={{width:"100%", height:"100%", objectFit:"cover"}}/>
                    <button onClick={() => setForm(f=>({...f, photos:f.photos.filter((_,j)=>j!==i)}))}
                      style={{position:"absolute", top:2, right:2, background:"rgba(0,0,0,0.75)", color:"#fff", border:"none", borderRadius:2, width:18, height:18, fontSize:10, cursor:"pointer"}}>✕</button>
                  </div>
                ))}
              </div>}
            </Card>

            {/* Site Sign-Off */}
            <Card style={{border:`1px solid ${errors.siteContactName||errors.signature?B.red:B.border}`}}>
              <SectionTitle>Site Sign-Off</SectionTitle>
              <p style={{margin:"0 0 16px", fontSize:13, color:B.muted, fontFamily:"'Barlow',sans-serif", lineHeight:1.5}}>
                Record who you dealt with on site, then hand the device to them to enter their name and sign below.
              </p>

              {/* Installer fills these */}
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20}}>
                <div>
                  <FieldLabel>Title / Role</FieldLabel>
                  <SInput value={form.siteContactTitle} onChange={e=>setForm(f=>({...f,siteContactTitle:e.target.value}))} placeholder="e.g. Store Manager"/>
                </div>
                <div>
                  <FieldLabel>Phone / Email</FieldLabel>
                  <SInput value={form.siteContactPhone} onChange={e=>setForm(f=>({...f,siteContactPhone:e.target.value}))} placeholder="Optional"/>
                </div>
              </div>

              {/* Hand-off divider */}
              <div style={{display:"flex", alignItems:"center", gap:12, margin:"4px 0 20px"}}>
                <div style={{flex:1, height:1, background:B.border}}/>
                <span style={{fontSize:11, color:B.muted, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", whiteSpace:"nowrap"}}>
                  Hand device to site contact
                </span>
                <div style={{flex:1, height:1, background:B.border}}/>
              </div>

              {/* Site contact fills these */}
              <div style={{marginBottom:16}}>
                <FieldLabel required>Full Name</FieldLabel>
                <SInput value={form.siteContactName} onChange={e=>{setForm(f=>({...f,siteContactName:e.target.value}));setErrors(er=>({...er,siteContactName:""}));}} placeholder="Print full name" error={errors.siteContactName}/>
                <ErrMsg msg={errors.siteContactName?"Required":""}/>
              </div>

              <FieldLabel required>Signature</FieldLabel>
              <SignaturePad value={form.signatureData} onChange={sig=>{setForm(f=>({...f,signatureData:sig}));if(sig)setErrors(e=>({...e,signature:""}));}}/>
              <ErrMsg msg={errors.signature?"A signature is required before submitting":""}/>
            </Card>

            {outcomeMode
              ? /* ── Outcome form (Aborted / Incomplete) ── */
                <div style={{background: outcomeMode==="aborted"?"rgba(149,165,166,0.08)":"rgba(241,196,15,0.06)", border:`1px solid ${outcomeMode==="aborted"?"#7f8c8d":"#d4ac0d"}`, borderRadius:6, padding:20}}>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:16, color:outcomeMode==="aborted"?"#95a5a6":"#f1c40f", letterSpacing:0.5, marginBottom:4}}>
                    {outcomeMode==="aborted" ? "⊘ Mark Job as Aborted" : "◑ Mark Job as Incomplete"}
                  </div>
                  <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted, marginBottom:16, lineHeight:1.5}}>
                    {outcomeMode==="aborted"
                      ? "The job was never started on site. Please explain why."
                      : "The job was started but could not be finished. Please explain what was completed and why you could not continue."
                    }
                  </div>
                  <FieldLabel required>Reason</FieldLabel>
                  <STextarea value={outcomeReason} onChange={e=>{setOutcomeReason(e.target.value);if(e.target.value.trim())setOutcomeErr("");}} placeholder={outcomeMode==="aborted"?"e.g. Store was closed on arrival, no site contact available.":"e.g. Completed fixture install but could not configure network — IT not on site."}/>
                  {outcomeErr && <ErrMsg msg={outcomeErr}/>}
                  <div style={{marginTop:14}}>
                    <FieldLabel>Supporting Photo (optional)</FieldLabel>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:6}}>
                      <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,border:`1px solid ${B.blue}`,background:B.blueDim,borderRadius:4,padding:"10px 12px",cursor:uploadingOutcome>0?"not-allowed":"pointer",opacity:uploadingOutcome>0?0.6:1,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:B.blueLt,fontSize:11,letterSpacing:1.5,textTransform:"uppercase"}}>
                        📷 Take Photo
                        <input type="file" accept="image/*" capture="environment" disabled={uploadingOutcome>0} onChange={e=>{
                          const file = e.target.files[0]; if(!file) return;
                          setUploadingOutcome(c=>c+1);
                          uploadPhoto(file,"outcome")
                            .then(photo=>setOutcomePhotos(p=>[...p,photo]))
                            .catch(err=>alert(`Photo upload failed: ${err.message}`))
                            .finally(()=>setUploadingOutcome(c=>c-1));
                          e.target.value = "";
                        }} style={{display:"none"}}/>
                      </label>
                      <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,border:`1px dashed ${B.border}`,background:B.black,borderRadius:4,padding:"10px 12px",cursor:uploadingOutcome>0?"not-allowed":"pointer",opacity:uploadingOutcome>0?0.6:1,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,color:B.blue,fontSize:11,letterSpacing:1.5,textTransform:"uppercase"}}>
                        🖼 Choose
                      <input type="file" accept="image/*" disabled={uploadingOutcome>0} onChange={e=>{
                        const file = e.target.files[0]; if(!file) return;
                        setUploadingOutcome(c=>c+1);
                        uploadPhoto(file,"outcome")
                          .then(photo=>setOutcomePhotos(p=>[...p,photo]))
                          .catch(err=>alert(`Photo upload failed: ${err.message}`))
                          .finally(()=>setUploadingOutcome(c=>c-1));
                      }} style={{display:"none"}}/>
                    </label>
                    </div>
                    {uploadingOutcome>0&&<div style={{display:"flex",alignItems:"center",gap:6,marginTop:6,fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif"}}><Spinner size={13} color={B.muted}/>Uploading…</div>}
                    {outcomePhotos.length > 0 && <div style={{display:"flex", gap:6, marginTop:8, flexWrap:"wrap"}}>
                      {outcomePhotos.map((p,i)=>(
                        <div key={i} style={{position:"relative", width:64, height:64, borderRadius:4, overflow:"hidden", border:`1px solid ${B.border}`}}>
                          <img src={p.url||p.data} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                          <button onClick={()=>setOutcomePhotos(prev=>prev.filter((_,j)=>j!==i))}
                            style={{position:"absolute",top:2,right:2,background:"rgba(0,0,0,0.75)",color:"#fff",border:"none",borderRadius:2,width:16,height:16,fontSize:9,cursor:"pointer"}}>✕</button>
                        </div>
                      ))}
                    </div>}
                  </div>
                  <div style={{display:"flex", gap:10, marginTop:18}}>
                    <button onClick={submitOutcome} disabled={submitting} className="sm-btn"
                      style={{flex:1, background:submitting?B.mid:(outcomeMode==="aborted"?"#7f8c8d":"#d4ac0d"), color:B.white, border:"none", borderRadius:6, padding:"14px", fontSize:13, fontWeight:800, cursor:submitting?"not-allowed":"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textTransform:"uppercase", display:"flex", alignItems:"center", justifyContent:"center", gap:8}}>
                      {submitting ? <><Spinner/> Submitting…</> : `Confirm — ${outcomeMode==="aborted"?"Job Aborted":"Job Incomplete"}`}
                    </button>
                    <button onClick={()=>{setOutcomeMode(null);setOutcomeReason("");setOutcomePhotos([]);setOutcomeErr("");}}
                      style={{background:"transparent", color:B.muted, border:`1px solid ${B.border}`, borderRadius:4, padding:"14px 18px", fontSize:13, cursor:"pointer", fontFamily:"'Barlow',sans-serif"}}>
                      Cancel
                    </button>
                  </div>
                </div>
              : <>
                  {Object.keys(errors).length > 0 && (
                    <div style={{background:"rgba(192,57,43,0.1)", border:`1px solid ${B.red}`, borderRadius:4, padding:"12px 16px"}}>
                      <div style={{fontWeight:700, color:B.red, fontSize:13, fontFamily:"'Barlow',sans-serif"}}>⚠ Please complete all required fields before submitting.</div>
                    </div>
                  )}

                  {dupWarning
                    ? <div style={{background:"rgba(230,126,34,0.10)", border:`1px solid ${B.orange}`, borderRadius:6, padding:"18px"}}>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:15, color:B.orange, letterSpacing:0.5, marginBottom:8}}>
                          ⚠ Duplicate Submission Detected
                        </div>
                        <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.light, lineHeight:1.6, marginBottom:16}}>
                          It looks like you've already submitted a job for SRO <strong style={{color:B.white}}>{form.sroNumber}</strong> with installation type <strong style={{color:B.white}}>{selectedType?.label}</strong> today. Are you sure you want to submit again?
                        </div>
                        <div style={{display:"flex", gap:10}}>
                          <button onClick={()=>{ setDupWarning(null); submit(); }} className="sm-btn sm-btn-primary"
                            style={{flex:1, background:B.orange, color:B.white, border:"none", borderRadius:4, padding:"13px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textTransform:"uppercase"}}>
                            Yes, Submit Anyway
                          </button>
                          <button onClick={()=>setDupWarning(null)} className="sm-btn"
                            style={{background:"transparent", color:B.muted, border:`1px solid ${B.border}`, borderRadius:4, padding:"13px 20px", fontSize:13, cursor:"pointer", fontFamily:"'Barlow',sans-serif"}}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    : <button onClick={handleSubmitClick} disabled={submitting} className="sm-btn sm-btn-primary"
                        style={{background:submitting?B.mid:`linear-gradient(135deg, ${B.blueLt} 0%, ${B.blue} 100%)`, color:B.onAccent, border:"none", borderRadius:6, padding:"16px", fontSize:14, fontWeight:800, cursor:submitting?"not-allowed":"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textTransform:"uppercase", boxShadow:submitting?"none":`0 4px 20px rgba(26,69,196,0.4)`, display:"flex", alignItems:"center", justifyContent:"center", gap:10}}>
                        {submitting ? <><Spinner/> Submitting…</> : "Submit Job Report & Clock Out →"}
                      </button>
                  }

                  {/* Unable to complete — Abort / Incomplete buttons */}
                  <div style={{borderTop:`1px solid ${B.border}`, paddingTop:14, marginTop:4}}>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:B.mid, letterSpacing:1.5, textTransform:"uppercase", marginBottom:10, textAlign:"center"}}>Unable to complete this job?</div>
                    <div style={{display:"flex", gap:10}}>
                      <button onClick={()=>setOutcomeMode("aborted")} className="sm-btn sm-btn-danger"
                        style={{flex:1, background:"transparent", color:"#95a5a6", border:`1px solid #7f8c8d`, borderRadius:4, padding:"11px 8px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1.5, textTransform:"uppercase"}}>
                        ⊘ Aborted
                      </button>
                      <button onClick={()=>setOutcomeMode("incomplete")} className="sm-btn"
                        style={{flex:1, background:"transparent", color:"#f1c40f", border:`1px solid #d4ac0d`, borderRadius:4, padding:"11px 8px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1.5, textTransform:"uppercase"}}>
                        ◑ Incomplete
                      </button>
                    </div>
                  </div>
                </>
            }
          </>
        )}
      </div>
      {draftToast && (
        <div className={draftToastExit ? "sm-toast-exit" : "sm-toast-enter"}
          style={{position:"fixed",bottom:72,left:20,background:B.glass,border:`1px solid ${B.border}`,borderRadius:6,padding:"9px 16px",fontSize:13,color:B.green,fontFamily:"'Barlow',sans-serif",fontWeight:600,zIndex:300,boxShadow:B.shadow,backdropFilter:"blur(8px)",pointerEvents:"none"}}>
          ✓ Draft saved
        </div>
      )}
      {viewSroPdf && (
        <SROPdfViewer
          pdfPath={viewSroPdf.path}
          filename={viewSroPdf.filename}
          onClose={()=>setViewSroPdf(null)}
        />
      )}
    </div>
  );
}

// ── REPORT HELPERS ────────────────────────────────────────────────────────────
const CHART_PALETTE = ["#1134A6","#27ae60","#e67e22","#c0392b","#52d9a0","#f1c40f","#8e44ad","#3498db","#1abc9c","#95a5a6"];

function RepStatCard({ label, value, sub, color }) {
  return (
    <div style={{background:B.card,border:`1px solid ${B.border}`,borderRadius:6,padding:"16px 14px",textAlign:"center"}}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:28,color:color||B.white,lineHeight:1}}>{value}</div>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:10,color:B.muted,letterSpacing:1.5,textTransform:"uppercase",marginTop:5,lineHeight:1.3}}>{label}</div>
      {sub && <div style={{fontFamily:"'Barlow',sans-serif",fontSize:11,color:B.mid,marginTop:3}}>{sub}</div>}
    </div>
  );
}

function RepPieWidget({ title, data, note }) {
  const [ready, setReady] = useState(!!window.Recharts);
  useEffect(() => {
    if (window.Recharts) { setReady(true); return; }
    const id = setInterval(() => { if (window.Recharts) { setReady(true); clearInterval(id); } }, 300);
    return () => clearInterval(id);
  }, []);

  const activeData = (data || []).filter(d => d.value > 0);

  return (
    <div style={{background:B.card,border:`1px solid ${B.border}`,borderRadius:6,padding:20,breakInside:"avoid"}}>
      <SectionTitle>{title}</SectionTitle>
      {note && <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",marginBottom:10,marginTop:-10,lineHeight:1.4}}>{note}</div>}
      {!ready
        ? <div style={{height:200,display:"flex",alignItems:"center",justifyContent:"center",color:B.muted,fontFamily:"'Barlow',sans-serif",fontSize:13}}>Loading chart…</div>
        : activeData.length === 0
          ? <div style={{height:200,display:"flex",alignItems:"center",justifyContent:"center",color:B.muted,fontFamily:"'Barlow',sans-serif",fontSize:13}}>No data for this period.</div>
          : (() => {
              const { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } = window.Recharts;
              return (
                <ResponsiveContainer width="100%" height={210}>
                  <PieChart>
                    <Pie data={activeData} cx="50%" cy="45%" innerRadius={48} outerRadius={82} paddingAngle={2} dataKey="value"
                      label={({percent}) => percent > 0.04 ? `${(percent*100).toFixed(0)}%` : ""}
                      labelLine={false}>
                      {activeData.map((entry, i) => <Cell key={i} fill={entry.color} stroke="transparent"/>)}
                    </Pie>
                    <Tooltip formatter={(val, name) => [val, name]}
                      contentStyle={{background:B.card,border:`1px solid ${B.border}`,borderRadius:4,fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.light}}/>
                    <Legend iconType="circle" iconSize={8}
                      wrapperStyle={{fontFamily:"'Barlow',sans-serif",fontSize:11,color:B.light,paddingTop:6}}/>
                  </PieChart>
                </ResponsiveContainer>
              );
            })()
      }
    </div>
  );
}

// ── PERFORMANCE REPORTS ───────────────────────────────────────────────────────
function PerformanceReports({ jobs, users, installationTypes, onBack }) {
  const [dateFrom,     setDateFrom]     = useState("");
  const [dateTo,       setDateTo]       = useState("");
  const [installerSel, setInstallerSel] = useState("all");
  const [typeSel,      setTypeSel]      = useState("all");
  const [statusSel,    setStatusSel]    = useState("all");
  const [report,       setReport]       = useState(null);

  const installers = users.filter(u => u.active !== false && u.role === "installer");

  // Inject print CSS
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "pr-print-css";
    style.textContent = `
      @media print {
        body > * { display: none !important; }
        #pr-printable { display: block !important; position: fixed; top:0; left:0; width:100%; background:#1a1a1a; color:#f5f5f5; overflow:auto; z-index:9999; padding:24px; box-sizing:border-box; }
        .pr-no-print { display: none !important; }
        .pr-chart-grid { grid-template-columns: 1fr 1fr !important; }
      }
    `;
    document.head.appendChild(style);
    return () => document.getElementById("pr-print-css")?.remove();
  }, []);

  const fmtMs = ms => {
    if (!ms || ms <= 0) return "—";
    const h = Math.floor(ms / 3600000);
    const m = Math.round((ms % 3600000) / 60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const computeMetrics = jobSet => {
    const total = jobSet.length;
    if (total === 0) return null;
    const get = k => jobSet.filter(j => j.status === k).length;
    const completed  = get("completed");
    const approved   = get("approved");
    const pending    = get("pending");
    const rejected   = get("rejected");
    const revisit    = get("requires_revisit");
    const aborted    = get("aborted");
    const incomplete = get("incomplete");
    const withTime   = jobSet.filter(j => j.startTime && j.endTime && new Date(j.endTime) > new Date(j.startTime));
    const avgMs      = withTime.length ? withTime.reduce((s, j) => s + (new Date(j.endTime) - new Date(j.startTime)), 0) / withTime.length : 0;
    const timeBuckets = {"< 1h":0,"1–2h":0,"2–4h":0,"> 4h":0};
    withTime.forEach(j => {
      const h = (new Date(j.endTime) - new Date(j.startTime)) / 3600000;
      if      (h < 1) timeBuckets["< 1h"]++;
      else if (h < 2) timeBuckets["1–2h"]++;
      else if (h < 4) timeBuckets["2–4h"]++;
      else            timeBuckets["> 4h"]++;
    });
    const byType = {};
    jobSet.forEach(j => { const t = j.typeSnapshot?.label || "Unknown"; byType[t] = (byType[t] || 0) + 1; });
    return {
      total, completed, approved, pending, rejected, revisit, aborted, incomplete,
      avgMs, withTimeCount: withTime.length, timeBuckets, byType,
      completionRate: (((completed + approved) / total) * 100).toFixed(1),
      rejectionRate:  (((rejected + revisit) / total) * 100).toFixed(1),
      outcomeRate:    (((aborted + incomplete) / total) * 100).toFixed(1),
    };
  };

  const generate = () => {
    const filtered = jobs.filter(j => {
      if (installerSel !== "all" && String(j.installerUserId) !== installerSel) return false;
      if (typeSel !== "all" && (j.typeSnapshot?.label || j.typeSnapshot?.id || j.installationTypeId) !== typeSel) return false;
      if (statusSel !== "all" && j.status !== statusSel) return false;
      if (dateFrom && new Date(j.submittedAt) < new Date(dateFrom + "T00:00:00")) return false;
      if (dateTo   && new Date(j.submittedAt) > new Date(dateTo   + "T23:59:59.999")) return false;
      return true;
    });
    const overall = computeMetrics(filtered);
    let perInstaller = null;
    if (installerSel === "all" && filtered.length > 0) {
      const uniqueIds = [...new Set(filtered.map(j => j.installerUserId))];
      perInstaller = uniqueIds.map(id => {
        const jbs = filtered.filter(j => j.installerUserId === id);
        const u = users.find(u => u.id === id);
        return { id, name: u?.name || jbs[0]?.installerName || `#${id}`, metrics: computeMetrics(jbs) };
      }).sort((a, b) => (b.metrics?.total || 0) - (a.metrics?.total || 0));
    }
    const installerLabel = installerSel === "all"
      ? "Team Summary"
      : users.find(u => String(u.id) === installerSel)?.name || "Unknown Installer";
    const typeLabel = typeSel === "all" ? "All Types" : typeSel;
    const statusLabel = statusSel === "all" ? "All Statuses" : statusSel.replace(/_/g," ").replace(/\w/g,c=>c.toUpperCase());
    setReport({ overall, perInstaller, filtered, dateFrom, dateTo, installerLabel, typeLabel, statusLabel });
  };

  const exportExcel = () => {
    const XLSX = window.XLSX;
    if (!XLSX || !report?.overall) return;
    const { overall: m, perInstaller, filtered, dateFrom: df, dateTo: dt, installerLabel, typeLabel, statusLabel } = report;
    const wb = XLSX.utils.book_new();
    const periodStr = df && dt ? `${df} to ${dt}` : df ? `From ${df}` : dt ? `To ${dt}` : "All Time";

    // Summary sheet
    const summaryRows = [
      ["SOMERVILLE MERCHANDISING — FIELD OPERATIONS"],
      ["Performance Report"],
      [`Scope: ${installerLabel} · ${typeLabel} · ${statusLabel}`],
      [`Period: ${periodStr}`],
      [`Generated: ${new Date().toLocaleString("en-CA", { dateStyle:"medium", timeStyle:"short" })}`],
      [],
      ["METRIC", "VALUE"],
      ["Total Jobs",           m.total],
      ["Completed",            m.completed],
      ["Approved",             m.approved],
      ["Pending",              m.pending],
      ["Rejected",             m.rejected],
      ["Requires Revisit",     m.revisit],
      ["Aborted",              m.aborted],
      ["Incomplete",           m.incomplete],
      [],
      ["Completion Rate",      `${m.completionRate}%`],
      ["Rejection Rate",       `${m.rejectionRate}%`],
      ["Aborted/Incomplete Rate", `${m.outcomeRate}%`],
      ["Avg. Time on Site",    m.avgMs > 0 ? fmtMs(m.avgMs) : "N/A"],
      ["Jobs with Time Recorded", m.withTimeCount],
    ];
    const ss = XLSX.utils.aoa_to_sheet(summaryRows);
    ss["!cols"] = [{wch:32},{wch:22}];
    ss["!merges"] = [{s:{r:0,c:0},e:{r:0,c:1}}];
    XLSX.utils.book_append_sheet(wb, ss, "Summary");

    // By Type sheet
    const typeRows = [
      ["Installation Type", "Job Count", "% of Total"],
      ...Object.entries(m.byType).map(([t, c]) => [t, c, `${((c/m.total)*100).toFixed(1)}%`]),
    ];
    const ts = XLSX.utils.aoa_to_sheet(typeRows);
    ts["!cols"] = [{wch:30},{wch:12},{wch:12}];
    XLSX.utils.book_append_sheet(wb, ts, "By Installation Type");

    // Time Distribution sheet
    const timeRows = [
      ["Duration Bucket", "Job Count", "% of Timed Jobs"],
      ...Object.entries(m.timeBuckets).map(([b, c]) => [b, c, m.withTimeCount > 0 ? `${((c/m.withTimeCount)*100).toFixed(1)}%` : "—"]),
    ];
    const tds = XLSX.utils.aoa_to_sheet(timeRows);
    tds["!cols"] = [{wch:18},{wch:12},{wch:18}];
    XLSX.utils.book_append_sheet(wb, tds, "Time Distribution");

    // Per Installer sheet
    if (perInstaller) {
      const piRows = [
        ["Installer", "Total", "Completed", "Approved", "Rejected", "Revisit", "Aborted", "Incomplete", "Completion %", "Rejection %", "Avg. Time"],
        ...perInstaller.map(p => {
          const pm = p.metrics;
          if (!pm) return [p.name, 0, ...Array(9).fill("N/A")];
          return [p.name, pm.total, pm.completed, pm.approved, pm.rejected, pm.revisit, pm.aborted, pm.incomplete,
            `${pm.completionRate}%`, `${pm.rejectionRate}%`, pm.avgMs > 0 ? fmtMs(pm.avgMs) : "N/A"];
        }),
      ];
      const pis = XLSX.utils.aoa_to_sheet(piRows);
      pis["!cols"] = [{wch:24},{wch:8},{wch:12},{wch:10},{wch:10},{wch:10},{wch:10},{wch:12},{wch:14},{wch:13},{wch:12}];
      XLSX.utils.book_append_sheet(wb, pis, "Per Installer");
    }

    // Raw data sheet
    const rawRows = [
      ["SRO Number","Site Name","Type","Installer","Status","Submitted","Clock-In","Clock-Out","Time on Site","Outcome Reason"],
      ...filtered.map(j => {
        const ms = j.startTime && j.endTime ? Math.max(0, new Date(j.endTime) - new Date(j.startTime)) : 0;
        return [j.sroNumber||"", j.storeName||"", j.typeSnapshot?.label||"", j.installerName||"",
          j.status||"",
          j.submittedAt ? new Date(j.submittedAt).toLocaleString("en-CA") : "",
          j.startTime   ? new Date(j.startTime).toLocaleString("en-CA") : "",
          j.endTime     ? new Date(j.endTime).toLocaleString("en-CA") : "",
          ms > 0 ? fmtMs(ms) : "", j.outcomeReason||""];
      }),
    ];
    const rs = XLSX.utils.aoa_to_sheet(rawRows);
    rs["!cols"] = [{wch:14},{wch:24},{wch:20},{wch:20},{wch:14},{wch:20},{wch:20},{wch:20},{wch:12},{wch:40}];
    XLSX.utils.book_append_sheet(wb, rs, "Raw Data");

    const safe = s => s.replace(/[^a-z0-9]/gi, "-").replace(/-+/g, "-").toLowerCase();
    const dateTag = df && dt ? `${df}-to-${dt}` : df ? `from-${df}` : dt ? `to-${dt}` : "all-time";
    XLSX.writeFile(wb, `performance-report-${safe(installerLabel)}-${dateTag}.xlsx`);
  };

  const r = report;
  const m = r?.overall;

  const genPeriodStr = () => {
    if (!r) return "";
    if (r.dateFrom && r.dateTo) return `${r.dateFrom} to ${r.dateTo}`;
    if (r.dateFrom) return `From ${r.dateFrom}`;
    if (r.dateTo)   return `To ${r.dateTo}`;
    return "All Time";
  };

  // Chart data
  const statusData = m ? [
    { name:"Completed",        value:m.completed,  color:STATUS_CONFIG.completed.color },
    { name:"Approved",         value:m.approved,   color:STATUS_CONFIG.approved.color },
    { name:"Pending",          value:m.pending,    color:STATUS_CONFIG.pending.color },
    { name:"Rejected",         value:m.rejected,   color:STATUS_CONFIG.rejected.color },
    { name:"Requires Revisit", value:m.revisit,    color:STATUS_CONFIG.requires_revisit.color },
    { name:"Aborted",          value:m.aborted,    color:STATUS_CONFIG.aborted.color },
    { name:"Incomplete",       value:m.incomplete, color:STATUS_CONFIG.incomplete.color },
  ] : [];
  const completionData = m ? [
    { name:"Approved / Completed", value:m.approved + m.completed,                         color:STATUS_CONFIG.completed.color },
    { name:"Not Completed",        value:m.total - m.approved - m.completed, color:"#3a3a3a" },
  ] : [];
  const rejectionData = m ? [
    { name:"Rejected / Revisit", value:m.rejected + m.revisit,             color:STATUS_CONFIG.rejected.color },
    { name:"Not Rejected",       value:m.total - m.rejected - m.revisit,    color:"#3a3a3a" },
  ] : [];
  const outcomeData = m ? [
    { name:"Aborted",    value:m.aborted,                                    color:STATUS_CONFIG.aborted.color },
    { name:"Incomplete", value:m.incomplete,                                 color:STATUS_CONFIG.incomplete.color },
    { name:"Resolved",   value:m.total - m.aborted - m.incomplete,           color:"#3a3a3a" },
  ] : [];
  const typeData = m ? Object.entries(m.byType).map(([name, value], i) => ({
    name, value, color: CHART_PALETTE[i % CHART_PALETTE.length],
  })) : [];
  const appVsRejData = m ? [
    { name:"Approved + Completed", value:m.approved + m.completed,       color:STATUS_CONFIG.completed.color },
    { name:"Rejected + Revisit",   value:m.rejected + m.revisit,         color:STATUS_CONFIG.rejected.color },
    { name:"Pending / Other",      value:m.pending + m.aborted + m.incomplete, color:"#3a3a3a" },
  ] : [];
  const timeData = m ? Object.entries(m.timeBuckets).map(([name, value], i) => ({
    name, value, color: ["#1a45c4","#1134A6","#0d2a80","#08194d"][i],
  })) : [];

  const inputStyle = { background:B.surface, border:`1px solid ${B.border}`, borderRadius:4, color:B.light, fontSize:13, padding:"8px 12px", fontFamily:"'Barlow',sans-serif", outline:"none", width:"100%", boxSizing:"border-box", cursor:"pointer" };

  return (
    <div style={{maxWidth:900, margin:"0 auto", paddingBottom:60}}>
      {/* ── Top bar ── */}
      <div className="pr-no-print" style={{background:B.dark, borderBottom:`1px solid ${B.border}`, padding:"16px 20px", marginBottom:20, position:"sticky", top:0, zIndex:50}}>
        <div style={{display:"flex", alignItems:"center", gap:12, flexWrap:"wrap"}}>
          <button onClick={onBack}
            style={{background:"transparent", color:B.muted, border:`1px solid ${B.border}`, borderRadius:3, padding:"5px 14px", cursor:"pointer", fontSize:11, fontFamily:"'Barlow',sans-serif", flexShrink:0}}>
            ← Back
          </button>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:10, color:B.muted, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif"}}>Head Office</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:22, color:B.white, letterSpacing:0.5}}>Performance Reports</div>
          </div>
          {r && m && (
            <div style={{display:"flex", gap:8, flexShrink:0}}>
              <button onClick={exportExcel}
                style={{display:"flex", alignItems:"center", gap:6, background:B.blueDim, border:`1px solid ${B.blue}`, borderRadius:3, padding:"7px 14px", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:B.blueLt, letterSpacing:1, textTransform:"uppercase"}}>
                ↓ Excel
              </button>
              <button onClick={()=>window.print()}
                style={{display:"flex", alignItems:"center", gap:6, background:B.surface, border:`1px solid ${B.border}`, borderRadius:3, padding:"7px 14px", cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:B.muted, letterSpacing:1, textTransform:"uppercase"}}>
                ⎙ PDF
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Print-only header ── */}
      <div id="pr-printable" style={{display:"none"}}>
        <div style={{padding:"0 24px 24px"}}>
          <LogoDisplay height={30}/>
          {r && <div style={{marginTop:16}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:22, color:B.white, marginBottom:4}}>Performance Report</div>
            <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted}}>
              {r.installerLabel} · {r.typeLabel} · {r.statusLabel} · {genPeriodStr()} · Generated {new Date().toLocaleDateString("en-CA", { dateStyle:"medium" })}
            </div>
          </div>}
        </div>
      </div>

      <div style={{padding:"0 20px", display:"flex", flexDirection:"column", gap:16}}>

        {/* ── Config card ── */}
        <div className="pr-no-print">
          <Card>
            <SectionTitle>Configure Report</SectionTitle>
            {/* Row 1 — Date range + Installer */}
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12}}>
              <div>
                <FieldLabel>Period — From</FieldLabel>
                <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={inputStyle}/>
              </div>
              <div>
                <FieldLabel>Period — To</FieldLabel>
                <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={inputStyle}/>
              </div>
              <div>
                <FieldLabel>Installer</FieldLabel>
                <select value={installerSel} onChange={e=>setInstallerSel(e.target.value)} style={inputStyle}>
                  <option value="all">All Installers</option>
                  {installers.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                </select>
              </div>
            </div>
            {/* Row 2 — Installation Type + Status */}
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16}}>
              <div>
                <FieldLabel>Installation Type</FieldLabel>
                <select value={typeSel} onChange={e=>setTypeSel(e.target.value)} style={inputStyle}>
                  <option value="all">All Installation Types</option>
                  {[...installationTypes].sort((a,b)=>a.label.localeCompare(b.label)).map(t => (
                    <option key={t.id} value={t.label}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>SRO Status</FieldLabel>
                <select value={statusSel} onChange={e=>setStatusSel(e.target.value)} style={inputStyle}>
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                  <option value="requires_revisit">Requires Revisit</option>
                  <option value="aborted">Aborted</option>
                  <option value="incomplete">Incomplete</option>
                </select>
              </div>
            </div>
            <button onClick={generate}
              style={{background:B.blue, color:B.onAccent, border:"none", borderRadius:4, padding:"12px 28px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textTransform:"uppercase", boxShadow:`0 4px 18px rgba(17,52,166,0.35)`}}>
              ◈ Generate Report
            </button>
          </Card>
        </div>

        {/* ── Empty state ── */}
        {!r && (
          <div style={{textAlign:"center", padding:"64px 24px", color:B.mid}}>
            <div style={{fontSize:52, marginBottom:14, opacity:0.2}}>◈</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:18, letterSpacing:1, marginBottom:6}}>Configure and generate a report above.</div>
            <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, lineHeight:1.6}}>Select a date range and installer, then click Generate Report to see performance metrics and charts.</div>
          </div>
        )}

        {/* ── Report content ── */}
        {r && (
          <>
            {/* Identity banner */}
            <div style={{background:B.card, border:`1px solid ${B.border}`, borderRadius:6, padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12}}>
              <div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:20, color:B.white}}>{r.installerLabel}</div>
                <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted, marginTop:2}}>
                  {genPeriodStr()} · {r.filtered.length} job{r.filtered.length !== 1 ? "s" : ""} in scope
                </div>
              </div>
              {!m && <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.muted}}>No jobs matched this filter.</div>}
            </div>

            {!m && (
              <div style={{textAlign:"center", padding:"40px 24px", color:B.mid, fontFamily:"'Barlow',sans-serif", fontSize:13}}>
                No jobs found for this period and installer. Try adjusting the filters.
              </div>
            )}

            {m && (
              <>
                {/* Stat summary cards */}
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10}}>
                  <RepStatCard label="Total Jobs"          value={m.total}                         color={B.white}/>
                  <RepStatCard label="Approved / Completed" value={m.approved + m.completed}       color={STATUS_CONFIG.completed.color}/>
                  <RepStatCard label="Avg. Time on Site"  value={fmtMs(m.avgMs)}                   sub={`${m.withTimeCount} jobs timed`} color={B.blueLt}/>
                  <RepStatCard label="Completion Rate"    value={`${m.completionRate}%`}            color={STATUS_CONFIG.completed.color}/>
                  <RepStatCard label="Rejection Rate"     value={`${m.rejectionRate}%`}   color={STATUS_CONFIG.rejected.color}/>
                  <RepStatCard label="Aborted / Incomplete" value={`${m.outcomeRate}%`}   color={STATUS_CONFIG.aborted.color}/>
                </div>

                {/* Charts grid — 2 columns */}
                <div className="pr-chart-grid" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16}}>
                  <RepPieWidget title="Job Status Breakdown" data={statusData}/>
                  <RepPieWidget title="Completion Rate"
                    note={`${m.approved + m.completed} of ${m.total} jobs approved or completed (${m.completionRate}%)`}
                    data={completionData}/>
                  <RepPieWidget title="Rejection Rate"
                    note={`${m.rejected + m.revisit} of ${m.total} rejected or escalated (${m.rejectionRate}%)`}
                    data={rejectionData}/>
                  <RepPieWidget title="Aborted / Incomplete Rate"
                    note={`${m.aborted + m.incomplete} of ${m.total} jobs (${m.outcomeRate}%)`}
                    data={outcomeData}/>
                  <RepPieWidget title="Jobs by Installation Type" data={typeData}/>
                  <RepPieWidget title="Approvals vs Rejections"
                    note={`${m.approved + m.completed} successful · ${m.rejected + m.revisit} rejected/escalated`}
                    data={appVsRejData}/>
                </div>

                {/* Time on site distribution */}
                {m.withTimeCount > 0 && (
                  <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:16}}>
                    <RepPieWidget title="Time on Site Distribution"
                      note={`Average: ${fmtMs(m.avgMs)} across ${m.withTimeCount} timed job${m.withTimeCount !== 1 ? "s" : ""}`}
                      data={timeData}/>
                    <Card>
                      <SectionTitle>Time on Site — Summary</SectionTitle>
                      <div style={{display:"flex", flexDirection:"column", gap:10}}>
                        {Object.entries(m.timeBuckets).map(([bucket, count]) => {
                          const pct = m.withTimeCount > 0 ? (count / m.withTimeCount) * 100 : 0;
                          return (
                            <div key={bucket}>
                              <div style={{display:"flex", justifyContent:"space-between", marginBottom:4}}>
                                <span style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.light}}>{bucket}</span>
                                <span style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:13, color:B.white}}>{count} <span style={{color:B.muted, fontWeight:400}}>({pct.toFixed(0)}%)</span></span>
                              </div>
                              <div style={{height:6, borderRadius:3, background:B.surface, overflow:"hidden"}}>
                                <div style={{height:"100%", width:`${pct}%`, background:B.blue, borderRadius:3, transition:"width 0.4s"}}/>
                              </div>
                            </div>
                          );
                        })}
                        <div style={{borderTop:`1px solid ${B.border}`, paddingTop:12, marginTop:4}}>
                          <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:24, color:B.blueLt}}>{fmtMs(m.avgMs)}</div>
                          <div style={{fontFamily:"'Barlow',sans-serif", fontSize:12, color:B.muted, marginTop:2}}>Average time on site</div>
                        </div>
                      </div>
                    </Card>
                  </div>
                )}

                {/* Per-installer breakdown (team summary only) */}
                {r.perInstaller && r.perInstaller.length > 0 && (
                  <Card>
                    <SectionTitle>Per-Installer Breakdown</SectionTitle>
                    <div style={{overflowX:"auto"}}>
                      <table style={{width:"100%", borderCollapse:"collapse", fontFamily:"'Barlow',sans-serif", fontSize:12}}>
                        <thead>
                          <tr style={{borderBottom:`2px solid ${B.border}`}}>
                            {["Installer","Total","Completed","Approved","Rejected","Revisit","Aborted","Incomplete","Completion %","Rejection %","Avg. Time"].map(h => (
                              <th key={h} style={{padding:"8px 10px", textAlign: h==="Installer"?"left":"right", fontSize:10, fontWeight:700, color:B.muted, letterSpacing:1.5, textTransform:"uppercase", whiteSpace:"nowrap"}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {r.perInstaller.map((p, i) => {
                            const pm = p.metrics;
                            const isEven = i % 2 === 0;
                            const td = (val, col) => (
                              <td style={{padding:"9px 10px", textAlign:"right", color:col||B.light, borderBottom:`1px solid ${B.border}`, whiteSpace:"nowrap"}}>{val}</td>
                            );
                            return (
                              <tr key={p.id} style={{background:isEven?B.black:B.card}}>
                                <td style={{padding:"9px 10px", fontWeight:600, color:B.white, borderBottom:`1px solid ${B.border}`, whiteSpace:"nowrap"}}>{p.name}</td>
                                {pm ? <>
                                  {td(pm.total)}
                                  {td(pm.completed, STATUS_CONFIG.completed.color)}
                                  {td(pm.approved,  STATUS_CONFIG.approved.color)}
                                  {td(pm.rejected,  pm.rejected > 0 ? STATUS_CONFIG.rejected.color : B.light)}
                                  {td(pm.revisit,   pm.revisit  > 0 ? STATUS_CONFIG.requires_revisit.color : B.light)}
                                  {td(pm.aborted,   pm.aborted  > 0 ? STATUS_CONFIG.aborted.color : B.light)}
                                  {td(pm.incomplete,pm.incomplete > 0 ? STATUS_CONFIG.incomplete.color : B.light)}
                                  {td(`${pm.completionRate}%`, parseFloat(pm.completionRate) >= 70 ? STATUS_CONFIG.completed.color : B.red)}
                                  {td(`${pm.rejectionRate}%`,  parseFloat(pm.rejectionRate)  > 20  ? STATUS_CONFIG.rejected.color  : B.light)}
                                  {td(fmtMs(pm.avgMs))}
                                </> : <td colSpan={10} style={{padding:"9px 10px", color:B.muted, borderBottom:`1px solid ${B.border}`}}>No data</td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── WJS SIGN — NEW INSTALL REPORT ────────────────────────────────────────────
// Type-scoped report for the "WJS Sign - NEW INSTALL" installation type.
// Pulls every job submitted under that type, surfaces the location's barcode +
// the final required-photo, and lets HO export a branded .xlsx with photos
// embedded inline (via lazily-loaded ExcelJS).

const WJS_NEW_INSTALL_LABEL = "WJS Sign - NEW INSTALL";

// Pull the value of the first barcode-comment-mode question's answer (we scan
// barcode in this flow as the location identifier). Falls back to empty string.
function getBarcodeValue(job) {
  const qs = job?.typeSnapshot?.questions;
  if (!Array.isArray(qs)) return "";
  const bq = qs.find(q => q && q.commentMode === "barcode");
  if (!bq) return "";
  return job?.answers?.[bq.id]?.notes || "";
}

// Pull the URL of the first photo on the LAST question that required a photo.
// "Last" is in the order of typeSnapshot.questions — i.e. the last installation
// step that demanded photo evidence is what the report shows.
function getLastRequiredPhotoUrl(job) {
  const qs = job?.typeSnapshot?.questions;
  if (!Array.isArray(qs)) return null;
  for (let i = qs.length - 1; i >= 0; i--) {
    const q = qs[i];
    if (!q || q.photoMode !== "required") continue;
    const photo = job?.answers?.[q.id]?.photos?.[0];
    if (photo?.url) return photo.url;
  }
  return null;
}

// Find the WJS Sign - NEW INSTALL type id from the live installation_types list.
// Falls back to label-match on the snapshot, so jobs are matched even if the
// admin renames the type later (typeSnapshot.label is frozen at submission).
function isWjsNewInstallJob(job, wjsTypeId) {
  if (wjsTypeId && job?.installationTypeId === wjsTypeId) return true;
  return (job?.typeSnapshot?.label || "").trim() === WJS_NEW_INSTALL_LABEL;
}

function WjsInstallReport({ jobs, users, installationTypes, onBack }) {
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [statusSel,   setStatusSel]   = useState("all");   // "all" | individual status keys
  const [search,      setSearch]      = useState("");
  const [exporting,   setExporting]   = useState(false);
  const [exportProgress, setExportProgress] = useState({ current:0, total:0 });

  // Inject print CSS — uses its own #wjs-printable id so it doesn't clash with
  // PerformanceReports' #pr-printable rules.
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "wjs-print-css";
    style.textContent = `
      @media print {
        body > * { display: none !important; }
        #wjs-printable { display: block !important; position: fixed; top:0; left:0; width:100%; background:#fff; color:#1a1a1a; overflow:auto; z-index:9999; padding:24px; box-sizing:border-box; }
        #wjs-printable * { color: #1a1a1a !important; background: transparent !important; }
        #wjs-printable .wjs-card { border: 1px solid #999 !important; background: #fff !important; }
        #wjs-printable .wjs-stat-card { border: 1px solid #999 !important; background: #fff !important; }
        #wjs-printable .wjs-table th, #wjs-printable .wjs-table td { border: 1px solid #999 !important; }
        #wjs-printable .wjs-thumb { max-width: 80px !important; max-height: 80px !important; }
        .wjs-no-print { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => document.getElementById("wjs-print-css")?.remove();
  }, []);

  // Locate the WJS Sign - NEW INSTALL installation type id (if it exists).
  const wjsTypeId = useMemo(
    () => installationTypes.find(t => (t.label || "").trim() === WJS_NEW_INSTALL_LABEL)?.id || null,
    [installationTypes]
  );

  // All WJS Sign - NEW INSTALL jobs in the system, before user filters.
  const wjsJobsAll = useMemo(
    () => (jobs || []).filter(j => isWjsNewInstallJob(j, wjsTypeId)),
    [jobs, wjsTypeId]
  );

  // Apply filters
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return wjsJobsAll.filter(j => {
      if (statusSel !== "all" && j.status !== statusSel) return false;
      if (dateFrom && new Date(j.submittedAt) < new Date(dateFrom + "T00:00:00")) return false;
      if (dateTo   && new Date(j.submittedAt) > new Date(dateTo   + "T23:59:59.999")) return false;
      if (term) {
        const hay = [
          j.sroNumber, j.rlNumber, j.storeName, j.storeAddress, j.installerName,
          getBarcodeValue(j),
        ].map(v => (v || "").toString().toLowerCase()).join(" | ");
        if (!hay.includes(term)) return false;
      }
      return true;
    }).sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  }, [wjsJobsAll, statusSel, dateFrom, dateTo, search]);

  // Dashboard summary metrics across the filtered set.
  const summary = useMemo(() => {
    const total = filtered.length;
    const get = k => filtered.filter(j => j.status === k).length;
    const thisMonth = (() => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      return filtered.filter(j => new Date(j.submittedAt).getTime() >= from).length;
    })();
    return {
      total,
      approved: get("approved") + get("completed"),
      pending:  get("pending"),
      rejected: get("rejected") + get("requires_revisit"),
      outcomes: get("aborted")  + get("incomplete"),
      thisMonth,
    };
  }, [filtered]);

  // Bar chart data — installs grouped by ISO week (YYYY-Www).
  const weeklyData = useMemo(() => {
    if (filtered.length === 0) return [];
    const buckets = {};
    filtered.forEach(j => {
      const d = new Date(j.submittedAt);
      // Monday-anchored week start
      const day = d.getDay() || 7;            // 1..7 (Mon..Sun)
      const monday = new Date(d);
      monday.setDate(d.getDate() - (day - 1));
      monday.setHours(0,0,0,0);
      const key = monday.toISOString().slice(0, 10);
      buckets[key] = (buckets[key] || 0) + 1;
    });
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({ week: k.slice(5), count: v })); // MM-DD label
  }, [filtered]);

  // ── Excel export with embedded photo thumbnails ────────────────────────────
  const exportExcel = async () => {
    if (filtered.length === 0) return;
    setExporting(true);
    setExportProgress({ current: 0, total: filtered.length });
    try {
      const ExcelJS = await loadExcelJs();
      const wb = new ExcelJS.Workbook();
      wb.creator = "Somerville FieldOps";
      wb.created = new Date();
      const ws = wb.addWorksheet("WJS Sign Installs", {
        views: [{ state: "frozen", ySplit: 1 }],
      });
      ws.columns = [
        { header: "Submitted",   key: "submitted", width: 18 },
        { header: "SRO #",        key: "sro",       width: 14 },
        { header: "RL #",         key: "rl",        width: 12 },
        { header: "Store Name",   key: "store",     width: 32 },
        { header: "Address",      key: "address",   width: 36 },
        { header: "Installer",    key: "installer", width: 22 },
        { header: "Status",       key: "status",    width: 16 },
        { header: "Barcode",      key: "barcode",   width: 22 },
        { header: "Photo",        key: "photo",     width: 24 },
      ];
      // Brand the header row
      const headerRow = ws.getRow(1);
      headerRow.font      = { name: "Calibri", size: 12, bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1134A6" } };  // Somerville blue
      headerRow.alignment = { vertical: "middle", horizontal: "left" };
      headerRow.height    = 24;

      // Add rows + embed photo thumbnails
      for (let i = 0; i < filtered.length; i++) {
        const j = filtered[i];
        const photoUrl = getLastRequiredPhotoUrl(j);
        const r = ws.addRow({
          submitted: j.submittedAt ? new Date(j.submittedAt).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "",
          sro:       j.sroNumber || "",
          rl:        j.rlNumber  || "",
          store:     j.storeName || "",
          address:   j.storeAddress || "",
          installer: j.installerName || "",
          status:    (STATUS_CONFIG[j.status]?.label) || j.status || "",
          barcode:   getBarcodeValue(j) || "",
          photo:     photoUrl ? "" : "—",
        });
        r.alignment = { vertical: "middle", wrapText: true };
        r.height    = 110;  // tall enough for thumbnails

        if (photoUrl) {
          try {
            const resp = await fetch(photoUrl);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const ab = await resp.arrayBuffer();
            const ext = (photoUrl.split("?")[0].split(".").pop() || "jpeg").toLowerCase();
            const fmt = (ext === "png") ? "png" : (ext === "gif" ? "gif" : "jpeg");
            const imageId = wb.addImage({ buffer: ab, extension: fmt });
            // Embed at the Photo column (col index = 8 zero-based), current row index = i+1 (header row 0).
            const rowIdx = i + 1; // ExcelJS row coords are 0-based for addImage tl
            ws.addImage(imageId, {
              tl: { col: 8, row: rowIdx },
              ext: { width: 140, height: 105 },
              editAs: "oneCell",
            });
          } catch (err) {
            console.warn("[WJS Excel] photo embed failed for job", j.id, err.message);
            r.getCell("photo").value = { text: "View", hyperlink: photoUrl };
          }
        }
        setExportProgress({ current: i + 1, total: filtered.length });
      }

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `wjs-sign-new-install-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[WJS Excel] export failed:", err);
      alert(`Export failed: ${err.message || err}`);
    } finally {
      setExporting(false);
      setExportProgress({ current: 0, total: 0 });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const inputStyle = { background:B.surface, border:`1px solid ${B.border}`, borderRadius:4, color:B.light, fontSize:13, padding:"8px 12px", fontFamily:"'Barlow',sans-serif", outline:"none", width:"100%", boxSizing:"border-box" };
  const today = new Date().toLocaleDateString("en-CA", { dateStyle: "long" });

  return (
    <div style={{maxWidth:1100, margin:"0 auto", paddingBottom:60}}>
      {/* ── Top bar ── */}
      <div className="wjs-no-print" style={{background:B.dark, borderBottom:`1px solid ${B.border}`, padding:"16px 20px", marginBottom:20, position:"sticky", top:0, zIndex:50}}>
        <div style={{display:"flex", alignItems:"center", gap:12, flexWrap:"wrap"}}>
          <button onClick={onBack}
            style={{background:"transparent", color:B.muted, border:`1px solid ${B.border}`, borderRadius:3, padding:"5px 14px", cursor:"pointer", fontSize:11, fontFamily:"'Barlow',sans-serif", flexShrink:0}}>
            ← Back
          </button>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:10, color:B.muted, letterSpacing:2, textTransform:"uppercase", fontFamily:"'Barlow',sans-serif"}}>Head Office</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:22, color:B.white, letterSpacing:0.5}}>WJS Sign — NEW INSTALL Report</div>
          </div>
          <div style={{display:"flex", gap:8, flexShrink:0}}>
            <button onClick={exportExcel} disabled={exporting || filtered.length === 0}
              style={{display:"flex", alignItems:"center", gap:6, background:filtered.length===0?B.surface:B.blueDim, border:`1px solid ${filtered.length===0?B.border:B.blue}`, borderRadius:3, padding:"7px 14px", cursor:exporting||filtered.length===0?"not-allowed":"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:filtered.length===0?B.mid:B.blueLt, letterSpacing:1, textTransform:"uppercase", opacity:exporting?0.7:1}}>
              {exporting
                ? <><Spinner size={11} color={B.blueLt}/> {exportProgress.total>0?`${exportProgress.current}/${exportProgress.total}`:"Preparing…"}</>
                : "↓ Excel"}
            </button>
            <button onClick={()=>window.print()} disabled={filtered.length === 0}
              style={{display:"flex", alignItems:"center", gap:6, background:B.surface, border:`1px solid ${B.border}`, borderRadius:3, padding:"7px 14px", cursor:filtered.length===0?"not-allowed":"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, color:B.muted, letterSpacing:1, textTransform:"uppercase"}}>
              ⎙ Print PDF
            </button>
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="wjs-no-print" style={{padding:"0 20px"}}>
        <Card>
          <SectionTitle>Filters</SectionTitle>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))", gap:12}}>
            <div>
              <FieldLabel>From</FieldLabel>
              <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={inputStyle}/>
            </div>
            <div>
              <FieldLabel>To</FieldLabel>
              <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={inputStyle}/>
            </div>
            <div>
              <FieldLabel>Status</FieldLabel>
              <select value={statusSel} onChange={e=>setStatusSel(e.target.value)} style={inputStyle}>
                <option value="all">All statuses</option>
                {Object.entries(STATUS_CONFIG).map(([k, cfg]) => (
                  <option key={k} value={k}>{cfg.label}</option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>Search</FieldLabel>
              <input type="text" value={search} onChange={e=>setSearch(e.target.value)} placeholder="SRO, RL, store, installer, barcode…" style={inputStyle}/>
            </div>
          </div>
          {(dateFrom || dateTo || statusSel !== "all" || search) && (
            <div style={{marginTop:12}}>
              <button onClick={()=>{setDateFrom("");setDateTo("");setStatusSel("all");setSearch("");}}
                style={{background:"transparent", color:B.muted, border:`1px solid ${B.border}`, borderRadius:3, padding:"5px 14px", cursor:"pointer", fontSize:11, fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1, textTransform:"uppercase"}}>
                Clear filters
              </button>
            </div>
          )}
        </Card>
      </div>

      {/* ── Printable region ── */}
      <div id="wjs-printable" style={{padding:"0 20px"}}>
        {/* Print-only header */}
        <div className="wjs-print-only" style={{display:"none", marginBottom:16, borderBottom:"2px solid #1134A6", paddingBottom:8}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:22, color:"#1134A6"}}>Somerville FieldOps</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600, fontSize:16}}>WJS Sign — NEW INSTALL Report</div>
          <div style={{fontFamily:"'Barlow',sans-serif", fontSize:11, color:"#555", marginTop:4}}>Generated {today} · {filtered.length} record{filtered.length===1?"":"s"}</div>
        </div>

        {/* ── Dashboard summary cards ── */}
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:10, marginBottom:16}}>
          <RepStatCard label="Total Installs" value={summary.total} sub={summary.thisMonth>0?`${summary.thisMonth} this month`:""} color={B.blueLt}/>
          <RepStatCard label="Approved / Completed" value={summary.approved} color={B.green}/>
          <RepStatCard label="Pending Review" value={summary.pending} color={B.blueLt}/>
          <RepStatCard label="Rejected / Revisit" value={summary.rejected} color={B.red}/>
          <RepStatCard label="Aborted / Incomplete" value={summary.outcomes} color="#f1c40f"/>
        </div>

        {/* ── Weekly bar chart ── */}
        {weeklyData.length > 0 && (
          <Card>
            <SectionTitle>Installs by Week</SectionTitle>
            <WeeklyBar data={weeklyData}/>
          </Card>
        )}

        {/* ── Results table ── */}
        <Card>
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:10}}>
            <SectionTitle>Records ({filtered.length})</SectionTitle>
          </div>

          {filtered.length === 0
            ? <div style={{padding:"20px 0", textAlign:"center", color:B.muted, fontFamily:"'Barlow',sans-serif", fontSize:13}}>
                {wjsJobsAll.length === 0
                  ? wjsTypeId
                    ? <>No WJS Sign - NEW INSTALL jobs submitted yet.</>
                    : <>An installation type called <strong>{WJS_NEW_INSTALL_LABEL}</strong> doesn't exist in System Config. Create it first to start filling this report.</>
                  : <>No jobs match the current filters.</>
                }
              </div>
            : <div style={{overflowX:"auto"}}>
                <table className="wjs-table" style={{width:"100%", borderCollapse:"collapse", fontFamily:"'Barlow',sans-serif", fontSize:12}}>
                  <thead>
                    <tr style={{background:"rgba(17,52,166,0.08)"}}>
                      {["Submitted","SRO #","RL #","Store","Installer","Status","Barcode","Photo"].map(h => (
                        <th key={h} style={{textAlign:"left", padding:"10px 12px", borderBottom:`2px solid ${B.blue}`, color:B.blueLt, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:1, textTransform:"uppercase", whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(j => {
                      const photoUrl = getLastRequiredPhotoUrl(j);
                      const sc = STATUS_CONFIG[j.status] || STATUS_CONFIG.pending;
                      return (
                        <tr key={j.id} style={{borderBottom:`1px solid ${B.border}`}}>
                          <td style={{padding:"10px 12px", color:B.light, whiteSpace:"nowrap"}}>
                            {j.submittedAt ? new Date(j.submittedAt).toLocaleDateString("en-CA", { dateStyle: "medium" }) : "—"}
                          </td>
                          <td style={{padding:"10px 12px", color:B.white, fontWeight:600, whiteSpace:"nowrap"}}>{j.sroNumber || "—"}</td>
                          <td style={{padding:"10px 12px", color:B.light, whiteSpace:"nowrap"}}>{j.rlNumber || "—"}</td>
                          <td style={{padding:"10px 12px", color:B.light, minWidth:160}}>
                            <div style={{fontWeight:600, color:B.white}}>{j.storeName || "—"}</div>
                            {j.storeAddress && <div style={{fontSize:11, color:B.muted, marginTop:2}}>{j.storeAddress}</div>}
                          </td>
                          <td style={{padding:"10px 12px", color:B.light, whiteSpace:"nowrap"}}>{j.installerName || "—"}</td>
                          <td style={{padding:"10px 12px", whiteSpace:"nowrap"}}>
                            <span style={{display:"inline-block", background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, borderRadius:3, padding:"2px 8px", fontSize:10, fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:1, textTransform:"uppercase"}}>
                              {sc.icon ? sc.icon + " " : ""}{sc.label}
                            </span>
                          </td>
                          <td style={{padding:"10px 12px", color:B.light, fontFamily:"monospace", fontSize:11}}>
                            {getBarcodeValue(j) || <span style={{color:B.mid, fontStyle:"italic", fontFamily:"'Barlow',sans-serif"}}>—</span>}
                          </td>
                          <td style={{padding:"10px 12px"}}>
                            {photoUrl
                              ? <a href={photoUrl} target="_blank" rel="noopener noreferrer">
                                  <img src={photoUrl} alt="" className="wjs-thumb"
                                    style={{width:80, height:80, objectFit:"cover", borderRadius:4, border:`1px solid ${B.border}`, display:"block", cursor:"pointer"}}/>
                                </a>
                              : <span style={{color:B.mid, fontStyle:"italic", fontSize:11}}>No photo</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          }
        </Card>
      </div>
    </div>
  );
}

// Bar chart of installs per week — uses recharts which is already loaded for
// PerformanceReports. Falls back to a "loading chart…" message until it boots.
function WeeklyBar({ data }) {
  const [ready, setReady] = useState(!!window.Recharts);
  useEffect(() => {
    if (window.Recharts) { setReady(true); return; }
    const id = setInterval(() => { if (window.Recharts) { setReady(true); clearInterval(id); } }, 300);
    return () => clearInterval(id);
  }, []);
  if (!ready) return <div style={{height:160, display:"flex", alignItems:"center", justifyContent:"center", color:B.muted, fontFamily:"'Barlow',sans-serif", fontSize:13}}>Loading chart…</div>;
  const { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } = window.Recharts;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false}/>
        <XAxis dataKey="week" stroke={B.muted} tick={{ fill: B.muted, fontFamily: "'Barlow',sans-serif", fontSize: 11 }}/>
        <YAxis allowDecimals={false} stroke={B.muted} tick={{ fill: B.muted, fontFamily: "'Barlow',sans-serif", fontSize: 11 }}/>
        <Tooltip cursor={{ fill: "rgba(17,52,166,0.10)" }}
          contentStyle={{ background: B.card, border: `1px solid ${B.border}`, borderRadius: 4, fontFamily: "'Barlow',sans-serif", fontSize: 12, color: B.light }}/>
        <Bar dataKey="count" fill={B.blue} radius={[3, 3, 0, 0]}/>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── SERVICES LIBRARY ──────────────────────────────────────────────────────────
function ServicesLibrary({ services, setServices, user }) {
  const [search,     setSearch]     = useState("");
  const [filter,     setFilter]     = useState("active"); // "all"|"active"|"obsolete"
  const [editId,     setEditId]     = useState(null);
  const [editForm,   setEditForm]   = useState({});
  const [saveBusy,   setSaveBusy]   = useState(false);
  const [showAdd,    setShowAdd]    = useState(false);
  const [addForm,    setAddForm]    = useState({ serviceId:"", description:"" });
  const [addErrors,  setAddErrors]  = useState({});
  const [addBusy,    setAddBusy]    = useState(false);
  const [toggleBusy, setToggleBusy] = useState(null); // id of row being toggled
  const [showImporter, setShowImporter] = useState(false);

  const isAdmin = user?.role === "super_admin";

  const filtered = services.filter(s => {
    const matchFilter = filter === "all" ? true : filter === "active" ? !s.obsolete : s.obsolete;
    const q = search.toLowerCase();
    const matchSearch = !q || s.service_id.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  const startEdit = s => { setEditId(s.id); setEditForm({ description: s.description, estimated_minutes: s.estimated_minutes ?? "" }); };
  const cancelEdit = () => { setEditId(null); setEditForm({}); };

  const saveEdit = async (s) => {
    if (!editForm.description?.trim()) return;
    // Parse estimated_minutes: empty/blank → null (clears it); otherwise integer.
    const rawMin = String(editForm.estimated_minutes ?? "").trim();
    let estMin = null;
    if (rawMin !== "") {
      const parsed = parseInt(rawMin, 10);
      if (isNaN(parsed) || parsed < 0) { alert("Estimated minutes must be a positive number."); return; }
      estMin = parsed;
    }
    setSaveBusy(true);
    try {
      const updates = { description: editForm.description.trim(), estimated_minutes: estMin };
      await dataService.updateService(s.id, updates);
      setServices(prev => prev.map(x => x.id === s.id ? { ...x, ...updates } : x));
      setEditId(null);
    } catch (e) { alert(`Save failed: ${e.message}`); }
    finally { setSaveBusy(false); }
  };

  const toggleObsolete = async (s) => {
    setToggleBusy(s.id);
    try {
      await dataService.updateService(s.id, { obsolete: !s.obsolete });
      setServices(prev => prev.map(x => x.id === s.id ? { ...x, obsolete: !s.obsolete } : x));
    } catch (e) { alert(`Update failed: ${e.message}`); }
    finally { setToggleBusy(null); }
  };

  const addService = async () => {
    const errs = {};
    if (!addForm.serviceId.trim()) errs.serviceId = "Required";
    if (!addForm.description.trim()) errs.description = "Required";
    if (services.some(s => s.service_id.toUpperCase() === addForm.serviceId.trim().toUpperCase())) errs.serviceId = "Service ID already exists";
    if (Object.keys(errs).length) { setAddErrors(errs); return; }
    setAddBusy(true);
    try {
      const row = await dataService.createService({ serviceId: addForm.serviceId, description: addForm.description });
      setServices(prev => [...prev, row].sort((a,b) => a.service_id.localeCompare(b.service_id)));
      setAddForm({ serviceId:"", description:"" });
      setAddErrors({});
      setShowAdd(false);
    } catch (e) { alert(`Create failed: ${e.message}`); }
    finally { setAddBusy(false); }
  };

  const activeCount   = services.filter(s => !s.obsolete).length;
  const obsoleteCount = services.filter(s =>  s.obsolete).length;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* Sub-header: count + Import / Add buttons */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>{activeCount} active · {obsoleteCount} obsolete</div>
        {isAdmin && (
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button onClick={()=>setShowImporter(true)} className="sm-btn"
              style={{background:"transparent",color:B.blueLt,border:`1px solid ${B.blue}`,borderRadius:4,padding:"7px 14px",fontSize:11,fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
              ↑ Import Times (Excel)
            </button>
            <button onClick={()=>{setShowAdd(true);setAddErrors({});}} className="sm-btn sm-btn-primary"
              style={{background:B.blue,color:B.onAccent,border:"none",borderRadius:4,padding:"7px 16px",fontSize:11,fontWeight:800,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
              + Add Service
            </button>
          </div>
        )}
      </div>

      {showImporter && (
        <ServiceTimesImporter
          services={services}
          setServices={setServices}
          onClose={()=>setShowImporter(false)}
        />
      )}

      <div>
        {/* Add form */}
        {showAdd && (
          <Card style={{marginBottom:16,padding:16}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,color:B.white,marginBottom:12,letterSpacing:1,textTransform:"uppercase"}}>New Service</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div>
                <FieldLabel>Service ID</FieldLabel>
                <SInput value={addForm.serviceId} onChange={e=>setAddForm(f=>({...f,serviceId:e.target.value}))} placeholder="e.g. 200-200INSTALL"/>
                {addErrors.serviceId && <ErrMsg msg={addErrors.serviceId}/>}
              </div>
              <div>
                <FieldLabel>Description</FieldLabel>
                <SInput value={addForm.description} onChange={e=>setAddForm(f=>({...f,description:e.target.value}))} placeholder="e.g. Install promotional end cap display"/>
                {addErrors.description && <ErrMsg msg={addErrors.description}/>}
              </div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <button onClick={addService} disabled={addBusy} className="sm-btn sm-btn-primary"
                  style={{background:B.blue,color:B.onAccent,border:"none",borderRadius:4,padding:"8px 18px",fontSize:12,fontWeight:800,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
                  {addBusy ? "Saving…" : "Save"}
                </button>
                <button onClick={()=>{setShowAdd(false);setAddErrors({});}} className="sm-btn"
                  style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"7px 14px",fontFamily:"'Barlow',sans-serif"}}>
                  Cancel
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* Search + filter */}
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search service ID or description…"
            style={{flex:1,minWidth:200,background:B.black,border:`1px solid ${B.border}`,borderRadius:4,padding:"8px 12px",color:B.white,fontSize:12,fontFamily:"'Barlow',sans-serif",outline:"none"}}/>
          {["active","obsolete","all"].map(f=>(
            <button key={f} onClick={()=>setFilter(f)} className="sm-btn"
              style={{background:filter===f?B.blue:"transparent",color:filter===f?B.white:B.muted,border:`1px solid ${filter===f?B.blue:B.border}`,borderRadius:4,padding:"6px 14px",fontSize:11,fontWeight:700,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
              {f==="all"?"All":f==="active"?"Active":"Obsolete"}
            </button>
          ))}
        </div>

        {/* Count */}
        <div style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif",marginBottom:10}}>
          Showing {filtered.length} of {services.length} services
        </div>

        {/* Table */}
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {filtered.length === 0 && (
            <div style={{textAlign:"center",color:B.muted,fontSize:13,padding:"40px 0",fontFamily:"'Barlow',sans-serif"}}>No services match your search.</div>
          )}
          {filtered.map(s => (
            <div key={s.id} style={{background:B.dark,border:`1px solid ${s.obsolete?B.border:B.border}`,borderRadius:4,padding:"10px 14px",opacity:s.obsolete?0.55:1,display:"flex",alignItems:"flex-start",gap:10}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span style={{fontFamily:"monospace",fontSize:11,color:B.blue,fontWeight:700,letterSpacing:0.5}}>{s.service_id}</span>
                  {Number.isFinite(s.estimated_minutes) && s.estimated_minutes > 0 && (
                    <span title="Estimated install time"
                      style={{fontSize:10,background:"rgba(17,52,166,0.15)",color:B.blueLt,border:`1px solid ${B.blue}`,borderRadius:2,padding:"1px 6px",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:0.8,textTransform:"uppercase"}}>
                      ⏱ {s.estimated_minutes} min
                    </span>
                  )}
                  {s.obsolete && <span style={{fontSize:10,background:B.border,color:B.muted,borderRadius:2,padding:"1px 6px",fontFamily:"'Barlow',sans-serif",letterSpacing:0.5,textTransform:"uppercase"}}>Obsolete</span>}
                </div>
                {editId === s.id ? (
                  <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center",flexWrap:"wrap"}}>
                    <input value={editForm.description} onChange={e=>setEditForm(f=>({...f,description:e.target.value}))}
                      style={{flex:"1 1 220px",background:B.black,border:`1px solid ${B.blue}`,borderRadius:3,padding:"5px 8px",color:B.white,fontSize:12,fontFamily:"'Barlow',sans-serif",outline:"none"}}
                      autoFocus onKeyDown={e=>{if(e.key==="Enter")saveEdit(s);if(e.key==="Escape")cancelEdit();}} placeholder="Description"/>
                    <div style={{display:"inline-flex",alignItems:"center",gap:4,background:B.black,border:`1px solid ${B.border}`,borderRadius:3,padding:"2px 6px"}}>
                      <input value={editForm.estimated_minutes ?? ""} onChange={e=>setEditForm(f=>({...f,estimated_minutes:e.target.value}))}
                        placeholder="0"
                        style={{width:50,background:"transparent",border:"none",color:B.white,fontSize:12,fontFamily:"'Barlow',sans-serif",outline:"none",textAlign:"right"}}
                        onKeyDown={e=>{if(e.key==="Enter")saveEdit(s);if(e.key==="Escape")cancelEdit();}}
                        inputMode="numeric"/>
                      <span style={{fontSize:10,color:B.muted,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5,textTransform:"uppercase"}}>min</span>
                    </div>
                    <button onClick={()=>saveEdit(s)} disabled={saveBusy} className="sm-btn sm-btn-primary"
                      style={{background:B.blue,color:B.onAccent,border:"none",borderRadius:3,padding:"5px 10px",fontSize:11,fontWeight:800,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
                      {saveBusy?"…":"Save"}
                    </button>
                    <button onClick={cancelEdit} className="sm-btn"
                      style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"4px 8px",fontFamily:"'Barlow',sans-serif"}}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{fontSize:12,color:B.mid,fontFamily:"'Barlow',sans-serif",marginTop:3}}>{s.description}</div>
                )}
              </div>
              {isAdmin && editId !== s.id && (
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button onClick={()=>startEdit(s)} className="sm-btn"
                    style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:10,padding:"4px 8px",fontFamily:"'Barlow',sans-serif"}}>
                    Edit
                  </button>
                  <button onClick={()=>toggleObsolete(s)} disabled={toggleBusy===s.id} className="sm-btn"
                    style={{background:"transparent",border:`1px solid ${s.obsolete?B.green:B.border}`,borderRadius:3,color:s.obsolete?B.green:B.muted,fontSize:10,padding:"4px 8px",fontFamily:"'Barlow',sans-serif"}}>
                    {toggleBusy===s.id?"…":s.obsolete?"Restore":"Obsolete"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── SERVICE TIMES IMPORTER ────────────────────────────────────────────────────
// Bulk-update services.estimated_minutes from the OLG Master Storage + Installation
// Price List spreadsheet. Reads sheets "3. (a) Installation", "3. (b) DeInstall
// REMOVE", "3. (c) Swap REPLACE". Header row at Excel row 12; columns expected:
// Part ID Number | Install ID | Category | DESCRIPTION | Time to Install (Hour) | ...
// For Install IDs that appear multiple times (mount-style variants), the MAX
// time across variants is used (conservative installer estimate).
function ServiceTimesImporter({ services, setServices, onClose }) {
  const [stage, setStage]               = useState("pick"); // "pick" | "preview" | "applying" | "done"
  const [parseError, setParseError]     = useState("");
  const [rows, setRows]                 = useState([]);     // [{ serviceId, descriptionExcel, hours, minutes, status, currentMinutes }]
  const [progress, setProgress]         = useState({ current: 0, total: 0, errors: [] });

  // Build a lookup { CODE_UPPER → service row } so we can mark matched / unmatched
  // and surface the current minutes for diff.
  const svcByCode = useMemo(() => {
    const m = new Map();
    (services || []).forEach(s => m.set(String(s.service_id || "").toUpperCase(), s));
    return m;
  }, [services]);

  // Wait for window.XLSX (SheetJS is lazy-loaded at boot — usually ready by now).
  const waitForXlsx = () => new Promise((resolve, reject) => {
    if (window.XLSX) return resolve(window.XLSX);
    const t0 = Date.now();
    const id = setInterval(() => {
      if (window.XLSX) { clearInterval(id); resolve(window.XLSX); }
      else if (Date.now() - t0 > 8000) { clearInterval(id); reject(new Error("SheetJS failed to load")); }
    }, 100);
  });

  const handleFile = async (file) => {
    setParseError("");
    if (!file) return;
    if (!/\.(xlsx|xlsm|xlsb|xls)$/i.test(file.name)) {
      setParseError("Please choose an Excel file (.xlsx, .xls, .xlsm).");
      return;
    }
    try {
      const XLSX = await waitForXlsx();
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: "array" });
      // We accept any sheet whose name starts with "3." — covers (a) Installation,
      // (b) DeInstall REMOVE, (c) Swap REPLACE, even if the user renames slightly.
      const sheetNames = wb.SheetNames.filter(n => /^3\./i.test(n.trim()));
      if (sheetNames.length === 0) {
        setParseError(`No sheets starting with "3." were found. Sheets present: ${wb.SheetNames.join(", ")}`);
        return;
      }
      // Aggregate rows from all matching sheets.
      const collected = new Map(); // CODE_UPPER → { serviceId, descriptionExcel, hours, minutes }
      sheetNames.forEach(sn => {
        const sheet = wb.Sheets[sn];
        // Header is around Excel row 12; use range option to skip the title block.
        // Read as array-of-arrays then locate the header row by scanning.
        const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null });
        // Find header row containing "Install ID" and "Time to Install"
        let headerIdx = -1;
        for (let i = 0; i < Math.min(aoa.length, 30); i++) {
          const row = aoa[i] || [];
          const txt = row.map(c => String(c || "").toLowerCase()).join("|");
          if (txt.includes("install id") && txt.includes("time to install")) { headerIdx = i; break; }
        }
        if (headerIdx < 0) return;
        const headers = (aoa[headerIdx] || []).map(c => String(c || "").trim().toLowerCase());
        const colInstall   = headers.findIndex(h => h === "install id");
        const colDesc      = headers.findIndex(h => h === "description");
        const colTime      = headers.findIndex(h => h.startsWith("time to install"));
        if (colInstall < 0 || colTime < 0) return;
        for (let r = headerIdx + 1; r < aoa.length; r++) {
          const row = aoa[r] || [];
          const sid = String(row[colInstall] || "").trim();
          const hrs = parseFloat(row[colTime]);
          if (!sid || !Number.isFinite(hrs) || hrs <= 0) continue;
          const minutes = Math.round(hrs * 60);
          const key     = sid.toUpperCase();
          const existing = collected.get(key);
          // Keep the MAX-time variant when the same Install ID appears multiple times.
          if (!existing || minutes > existing.minutes) {
            collected.set(key, {
              serviceId: sid,
              descriptionExcel: String(row[colDesc] || "").trim(),
              hours: hrs,
              minutes,
            });
          }
        }
      });
      if (collected.size === 0) {
        setParseError("No service rows found. Check that the file contains the Install ID + Time to Install (Hour) columns on tabs 3.(a) / 3.(b) / 3.(c).");
        return;
      }
      // Annotate with match status + current minutes for the preview.
      const out = [];
      for (const r of collected.values()) {
        const code    = r.serviceId.toUpperCase();
        const svc     = svcByCode.get(code);
        out.push({
          ...r,
          status: svc ? (svc.estimated_minutes === r.minutes ? "unchanged" : (svc.estimated_minutes == null ? "new" : "update")) : "no-match",
          currentMinutes: svc?.estimated_minutes ?? null,
          serviceLibraryId: svc?.id || null,
        });
      }
      // Sort: changes first, then unchanged, then unmatched
      const rank = { update: 0, "new": 1, unchanged: 2, "no-match": 3 };
      out.sort((a, b) => rank[a.status] - rank[b.status] || a.serviceId.localeCompare(b.serviceId));
      setRows(out);
      setStage("preview");
    } catch (err) {
      console.error("[importer] parse failed:", err);
      setParseError(err.message || "Failed to parse the Excel file.");
    }
  };

  const applyChanges = async () => {
    const toApply = rows.filter(r => (r.status === "update" || r.status === "new") && r.serviceLibraryId);
    if (toApply.length === 0) { setStage("done"); return; }
    setProgress({ current: 0, total: toApply.length, errors: [] });
    setStage("applying");
    const errors = [];
    for (let i = 0; i < toApply.length; i++) {
      const r = toApply[i];
      try {
        await dataService.updateService(r.serviceLibraryId, { estimated_minutes: r.minutes });
      } catch (err) {
        errors.push(`${r.serviceId}: ${err.message}`);
      }
      setProgress(p => ({ ...p, current: i + 1, errors }));
    }
    // Reflect the new minutes in app state without a full reload.
    setServices(prev => prev.map(s => {
      const match = toApply.find(r => r.serviceLibraryId === s.id);
      return match ? { ...s, estimated_minutes: match.minutes } : s;
    }));
    setStage("done");
  };

  // Counts for the preview header
  const counts = useMemo(() => {
    const c = { update: 0, "new": 0, unchanged: 0, "no-match": 0 };
    rows.forEach(r => c[r.status]++);
    return c;
  }, [rows]);

  // ── Render — modal overlay ─────────────────────────────────────────────────
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,boxSizing:"border-box"}}
      onClick={e=>{if(e.target===e.currentTarget && stage!=="applying") onClose();}}>
      <div style={{background:B.dark,border:`1px solid ${B.border}`,borderRadius:8,padding:0,maxWidth:900,width:"100%",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 12px 40px rgba(0,0,0,0.6)"}}>
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${B.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
          <div>
            <div style={{fontSize:10,color:B.muted,letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow',sans-serif"}}>Services Library</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,color:B.white}}>Import Service Times from Excel</div>
          </div>
          <button onClick={onClose} disabled={stage==="applying"}
            style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"5px 12px",cursor:stage==="applying"?"not-allowed":"pointer",fontFamily:"'Barlow',sans-serif"}}>
            Close
          </button>
        </div>

        <div style={{padding:20,overflow:"auto",flex:1}}>
          {stage === "pick" && (
            <>
              <p style={{margin:"0 0 14px",fontSize:13,color:B.light,fontFamily:"'Barlow',sans-serif",lineHeight:1.6}}>
                Drop the <strong>OLG Master Storage + Installation Price List</strong> Excel below. The importer reads sheets <strong>3.(a) Installation</strong>, <strong>3.(b) DeInstall REMOVE</strong>, and <strong>3.(c) Swap REPLACE</strong>, converts <em>Time to Install (Hour)</em> × 60 to minutes, and previews the changes before any updates are applied.
              </p>
              <label style={{display:"flex",flexDirection:"column",alignItems:"center",border:`2px dashed ${B.blue}`,borderRadius:8,padding:"30px 20px",cursor:"pointer",background:"rgba(17,52,166,0.05)",gap:8}}>
                <div style={{fontSize:32}}>📊</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,color:B.blueLt,fontSize:14,letterSpacing:1.5,textTransform:"uppercase"}}>Drop Excel file here</div>
                <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>.xlsx, .xls, .xlsm</div>
                <input type="file" accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={e=>{const f=e.target.files?.[0]; e.target.value=""; if(f) handleFile(f);}}
                  style={{display:"none"}}/>
              </label>
              {parseError && (
                <div style={{marginTop:14,background:"rgba(192,57,43,0.1)",border:`1px solid ${B.red}`,borderRadius:4,padding:"10px 14px",color:B.red,fontFamily:"'Barlow',sans-serif",fontSize:13,lineHeight:1.5}}>
                  {parseError}
                </div>
              )}
            </>
          )}

          {stage === "preview" && (
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(130px, 1fr))",gap:8,marginBottom:14}}>
                <div style={{background:"rgba(17,52,166,0.10)",border:`1px solid ${B.blue}`,borderRadius:4,padding:"10px 12px"}}>
                  <div style={{fontSize:9,color:B.blueLt,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>To Update</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.white,marginTop:2}}>{counts.update}</div>
                </div>
                <div style={{background:"rgba(82,217,160,0.10)",border:`1px solid #52d9a0`,borderRadius:4,padding:"10px 12px"}}>
                  <div style={{fontSize:9,color:"#52d9a0",letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>New Times</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.white,marginTop:2}}>{counts["new"]}</div>
                </div>
                <div style={{background:B.surface,border:`1px solid ${B.border}`,borderRadius:4,padding:"10px 12px"}}>
                  <div style={{fontSize:9,color:B.muted,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>Unchanged</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.muted,marginTop:2}}>{counts.unchanged}</div>
                </div>
                <div style={{background:"rgba(230,126,34,0.08)",border:`1px solid ${B.orange}`,borderRadius:4,padding:"10px 12px"}}>
                  <div style={{fontSize:9,color:B.orange,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>No Match</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.orange,marginTop:2}}>{counts["no-match"]}</div>
                </div>
              </div>

              <div style={{maxHeight:380,overflow:"auto",border:`1px solid ${B.border}`,borderRadius:4}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontFamily:"'Barlow',sans-serif",fontSize:12}}>
                  <thead style={{position:"sticky",top:0,background:B.surface,zIndex:1}}>
                    <tr>
                      {["Service ID","Description (Excel)","Current","From File","Δ","Status"].map(h => (
                        <th key={h} style={{textAlign:"left",padding:"8px 10px",borderBottom:`1px solid ${B.border}`,color:B.blueLt,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:10,letterSpacing:1,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const delta = (r.currentMinutes != null) ? r.minutes - r.currentMinutes : null;
                      const dColor = delta == null ? B.muted : delta === 0 ? B.muted : delta > 0 ? B.orange : "#52d9a0";
                      const dLabel = delta == null ? "—" : delta === 0 ? "0" : delta > 0 ? `+${delta}` : String(delta);
                      const statusCfg = {
                        update:      { label: "Update",      color: B.blueLt,   bg: "rgba(17,52,166,0.10)"    },
                        "new":       { label: "New",         color: "#52d9a0",  bg: "rgba(82,217,160,0.10)"   },
                        unchanged:   { label: "Unchanged",    color: B.muted,    bg: "transparent"             },
                        "no-match":  { label: "Not in lib",   color: B.orange,   bg: "rgba(230,126,34,0.08)"   },
                      }[r.status];
                      return (
                        <tr key={r.serviceId} style={{borderBottom:`1px solid ${B.border}`}}>
                          <td style={{padding:"6px 10px",fontFamily:"monospace",color:B.white,fontWeight:600,whiteSpace:"nowrap"}}>{r.serviceId}</td>
                          <td style={{padding:"6px 10px",color:B.muted,maxWidth:280,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.descriptionExcel}</td>
                          <td style={{padding:"6px 10px",color:B.muted,whiteSpace:"nowrap"}}>{r.currentMinutes != null ? `${r.currentMinutes} min` : "—"}</td>
                          <td style={{padding:"6px 10px",color:B.white,fontWeight:600,whiteSpace:"nowrap"}}>{r.minutes} min</td>
                          <td style={{padding:"6px 10px",color:dColor,whiteSpace:"nowrap",fontWeight:600}}>{dLabel}</td>
                          <td style={{padding:"6px 10px",whiteSpace:"nowrap"}}>
                            <span style={{display:"inline-block",background:statusCfg.bg,color:statusCfg.color,border:`1px solid ${statusCfg.color}`,borderRadius:3,padding:"1px 7px",fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>
                              {statusCfg.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:16,gap:8,flexWrap:"wrap"}}>
                <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>
                  Applying will UPDATE {counts.update + counts["new"]} service{counts.update+counts["new"]===1?"":"s"} · ignore {counts.unchanged + counts["no-match"]} row{counts.unchanged+counts["no-match"]===1?"":"s"}.
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{setRows([]);setStage("pick");}} className="sm-btn"
                    style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"7px 14px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
                    Choose Different File
                  </button>
                  <button onClick={applyChanges} disabled={counts.update + counts["new"] === 0} className="sm-btn sm-btn-primary"
                    style={{background:counts.update+counts["new"]===0?B.surface:`linear-gradient(135deg,${B.blueLt} 0%,${B.blue} 100%)`,color:counts.update+counts["new"]===0?B.mid:B.white,border:"none",borderRadius:4,padding:"8px 18px",fontSize:12,fontWeight:800,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase",cursor:counts.update+counts["new"]===0?"not-allowed":"pointer"}}>
                    ✓ Apply {counts.update + counts["new"]} Update{counts.update+counts["new"]===1?"":"s"}
                  </button>
                </div>
              </div>
            </>
          )}

          {stage === "applying" && (
            <div style={{padding:"30px 0",textAlign:"center"}}>
              <Spinner size={20}/>
              <div style={{marginTop:14,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,color:B.white}}>Applying updates…</div>
              <div style={{marginTop:6,fontSize:13,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>{progress.current} / {progress.total}</div>
              {progress.errors.length > 0 && <div style={{marginTop:10,fontSize:11,color:B.red,fontFamily:"'Barlow',sans-serif"}}>{progress.errors.length} error{progress.errors.length===1?"":"s"} so far</div>}
            </div>
          )}

          {stage === "done" && (
            <div style={{padding:"24px 0",textAlign:"center"}}>
              <div style={{fontSize:38,color:B.green}}>✓</div>
              <div style={{marginTop:10,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,color:B.white}}>Import complete</div>
              <div style={{marginTop:6,fontSize:13,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>
                {progress.current} service{progress.current===1?"":"s"} updated{progress.errors.length > 0 ? ` · ${progress.errors.length} error${progress.errors.length===1?"":"s"}` : ""}.
              </div>
              {progress.errors.length > 0 && (
                <div style={{marginTop:14,textAlign:"left",maxHeight:200,overflow:"auto",background:"rgba(192,57,43,0.08)",border:`1px solid ${B.red}`,borderRadius:4,padding:"10px 14px",fontSize:12,color:B.red,fontFamily:"'Barlow',sans-serif"}}>
                  {progress.errors.map((e, i) => <div key={i}>· {e}</div>)}
                </div>
              )}
              <button onClick={onClose} className="sm-btn sm-btn-primary"
                style={{marginTop:18,background:`linear-gradient(135deg,${B.blueLt} 0%,${B.blue} 100%)`,color:B.onAccent,border:"none",borderRadius:4,padding:"10px 24px",fontSize:12,fontWeight:800,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── BARCODE SCANNER MODAL ─────────────────────────────────────────────────────
function BarcodeScanner({ onDetect, onClose }) {
  const videoRef    = useRef(null);
  const streamRef   = useRef(null);
  const timerRef    = useRef(null);
  const activeRef   = useRef(true);   // component alive
  const acceptRef   = useRef(true);   // accept new detections
  const [status,   setStatus]   = useState("starting"); // starting|scanning|detected|error
  const [detected, setDetected] = useState(null);
  const [manual,   setManual]   = useState("");
  const [errMsg,   setErrMsg]   = useState("");

  useEffect(() => {
    (async () => {
      // ── Camera setup ──
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        });
        if (!activeRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      } catch (e) {
        if (!activeRef.current) return;
        setErrMsg(e.name === "NotAllowedError"
          ? "Camera permission denied. Use manual entry below."
          : `Camera error: ${e.message}. Use manual entry below.`);
        setStatus("error"); return;
      }
      // ── BarcodeDetector setup ──
      if (!("BarcodeDetector" in window)) {
        setErrMsg("Barcode scanning not supported in this browser. Use manual entry below.");
        setStatus("error"); return;
      }
      const detector = new window.BarcodeDetector({
        formats: ["code_128","code_39","code_93","ean_13","ean_8","upc_a","upc_e","qr_code","data_matrix","aztec","pdf417","itf"],
      });
      setStatus("scanning");
      // ── Scan loop ──
      const tick = async () => {
        if (!activeRef.current) return;
        if (videoRef.current?.readyState === 4 && acceptRef.current) {
          try {
            const hits = await detector.detect(videoRef.current);
            if (hits.length > 0 && acceptRef.current) {
              acceptRef.current = false;
              setDetected(hits[0].rawValue);
              setManual(hits[0].rawValue);
              setStatus("detected");
            }
          } catch {}
        }
        timerRef.current = setTimeout(tick, 200);
      };
      timerRef.current = setTimeout(tick, 600);
    })();
    return () => {
      activeRef.current = false;
      clearTimeout(timerRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  const rescan = () => { setDetected(null); setManual(""); setStatus("scanning"); acceptRef.current = true; };
  const applyValue = (val) => { if (val?.trim()) onDetect(val.trim()); };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.96)",zIndex:9999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 16px"}}>
      <div style={{width:"100%",maxWidth:460}}>
        {/* Title bar */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:20,color:B.white,letterSpacing:1}}>Scan Barcode</div>
          <button onClick={onClose} style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:4,color:B.muted,padding:"6px 14px",cursor:"pointer",fontFamily:"'Barlow',sans-serif",fontSize:13}}>Cancel</button>
        </div>
        {/* Viewfinder */}
        <div style={{position:"relative",borderRadius:8,overflow:"hidden",background:B.black,width:"100%",aspectRatio:"4/3",marginBottom:14}}>
          <video ref={videoRef} style={{width:"100%",height:"100%",objectFit:"cover"}} playsInline muted/>
          {status === "scanning" && (
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
              <div style={{width:"65%",height:"35%",border:`2px solid rgba(255,255,255,0.6)`,borderRadius:6,boxShadow:"0 0 0 9999px rgba(0,0,0,0.35)"}}/>
            </div>
          )}
          {status === "detected" && (
            <div style={{position:"absolute",inset:0,background:"rgba(39,174,96,0.25)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <div style={{fontSize:56,lineHeight:1}}>✓</div>
            </div>
          )}
          {status === "starting" && (
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Spinner size={28} color={B.muted}/>
            </div>
          )}
        </div>
        {status === "scanning" && (
          <div style={{textAlign:"center",color:B.muted,fontFamily:"'Barlow',sans-serif",fontSize:13,marginBottom:14}}>Point camera at barcode — it will detect automatically</div>
        )}
        {/* Detected result */}
        {status === "detected" && (
          <div style={{background:B.card,border:`1px solid rgba(39,174,96,0.5)`,borderRadius:8,padding:14,marginBottom:14}}>
            <div style={{fontSize:10,color:B.muted,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1.5,textTransform:"uppercase",marginBottom:4}}>Barcode detected</div>
            <div style={{fontFamily:"monospace",fontSize:17,color:B.white,fontWeight:700,wordBreak:"break-all",marginBottom:10}}>{detected}</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>applyValue(detected)} style={{flex:1,background:B.blue,color:B.onAccent,border:"none",borderRadius:4,padding:"11px",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:13,letterSpacing:1,textTransform:"uppercase",cursor:"pointer"}}>Use This</button>
              <button onClick={rescan} style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:4,color:B.muted,padding:"11px 18px",fontFamily:"'Barlow',sans-serif",fontSize:13,cursor:"pointer"}}>Rescan</button>
            </div>
          </div>
        )}
        {/* Error */}
        {status === "error" && errMsg && (
          <div style={{background:"rgba(192,57,43,0.1)",border:`1px solid ${B.red}`,borderRadius:6,padding:"10px 14px",marginBottom:14,fontSize:13,color:B.red,fontFamily:"'Barlow',sans-serif"}}>{errMsg}</div>
        )}
        {/* Manual entry fallback */}
        <div style={{borderTop:`1px solid ${B.border}`,paddingTop:12}}>
          <div style={{fontSize:10,color:B.muted,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1.5,textTransform:"uppercase",marginBottom:6}}>Enter manually</div>
          <div style={{display:"flex",gap:8}}>
            <SInput value={manual} onChange={e=>setManual(e.target.value)} placeholder="Type serial / barcode…" style={{flex:1}} onKeyDown={e=>e.key==="Enter"&&applyValue(manual)}/>
            <button onClick={()=>applyValue(manual)} disabled={!manual.trim()} style={{background:manual.trim()?B.blue:B.surface,color:manual.trim()?B.white:B.mid,border:"none",borderRadius:4,padding:"8px 16px",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1,cursor:manual.trim()?"pointer":"not-allowed"}}>Use</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SRO PDF VIEWER MODAL ──────────────────────────────────────────────────────
// Iframe modal backed by a signed Supabase Storage URL. Used from:
//   • HO SROs list (preview an uploaded PDF)
//   • HO job detail (review the source PDF for a completed preloaded-SRO job)
//   • Installer Phase 2 (source-of-truth when the parser mangles text)
function SROPdfViewer({ pdfPath, filename, onClose }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const signed = await getSroPdfSignedUrl(pdfPath, 3600);
        if (!cancelled) setUrl(signed);
      } catch(e) { if (!cancelled) setErr(e.message || "Failed to load PDF"); }
    })();
    return () => { cancelled = true; };
  }, [pdfPath]);
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:10000,display:"flex",flexDirection:"column"}}>
      <div style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${B.border}`,background:B.dark}}>
        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,color:B.white,letterSpacing:1,textTransform:"uppercase"}}>SRO PDF</span>
        <span style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.muted,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{filename || pdfPath}</span>
        {url && <a href={url} target="_blank" rel="noreferrer" className="sm-btn" style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"5px 12px",textDecoration:"none",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Open in new tab</a>}
        <button onClick={onClose} className="sm-btn" style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.white,fontSize:12,padding:"5px 14px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Close</button>
      </div>
      <div style={{flex:1,background:"#222"}}>
        {err ? (
          <div style={{padding:40,color:B.red,fontFamily:"'Barlow',sans-serif",textAlign:"center"}}>{err}</div>
        ) : url ? (
          <iframe src={url} title="SRO PDF" style={{width:"100%",height:"100%",border:"none",background:"#fff"}} />
        ) : (
          <div style={{padding:40,color:B.muted,fontFamily:"'Barlow',sans-serif",textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
            <Spinner size={16} /> Loading signed URL…
          </div>
        )}
      </div>
    </div>
  );
}

// ── SRO MANAGER (HO view) ────────────────────────────────────────────────────
// Bulk drop-zone for head office to upload pre-approved SRO PDFs. Each dropped
// file is parsed client-side; a preview table shows parse quality + service-
// match counts; HO confirms and the row + the raw PDF are saved to Supabase.
// Below the upload area, a list of existing SROs shows lifecycle status and
// provides a PDF viewer for each.
function SroManager({ user, services, onBack, VIEWS, hoView, setHoView }) {
  const [sros,         setSros]         = useState([]);
  const [sroLoading,   setSroLoading]   = useState(true);
  const [queue,        setQueue]        = useState([]);  // preview entries before save
  const [saving,       setSaving]       = useState(false);
  const [dragOver,     setDragOver]     = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");
  const [viewPdfFor,   setViewPdfFor]   = useState(null); // pre_loaded_sros row or queue entry
  const [deleteId,     setDeleteId]     = useState(null);
  const fileInputRef = useRef(null);

  // Load the list
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await dataService.loadPreloadedSros();
        if (!cancelled) setSros(list);
      } catch(e) { console.warn("loadPreloadedSros:", e.message); }
      finally { if (!cancelled) setSroLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Drop handler
  const handleFiles = async (fileList) => {
    const pdfs = Array.from(fileList || []).filter(f => f.type === "application/pdf" || /\.pdf$/i.test(f.name));
    if (!pdfs.length) return;
    // Parse each file, build queue entries
    const entries = await Promise.all(pdfs.map(async (file) => {
      try {
        const parsed = await parseSroPdf(file, services);
        // Detect duplicate SRO# already in the DB
        const dup = sros.find(s => s.sroNumber === parsed.header.sroNumber);
        return { id: `q-${Date.now()}-${Math.random().toString(36).slice(2)}`, file, parsed, duplicate: !!dup, error: null };
      } catch(e) {
        return { id: `q-${Date.now()}-${Math.random().toString(36).slice(2)}`, file, parsed: null, duplicate: false, error: e.message || "Parse failed" };
      }
    }));
    setQueue(prev => [...prev, ...entries]);
  };

  const onInputChange = (e) => { handleFiles(e.target.files); e.target.value = ""; };
  const onDrop  = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };
  const onDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };

  const removeQueueEntry = (id) => setQueue(prev => prev.filter(q => q.id !== id));
  const clearQueue = () => setQueue([]);

  // Save all queued (skip ones with errors / duplicates that user hasn't resolved)
  const saveAll = async () => {
    const toSave = queue.filter(q => q.parsed && !q.duplicate && !q.error);
    if (!toSave.length) return;
    setSaving(true);
    const saved = [];
    const failed = [];
    for (const entry of toSave) {
      try {
        const pdf = await uploadSroPdf(entry.file);
        const row = await dataService.savePreloadedSro({ parsed: entry.parsed, pdf, uploadedBy: user?.id });
        saved.push(row);
      } catch(e) {
        failed.push({ entry, error: e.message });
      }
    }
    setSros(prev => [...saved, ...prev]);
    // Remove saved entries from the queue; keep failed ones with an error stamp
    setQueue(prev => prev
      .filter(q => !toSave.includes(q) || failed.some(f => f.entry === q))
      .map(q => {
        const f = failed.find(f => f.entry === q);
        return f ? { ...q, error: f.error } : q;
      })
    );
    setSaving(false);
  };

  const deleteOne = async (sro) => {
    setDeleteId(sro.id);
    try {
      await dataService.deletePreloadedSro(sro.id, sro.pdfPath);
      setSros(prev => prev.filter(s => s.id !== sro.id));
    } catch(e) { alert("Delete failed: " + e.message); }
    finally { setDeleteId(null); }
  };

  const filteredSros = sros.filter(s => statusFilter === "all" ? true : s.status === statusFilter);

  const statusPill = (status) => {
    const map = {
      active:    { label:"Active",    bg:B.blueDim,    color:B.blueLt,  border:B.blue  },
      claimed:   { label:"Claimed",   bg:B.orangeDim,  color:B.orange,  border:B.orange},
      completed: { label:"Completed", bg:"rgba(39,174,96,0.15)", color:B.green, border:B.green },
      archived:  { label:"Archived",  bg:"rgba(149,165,166,0.12)", color:"#95a5a6", border:"#7f8c8d" },
    };
    const c = map[status] || { label:status, bg:B.surface, color:B.muted, border:B.border };
    return <span style={{display:"inline-block",background:c.bg,color:c.color,border:`1px solid ${c.border}`,borderRadius:3,padding:"2px 9px",fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>{c.label}</span>;
  };

  return (
    <div style={{maxWidth:900,margin:"0 auto",paddingBottom:40}}>
      {/* Top nav mirrors the other HO views */}
      <div style={{background:B.dark,borderBottom:`1px solid ${B.border}`,padding:"20px 20px 16px",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
          {VIEWS.map(({v,l})=>(
            <button key={v} onClick={()=>setHoView(v)} className="sm-btn" style={{background:hoView===v?B.blue:"transparent",color:hoView===v?B.white:v==="reports"?B.blueLt:B.muted,border:`1px solid ${hoView===v?B.blue:v==="reports"?B.blueLt:B.border}`,borderRadius:4,padding:"6px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
              {l}
            </button>
          ))}
        </div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.white}}>Pre-loaded SROs</div>
        <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",marginTop:3}}>
          {sros.filter(s=>s.status==="active").length} active ·
          {" "}{sros.filter(s=>s.status==="claimed").length} claimed ·
          {" "}{sros.filter(s=>s.status==="completed").length} completed
        </div>
      </div>

      {/* Drop zone */}
      <div style={{padding:"0 16px"}}>
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={()=>fileInputRef.current?.click()}
          style={{
            border:`2px dashed ${dragOver?B.blue:B.border}`,
            background:dragOver?B.blueDim:B.card,
            borderRadius:8, padding:"28px 20px",
            textAlign:"center", cursor:"pointer",
            transition:"background-color 0.15s ease, border-color 0.15s ease",
            marginBottom:18,
          }}>
          <div style={{fontSize:28,color:dragOver?B.blueLt:B.mid,marginBottom:8}}>⬇</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,color:B.white,letterSpacing:1,textTransform:"uppercase",marginBottom:4}}>
            Drop SRO PDFs here
          </div>
          <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>
            or click to choose files. Multiple files supported.
          </div>
          <input ref={fileInputRef} type="file" multiple accept="application/pdf,.pdf" style={{display:"none"}} onChange={onInputChange} />
        </div>

        {/* Queue / preview table */}
        {queue.length > 0 && (
          <div style={{marginBottom:24}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,color:B.white,letterSpacing:1,textTransform:"uppercase"}}>
                Parsed {queue.length} — ready to save
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={clearQueue} disabled={saving} className="sm-btn" style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"6px 14px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Clear</button>
                <button onClick={saveAll} disabled={saving||queue.every(q=>q.error||q.duplicate||!q.parsed)} className="sm-btn sm-btn-primary" style={{background:B.blue,border:`1px solid ${B.blue}`,borderRadius:3,color:B.onAccent,fontSize:11,padding:"6px 16px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
                  {saving && <Spinner size={11} />} Save All
                </button>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {queue.map(q => {
                const matchCount = q.parsed ? q.parsed.lineItems.filter(li => li.isService).length : 0;
                const itemCount = q.parsed ? q.parsed.lineItems.length : 0;
                const warnCount = q.parsed ? q.parsed.warnings.length : 0;
                const disabledReason = q.error ? "error" : q.duplicate ? "duplicate" : null;
                return (
                  <div key={q.id} style={{background:B.card,border:`1px solid ${q.error?B.red:q.duplicate?B.orange:B.border}`,borderRadius:6,padding:12,display:"flex",gap:12,alignItems:"flex-start"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,color:B.white,letterSpacing:0.5}}>
                          {q.parsed?.header.sroNumber || "(no SRO#)"}
                        </span>
                        {q.parsed?.site.rlNumber && <span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>RL {q.parsed.site.rlNumber}</span>}
                        {disabledReason==="duplicate" && <span style={{background:B.orangeDim,color:B.orange,border:`1px solid ${B.orange}`,borderRadius:3,padding:"1px 7px",fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>Already on File</span>}
                        {disabledReason==="error"     && <span style={{background:"rgba(192,57,43,0.15)",color:B.red,border:`1px solid ${B.red}`,borderRadius:3,padding:"1px 7px",fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>Parse Error</span>}
                      </div>
                      <div style={{fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif",marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {q.parsed?.site.storeName || q.file.name} — {q.parsed?.site.storeCity}{q.parsed?.site.storeProvince ? ", " + q.parsed.site.storeProvince : ""}
                      </div>
                      <div style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>
                        {q.parsed ? (
                          <>
                            {itemCount} line item{itemCount===1?"":"s"} ·{" "}
                            <span style={{color:matchCount?B.green:B.muted}}>{matchCount} service match{matchCount===1?"":"es"}</span>
                            {warnCount>0 && <> · <span style={{color:B.orange}}>{warnCount} warning{warnCount===1?"":"s"}</span></>}
                          </>
                        ) : (q.error || "Parsing…")}
                      </div>
                      {q.parsed && warnCount>0 && (
                        <div style={{marginTop:4,fontSize:11,color:B.orange,fontFamily:"'Barlow',sans-serif"}}>
                          ⚠ {q.parsed.warnings.join("; ")}
                        </div>
                      )}
                    </div>
                    <button onClick={()=>removeQueueEntry(q.id)} className="sm-btn" style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"4px 10px",cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>Remove</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Existing SROs list */}
        <div style={{display:"flex",borderRadius:4,overflow:"hidden",border:`1px solid ${B.border}`,background:B.surface,alignSelf:"flex-start",marginBottom:12}}>
          {[["active","Active"],["claimed","Claimed"],["completed","Completed"],["all","All"]].map(([v,l])=>(
            <button key={v} onClick={()=>setStatusFilter(v)} className="sm-tab"
              style={{padding:"7px 18px",border:"none",borderRight:v!=="all"?`1px solid ${B.border}`:"none",background:statusFilter===v?B.blueDim:"transparent",color:statusFilter===v?B.white:B.muted,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:11,letterSpacing:1,textTransform:"uppercase",borderBottom:statusFilter===v?`2px solid ${B.blue}`:"2px solid transparent"}}>
              {l}
            </button>
          ))}
        </div>

        {sroLoading ? (
          <div style={{padding:30,textAlign:"center",color:B.muted,fontFamily:"'Barlow',sans-serif",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            <Spinner size={14} /> Loading SROs…
          </div>
        ) : filteredSros.length === 0 ? (
          <div style={{padding:30,textAlign:"center",color:B.muted,fontFamily:"'Barlow',sans-serif",fontSize:13,background:B.card,border:`1px dashed ${B.border}`,borderRadius:6}}>
            No {statusFilter==="all"?"":statusFilter} SROs yet. Drop PDFs above to add some.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {filteredSros.map(s => {
              const matchCount = (s.lineItems||[]).filter(li => li.isService).length;
              const itemCount = (s.lineItems||[]).length;
              return (
                <div key={s.id} style={{background:B.card,border:`1px solid ${B.border}`,borderRadius:6,padding:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:5}}>
                    <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,color:B.white,letterSpacing:0.5}}>{s.sroNumber}</span>
                    {s.rlNumber && <span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>RL {s.rlNumber}</span>}
                    {statusPill(s.status)}
                    <span style={{marginLeft:"auto",display:"flex",gap:6}}>
                      {s.pdfPath && <button onClick={()=>setViewPdfFor(s)} className="sm-btn" style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.light,fontSize:11,padding:"4px 10px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>View PDF</button>}
                      {s.status !== "completed" && (
                        deleteId===s.id
                          ? <span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif",display:"flex",alignItems:"center",gap:5}}><Spinner size={10}/>Deleting…</span>
                          : <button onClick={()=>{ if (window.confirm(`Delete SRO ${s.sroNumber}?\nThis also deletes the archived PDF.`)) deleteOne(s); }} className="sm-btn sm-btn-danger" style={{background:"transparent",border:`1px solid ${B.red}`,borderRadius:3,color:B.red,fontSize:11,padding:"4px 10px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Delete</button>
                      )}
                    </span>
                  </div>
                  <div style={{fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif",marginBottom:3}}>{s.storeName} — {s.storeStreet}, {s.storeCity} {s.storeProvince} {s.storePostalCode}</div>
                  <div style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>
                    {itemCount} line item{itemCount===1?"":"s"} · <span style={{color:matchCount?B.green:B.muted}}>{matchCount} service match{matchCount===1?"":"es"}</span>
                    {" "}· uploaded {s.uploadedAt ? new Date(s.uploadedAt).toLocaleDateString() : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {viewPdfFor && <SROPdfViewer pdfPath={viewPdfFor.pdfPath} filename={viewPdfFor.pdfFilename} onClose={()=>setViewPdfFor(null)} />}
    </div>
  );
}

// ── HEAD OFFICE VIEW ──────────────────────────────────────────────────────────
function HeadOfficeView({ jobs, setJobs, user, installationTypes, setInstallationTypes, services, setServices, users, setUsers, activeSessions, onRegisterNav }) {
  const [search,setSearch]=useState(""); const [selected,setSelected]=useState(null);
  const [filter,setFilter]=useState("all");
  const [rejecting,setRejecting]=useState(false);
  const [rejectionReason,setRejectionReason]=useState("");
  const [hoView,setHoView]=useState("records"); // "records"|"manage"|"users"|"reports"|"sros"
  const [configTab,setConfigTab]=useState("types"); // sub-tab inside manage: "types"|"services"
  const [reportsTab,setReportsTab]=useState("performance"); // sub-tab inside reports: "performance"|"wjs"
  const [editType,setEditType]=useState(null);
  const [editTypeDraft,setEditTypeDraft]=useState(null); // local edits before save
  // Instructions doc upload (PDF/Word → installation-docs bucket; URL persists in
  // the same instructions_url column that previously held pasted external links).
  const [uploadingInstructions,setUploadingInstructions]=useState(false);
  const [instructionsError,setInstructionsError]=useState("");
  const [typeSavedToast,setTypeSavedToast]=useState("");                // "" | "Saved" | "Document uploaded" — auto-clears
  const [editQIdx,setEditQIdx]=useState(null); // index or -1 for new
  const [editQForm,setEditQForm]=useState(null);
  const [deleteConfirmId,setDeleteConfirmId]=useState(null);
  const [newTypeForm,setNewTypeForm]=useState(null); // {label,icon} | null
  // User management state
  const [editUserId,setEditUserId]=useState(null);
  const [editUserForm,setEditUserForm]=useState(null); // {name,email,role,newPassword,confirmPassword}
  const [editUserErrors,setEditUserErrors]=useState({});
  const [showNewUserForm,setShowNewUserForm]=useState(false);
  const [newUserForm,setNewUserForm]=useState({name:"",email:"",password:"",confirmPassword:"",role:"installer"});
  const [newUserErrors,setNewUserErrors]=useState({});
  const [deactivateConfirmId,setDeactivateConfirmId]=useState(null);
  const [userTab,setUserTab]=useState("active"); // "active"|"inactive"
  const [actionConfirm,setActionConfirm]=useState(null); // { action:"approved"|"rejected", job }
  const [dateFrom,   setDateFrom]   = useState("");
  const [dateTo,     setDateTo]     = useState("");
  const [datePreset, setDatePreset] = useState("all"); // "all"|"thisMonth"|"lastMonth"|"nav"|"custom"
  const [navYear,    setNavYear]    = useState(()=>new Date().getFullYear());
  const [navMonth,   setNavMonth]   = useState(()=>new Date().getMonth());
  const [showLive,       setShowLive]       = useState(false);
  const [tick,           setTick]           = useState(0);
  const [lastRefresh,    setLastRefresh]    = useState(()=>new Date());
  const [sessionApprovals, setSessionApprovals] = useState(0);
  const [rollToast,        setRollToast]        = useState(false); // "You're on a roll"
  const rollToastTimer = useRef(null);
  // Loading / busy states
  const [dashLoading,  setDashLoading]  = useState(true);  // skeleton on mount + 60s refresh
  const [approveBusy,  setApproveBusy]  = useState(false);
  const [rejectBusy,   setRejectBusy]   = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);
  const [exportBusy,   setExportBusy]   = useState(false);
  const [deleteBusy,   setDeleteBusy]   = useState(false);
  const [excludeBusy,  setExcludeBusy]  = useState(false);
  const [deleteConfirm,setDeleteConfirm]= useState(false);
  const [deleteInput,  setDeleteInput]  = useState("");
  const [lightbox,     setLightbox]     = useState(null); // null | { src, label }

  // Close lightbox on Escape
  useEffect(() => {
    if (!lightbox) return;
    const onKey = e => { if (e.key === "Escape") setLightbox(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox]);

  // Reset rejection UI when changing selected job
  useEffect(()=>{ setRejecting(false); setRejectionReason(""); },[selected?.id]);

  // Drive live elapsed timers (1 s) and "last updated" timestamp (60 s)
  useEffect(()=>{ const id=setInterval(()=>setTick(t=>t+1),1000); return ()=>clearInterval(id); },[]);
  useEffect(()=>{
    const id=setInterval(()=>{
      setLastRefresh(new Date());
      setDashLoading(true);
      setTimeout(()=>setDashLoading(false), 400);
    },60000);
    return ()=>clearInterval(id);
  },[]);
  // Initial mount skeleton — 400ms minimum
  useEffect(()=>{ const id=setTimeout(()=>setDashLoading(false),400); return ()=>clearTimeout(id); },[]);

  // Register nav Back state for header and bottom nav
  useEffect(() => {
    if (!onRegisterNav) return;
    if (actionConfirm) onRegisterNav(true, () => setActionConfirm(null));
    else if (selected) onRegisterNav(true, () => setSelected(null));
    else if (hoView !== "records") onRegisterNav(true, () => setHoView("records"));
    else onRegisterNav(false, null);
  }, [actionConfirm, selected, hoView, onRegisterNav]);

  // ── Reports view early return (after all hooks) ──
  // Sub-tabs: Performance Reports (existing) | WJS Sign — NEW INSTALL Report.
  if (hoView === "reports") {
    return (
      <div>
        {/* Sub-tab bar — sits above either child report. Hidden on print so the
            child's printable region renders cleanly. */}
        <div className="pr-no-print wjs-no-print"
          style={{maxWidth:1100, margin:"0 auto", padding:"16px 20px 0", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
          {[
            { v: "performance", l: "Performance Reports" },
            { v: "wjs",         l: "WJS Sign — NEW INSTALL" },
          ].map(({v, l}) => (
            <button key={v} onClick={()=>setReportsTab(v)}
              style={{padding:"7px 16px", border:`1px solid ${reportsTab===v?B.blue:B.border}`, background:reportsTab===v?B.blueDim:"transparent", color:reportsTab===v?B.white:B.muted, cursor:"pointer", fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:11, letterSpacing:1, textTransform:"uppercase", borderRadius:3}}>
              {l}
            </button>
          ))}
        </div>
        {reportsTab === "wjs"
          ? <WjsInstallReport jobs={jobs} users={users} installationTypes={installationTypes} onBack={()=>setHoView("records")}/>
          : <PerformanceReports jobs={jobs} users={users} installationTypes={installationTypes} onBack={()=>setHoView("records")}/>
        }
      </div>
    );
  }

  // ── SROs view early return ──
  if (hoView === "sros") {
    const VIEWS = [{v:"records",l:"Records"},{v:"sros",l:"SROs"},{v:"manage",l:"System Config"},{v:"users",l:"Users"},{v:"reports",l:"◈ Reports"}]
      .filter(({v})=>v==="records"||v==="reports"||(v==="manage"||v==="users"||v==="sros")?user.role==="super_admin":true);
    return <SroManager user={user} services={services} VIEWS={VIEWS} hoView={hoView} setHoView={setHoView} onBack={()=>setHoView("records")} />;
  }

  // ── Date filter helpers ──
  const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const isoDate = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const monthRange = (y, m) => ({ from: isoDate(new Date(y,m,1)), to: isoDate(new Date(y,m+1,0)) });

  const applyPreset = preset => {
    const n = new Date();
    if (preset === "all") {
      setDateFrom(""); setDateTo(""); setDatePreset("all");
    } else if (preset === "thisMonth") {
      const r = monthRange(n.getFullYear(), n.getMonth());
      setDateFrom(r.from); setDateTo(r.to); setDatePreset("thisMonth");
    } else if (preset === "lastMonth") {
      const lm = new Date(n.getFullYear(), n.getMonth()-1, 1);
      const r = monthRange(lm.getFullYear(), lm.getMonth());
      setDateFrom(r.from); setDateTo(r.to); setDatePreset("lastMonth");
    }
  };
  const applyNav = (y, m) => {
    const r = monthRange(y, m);
    setDateFrom(r.from); setDateTo(r.to); setNavYear(y); setNavMonth(m); setDatePreset("nav");
  };
  const navPrev = () => {
    const m = navMonth===0?11:navMonth-1, y = navMonth===0?navYear-1:navYear;
    applyNav(y, m);
  };
  const navNext = () => {
    const n = new Date();
    if (navYear===n.getFullYear() && navMonth>=n.getMonth()) return;
    const m = navMonth===11?0:navMonth+1, y = navMonth===11?navYear+1:navYear;
    applyNav(y, m);
  };
  const clearDates = () => { setDateFrom(""); setDateTo(""); setDatePreset("all"); };

  // ── Manage helpers ──
  const syncEditType = async updated => {
    const saved = await dataService.updateInstallationType({ ...updated, updatedBy: user.name });
    setInstallationTypes(p => p.map(t => t.id === saved.id ? saved : t));
    setEditType(saved);
  };
  const addType = async () => {
    if (!newTypeForm?.label?.trim()) return;
    const t = await dataService.createInstallationType({
      label: newTypeForm.label.trim(), icon: newTypeForm.icon || "◈", questions: [],
      createdBy: user.name,
    });
    setInstallationTypes(p => [...p, t]);
    setEditType(t);
    setNewTypeForm(null);
  };
  const deleteType = async id => {
    await dataService.deleteInstallationType(id);
    setInstallationTypes(p => p.filter(t => t.id !== id));
    setDeleteConfirmId(null);
    if (editType?.id === id) setEditType(null);
  };
  const saveQuestion = async () => {
    if (!editQForm?.text?.trim() || editQIdx === null) return;
    const existing = editQIdx >= 0 ? editType.questions[editQIdx] : null;
    const q = {
      id: existing?.id || `q_${Date.now()}`,
      text: editQForm.text.trim(),
      answerType: editQForm.answerType || "confirm",
      photoMode: editQForm.photoMode || "none",
      commentMode: editQForm.commentMode || "optional",
      ...(editQForm.placeholder?.trim() ? { placeholder: editQForm.placeholder.trim() } : {}),
    };
    const questions = editQIdx === -1
      ? [...editType.questions, q]
      : editType.questions.map((x, i) => i === editQIdx ? q : x);
    try {
      await syncEditType({ ...editType, questions });
      setEditQIdx(null); setEditQForm(null);
    } catch (e) { alert(`Save failed: ${e.message}`); }
  };
  const deleteQuestion = async idx => {
    try {
      await syncEditType({ ...editType, questions: editType.questions.filter((_, i) => i !== idx) });
    } catch (e) { alert(`Delete failed: ${e.message}`); }
  };
  const moveQuestion = async (idx, dir) => {
    const qs = [...editType.questions];
    const target = idx + dir;
    if (target < 0 || target >= qs.length) return;
    [qs[idx], qs[target]] = [qs[target], qs[idx]];
    try {
      await syncEditType({ ...editType, questions: qs });
    } catch (e) { alert(`Reorder failed: ${e.message}`); }
  };

  // ── User management helpers ──
  const validateNewUser = () => {
    const e = {};
    if (!newUserForm.name.trim())                              e.name = "Required";
    if (!newUserForm.email.trim())                             e.email = "Required";
    else if (users.some(u => u.email.toLowerCase() === newUserForm.email.trim().toLowerCase())) e.email = "Email already in use";
    if (!newUserForm.password)                                 e.password = "Required";
    else if (newUserForm.password.length < 6)                  e.password = "Minimum 6 characters";
    if (newUserForm.confirmPassword !== newUserForm.password)  e.confirmPassword = "Passwords do not match";
    setNewUserErrors(e);
    return Object.keys(e).length === 0;
  };
  const createUser = async () => {
    if (!validateNewUser()) return;
    try {
      const u = await dataService.createUser({
        name:     newUserForm.name.trim(),
        email:    newUserForm.email.trim(),
        password: newUserForm.password,
        role:     newUserForm.role,
      });
      setUsers(p => [...p, u]);
      setNewUserForm({name:"",email:"",password:"",confirmPassword:"",role:"installer"});
      setNewUserErrors({});
      setShowNewUserForm(false);
    } catch (err) {
      setNewUserErrors(e => ({ ...e, submit: err.message || "Failed to create account. Please try again." }));
    }
  };
  const startEditUser = u => {
    setEditUserId(u.id);
    setEditUserForm({name:u.name, email:u.email, role:u.role, newPassword:"", confirmPassword:""});
    setEditUserErrors({});
  };
  const saveEditUser = async () => {
    const e = {};
    if (!editUserForm.name.trim()) e.name = "Required";
    if (!editUserForm.email.trim()) e.email = "Required";
    else if (users.some(u => u.id !== editUserId && u.email.toLowerCase() === editUserForm.email.trim().toLowerCase())) e.email = "Email already in use";
    if (editUserForm.newPassword) {
      if (editUserForm.newPassword.length < 6)                         e.newPassword = "Minimum 6 characters";
      if (editUserForm.confirmPassword !== editUserForm.newPassword)    e.confirmPassword = "Passwords do not match";
    }
    setEditUserErrors(e);
    if (Object.keys(e).length > 0) return;
    const existing = users.find(u => u.id === editUserId);
    const updates = {
      name:  editUserForm.name.trim(),
      email: editUserForm.email.trim(),
      role:  editUserForm.role,
      // NOTE: new password sent as plaintext — server must hash before storing.
      ...(editUserForm.newPassword ? { password: editUserForm.newPassword } : {}),
    };
    const updated = await dataService.updateUser(existing, updates);
    setUsers(p => p.map(u => u.id === editUserId ? updated : u));
    setEditUserId(null); setEditUserForm(null); setEditUserErrors({});
  };
  const toggleActive = async id => {
    const existing = users.find(u => u.id === id);
    const becomingInactive = existing?.active !== false;
    const updated = await dataService.setUserActive(existing, !becomingInactive);
    setUsers(p => p.map(u => u.id === id ? updated : u));
    setDeactivateConfirmId(null);
    setUserTab(becomingInactive ? "inactive" : "active");
  };

  const dateLabel = (() => {
    if (!dateFrom && !dateTo) return "All time";
    if (datePreset==="thisMonth") return `This Month (${MONTH_NAMES[new Date().getMonth()]} ${new Date().getFullYear()})`;
    if (datePreset==="lastMonth") { const lm=new Date(new Date().getFullYear(),new Date().getMonth()-1,1); return `Last Month (${MONTH_NAMES[lm.getMonth()]} ${lm.getFullYear()})`; }
    if (datePreset==="nav") return `${MONTH_NAMES[navMonth]} ${navYear}`;
    if (dateFrom && dateTo) return `${dateFrom} – ${dateTo}`;
    return dateFrom ? `From ${dateFrom}` : `To ${dateTo}`;
  })();

  const statCounts = {
    pending:          jobs.filter(j=>j.status==="pending").length,
    approved:         jobs.filter(j=>j.status==="approved").length,
    completed:        jobs.filter(j=>j.status==="completed").length,
    rejected:         jobs.filter(j=>j.status==="rejected").length,
    requires_revisit: jobs.filter(j=>j.status==="requires_revisit").length,
    aborted:          jobs.filter(j=>j.status==="aborted").length,
    incomplete:       jobs.filter(j=>j.status==="incomplete").length,
  };

  const filtered=jobs.filter(j=>{
    const q=search.toLowerCase();
    const ms=!q||[j.sroNumber,j.rlNumber,j.storeName,j.installerName].some(v=>v?.toLowerCase().includes(q));
    const mf=filter==="all"||(j.status===filter);
    const md=(()=>{
      if (!dateFrom && !dateTo) return true;
      const d=new Date(j.submittedAt);
      if (dateFrom && d<new Date(dateFrom+"T00:00:00")) return false;
      if (dateTo   && d>new Date(dateTo  +"T23:59:59.999")) return false;
      return true;
    })();
    return ms&&mf&&md;
  });

  const revisitCount = jobs.filter(j=>j.status==="requires_revisit").length;

  // Export wrapper — defers XLSX computation one tick so React can render the spinner first
  const handleExport = () => {
    setExportBusy(true);
    setTimeout(() => {
      exportToExcel(filtered, filter, {datePreset, navYear, navMonth, dateFrom, dateTo});
      setExportBusy(false);
    }, 50);
  };

  // "On Fire" 🔥 — installers with 5+ completed/submitted jobs today
  const todayStr = new Date().toDateString();
  const onFireInstallers = (() => {
    const counts = {};
    jobs.forEach(j => {
      if (j.submittedAt && new Date(j.submittedAt).toDateString() === todayStr) {
        counts[j.installerName] = (counts[j.installerName] || 0) + 1;
      }
    });
    return new Set(Object.entries(counts).filter(([,n]) => n >= 5).map(([name]) => name));
  })();

  const approve = async id => {
    setApproveBusy(true);
    try {
    const job = jobs.find(j => j.id === id);
    const updated = await dataService.approveJob(job, user.name);
    setJobs(p => p.map(j => j.id === id ? updated : j));
    setSelected(null);
    setActionConfirm({ action: "approved", job: updated });
    // Confetti burst — top-centre, ≤2s, respects prefers-reduced-motion via disableForReducedMotion
    if (window.confetti) {
      const COLORS = ["#2550d4","#1134A6","#52d9a0","#27ae60","#f0f0f8","#FFD700"];
      const ORIGIN = { x: 0.5, y: 0 };
      // Primary burst — shoots downward from top centre
      window.confetti({ particleCount: 80, spread: 72, angle: 270, origin: ORIGIN, colors: COLORS, scalar: 0.9, startVelocity: 32, gravity: 0.85, ticks: 160, disableForReducedMotion: true });
      // Echo burst at 280ms — tighter, slower-falling trail
      setTimeout(() => window.confetti({ particleCount: 38, spread: 48, angle: 270, origin: ORIGIN, colors: COLORS, scalar: 0.75, startVelocity: 20, gravity: 0.7, ticks: 130, disableForReducedMotion: true }), 280);
    }
    // Track session approvals — show toast at 10
    setSessionApprovals(prev => {
      const next = prev + 1;
      if (next === 10) {
        setRollToast(true);
        if (rollToastTimer.current) clearTimeout(rollToastTimer.current);
        rollToastTimer.current = setTimeout(() => setRollToast(false), 3200);
      }
      return next;
    });
    } finally { setApproveBusy(false); }
  };

  const markComplete = async id => {
    setCompleteBusy(true);
    try {
      const job = jobs.find(j => j.id === id);
      const updated = await dataService.completeJob(job, user.name);
      setJobs(p => p.map(j => j.id === id ? updated : j));
      setSelected(updated);
    } finally { setCompleteBusy(false); }
  };

  const reject = async (id, reason) => {
    setRejectBusy(true);
    try {
      const job = jobs.find(j => j.id === id);
      const updated = await dataService.rejectJob(job, reason, user.name);
      setJobs(p => p.map(j => j.id === id ? updated : j));
      setSelected(null);
      setRejecting(false); setRejectionReason("");
      setActionConfirm({ action: "rejected", job: updated });
      notifyJobRejected(updated, reason, user.name, users);
    } finally { setRejectBusy(false); }
  };

  const deleteJob = async id => {
    setDeleteBusy(true);
    try {
      await dataService.deleteJob(id);
      setJobs(p=>p.filter(j=>j.id!==id));
      setSelected(null); setDeleteConfirm(false); setDeleteInput("");
    } catch(e) { console.error("[Delete]",e.message); }
    finally { setDeleteBusy(false); }
  };
  const toggleExclude = async job => {
    setExcludeBusy(true);
    try {
      const updated = await dataService.toggleExcludeFromReports(job);
      setJobs(p=>p.map(j=>j.id===updated.id?updated:j));
      setSelected(updated);
    } catch(e) { console.error("[Exclude]",e.message); }
    finally { setExcludeBusy(false); }
  };

  if (actionConfirm) {
    const isApproved = actionConfirm.action === "approved";
    const accentColor = isApproved ? B.green : B.red;
    const accentBg    = isApproved ? "rgba(39,174,96,0.12)" : "rgba(192,57,43,0.12)";
    const accentBorder= isApproved ? "rgba(39,174,96,0.4)"  : "rgba(192,57,43,0.4)";
    const icon        = isApproved ? "✓" : "✗";
    const headline    = isApproved ? "Installation Approved" : "Installation Rejected";
    return (
      <div style={{maxWidth:600,margin:"0 auto",paddingBottom:48}}>
        <div style={{background:B.dark,borderBottom:`1px solid ${B.border}`,padding:"20px 20px 16px",marginBottom:20}}>
          <div style={{fontSize:10,color:B.muted,letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow',sans-serif",marginBottom:4}}>Action Confirmed</div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:26,color:B.white}}>Job Review</div>
        </div>
        <div style={{padding:"0 16px"}}>
          <div style={{background:accentBg,border:`1px solid ${accentBorder}`,borderRadius:8,padding:"40px 24px",display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",gap:16}}>
            <div style={{width:64,height:64,borderRadius:"50%",background:accentBg,border:`2px solid ${accentColor}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,color:accentColor,flexShrink:0}}>
              {icon}
            </div>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:28,color:accentColor,letterSpacing:1}}>{headline}</div>
              <div style={{fontFamily:"'Barlow',sans-serif",fontSize:14,color:B.muted,marginTop:6}}>
                {isApproved ? "This job has been approved and the installer has been notified." : "This job has been rejected and the installer has been notified."}
              </div>
            </div>
            <div style={{width:"100%",maxWidth:360,background:B.card,border:`1px solid ${B.border}`,borderRadius:6,padding:"16px 20px",textAlign:"left",marginTop:4}}>
              <div style={{display:"flex",gap:10,padding:"6px 0",borderBottom:`1px solid ${B.border}`}}>
                <span style={{width:100,flexShrink:0,fontSize:11,fontWeight:600,color:B.muted,textTransform:"uppercase",letterSpacing:1,fontFamily:"'Barlow',sans-serif"}}>SRO</span>
                <span style={{fontSize:14,color:B.white,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700}}>{actionConfirm.job.sroNumber}</span>
              </div>
              <div style={{display:"flex",gap:10,padding:"6px 0"}}>
                <span style={{width:100,flexShrink:0,fontSize:11,fontWeight:600,color:B.muted,textTransform:"uppercase",letterSpacing:1,fontFamily:"'Barlow',sans-serif"}}>Site</span>
                <span style={{fontSize:14,color:B.light,fontFamily:"'Barlow',sans-serif"}}>{actionConfirm.job.storeName}</span>
              </div>
            </div>
            <button
              onClick={()=>setActionConfirm(null)} className="sm-btn"
              style={{marginTop:8,background:`linear-gradient(135deg,${B.blueLt} 0%,${B.blue} 100%)`,color:B.onAccent,border:"none",borderRadius:6,padding:"13px 32px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",boxShadow:"0 4px 16px rgba(26,69,196,0.35)"}}>
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selected) {
    const snap=selected.typeSnapshot||null;
    const allPhotos=[
      ...(selected.photos||[]).map(p=>({...p,label:"General"})),
      ...(snap?snap.questions.flatMap((q,qi)=>(selected.answers?.[q.id]?.photos||[]).map(p=>({...p,label:`Q${qi+1}`}))):[]),
      ...(selected.outcomePhotos||[]).map(p=>({...p,label:"Outcome"})),
    ];
    const row=(label,value)=>(
      <div style={{display:"flex",gap:12,padding:"10px 0",borderBottom:`1px solid ${B.border}`}}>
        <span style={{width:150,flexShrink:0,fontSize:11,fontWeight:600,color:B.muted,textTransform:"uppercase",letterSpacing:1,fontFamily:"'Barlow',sans-serif"}}>{label}</span>
        <span style={{fontSize:14,color:B.light,fontFamily:"'Barlow',sans-serif"}}>{value}</span>
      </div>
    );
    // Renders a clickable static map thumbnail that opens Google Maps.
    // Falls back to a plain coordinates link if the tile image fails to load.
    // When loc is null we render a visible "No GPS coordinates captured" callout
    // rather than a silent dash, so reviewers don't miss that the gate failed.
    const mapTile = (loc, label) => {
      if (!loc) {
        return (
          <div style={{display:"flex",gap:12,padding:"10px 0",borderBottom:`1px solid ${B.border}`,alignItems:"center"}}>
            <span style={{width:150,flexShrink:0,fontSize:11,fontWeight:600,color:B.muted,textTransform:"uppercase",letterSpacing:1,fontFamily:"'Barlow',sans-serif"}}>{label}</span>
            <span title="No GPS coordinates were captured at this stage" style={{display:"inline-flex",alignItems:"center",gap:6,color:B.orange,background:"rgba(230,126,34,0.10)",border:`1px solid ${B.orange}`,borderRadius:3,padding:"3px 9px",fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>
              📍 No GPS captured
            </span>
          </div>
        );
      }
      const gmUrl   = `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;
      const tileUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${loc.lat},${loc.lng}&zoom=15&size=300x160&markers=${loc.lat},${loc.lng},red`;
      return (
        <div style={{padding:"10px 0", borderBottom:`1px solid ${B.border}`}}>
          <div style={{fontSize:11,fontWeight:600,color:B.muted,textTransform:"uppercase",letterSpacing:1,fontFamily:"'Barlow',sans-serif",marginBottom:8}}>{label}</div>
          <a href={gmUrl} target="_blank" rel="noreferrer" style={{display:"inline-block",textDecoration:"none"}}>
            <img src={tileUrl} alt={label}
              onError={e=>{ e.target.style.display="none"; e.target.nextSibling.style.display="block"; }}
              style={{width:"100%",maxWidth:300,height:150,objectFit:"cover",borderRadius:6,border:`1px solid ${B.border}`,display:"block",cursor:"pointer",transition:"opacity 0.15s"}}
              onMouseOver={e=>e.target.style.opacity="0.85"} onMouseOut={e=>e.target.style.opacity="1"}
            />
            {/* Fallback shown only if tile image fails */}
            <span style={{display:"none",color:B.blueLt,fontFamily:"'Barlow',sans-serif",fontSize:13}}>
              {loc.lat.toFixed(6)}, {loc.lng.toFixed(6)} ↗
            </span>
          </a>
          <div style={{fontSize:10,color:B.muted,marginTop:5,fontFamily:"'Barlow',sans-serif",letterSpacing:0.3}}>
            {loc.lat.toFixed(6)}, {loc.lng.toFixed(6)} · <span style={{color:B.blueLt}}>↗ Open in Google Maps</span>
          </div>
        </div>
      );
    };
    return (
      <div style={{maxWidth:700,margin:"0 auto",paddingBottom:40}}>
        <div style={{background:B.dark,borderBottom:`1px solid ${B.border}`,padding:"16px 20px",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
            <button onClick={()=>{setSelected(null);setRejecting(false);setRejectionReason("");}} style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:3,padding:"5px 14px",cursor:"pointer",fontSize:11,fontFamily:"'Barlow',sans-serif"}}>← Back to Records</button>
            {(selected.status==="approved"||selected.status==="completed")&&(
              <button onClick={()=>generateSiteCompletionReport(selected)}
                style={{display:"flex",alignItems:"center",gap:6,background:B.blueDim,border:`1px solid ${B.blue}`,borderRadius:3,padding:"5px 14px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:11,color:B.blueLt,letterSpacing:1,textTransform:"uppercase"}}>
                ⎙ Export Site Completion Report
              </button>
            )}
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontSize:10,color:B.muted,letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow',sans-serif",marginBottom:4}}>Job Report</div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:24,color:B.white}}>{selected.sroNumber}</div>
                {selected.excludedFromReports&&<span style={{fontSize:10,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",background:"rgba(192,57,43,0.15)",color:B.red,border:`1px solid ${B.red}`,borderRadius:3,padding:"3px 8px"}}>⊘ Excluded from Reports</span>}
              </div>
            </div>
            <StatusBadge status={selected.status}/>
          </div>
        </div>
        <div style={{padding:"0 16px",display:"flex",flexDirection:"column",gap:14}}>
          <Card>
            <SectionTitle>Job Details</SectionTitle>
            {row("Store",selected.storeName)}
            {row("RL #",selected.rlNumber||"—")}
            {row("Address",selected.storeAddress)}
            {row("Type",snap?.label||"—")}
            {row("Lead Installer",selected.installerName)}
            {row("Installer Email",selected.installerEmail||"—")}
            {row("2nd Installer",selected.secondInstaller||"N/A")}
            {row("Clocked In",`${fmtTime(selected.startTime)} · ${fmtDate(selected.startTime)}`)}
            {row("Clocked Out",`${fmtTime(selected.endTime)} · ${fmtDate(selected.endTime)}`)}
            {row("Time on Site",elapsed(selected.startTime,selected.endTime)||"—")}
            {row("Submitted",fmt(selected.submittedAt))}
            {mapTile(selected.clockInLocation, "Clock-In Location")}
            {mapTile(selected.submitLocation, "Submission Location")}
          </Card>

          {snap&&snap.questions.length>0&&<Card>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:6}}>
              <SectionTitle>{snap.label} — Checklist</SectionTitle>
              <InstructionsLink docs={getInstructionDocs(snap)} label="📄 Instructions" compact/>
            </div>
            {snap.questions.map((q,i)=>{
              const ans=selected.answers?.[q.id]||{};
              const confirm=ans.confirmed;
              const text=ans.value;
              return <div key={q.id} style={{padding:"12px 0",borderBottom:`1px solid ${B.border}`}}>
                <div style={{fontSize:10,color:B.blueLt,marginBottom:3,fontWeight:700,letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>Q{i+1}</div>
                <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:500,fontSize:13,color:B.light,marginBottom:6}}>{q.text}</div>
                {confirm&&<div style={{fontWeight:700,fontSize:14,color:confirm==="Yes"?B.green:confirm==="No"?B.red:B.light,fontFamily:"'Barlow',sans-serif",marginBottom:text?6:0}}>{confirm}</div>}
                {text&&<div style={{fontSize:13,color:B.light,fontFamily:"'Barlow',sans-serif",marginBottom:4}}>{text}</div>}
                {!confirm&&!text&&<span style={{color:B.mid,fontWeight:400,fontStyle:"italic",fontSize:13,fontFamily:"'Barlow',sans-serif"}}>No answer recorded</span>}
                {ans.notes&&<div style={{fontSize:12,color:B.muted,marginTop:4,fontStyle:"italic",fontFamily:"'Barlow',sans-serif"}}>Note: {ans.notes}</div>}
                {ans.photos?.length>0&&<div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                  {ans.photos.map((p,pi)=><img key={pi} src={p.url||p.data} alt="" onClick={()=>setLightbox({src:p.url||p.data,label:p.label||`Photo ${pi+1}`})} style={{width:66,height:66,borderRadius:4,objectFit:"cover",border:`1px solid ${B.blue}`,cursor:"zoom-in"}}/>)}
                </div>}
              </div>;
            })}
          </Card>}

          <Card>
            <SectionTitle>Site Sign-Off</SectionTitle>
            {row("Title / Role",selected.siteContactTitle||"—")}
            {row("Phone / Email",selected.siteContactPhone||"—")}
            <div style={{marginTop:16}}>
              <FieldLabel>Signature</FieldLabel>
              {selected.signatureData
                ? <div style={{marginTop:8, display:"inline-block"}}>
                    <div style={{background:"#111", borderRadius:"4px 4px 0 0", border:`1px solid ${B.green}`, borderBottom:"none", padding:"10px 14px"}}>
                      <img src={selected.signatureData} alt="Signature" style={{maxWidth:"100%", height:100, display:"block"}}/>
                    </div>
                    <div style={{background:B.surface, borderRadius:"0 0 4px 4px", border:`1px solid ${B.green}`, borderTop:`1px solid ${B.border}`, padding:"8px 14px", minWidth:200}}>
                      <div style={{fontFamily:"'Barlow',sans-serif", fontSize:13, color:B.white, fontWeight:600}}>
                        {selected.siteContactName || <span style={{color:B.mid, fontStyle:"italic", fontWeight:400}}>Name not recorded</span>}
                      </div>
                      {selected.siteContactTitle && <div style={{fontSize:11, color:B.muted, fontFamily:"'Barlow',sans-serif", marginTop:2}}>{selected.siteContactTitle}</div>}
                    </div>
                  </div>
                : <div style={{marginTop:8, fontSize:13, color:B.mid, fontFamily:"'Barlow',sans-serif"}}>
                    No signature captured.
                    {selected.siteContactName && <span style={{marginLeft:6, color:B.light}}>{selected.siteContactName}</span>}
                  </div>
              }
            </div>
          </Card>

          {allPhotos.length>0&&<Card>
            <SectionTitle>Photo Evidence ({allPhotos.length})</SectionTitle>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8}}>
              {allPhotos.map((p,i)=>(
                <div key={i}>
                  <img src={p.url||p.data} alt="" onClick={()=>setLightbox({src:p.url||p.data,label:p.label})} style={{width:"100%",borderRadius:4,border:`1px solid ${B.border}`,objectFit:"cover",aspectRatio:"1",display:"block",cursor:"zoom-in"}}/>
                  <div style={{fontSize:10,color:B.mid,marginTop:3,fontFamily:"'Barlow',sans-serif"}}>{p.label}</div>
                </div>
              ))}
            </div>
          </Card>}

          {/* Status History */}
          {selected.statusHistory?.length>0&&<Card>
            <SectionTitle>Status History</SectionTitle>
            <div style={{display:"flex",flexDirection:"column",gap:0}}>
              {selected.statusHistory.map((h,i)=>{
                const cfg=STATUS_CONFIG[h.status]||STATUS_CONFIG.pending;
                return (
                  <div key={i} style={{display:"flex",gap:14,padding:"10px 0",borderBottom:i<selected.statusHistory.length-1?`1px solid ${B.border}`:"none"}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:0,width:16,flexShrink:0}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:cfg.color,flexShrink:0,marginTop:3}}/>
                      {i<selected.statusHistory.length-1&&<div style={{width:1,flex:1,background:B.border,marginTop:4}}/>}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,color:cfg.color}}>{cfg.label}</span>
                        <span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>{fmt(h.timestamp)}</span>
                      </div>
                      <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",marginTop:1}}>by {h.actor}</div>
                      {h.note&&<div style={{fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif",marginTop:4,padding:"6px 10px",background:B.surface,borderRadius:3,borderLeft:`2px solid ${B.red}`}}>"{h.note}"</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>}

          {/* Activity Log */}
          {selected.auditLog?.length>0&&<Card>
            <SectionTitle>Activity Log</SectionTitle>
            <div style={{display:"flex",flexDirection:"column",gap:0}}>
              {selected.auditLog.map((entry,i)=>{
                const cfg=ACTION_CONFIG[entry.action]||{label:entry.action,icon:"•",color:B.muted};
                const isLast=i===selected.auditLog.length-1;
                return (
                  <div key={i} style={{display:"flex",gap:14,padding:"10px 0",borderBottom:!isLast?`1px solid ${B.border}`:"none"}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:0,width:20,flexShrink:0}}>
                      <div style={{width:20,height:20,borderRadius:"50%",background:`${cfg.color}22`,border:`1px solid ${cfg.color}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:cfg.color,flexShrink:0}}>
                        {cfg.icon}
                      </div>
                      {!isLast&&<div style={{width:1,flex:1,background:B.border,marginTop:4}}/>}
                    </div>
                    <div style={{flex:1,paddingTop:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:13,color:cfg.color}}>{cfg.label}</span>
                        <span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>{fmt(entry.timestamp)}</span>
                      </div>
                      <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",marginTop:1}}>by {entry.actor}</div>
                      {entry.note&&<div style={{fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif",marginTop:4,padding:"6px 10px",background:B.surface,borderRadius:3,borderLeft:`2px solid ${cfg.color}`}}>"{entry.note}"</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>}

          {/* Actions */}
          {selected.status==="pending"&&<Card>
            <SectionTitle>Review Job</SectionTitle>
            <div style={{background:B.blueDim,border:`1px solid ${B.blue}`,borderRadius:4,padding:"10px 14px",marginBottom:14,fontSize:13,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
              Acting as <strong style={{color:B.white}}>{user.name}</strong>
            </div>
            {!rejecting
              ?<div style={{display:"flex",gap:10}}>
                <button onClick={()=>approve(selected.id)} disabled={approveBusy} className="sm-btn sm-btn-primary"
                  style={{flex:1,background:`linear-gradient(135deg,#31bf77 0%,${B.green} 100%)`,color:"#fff",border:"none",borderRadius:6,padding:"13px",fontSize:13,fontWeight:800,cursor:approveBusy?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",boxShadow:"0 4px 16px rgba(39,174,96,0.35)",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {approveBusy ? <><Spinner size={13}/> Approving…</> : "✓ Approve"}
                </button>
                <button onClick={()=>setRejecting(true)} disabled={approveBusy} className="sm-btn sm-btn-danger"
                  style={{flex:1,background:"rgba(192,57,43,0.12)",color:B.red,border:`1px solid ${B.red}`,borderRadius:6,padding:"13px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase"}}>
                  ✗ Reject
                </button>
              </div>
              :<div style={{display:"flex",flexDirection:"column",gap:10}}>
                <FieldLabel required>Rejection Reason</FieldLabel>
                <STextarea value={rejectionReason} onChange={e=>setRejectionReason(e.target.value)} placeholder="Describe what needs to be corrected or why this job is being rejected..."/>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>reject(selected.id,rejectionReason)} disabled={!rejectionReason.trim()||rejectBusy} className="sm-btn sm-btn-danger"
                    style={{flex:1,background:rejectionReason.trim()?"rgba(192,57,43,0.85)":B.surface,color:rejectionReason.trim()?B.white:B.mid,border:`1px solid ${rejectionReason.trim()?B.red:B.border}`,borderRadius:4,padding:"11px",fontSize:13,fontWeight:800,cursor:(rejectionReason.trim()&&!rejectBusy)?"pointer":"not-allowed",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                    {rejectBusy ? <><Spinner size={13} color={B.white}/> Rejecting…</> : "Confirm Rejection"}
                  </button>
                  <button onClick={()=>setRejecting(false)} disabled={rejectBusy} className="sm-btn"
                    style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:4,padding:"11px 18px",fontSize:13,cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>
                    Cancel
                  </button>
                </div>
              </div>
            }
          </Card>}

          {selected.status==="approved"&&<Card>
            <SectionTitle>Mark as Complete</SectionTitle>
            <p style={{margin:"0 0 14px",fontSize:13,color:B.muted,fontFamily:"'Barlow',sans-serif",lineHeight:1.5}}>
              Mark this job as completed to flag it as billable and ready for invoicing.
            </p>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>markComplete(selected.id)} disabled={completeBusy} className="sm-btn sm-btn-primary"
                style={{flex:1,background:`linear-gradient(135deg,#31bf77 0%,${B.green} 100%)`,color:"#fff",border:"none",borderRadius:6,padding:"13px",fontSize:13,fontWeight:800,cursor:completeBusy?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",boxShadow:"0 4px 16px rgba(39,174,96,0.35)",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                {completeBusy ? <><Spinner size={13}/> Completing…</> : "✓ Mark Complete — Ready to Invoice"}
              </button>
              {!rejecting&&<button onClick={()=>setRejecting(true)} disabled={completeBusy} className="sm-btn sm-btn-danger"
                style={{background:"rgba(192,57,43,0.12)",color:B.red,border:`1px solid ${B.red}`,borderRadius:4,padding:"13px 18px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase"}}>
                ✗ Reject
              </button>}
            </div>
            {rejecting&&<div style={{display:"flex",flexDirection:"column",gap:10,marginTop:12}}>
              <FieldLabel required>Rejection Reason</FieldLabel>
              <STextarea value={rejectionReason} onChange={e=>setRejectionReason(e.target.value)} placeholder="Describe what needs to be corrected..."/>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>reject(selected.id,rejectionReason)} disabled={!rejectionReason.trim()||rejectBusy} className="sm-btn sm-btn-danger"
                  style={{flex:1,background:rejectionReason.trim()?"rgba(192,57,43,0.85)":B.surface,color:rejectionReason.trim()?B.white:B.mid,border:`1px solid ${rejectionReason.trim()?B.red:B.border}`,borderRadius:4,padding:"11px",fontSize:13,fontWeight:800,cursor:(rejectionReason.trim()&&!rejectBusy)?"pointer":"not-allowed",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  {rejectBusy ? <><Spinner size={13} color={B.white}/> Rejecting…</> : "Confirm Rejection"}
                </button>
                <button onClick={()=>setRejecting(false)} disabled={rejectBusy} className="sm-btn"
                  style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:4,padding:"11px 18px",fontSize:13,cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>
                  Cancel
                </button>
              </div>
            </div>}
          </Card>}

          {selected.status==="completed"&&<div style={{background:"rgba(39,174,96,0.08)",border:`1px solid ${B.green}`,borderRadius:6,padding:16}}>
            <div style={{fontWeight:700,color:B.green,fontFamily:"'Barlow Condensed',sans-serif",fontSize:15}}>✓ Completed — Ready for Invoicing</div>
            <div style={{fontSize:12,color:B.muted,marginTop:4,fontFamily:"'Barlow',sans-serif"}}>Approved by {selected.approvedBy} · Completed {fmt(selected.completedAt)}</div>
          </div>}

          {selected.status==="rejected"&&<div style={{background:"rgba(192,57,43,0.08)",border:`1px solid ${B.red}`,borderRadius:6,padding:16}}>
            <div style={{fontWeight:700,color:B.red,fontFamily:"'Barlow Condensed',sans-serif",fontSize:15}}>✗ Rejected by {selected.rejectedBy}</div>
            {selected.rejectionReason&&<div style={{fontSize:13,color:B.light,fontFamily:"'Barlow',sans-serif",marginTop:6}}>Reason: {selected.rejectionReason}</div>}
            <div style={{fontSize:12,color:B.muted,marginTop:4,fontFamily:"'Barlow',sans-serif"}}>{fmt(selected.rejectedAt)} · Installer notified</div>
            <div style={{fontSize:11,color:B.orange,fontFamily:"'Barlow',sans-serif",marginTop:8}}>⚠ Will auto-escalate to Requires Revisit if not resolved within 48 hours of rejection.</div>
          </div>}

          {selected.status==="requires_revisit"&&<div style={{background:B.orangeDim,border:`1px solid ${B.orange}`,borderRadius:6,padding:16}}>
            <div style={{fontWeight:700,color:B.orange,fontFamily:"'Barlow Condensed',sans-serif",fontSize:15}}>⚠ Warranty Revisit Required</div>
            <div style={{fontSize:13,color:B.light,fontFamily:"'Barlow',sans-serif",marginTop:6}}>This job was rejected and not resolved within 48 hours. A warranty revisit must be scheduled.</div>
            <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",marginTop:4}}>Escalated: {fmt(selected.revisitFlaggedAt)}</div>
          </div>}

          {(selected.status==="aborted"||selected.status==="incomplete")&&(()=>{
            const sc=STATUS_CONFIG[selected.status];
            return (
              <div style={{background:sc.bg,border:`1px solid ${sc.border}`,borderRadius:6,padding:16}}>
                <div style={{fontWeight:700,color:sc.color,fontFamily:"'Barlow Condensed',sans-serif",fontSize:15}}>
                  {sc.icon} {selected.status==="aborted"?"Job Aborted":"Job Incomplete"} — Reported by {selected.installerName}
                </div>
                {selected.outcomeReason&&<div style={{fontSize:13,color:B.light,fontFamily:"'Barlow',sans-serif",marginTop:8,lineHeight:1.5}}>Reason: {selected.outcomeReason}</div>}
                <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",marginTop:4}}>{fmt(selected.submittedAt)}</div>
              </div>
            );
          })()}

          {(selected.status==="approved"||selected.status==="completed")&&(
            <div style={{background:"rgba(17,52,166,0.08)",border:`1px solid ${B.blue}`,borderRadius:6,padding:"18px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,flexWrap:"wrap"}}>
              <div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,color:B.blueLt,letterSpacing:0.5,marginBottom:3}}>⎙ Site Completion Report</div>
                <div style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.muted,lineHeight:1.5}}>
                  Generate a branded PDF containing all job details, checklist responses, photos, signature, and approval information.
                </div>
              </div>
              <button onClick={()=>generateSiteCompletionReport(selected)}
                style={{background:B.blue,color:B.onAccent,border:"none",borderRadius:4,padding:"12px 22px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1.5,textTransform:"uppercase",whiteSpace:"nowrap",flexShrink:0,boxShadow:`0 4px 16px rgba(17,52,166,0.35)`}}>
                Export PDF →
              </button>
            </div>
          )}

          {/* Super Admin Controls */}
          {user.role === "super_admin" && (
            <Card style={{border:`1px solid rgba(192,57,43,0.3)`,background:"rgba(192,57,43,0.04)"}}>
              <SectionTitle>Super Admin Controls</SectionTitle>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,padding:"12px 0",borderBottom:`1px solid ${B.border}`}}>
                <div>
                  <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:13,color:B.white,marginBottom:3}}>
                    {selected.excludedFromReports ? "⊘ Excluded from Reports" : "Include in Reports"}
                  </div>
                  <div style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.muted,lineHeight:1.4}}>
                    {selected.excludedFromReports ? "This job is hidden from Excel exports and performance reports." : "This job appears in all Excel exports and performance data."}
                  </div>
                </div>
                <button onClick={()=>toggleExclude(selected)} disabled={excludeBusy}
                  style={{background:selected.excludedFromReports?"rgba(39,174,96,0.12)":"rgba(192,57,43,0.12)",color:selected.excludedFromReports?B.green:B.red,border:`1px solid ${selected.excludedFromReports?B.green:B.red}`,borderRadius:6,padding:"9px 18px",fontSize:12,fontWeight:800,cursor:excludeBusy?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1.5,textTransform:"uppercase",whiteSpace:"nowrap",flexShrink:0,display:"flex",alignItems:"center",gap:6}}>
                  {excludeBusy?<Spinner size={12}/>:(selected.excludedFromReports?"✓ Restore":"⊘ Exclude")}
                </button>
              </div>
              <div style={{paddingTop:14}}>
                <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:13,color:B.red,marginBottom:3}}>Delete This Record</div>
                <div style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.muted,marginBottom:12,lineHeight:1.4}}>Permanently removes this job from the database. This cannot be undone.</div>
                {!deleteConfirm
                  ?<button onClick={()=>{setDeleteConfirm(true);setDeleteInput("");}} style={{background:"rgba(192,57,43,0.12)",color:B.red,border:`1px solid ${B.red}`,borderRadius:6,padding:"9px 20px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1.5,textTransform:"uppercase"}}>✕ Delete Record</button>
                  :<div style={{background:"rgba(192,57,43,0.08)",border:`1px solid ${B.red}`,borderRadius:6,padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
                    <div style={{fontFamily:"'Barlow',sans-serif",fontSize:13,color:B.white,fontWeight:600}}>Type <span style={{color:B.red,fontFamily:"monospace"}}>DELETE</span> to confirm permanent deletion of SRO# {selected.sroNumber}</div>
                    <SInput value={deleteInput} onChange={e=>setDeleteInput(e.target.value)} placeholder="Type DELETE here"/>
                    <div style={{display:"flex",gap:10}}>
                      <button onClick={()=>deleteJob(selected.id)} disabled={deleteInput!=="DELETE"||deleteBusy}
                        style={{flex:1,background:deleteInput==="DELETE"?"rgba(192,57,43,0.85)":B.surface,color:deleteInput==="DELETE"?B.white:B.mid,border:`1px solid ${deleteInput==="DELETE"?B.red:B.border}`,borderRadius:4,padding:"11px",fontSize:13,fontWeight:800,cursor:(deleteInput==="DELETE"&&!deleteBusy)?"pointer":"not-allowed",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                        {deleteBusy?<><Spinner size={13} color={B.white}/> Deleting…</>:"Confirm Delete"}
                      </button>
                      <button onClick={()=>{setDeleteConfirm(false);setDeleteInput("");}} style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:4,padding:"11px 18px",fontSize:13,cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>Cancel</button>
                    </div>
                  </div>
                }
              </div>
            </Card>
          )}

        </div>

        {/* Photo lightbox — inside selected return so it's reachable */}
        {lightbox && (
          <div onClick={()=>setLightbox(null)}
            style={{position:"fixed",inset:0,zIndex:800,background:"rgba(0,0,0,0.88)",backdropFilter:"blur(10px)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
            <button onClick={()=>setLightbox(null)}
              style={{position:"absolute",top:20,right:20,background:"rgba(255,255,255,0.08)",border:`1px solid rgba(255,255,255,0.15)`,borderRadius:"50%",width:38,height:38,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",color:"rgba(255,255,255,0.7)",fontSize:18,lineHeight:1,flexShrink:0}}>
              ×
            </button>
            <img src={lightbox.src} alt={lightbox.label||""} onClick={e=>e.stopPropagation()}
              style={{maxWidth:"min(92vw,900px)",maxHeight:"80vh",borderRadius:6,objectFit:"contain",boxShadow:"0 24px 80px rgba(0,0,0,0.7)",display:"block"}}/>
            {lightbox.label && (
              <div style={{marginTop:14,fontSize:12,color:"rgba(200,200,220,0.6)",fontFamily:"'Barlow',sans-serif",letterSpacing:1,textAlign:"center"}}>
                {lightbox.label}
              </div>
            )}
            <div style={{marginTop:10,fontSize:10,color:"rgba(200,200,220,0.3)",fontFamily:"'Barlow',sans-serif",letterSpacing:1.5,textTransform:"uppercase"}}>
              Click anywhere or press Esc to close
            </div>
          </div>
        )}

      </div>
    );
  }

  const atCurrentMonth = navYear===new Date().getFullYear() && navMonth===new Date().getMonth();

  // ── Manage Types UI ──
  // ── Users View ──
  if (hoView === "users") {
    const initials = u => u.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
    const VIEWS = [{v:"records",l:"Records"},{v:"sros",l:"SROs"},{v:"manage",l:"System Config"},{v:"users",l:"Users"},{v:"reports",l:"◈ Reports"}].filter(({v})=>v==="records"||v==="reports"||(v==="manage"||v==="users"||v==="sros")?user.role==="super_admin":true);
    return (
      <div style={{maxWidth:700,margin:"0 auto",paddingBottom:40}}>
        <div style={{background:B.dark,borderBottom:`1px solid ${B.border}`,padding:"20px 20px 16px",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            {VIEWS.map(({v,l})=>(
              <button key={v} onClick={()=>setHoView(v)} className="sm-btn" style={{background:hoView===v?B.blue:"transparent",color:hoView===v?B.white:v==="reports"?B.blueLt:B.muted,border:`1px solid ${hoView===v?B.blue:v==="reports"?B.blueLt:B.border}`,borderRadius:4,padding:"6px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.white}}>User Management</div>
          <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",marginTop:3}}>{users.filter(u=>u.active!==false).length} active · {users.filter(u=>u.active===false).length} inactive</div>
        </div>

        <div style={{padding:"0 16px",display:"flex",flexDirection:"column",gap:10}}>
          {/* Active / Inactive tab bar */}
          <div style={{display:"flex",borderRadius:4,overflow:"hidden",border:`1px solid ${B.border}`,background:B.surface,alignSelf:"flex-start"}}>
            {[["active","Active"],["inactive","Inactive"]].map(([v,l])=>(
              <button key={v} onClick={()=>{setUserTab(v);setDeactivateConfirmId(null);setEditUserId(null);setEditUserForm(null);setEditUserErrors({});}} className="sm-tab"
                style={{padding:"7px 22px",border:"none",borderRight:v==="active"?`1px solid ${B.border}`:"none",background:userTab===v?B.blueDim:"transparent",color:userTab===v?B.white:B.muted,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1,textTransform:"uppercase",borderBottom:userTab===v?`2px solid ${B.blue}`:"2px solid transparent"}}>
                {l}
                <span style={{marginLeft:7,fontSize:11,fontFamily:"'Barlow',sans-serif",fontWeight:400,color:userTab===v?B.muted:B.mid}}>
                  {v==="active"?users.filter(u=>u.active!==false).length:users.filter(u=>u.active===false).length}
                </span>
              </button>
            ))}
          </div>

          {/* ── Active users list ── */}
          {userTab === "active" && users.filter(u=>u.active!==false).map(u => {
            const isEditing = editUserId === u.id;
            const isMe = u.id === user.id;
            return (
              <div key={u.id} style={{background:B.card,border:`1px solid ${deactivateConfirmId===u.id?B.red:B.border}`,borderRadius:6,padding:16}}>
                {isEditing && editUserForm
                  ? /* ── Edit form ── */
                    <div>
                      <div style={{fontSize:11,color:B.blue,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",marginBottom:12}}>Edit User</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                        <div>
                          <FieldLabel required>Full Name</FieldLabel>
                          <SInput value={editUserForm.name} onChange={e=>setEditUserForm(f=>({...f,name:e.target.value}))} placeholder="Full name" error={editUserErrors.name}/>
                          <ErrMsg msg={editUserErrors.name||""}/>
                        </div>
                        <div>
                          <FieldLabel required>Email (login)</FieldLabel>
                          <SInput value={editUserForm.email} onChange={e=>setEditUserForm(f=>({...f,email:e.target.value}))} placeholder="email@somerville.com" type="email" error={editUserErrors.email}/>
                          <ErrMsg msg={editUserErrors.email||""}/>
                        </div>
                      </div>
                      <div style={{marginBottom:10}}>
                        <FieldLabel>Role</FieldLabel>
                        <div style={{display:"flex",gap:10}}>
                          {[["installer","Installer"],["admin","Head Office Admin"],["super_admin","Super Admin"]].map(([val,lbl])=>(
                            <label key={val} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:13,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
                              <input type="radio" checked={editUserForm.role===val} onChange={()=>setEditUserForm(f=>({...f,role:val}))} style={{accentColor:B.blue}}/>
                              {lbl}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div style={{borderTop:`1px solid ${B.border}`,paddingTop:10,marginTop:4,marginBottom:10}}>
                        <div style={{fontSize:11,color:B.muted,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:8}}>Reset Password (leave blank to keep current)</div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                          <div>
                            <FieldLabel>New Password</FieldLabel>
                            <SInput value={editUserForm.newPassword} onChange={e=>setEditUserForm(f=>({...f,newPassword:e.target.value}))} placeholder="New password" type="password" error={editUserErrors.newPassword}/>
                            <ErrMsg msg={editUserErrors.newPassword||""}/>
                          </div>
                          <div>
                            <FieldLabel>Confirm</FieldLabel>
                            <SInput value={editUserForm.confirmPassword} onChange={e=>setEditUserForm(f=>({...f,confirmPassword:e.target.value}))} placeholder="Confirm password" type="password" error={editUserErrors.confirmPassword}/>
                            <ErrMsg msg={editUserErrors.confirmPassword||""}/>
                          </div>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={saveEditUser} className="sm-btn sm-btn-primary" style={{background:B.blue,color:B.onAccent,border:"none",borderRadius:3,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>Save Changes</button>
                        <button onClick={()=>{setEditUserId(null);setEditUserForm(null);setEditUserErrors({});}} className="sm-btn" style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"7px 14px",cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>Cancel</button>
                      </div>
                    </div>
                  : deactivateConfirmId === u.id
                  ? /* ── Deactivate confirm ── */
                    <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                      <span style={{flex:1,fontFamily:"'Barlow',sans-serif",fontSize:13,color:B.red}}>Deactivate "{u.name}"? They will no longer be able to log in.</span>
                      <button onClick={()=>toggleActive(u.id)} className="sm-btn sm-btn-danger" style={{background:"rgba(192,57,43,0.85)",color:B.white,border:"none",borderRadius:3,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>Deactivate</button>
                      <button onClick={()=>setDeactivateConfirmId(null)} className="sm-btn" style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"5px 12px",cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>Cancel</button>
                    </div>
                  : /* ── Active user row ── */
                    <div style={{display:"flex",alignItems:"center",gap:14}}>
                      <div style={{width:36,height:36,borderRadius:"50%",background:u.role==="super_admin"?B.gold:u.role==="admin"?B.blue:"rgba(39,174,96,0.2)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:13,color:u.role==="super_admin"?B.white:u.role==="admin"?B.white:B.green}}>{initials(u)}</span>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:14,color:B.white}}>{u.name}</span>
                          {isMe&&<span style={{fontSize:9,color:B.blue,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase",background:B.blueDim,border:`1px solid ${B.blue}`,borderRadius:2,padding:"1px 6px"}}>You</span>}
                          <span style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",color:u.role==="super_admin"?B.gold:u.role==="admin"?B.blueLt:B.muted,background:u.role==="super_admin"?B.goldDim:u.role==="admin"?B.blueDim:"transparent",border:`1px solid ${u.role==="super_admin"?B.gold:u.role==="admin"?B.blue:B.border}`,borderRadius:2,padding:"2px 7px"}}>{u.role==="super_admin"?"Super Admin":u.role==="admin"?"Head Office":"Installer"}</span>
                        </div>
                        <div style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.muted,marginTop:2}}>{u.email}</div>
                      </div>
                      <div style={{display:"flex",gap:6,flexShrink:0}}>
                        <button onClick={()=>startEditUser(u)} className="sm-btn" style={{background:B.blueDim,color:B.blueLt,border:`1px solid ${B.blue}`,borderRadius:3,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>Edit</button>
                        {!isMe&&<button onClick={()=>setDeactivateConfirmId(u.id)} className="sm-btn sm-btn-danger" style={{background:"transparent",color:B.red,border:`1px solid ${B.red}`,borderRadius:3,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>Deactivate</button>}
                      </div>
                    </div>
                }
              </div>
            );
          })}

          {/* ── Inactive users list ── */}
          {userTab === "inactive" && (
            users.filter(u=>u.active===false).length === 0
              ? <div style={{textAlign:"center",padding:"48px 24px",color:B.mid}}>
                  <div style={{fontSize:32,marginBottom:10,opacity:0.3}}>◎</div>
                  <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:1}}>No inactive users</div>
                </div>
              : users.filter(u=>u.active===false).map(u=>(
                  <div key={u.id} style={{background:B.card,border:`1px solid ${B.border}`,borderRadius:6,padding:16,opacity:0.7}}>
                    <div style={{display:"flex",alignItems:"center",gap:14}}>
                      <div style={{width:36,height:36,borderRadius:"50%",background:B.surface,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:13,color:B.mid}}>{initials(u)}</span>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:14,color:B.muted}}>{u.name}</span>
                          <span style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",color:u.role==="super_admin"?B.gold:u.role==="admin"?B.blueLt:B.muted,background:u.role==="super_admin"?B.goldDim:u.role==="admin"?B.blueDim:"transparent",border:`1px solid ${u.role==="super_admin"?B.gold:u.role==="admin"?B.blue:B.border}`,borderRadius:2,padding:"2px 7px"}}>{u.role==="super_admin"?"Super Admin":u.role==="admin"?"Head Office":"Installer"}</span>
                        </div>
                        <div style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.mid,marginTop:2}}>{u.email}</div>
                      </div>
                      <button onClick={()=>toggleActive(u.id)} className="sm-btn sm-btn-primary" style={{background:"rgba(39,174,96,0.1)",color:B.green,border:`1px solid ${B.green}`,borderRadius:3,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,flexShrink:0}}>Reactivate</button>
                    </div>
                  </div>
                ))
          )}

          {/* Create user */}
          {!showNewUserForm
            ? <button onClick={()=>setShowNewUserForm(true)} className="sm-btn" style={{background:"transparent",border:`1px dashed ${B.border}`,borderRadius:6,padding:"14px",fontSize:13,color:B.blue,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>
                + Add New User
              </button>
            : <Card>
                <SectionTitle>New User Account</SectionTitle>
                <div style={{background:"rgba(17,52,166,0.08)",border:`1px solid rgba(17,52,166,0.3)`,borderRadius:4,padding:"8px 12px",marginBottom:14,fontSize:12,color:B.blueLt,fontFamily:"'Barlow',sans-serif",lineHeight:1.5}}>
                  Accounts are created in Supabase Auth. The user can log in immediately with these credentials.
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div>
                    <FieldLabel required>Full Name</FieldLabel>
                    <SInput value={newUserForm.name} onChange={e=>{setNewUserForm(f=>({...f,name:e.target.value}));setNewUserErrors(e=>({...e,name:""}));}} placeholder="e.g. Alex Chen" error={newUserErrors.name}/>
                    <ErrMsg msg={newUserErrors.name||""}/>
                  </div>
                  <div>
                    <FieldLabel required>Email (used to log in)</FieldLabel>
                    <SInput value={newUserForm.email} onChange={e=>{setNewUserForm(f=>({...f,email:e.target.value}));setNewUserErrors(er=>({...er,email:""}));}} placeholder="a.chen@somerville.com" type="email" error={newUserErrors.email}/>
                    <ErrMsg msg={newUserErrors.email||""}/>
                  </div>
                  <div>
                    <FieldLabel required>Password</FieldLabel>
                    <SInput value={newUserForm.password} onChange={e=>{setNewUserForm(f=>({...f,password:e.target.value}));setNewUserErrors(er=>({...er,password:""}));}} placeholder="Min. 6 characters" type="password" error={newUserErrors.password}/>
                    <ErrMsg msg={newUserErrors.password||""}/>
                  </div>
                  <div>
                    <FieldLabel required>Confirm Password</FieldLabel>
                    <SInput value={newUserForm.confirmPassword} onChange={e=>{setNewUserForm(f=>({...f,confirmPassword:e.target.value}));setNewUserErrors(er=>({...er,confirmPassword:""}));}} placeholder="Repeat password" type="password" error={newUserErrors.confirmPassword}/>
                    <ErrMsg msg={newUserErrors.confirmPassword||""}/>
                  </div>
                </div>
                <div style={{marginBottom:14}}>
                  <FieldLabel>Role</FieldLabel>
                  <div style={{display:"flex",gap:16}}>
                    {[["installer","Installer"],["admin","Head Office Admin"],["super_admin","Super Admin"]].map(([val,lbl])=>(
                      <label key={val} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:13,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
                        <input type="radio" checked={newUserForm.role===val} onChange={()=>setNewUserForm(f=>({...f,role:val}))} style={{accentColor:B.blue}}/>
                        {lbl}
                      </label>
                    ))}
                  </div>
                </div>
                {newUserErrors.submit && <div style={{color:B.red,fontSize:12,fontFamily:"'Barlow',sans-serif",marginBottom:4}}>{newUserErrors.submit}</div>}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={createUser} className="sm-btn sm-btn-primary" style={{background:B.blue,color:B.onAccent,border:"none",borderRadius:4,padding:"10px 22px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>Create Account</button>
                  <button onClick={()=>{setShowNewUserForm(false);setNewUserErrors({});}} className="sm-btn" style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"9px 14px",cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>Cancel</button>
                </div>
              </Card>
          }
        </div>
      </div>
    );
  }

  if (hoView === "manage") {
    const btnSm = (label, onClick, style={}, cls="") => (
      <button onClick={onClick} className={`sm-btn${cls ? " "+cls : ""}`} style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"4px 10px",cursor:"pointer",fontFamily:"'Barlow',sans-serif",...style}}>{label}</button>
    );
    return (
      <div style={{maxWidth:700,margin:"0 auto",paddingBottom:40}}>
        <div style={{background:B.dark,borderBottom:`1px solid ${B.border}`,padding:"20px 20px 16px",marginBottom:20}}>
          {/* Top-level nav */}
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
            {[{v:"records",l:"Records"},{v:"sros",l:"SROs"},{v:"manage",l:"System Config"},{v:"users",l:"Users"},{v:"reports",l:"◈ Reports"}].filter(({v})=>v==="records"||v==="reports"||(v==="manage"||v==="users"||v==="sros")?user.role==="super_admin":true).map(({v,l})=>(
              <button key={v} onClick={()=>setHoView(v)} className="sm-btn" style={{background:hoView===v?B.blue:"transparent",color:hoView===v?B.white:v==="reports"?B.blueLt:B.muted,border:`1px solid ${hoView===v?B.blue:v==="reports"?B.blueLt:B.border}`,borderRadius:4,padding:"6px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
                {l}
              </button>
            ))}
          </div>
          {/* Sub-tabs */}
          <div style={{display:"flex",gap:6,marginBottom:14}}>
            {[{v:"types",l:"Installation Types"},{v:"services",l:"Services Library"}].map(({v,l})=>(
              <button key={v} onClick={()=>setConfigTab(v)} className="sm-btn"
                style={{background:configTab===v?B.blueDim:"transparent",color:configTab===v?B.white:B.muted,border:`1px solid ${configTab===v?B.blue:B.border}`,borderRadius:4,padding:"5px 16px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase",borderBottom:configTab===v?`2px solid ${B.blue}`:"1px solid "+B.border}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.white}}>
            {configTab==="services" ? "Services Library" : editType ? editType.label : "Installation Types"}
          </div>
        </div>

        <div style={{padding:"0 16px",display:"flex",flexDirection:"column",gap:14}}>
          {configTab === "services"
            ? <ServicesLibrary services={services} setServices={setServices} user={user}/>
            : <>
          {/* ── Type List ── */}
          {!editType && <>
            {[...installationTypes].sort((a,b)=>a.label.localeCompare(b.label)).map(t=>(
              <div key={t.id} style={{background:B.card,border:`1px solid ${deleteConfirmId===t.id?B.red:B.border}`,borderRadius:6,padding:"14px 16px"}}>
                {deleteConfirmId===t.id
                  ? <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                      <span style={{flex:1,fontFamily:"'Barlow',sans-serif",fontSize:13,color:B.red}}>Delete "{t.label}"? This cannot be undone.</span>
                      <button onClick={()=>deleteType(t.id)} className="sm-btn sm-btn-danger" style={{background:"rgba(192,57,43,0.85)",color:B.white,border:"none",borderRadius:3,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>Delete</button>
                      {btnSm("Cancel",()=>setDeleteConfirmId(null))}
                    </div>
                  : <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <span style={{fontSize:20,color:B.mid,flexShrink:0}}>{t.icon}</span>
                      <span style={{flex:1,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,color:B.white}}>{t.label}</span>
                      {(() => {
                        const docs = getInstructionDocs(t);
                        if (docs.length === 0) return null;
                        const tip = docs.length === 1
                          ? (docs[0].filename || "View installation instructions")
                          : `${docs.length} documents attached`;
                        // Single doc: clickable. Multi: open the editor instead so they can pick.
                        if (docs.length === 1) {
                          return (
                            <a href={getViewableUrl(docs[0])} target="_blank" rel="noopener noreferrer"
                              title={tip}
                              onClick={e=>e.stopPropagation()}
                              style={{fontSize:14,color:B.blueLt,textDecoration:"none",flexShrink:0,lineHeight:1}}>
                              📄
                            </a>
                          );
                        }
                        return (
                          <span title={tip} style={{fontSize:14,color:B.blueLt,flexShrink:0,lineHeight:1,display:"inline-flex",alignItems:"center",gap:2}}>
                            📄<span style={{fontSize:10,fontFamily:"'Barlow',sans-serif",fontWeight:700,color:B.blueLt}}>×{docs.length}</span>
                          </span>
                        );
                      })()}
                      <span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>{t.questions.length} question{t.questions.length!==1?"s":""}</span>
                      <button onClick={()=>{setEditType(t);setEditTypeDraft({label:t.label,icon:t.icon,instructionsDocs:[...getInstructionDocs(t)],_iconOpen:false});setInstructionsError("");setUploadingInstructions(false);}} className="sm-btn" style={{background:B.blueDim,color:B.blueLt,border:`1px solid ${B.blue}`,borderRadius:3,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>Edit</button>
                      {btnSm("Delete",()=>setDeleteConfirmId(t.id),{color:B.red,borderColor:B.red},"sm-btn-danger")}
                    </div>
                }
              </div>
            ))}
            {!newTypeForm
              ? <button onClick={()=>setNewTypeForm({label:"",icon:"◈"})} className="sm-btn" style={{background:"transparent",border:`1px dashed ${B.border}`,borderRadius:6,padding:"14px",fontSize:13,color:B.blue,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>
                  + Add New Installation Type
                </button>
              : <Card>
                  <SectionTitle>New Program Type</SectionTitle>
                  {/* Program Type label */}
                  <div style={{marginBottom:14}}>
                    <FieldLabel required>Program Type Name</FieldLabel>
                    <SInput value={newTypeForm.label} onChange={e=>setNewTypeForm(f=>({...f,label:e.target.value}))} placeholder="e.g. Electrical Signage" onKeyDown={e=>e.key==="Enter"&&newTypeForm.label.trim()&&addType()}/>
                  </div>
                  {/* Icon dropdown */}
                  <div style={{marginBottom:20}}>
                    <FieldLabel>Icon</FieldLabel>
                    {/* Selected icon preview + toggle */}
                    <div style={{position:"relative"}}>
                      <div onClick={()=>setNewTypeForm(f=>({...f,_iconOpen:!f._iconOpen}))}
                        style={{display:"flex",alignItems:"center",gap:10,background:B.surface,border:`1px solid ${B.border}`,borderRadius:6,padding:"10px 14px",cursor:"pointer",userSelect:"none"}}>
                        <span style={{fontSize:18}}>{newTypeForm.icon||"◈"}</span>
                        <span style={{flex:1,fontFamily:"'Barlow',sans-serif",fontSize:13,color:B.light}}>Select an icon</span>
                        <span style={{color:B.muted,fontSize:11}}>{newTypeForm._iconOpen?"▲":"▼"}</span>
                      </div>
                      {newTypeForm._iconOpen&&<div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:100,background:B.card,border:`1px solid ${B.border}`,borderRadius:6,padding:12,marginTop:4,boxShadow:"0 8px 24px rgba(0,0,0,0.4)"}}>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(8,1fr)",gap:6}}>
                          {["◈","◼","▣","▤","◉","◎","⬡","⬢","▲","◆","✦","⚙","⬛","▪","◐","★"].map(ic=>(
                            <div key={ic} onClick={()=>setNewTypeForm(f=>({...f,icon:ic,_iconOpen:false}))}
                              style={{width:"100%",aspectRatio:"1",borderRadius:4,border:`1px solid ${newTypeForm.icon===ic?B.blue:B.border}`,background:newTypeForm.icon===ic?B.blueDim:B.surface,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16,color:newTypeForm.icon===ic?B.white:B.light}}>
                              {ic}
                            </div>
                          ))}
                        </div>
                      </div>}
                    </div>
                  </div>
                  {/* Save + Cancel */}
                  <div style={{display:"flex",gap:10,borderTop:`1px solid ${B.border}`,paddingTop:16}}>
                    <button onClick={addType} disabled={!newTypeForm.label.trim()} className="sm-btn sm-btn-primary"
                      style={{flex:1,background:newTypeForm.label.trim()?`linear-gradient(135deg,${B.blueLt} 0%,${B.blue} 100%)`:B.surface,color:newTypeForm.label.trim()?B.white:B.mid,border:"none",borderRadius:6,padding:"13px",fontSize:13,fontWeight:800,cursor:newTypeForm.label.trim()?"pointer":"not-allowed",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",boxShadow:newTypeForm.label.trim()?"0 4px 16px rgba(26,69,196,0.35)":"none"}}>
                      ✓ Save Program Type
                    </button>
                    <button onClick={()=>setNewTypeForm(null)} className="sm-btn"
                      style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:6,padding:"13px 20px",fontSize:13,cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>
                      Cancel
                    </button>
                  </div>
                </Card>
            }
          </>}

          {/* ── Type Editor ── */}
          {editType && <>
            <button onClick={()=>{setEditType(null);setEditTypeDraft(null);setEditQIdx(null);setEditQForm(null);setInstructionsError("");setUploadingInstructions(false);}} style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:3,padding:"5px 14px",cursor:"pointer",fontSize:11,fontFamily:"'Barlow',sans-serif",alignSelf:"flex-start"}}>
              ← All Types
            </button>

            {/* Type meta editor */}
            <Card>
              <SectionTitle>Program Type Settings</SectionTitle>
              {/* Program Type Name — full width */}
              <div style={{marginBottom:14}}>
                <FieldLabel required>Program Type Name</FieldLabel>
                <SInput value={editTypeDraft?.label||""} onChange={e=>setEditTypeDraft(d=>({...d,label:e.target.value}))} placeholder="e.g. Electrical Signage"/>
              </div>
              {/* Icon dropdown */}
              <div style={{marginBottom:14,position:"relative"}}>
                <FieldLabel>Icon</FieldLabel>
                <div onClick={()=>setEditTypeDraft(d=>({...d,_iconOpen:!d._iconOpen}))}
                  style={{display:"flex",alignItems:"center",gap:10,background:B.surface,border:`1px solid ${B.border}`,borderRadius:6,padding:"10px 14px",cursor:"pointer",userSelect:"none"}}>
                  <span style={{fontSize:18}}>{editTypeDraft?.icon||"◈"}</span>
                  <span style={{flex:1,fontFamily:"'Barlow',sans-serif",fontSize:13,color:B.light}}>Select an icon</span>
                  <span style={{color:B.muted,fontSize:11}}>{editTypeDraft?._iconOpen?"▲":"▼"}</span>
                </div>
                {editTypeDraft?._iconOpen&&<div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:100,background:B.card,border:`1px solid ${B.border}`,borderRadius:6,padding:12,marginTop:4,boxShadow:"0 8px 24px rgba(0,0,0,0.4)"}}>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(8,1fr)",gap:6}}>
                    {["◈","◼","▣","▤","◉","◎","⬡","⬢","▲","◆","✦","⚙","⬛","▪","◐","★"].map(ic=>(
                      <div key={ic} onClick={()=>setEditTypeDraft(d=>({...d,icon:ic,_iconOpen:false}))}
                        style={{width:"100%",aspectRatio:"1",borderRadius:4,border:`1px solid ${editTypeDraft?.icon===ic?B.blue:B.border}`,background:editTypeDraft?.icon===ic?B.blueDim:B.surface,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:16,color:editTypeDraft?.icon===ic?B.white:B.light}}>
                        {ic}
                      </div>
                    ))}
                  </div>
                </div>}
              </div>
              {/* Installation Instructions — multi-document upload (PDF / Word).
                  Files are uploaded immediately on selection to the installation-docs
                  bucket and appended to editTypeDraft.instructionsDocs. The array is
                  persisted on Save. Each entry tracks filename, upload date, and the
                  uploader's display name. */}
              <div style={{marginBottom:14}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:600,fontSize:12,color:B.white,letterSpacing:0.5,textTransform:"uppercase",marginBottom:6}}>
                  Installation Instructions (optional)
                </div>
                <div style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:14,display:"flex",flexDirection:"column",gap:10}}>
                  {/* Saved + freshly-uploaded documents */}
                  {(() => {
                    const docs = Array.isArray(editTypeDraft?.instructionsDocs) ? editTypeDraft.instructionsDocs : [];
                    if (docs.length === 0) return (
                      <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",fontStyle:"italic"}}>No documents attached yet.</div>
                    );
                    return (
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {docs.map((doc, i) => {
                          const dateStr = doc.uploaded_at
                            ? new Date(doc.uploaded_at).toLocaleDateString("en-CA", { dateStyle: "medium" })
                            : (doc._legacy ? "Legacy upload" : "—");
                          const byStr = doc.uploaded_by || (doc._legacy ? "Unknown" : "—");
                          return (
                            <div key={(doc.path||doc.url)+"_"+i} style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",background:"rgba(0,0,0,0.25)",border:`1px solid ${B.border}`,borderRadius:6,padding:"10px 12px"}}>
                              <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:2}}>
                                <a href={getViewableUrl(doc)} target="_blank" rel="noopener noreferrer"
                                  title={doc.filename || "Installation Instructions"}
                                  style={{fontSize:13,color:B.blueLt,textDecoration:"none",fontFamily:"'Barlow',sans-serif",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                                  onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"}
                                  onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>
                                  📄 {doc.filename || "Installation Instructions"}
                                </a>
                                <div style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>
                                  Uploaded {dateStr} · by {byStr}
                                </div>
                              </div>
                              <button type="button" onClick={()=>{
                                setEditTypeDraft(d=>({...(d||{}),instructionsDocs:(d?.instructionsDocs||[]).filter((_,j)=>j!==i)}));
                                setInstructionsError("");
                              }} disabled={uploadingInstructions}
                                style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:3,padding:"4px 12px",fontSize:11,cursor:uploadingInstructions?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase",opacity:uploadingInstructions?0.5:1}}>
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {/* Add document picker — uploads immediately on selection */}
                  <label style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,border:`1px dashed ${uploadingInstructions?B.blue:B.border}`,borderRadius:6,padding:"12px 14px",background:"rgba(0,0,0,0.25)",cursor:uploadingInstructions?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:12,letterSpacing:1.5,textTransform:"uppercase",color:B.blueLt,opacity:uploadingInstructions?0.85:1}}>
                    {uploadingInstructions
                      ? <><Spinner size={13} color={B.blueLt}/> Uploading…</>
                      : "+ Add Document"
                    }
                    <input type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      disabled={uploadingInstructions}
                      onChange={async e=>{
                        const f = e.target.files && e.target.files[0];
                        e.target.value = "";
                        if (!f) return;
                        // Fast client-side validation
                        const lower = (f.name||"").toLowerCase();
                        const ext = INSTALLATION_DOC_EXT_OK.find(x => lower.endsWith(x));
                        const mimeOk = INSTALLATION_DOC_MIME_OK.has(f.type) || f.type === "" || f.type === "application/octet-stream";
                        if (!ext || !mimeOk) { setInstructionsError("Only PDF or Word documents (.pdf, .doc, .docx) are allowed."); return; }
                        if (f.size > INSTALLATION_DOC_MAX_BYTES) { setInstructionsError(`File is ${(f.size/1024/1024).toFixed(1)} MB — limit is 25 MB.`); return; }
                        setInstructionsError("");
                        setUploadingInstructions(true);
                        try {
                          const uploaded = await uploadInstallationDoc(f, editTypeDraft?.label || editType.label);
                          const entry = {
                            url:         uploaded.url,
                            path:        uploaded.path,
                            filename:    uploaded.filename,
                            uploaded_at: new Date().toISOString(),
                            uploaded_by: user.name,
                          };
                          setEditTypeDraft(d=>({...(d||{}),instructionsDocs:[...((d?.instructionsDocs)||[]),entry]}));
                        } catch (err) {
                          setInstructionsError(err.message || "Upload failed");
                        } finally {
                          setUploadingInstructions(false);
                        }
                      }}
                      style={{display:"none"}}/>
                  </label>
                  {/* Error */}
                  {instructionsError && (
                    <div style={{fontSize:12,color:"#ff6b6b",fontFamily:"'Barlow',sans-serif",lineHeight:1.4}}>
                      {instructionsError}
                    </div>
                  )}
                  {/* Helper text */}
                  <div style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>
                    PDF or Word document, 25 MB max. Multiple documents allowed. Word docs open inline via Microsoft Office Online viewer; PDFs render natively.
                  </div>
                </div>
              </div>

              {/* Audit info */}
              {(editType.updatedBy||editType.createdBy)&&<div style={{marginTop:10,fontSize:11,color:B.mid,fontFamily:"'Barlow',sans-serif"}}>
                {editType.updatedBy?"Last updated by "+editType.updatedBy:"Created by "+editType.createdBy}
                {(editType.updatedAt||editType.createdAt)?" · "+(new Date(editType.updatedAt||editType.createdAt)).toLocaleDateString("en-CA",{dateStyle:"medium"}):""}
              </div>}
            </Card>

            {/* Question list */}
            <Card>
              <SectionTitle>Checklist Items ({editType.questions.length})</SectionTitle>
              {editType.questions.length===0&&<p style={{margin:"0 0 14px",fontSize:13,color:B.muted,fontFamily:"'Barlow',sans-serif"}}>No items yet. Add one below.</p>}
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                {editType.questions.map((q,i)=>(
                  <div key={q.id} style={{background:B.surface,borderRadius:4,border:`1px solid ${editQIdx===i?B.blue:B.border}`,padding:"10px 12px"}}>
                    {editQIdx===i && editQForm
                      ? <div>
                          <div style={{marginBottom:10}}>
                            <FieldLabel required>Item Text</FieldLabel>
                            <SInput value={editQForm.text} onChange={e=>setEditQForm(f=>({...f,text:e.target.value}))} placeholder="e.g. Were all panels secured? or: Ensure area is clear before proceeding."/>
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
                            <div>
                              <FieldLabel>Response Type</FieldLabel>
                              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                                {[["confirm","Yes/No/N/A"],["text","Text entry"],["both","Both"],["none","Instruction only"]].map(([val,lbl])=>(
                                  <label key={val} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
                                    <input type="radio" checked={editQForm.answerType===val} onChange={()=>setEditQForm(f=>({...f,answerType:val}))} style={{accentColor:B.blue}}/>
                                    {lbl}
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Photo</FieldLabel>
                              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                                {[["none","Not required"],["optional","Optional"],["required","Required"]].map(([val,lbl])=>(
                                  <label key={val} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
                                    <input type="radio" checked={editQForm.photoMode===val} onChange={()=>setEditQForm(f=>({...f,photoMode:val}))} style={{accentColor:B.blue}}/>
                                    {lbl}
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Comment Box</FieldLabel>
                              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                                {[["optional","Show (optional)"],["barcode","Barcode scan"],["hidden","Hide"]].map(([val,lbl])=>(
                                  <label key={val} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
                                    <input type="radio" checked={(editQForm.commentMode||"optional")===val} onChange={()=>setEditQForm(f=>({...f,commentMode:val}))} style={{accentColor:B.blue}}/>
                                    {lbl}
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                          {(editQForm.answerType==="text"||editQForm.answerType==="both")&&(
                            <div style={{marginBottom:10}}>
                              <FieldLabel>Placeholder text (optional)</FieldLabel>
                              <SInput value={editQForm.placeholder||""} onChange={e=>setEditQForm(f=>({...f,placeholder:e.target.value}))} placeholder="e.g. Enter serial number..."/>
                            </div>
                          )}
                          <div style={{display:"flex",gap:8}}>
                            <button onClick={saveQuestion} disabled={!editQForm.text?.trim()} className="sm-btn sm-btn-primary" style={{background:editQForm.text?.trim()?B.blue:B.surface,color:editQForm.text?.trim()?B.white:B.mid,border:"none",borderRadius:3,padding:"7px 16px",fontSize:11,fontWeight:700,cursor:editQForm.text?.trim()?"pointer":"not-allowed",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
                              Save
                            </button>
                            {btnSm("Cancel",()=>{setEditQIdx(null);setEditQForm(null);})}
                          </div>
                        </div>
                      : <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{display:"flex",flexDirection:"column",gap:3,marginRight:4}}>
                            <button onClick={()=>moveQuestion(i,-1)} disabled={i===0} style={{background:"transparent",border:"none",color:i===0?B.border:B.muted,cursor:i===0?"default":"pointer",fontSize:11,padding:"0 2px",lineHeight:1}}>▲</button>
                            <button onClick={()=>moveQuestion(i,1)} disabled={i===editType.questions.length-1} style={{background:"transparent",border:"none",color:i===editType.questions.length-1?B.border:B.muted,cursor:i===editType.questions.length-1?"default":"pointer",fontSize:11,padding:"0 2px",lineHeight:1}}>▼</button>
                          </div>
                          <span style={{flex:1,fontFamily:"'Barlow',sans-serif",fontSize:13,color:B.light}}>{q.text}</span>
                          <span style={{fontSize:10,color:B.mid,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:0.5}}>
                            {q.answerType==="none"?"instruction":q.answerType} · {q.photoMode}{(q.commentMode||"optional")==="optional"?" · comment":(q.commentMode)==="barcode"?" · barcode":""}
                          </span>
                          <button onClick={()=>{setEditQIdx(i);setEditQForm({text:q.text,answerType:q.answerType,photoMode:q.photoMode,commentMode:q.commentMode||"optional",placeholder:q.placeholder||""});}} className="sm-btn" style={{background:B.blueDim,color:B.blueLt,border:`1px solid ${B.blue}`,borderRadius:3,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif"}}>Edit</button>
                          {btnSm("✕",()=>deleteQuestion(i),{color:B.red,borderColor:B.red,padding:"4px 8px"},"sm-btn-danger")}
                        </div>
                    }
                  </div>
                ))}
              </div>
              {editQIdx!==null&&editQIdx===-1&&editQForm
                ? <div style={{background:B.black,borderRadius:4,border:`1px solid ${B.blue}`,padding:14}}>
                    <div style={{fontSize:11,color:B.blue,fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",marginBottom:10}}>New Checklist Item</div>
                    <div style={{marginBottom:10}}>
                      <FieldLabel required>Item Text</FieldLabel>
                      <SInput value={editQForm.text} onChange={e=>setEditQForm(f=>({...f,text:e.target.value}))} placeholder="e.g. Were all panels secured? or: Ensure area is clear before proceeding."/>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
                      <div>
                        <FieldLabel>Response Type</FieldLabel>
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          {[["confirm","Yes/No/N/A"],["text","Text entry"],["both","Both"],["none","Instruction only"]].map(([val,lbl])=>(
                            <label key={val} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
                              <input type="radio" checked={editQForm.answerType===val} onChange={()=>setEditQForm(f=>({...f,answerType:val}))} style={{accentColor:B.blue}}/>
                              {lbl}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <FieldLabel>Photo</FieldLabel>
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          {[["none","Not required"],["optional","Optional"],["required","Required"]].map(([val,lbl])=>(
                            <label key={val} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
                              <input type="radio" checked={editQForm.photoMode===val} onChange={()=>setEditQForm(f=>({...f,photoMode:val}))} style={{accentColor:B.blue}}/>
                              {lbl}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <FieldLabel>Comment Box</FieldLabel>
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          {[["optional","Show (optional)"],["hidden","Hide"]].map(([val,lbl])=>(
                            <label key={val} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12,color:B.light,fontFamily:"'Barlow',sans-serif"}}>
                              <input type="radio" checked={(editQForm.commentMode||"optional")===val} onChange={()=>setEditQForm(f=>({...f,commentMode:val}))} style={{accentColor:B.blue}}/>
                              {lbl}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    {(editQForm.answerType==="text"||editQForm.answerType==="both")&&(
                      <div style={{marginBottom:10}}>
                        <FieldLabel>Placeholder text (optional)</FieldLabel>
                        <SInput value={editQForm.placeholder||""} onChange={e=>setEditQForm(f=>({...f,placeholder:e.target.value}))} placeholder="e.g. Enter serial number..."/>
                      </div>
                    )}
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={saveQuestion} disabled={!editQForm.text?.trim()} className="sm-btn sm-btn-primary" style={{background:editQForm.text?.trim()?B.blue:B.surface,color:editQForm.text?.trim()?B.white:B.mid,border:"none",borderRadius:3,padding:"7px 16px",fontSize:11,fontWeight:700,cursor:editQForm.text?.trim()?"pointer":"not-allowed",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
                        Add Item
                      </button>
                      {btnSm("Cancel",()=>{setEditQIdx(null);setEditQForm(null);})}
                    </div>
                  </div>
                : editQIdx===null&&<button onClick={()=>{setEditQIdx(-1);setEditQForm({text:"",answerType:"confirm",photoMode:"none",commentMode:"optional",placeholder:""}); }} className="sm-btn" style={{background:"transparent",border:`1px dashed ${B.border}`,borderRadius:4,padding:"10px",width:"100%",fontSize:12,color:B.blue,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>
                    + Add Checklist Item
                  </button>
              }
            </Card>

            {/* ── Save Program Type Settings — bottom of page ── */}
            <div style={{borderTop:`1px solid ${B.border}`,paddingTop:16,display:"flex",gap:10}}>
              <button onClick={async ()=>{
                // Uploads happen on selection (immediate-upload UX), so the save
                // path is just a persist. The docs array on the draft is the
                // source of truth.
                const finalLabel = editTypeDraft?.label || editType.label;
                const docsArr    = Array.isArray(editTypeDraft?.instructionsDocs) ? editTypeDraft.instructionsDocs : [];
                try {
                  await syncEditType({
                    ...editType,
                    label: finalLabel,
                    icon:  editTypeDraft?.icon || editType.icon,
                    instructionsDocs: docsArr,
                  });
                } catch (err) {
                  setInstructionsError("Save failed: " + (err.message || "unknown error"));
                  return;
                }
                setInstructionsError("");
                setTypeSavedToast("Saved ✓");
                setTimeout(() => setTypeSavedToast(""), 2500);
              }} disabled={!editTypeDraft?.label?.trim() || uploadingInstructions} className="sm-btn sm-btn-primary"
                style={{flex:1,background:(!editTypeDraft?.label?.trim()||uploadingInstructions)?B.surface:`linear-gradient(135deg,${B.blueLt} 0%,${B.blue} 100%)`,color:(!editTypeDraft?.label?.trim()||uploadingInstructions)?B.mid:B.white,border:"none",borderRadius:6,padding:"14px",fontSize:13,fontWeight:800,cursor:(!editTypeDraft?.label?.trim()||uploadingInstructions)?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2,textTransform:"uppercase",boxShadow:(!editTypeDraft?.label?.trim()||uploadingInstructions)?"none":"0 4px 16px rgba(26,69,196,0.35)",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                {uploadingInstructions ? <><Spinner/> Uploading…</> : "✓ Save Program Type"}
              </button>
            </div>
          </>}
            </>
          }
          {/* Save confirmation toast — auto-clears via setTimeout in the save handler */}
          {typeSavedToast && (
            <div className="sm-toast-enter"
              style={{position:"fixed",bottom:28,right:28,background:B.glass,border:`1px solid rgba(39,174,96,0.4)`,borderRadius:8,padding:"12px 18px",boxShadow:B.shadow,zIndex:9999,display:"flex",alignItems:"center",gap:10,fontFamily:"'Barlow',sans-serif",fontSize:14,color:B.green,fontWeight:600,backdropFilter:"blur(8px)",pointerEvents:"none"}}>
              {typeSavedToast}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{maxWidth:700,margin:"0 auto",paddingBottom:40}}>
      {/* "You're on a roll" toast */}
      {rollToast && (
        <div className={`sm-toast-enter`}
          style={{position:"fixed",bottom:28,right:28,background:"#1c1c26",border:`1px solid ${B.border}`,borderRadius:10,padding:"13px 20px",boxShadow:"0 12px 40px rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",gap:10,fontFamily:"'Barlow',sans-serif",fontSize:14,color:B.white,fontWeight:600}}>
          <span style={{fontSize:20}}>🚀</span>
          <span>You're on a roll</span>
        </div>
      )}
      <div style={{background:B.dark,borderBottom:`1px solid ${B.border}`,padding:"20px 20px 16px",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
          {[{v:"records",l:"Records"},{v:"sros",l:"SROs"},{v:"manage",l:"System Config"},{v:"users",l:"Users"},{v:"reports",l:"◈ Reports"}].filter(({v})=>v==="records"||v==="reports"||(v==="manage"||v==="users"||v==="sros")?user.role==="super_admin":true).map(({v,l})=>(
            <button key={v} onClick={()=>setHoView(v)} className="sm-btn" style={{background:hoView===v?B.blue:"transparent",color:hoView===v?B.white:v==="reports"?B.blueLt:B.muted,border:`1px solid ${hoView===v?B.blue:v==="reports"?B.blueLt:B.border}`,borderRadius:4,padding:"6px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,textTransform:"uppercase"}}>
              {l}
            </button>
          ))}
        </div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.white,marginBottom:14}}>Installation Records</div>

        {/* Status filter tabs + export button */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
          {[
            {key:"all",     label:"All"},
            {key:"pending", label:"Pending"},
            {key:"approved",label:"Approved"},
          ].map(({key,label})=>{
            const sc = STATUS_CONFIG[key];
            const accent = sc ? sc.color : B.blue;
            const accentBorder = sc ? sc.border : B.blue;
            const active=filter===key;
            return (
              <button key={key} onClick={()=>setFilter(key)} className="sm-tab"
                style={{background:active?(key==="all"?B.blue:sc?.bg||B.blue):"transparent",color:active?B.white:(key==="all"?B.muted:accent),border:`1px solid ${active?(key==="all"?B.blue:accentBorder):(key==="all"?B.border:accentBorder)}`,borderRadius:3,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,position:"relative"}}>
                {label}
                {key==="requires_revisit"&&revisitCount>0&&<span style={{marginLeft:5,background:B.orange,color:B.white,borderRadius:10,padding:"1px 5px",fontSize:10}}>{revisitCount}</span>}
              </button>
            );
          })}
          <button
            onClick={handleExport} disabled={exportBusy} className="sm-btn sm-btn-primary"
            style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,background:B.blueDim,border:`1px solid ${B.blue}`,borderRadius:3,padding:"5px 13px",cursor:exportBusy?"not-allowed":"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:11,color:B.blueLt,letterSpacing:1,textTransform:"uppercase",whiteSpace:"nowrap"}}>
            {exportBusy ? <><Spinner size={11} color={B.blueLt}/> Exporting…</> : "↓ Export .xlsx"}
          </button>
        </div>

        {/* Date filter */}
        <div style={{borderTop:`1px solid ${B.border}`,paddingTop:14}}>
          {/* Quick presets + month navigator */}
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
            <span style={{fontSize:10,color:B.mid,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginRight:2}}>Period</span>
            {[{key:"all",label:"All Time"},{key:"thisMonth",label:"This Month"},{key:"lastMonth",label:"Last Month"}].map(({key,label})=>(
              <button key={key} onClick={()=>applyPreset(key)} className="sm-tab"
                style={{background:datePreset===key?B.blue:"transparent",color:datePreset===key?B.white:B.muted,border:`1px solid ${datePreset===key?B.blue:B.border}`,borderRadius:3,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>
                {label}
              </button>
            ))}
            {/* Month navigator */}
            <div style={{display:"flex",alignItems:"center",marginLeft:4,border:`1px solid ${datePreset==="nav"?B.blue:B.border}`,borderRadius:3,overflow:"hidden"}}>
              <button onClick={navPrev} className="sm-btn"
                style={{background:"transparent",border:"none",color:B.muted,padding:"5px 10px",cursor:"pointer",fontSize:13,lineHeight:"1",display:"flex",alignItems:"center"}}>‹</button>
              <button onClick={()=>applyNav(navYear,navMonth)} className="sm-tab"
                style={{background:datePreset==="nav"?B.blueDim:"transparent",border:"none",borderLeft:`1px solid ${B.border}`,borderRight:`1px solid ${B.border}`,color:datePreset==="nav"?B.white:B.light,padding:"5px 12px",cursor:"pointer",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:11,letterSpacing:1,whiteSpace:"nowrap"}}>
                {MONTH_NAMES[navMonth]} {navYear}
              </button>
              <button onClick={navNext} className="sm-btn"
                style={{background:"transparent",border:"none",color:atCurrentMonth?B.border:B.muted,padding:"5px 10px",cursor:atCurrentMonth?"default":"pointer",fontSize:13,lineHeight:"1",display:"flex",alignItems:"center"}}>›</button>
            </div>
          </div>

          {/* Custom date range inputs */}
          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,flexWrap:"wrap"}}>
            <span style={{fontSize:10,color:B.mid,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase"}}>Custom</span>
            <input type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setDatePreset("custom");}}
              style={{background:B.surface,border:`1px solid ${datePreset==="custom"&&dateFrom?B.blue:B.border}`,borderRadius:3,color:dateFrom?B.light:B.mid,fontSize:12,padding:"4px 8px",fontFamily:"'Barlow',sans-serif",cursor:"pointer",outline:"none"}}/>
            <span style={{color:B.mid,fontSize:11,fontFamily:"'Barlow',sans-serif"}}>to</span>
            <input type="date" value={dateTo} onChange={e=>{setDateTo(e.target.value);setDatePreset("custom");}}
              style={{background:B.surface,border:`1px solid ${datePreset==="custom"&&dateTo?B.blue:B.border}`,borderRadius:3,color:dateTo?B.light:B.mid,fontSize:12,padding:"4px 8px",fontFamily:"'Barlow',sans-serif",cursor:"pointer",outline:"none"}}/>
            {(dateFrom||dateTo)&&(
              <button onClick={clearDates} className="sm-btn"
                style={{background:"transparent",border:`1px solid ${B.border}`,borderRadius:3,color:B.muted,fontSize:11,padding:"4px 10px",cursor:"pointer",fontFamily:"'Barlow',sans-serif"}}>
                × Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{padding:"0 16px",display:"flex",flexDirection:"column",gap:12}}>

        {/* ── Stats summary panel ───────────────────────────────────────────── */}
        {dashLoading ? <SkeletonStatCards/> : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(75px,1fr))",gap:6}}>
          {[
            { key:"inProgress",      label:"In Progress",  color:B.green,                              bg:"rgba(39,174,96,0.08)",  border:"rgba(39,174,96,0.35)", count:activeSessions.length },
            { key:"pending",         label:"Pending",       color:STATUS_CONFIG.pending.color,          bg:STATUS_CONFIG.pending.bg,          border:STATUS_CONFIG.pending.border,          count:statCounts.pending },
            { key:"approved",        label:"Approved",      color:STATUS_CONFIG.approved.color,         bg:STATUS_CONFIG.approved.bg,         border:STATUS_CONFIG.approved.border,         count:statCounts.approved },
            { key:"rejected",        label:"Rejected",      color:STATUS_CONFIG.rejected.color,         bg:STATUS_CONFIG.rejected.bg,         border:STATUS_CONFIG.rejected.border,         count:statCounts.rejected },
            { key:"requires_revisit",label:"Revisit",       color:STATUS_CONFIG.requires_revisit.color, bg:STATUS_CONFIG.requires_revisit.bg, border:STATUS_CONFIG.requires_revisit.border, count:statCounts.requires_revisit },
            { key:"aborted",         label:"Aborted",       color:STATUS_CONFIG.aborted.color,          bg:STATUS_CONFIG.aborted.bg,          border:STATUS_CONFIG.aborted.border,          count:statCounts.aborted },
            { key:"incomplete",      label:"Incomplete",    color:STATUS_CONFIG.incomplete.color,        bg:STATUS_CONFIG.incomplete.bg,        border:STATUS_CONFIG.incomplete.border,        count:statCounts.incomplete },
          ].map(card => {
            const isActive = card.key==="inProgress" ? showLive : filter===card.key;
            return (
              <button key={card.key}
                className={`sm-btn sm-tab${card.key==="inProgress"?" sm-badge-pulse-green":""}`}
                onClick={()=>{ if(card.key==="inProgress"){setShowLive(v=>!v);}else{setFilter(card.key);setShowLive(false);} }}
                style={{background:isActive?card.bg:B.card,border:`1px solid ${isActive?card.border:B.border}`,borderRadius:6,padding:"12px 8px",textAlign:"center",cursor:"pointer",boxSizing:"border-box"}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:card.color,lineHeight:1}}>{card.count}</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:9,color:isActive?card.color:B.muted,letterSpacing:1.5,textTransform:"uppercase",marginTop:4,lineHeight:1.3}}>{card.label}</div>
              </button>
            );
          })}
        </div>}
        <div style={{textAlign:"right",fontSize:11,color:B.mid,fontFamily:"'Barlow',sans-serif",marginTop:-6}}>
          Auto-refreshes every 60 s · Last updated {lastRefresh.toLocaleTimeString("en-CA",{hour:"2-digit",minute:"2-digit"})}
        </div>

        {/* ── Live in-progress tracking panel ──────────────────────────────── */}
        {showLive&&(
          <div style={{background:B.card,border:`1px solid rgba(39,174,96,0.4)`,borderRadius:6,overflow:"hidden"}}>
            <div style={{padding:"10px 14px",borderBottom:`1px solid ${B.border}`,display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:B.green,flexShrink:0}}/>
              <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:13,color:B.green,letterSpacing:1.5,textTransform:"uppercase"}}>Live — Installers On Site</span>
              <span style={{fontSize:11,color:B.muted,fontFamily:"'Barlow',sans-serif",marginLeft:"auto"}}>{activeSessions.length} active</span>
            </div>
            <div style={{padding:"0 14px"}}>
              {activeSessions.length===0
                ?<div style={{padding:"20px 0",fontSize:13,color:B.muted,fontFamily:"'Barlow',sans-serif",textAlign:"center"}}>No installers are currently clocked in.</div>
                :activeSessions.map(s=>{
                  const initials=s.installerName.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
                  return(
                    <div key={s.sessionId} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`1px solid ${B.border}`}}>
                      <div style={{width:34,height:34,borderRadius:"50%",background:"rgba(39,174,96,0.12)",border:`1px solid rgba(39,174,96,0.3)`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:12,color:B.green}}>{initials}</span>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:600,fontSize:13,color:B.white}}>{s.installerName}</div>
                        <div style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.light,marginTop:1}}>{s.sroNumber} · {s.storeName}</div>
                        <div style={{fontFamily:"'Barlow',sans-serif",fontSize:11,color:B.muted,marginTop:1}}>{s.typeLabel}</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,color:B.green,letterSpacing:-0.5}}>{elapsed(s.startTime,null)||"0h 0m"}</div>
                        <div style={{fontSize:10,color:B.muted,fontFamily:"'Barlow',sans-serif",letterSpacing:1,textTransform:"uppercase",marginTop:2}}>On Site</div>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
        )}

        {revisitCount>0&&(
          <div style={{background:B.orangeDim,border:`1px solid ${B.orange}`,borderRadius:6,padding:"12px 16px",display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:20,color:B.orange,flexShrink:0}}>⚠</span>
            <div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:14,color:B.orange,letterSpacing:0.5}}>
                {revisitCount} Job{revisitCount>1?"s":""} Require a Warranty Revisit
              </div>
              <div style={{fontSize:12,color:B.muted,fontFamily:"'Barlow',sans-serif",marginTop:2}}>
                These jobs were rejected and not resolved within 48 hours. Schedule revisits immediately.
              </div>
            </div>
          </div>
        )}

        <div style={{position:"relative"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by SRO#, RL#, store, or installer..."
            style={{width:"100%",padding:"12px 16px 12px 40px",borderRadius:4,border:`1px solid ${B.border}`,background:B.card,color:B.white,fontSize:13,boxSizing:"border-box",fontFamily:"'Barlow',sans-serif",outline:"none"}}/>
          <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:B.muted,fontSize:14}}>⌕</span>
        </div>
        {dashLoading
          ? Array.from({length:5}).map((_,i)=><SkeletonJobRow key={i}/>)
          : filtered.length===0
          ?<div style={{textAlign:"center",padding:"60px 24px",color:B.mid}}>
            <div style={{fontSize:40,marginBottom:12,opacity:0.3}}>◫</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,letterSpacing:1}}>{jobs.length===0?"No jobs submitted yet":"No results found"}</div>
          </div>
          :filtered.map(job=>{
            const snap=job.typeSnapshot||null;
            const total=snap?.questions.length||0;
            const done=snap?snap.questions.filter(q=>{const a=job.answers?.[q.id]||{};return q.answerType==="confirm"?!!a.confirmed:q.answerType==="text"?!!a.value?.trim():(!!a.confirmed&&!!a.value?.trim());}).length:0;
            const photoCount=(job.photos?.length||0)+(snap?snap.questions.reduce((s,q)=>s+(job.answers?.[q.id]?.photos?.length||0),0):0);
            const jobSc = STATUS_CONFIG[job.status];
            const jobAccent = jobSc ? jobSc.border : B.border;
            const hasAccentBorder = job.status==="requires_revisit"||job.status==="aborted"||job.status==="incomplete";
            return (
              <div key={job.id} onClick={()=>setSelected(job)} className="sm-row"
                style={{background:B.card,borderRadius:10,padding:18,border:`1px solid ${hasAccentBorder?jobAccent:B.border}`,cursor:"pointer",boxShadow:B.shadowSm,borderLeft:hasAccentBorder?`3px solid ${jobAccent}`:undefined}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,color:B.white}}>
                      {job.sroNumber} {job.rlNumber&&<span style={{fontSize:13,fontWeight:400,color:B.muted}}>· RL# {job.rlNumber}</span>}
                    </div>
                    <div style={{fontFamily:"'Barlow',sans-serif",fontSize:13,color:B.light,marginTop:2}}>{job.storeName}</div>
                  </div>
                  <StatusBadge status={job.status}/>
                </div>
                <div style={{display:"flex",gap:14,fontSize:12,color:B.muted,flexWrap:"wrap",fontFamily:"'Barlow',sans-serif",alignItems:"center"}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                    ↳ {job.installerName}
                    {onFireInstallers.has(job.installerName) && <span title="5+ jobs today" style={{fontSize:13,lineHeight:1}}>🔥</span>}
                  </span>
                  <span>{snap?.label||"—"}</span>
                  <span>{fmt(job.submittedAt)}</span>
                  <span style={{color:done===total&&total>0?B.green:B.muted}}>✓ {done}/{total}</span>
                  {photoCount>0&&<span style={{color:B.blueLt}}>⬡ {photoCount} photos</span>}
                  {job.signatureData&&<span style={{color:B.green}}>✍ Signed</span>}
                  {(() => {
                    // Flag jobs missing clock-in or submit coordinates so HO can spot
                    // them at a glance. The capture is best-effort (see captureLocation).
                    const missing = [];
                    if (!job.clockInLocation) missing.push("clock-in");
                    if (!job.submitLocation)  missing.push("submit");
                    if (missing.length === 0) return null;
                    const tip = `No GPS coordinates captured for: ${missing.join(", ")}`;
                    return (
                      <span title={tip} style={{display:"inline-flex",alignItems:"center",gap:3,color:B.orange,background:"rgba(230,126,34,0.10)",border:`1px solid ${B.orange}`,borderRadius:3,padding:"1px 7px",fontSize:10,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif"}}>
                        📍 No GPS
                      </span>
                    );
                  })()}
                </div>
              </div>
            );
          })
        }
      </div>
    </div>
  );
}

// ── WELCOME SCREEN ────────────────────────────────────────────────────────────
function WelcomeScreen({ user, onBeginJob, onMyHistory, onDashboard, draft, installationTypes, onResumeDraft }) {
  const firstName = user.name.split(" ")[0];
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? "Good Morning" : "Good Afternoon";
  const isAdmin   = user.role === "admin" || user.role === "super_admin";
  const initials  = user.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  // Active draft slots: clocked in but not yet submitted, installer only
  const activeDraftSlots = !isAdmin && draft?.slots
    ? draft.slots.filter(s => s.form.startTime && !s.done)
    : [];

  const primaryBtn = {
    width:"100%", background:B.blue, color:B.onAccent, border:"none", borderRadius:8,
    padding:"15px 24px", cursor:"pointer", fontFamily:"'Barlow',sans-serif",
    fontWeight:700, fontSize:15, letterSpacing:0.3,
  };
  const ghostBtn = {
    width:"100%", background:"transparent", color:B.light, border:`1px solid ${B.border}`,
    borderRadius:8, padding:"15px 24px", cursor:"pointer", fontFamily:"'Barlow',sans-serif",
    fontWeight:600, fontSize:15, letterSpacing:0.3,
  };

  const wfog = (cls, top, left, w, h, color, blur, delay) => (
    <div className={cls} style={{
      position:"absolute", width:w, height:h, top, left,
      background:`radial-gradient(ellipse, ${color} 0%, transparent 70%)`,
      filter:`blur(${blur}px)`, animationDelay:delay, willChange:"transform",
      pointerEvents:"none",
    }}/>
  );

  // Hero aesthetic differs between themes. In dark mode we render a deep-navy
  // backdrop with brand-blue fog and a tight vignette — the "wow" moment. In
  // light mode that whole stack would either turn into dark-on-dark text
  // (`B.white` flips to near-black) or wash out into mud. Instead we render a
  // clean light page with much softer blue accents so the welcome still has
  // brand presence without burying the headline.
  const isLight = getActiveTheme() === "light";
  return (
    <div style={{position:"relative",overflow:"hidden",minHeight:"calc(100vh - 57px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"40px 20px",background:isLight ? B.black : "#05050d"}}>

      {/* ── Fog layer — dark theme only; light theme uses a single soft tint ── */}
      <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
        {isLight ? (
          <>
            {wfog("sm-fog1",  "5%",   "-10%",  "70vw", "60vh", "rgba(37,80,212,0.10)",  60, "0s"  )}
            {wfog("sm-fog2",  "55%",  "30%",   "60vw", "55vh", "rgba(17,52,166,0.08)",  72, "-4s" )}
          </>
        ) : (
          <>
            {wfog("sm-fog1",  "0%",   "-10%",  "75vw", "68vh", "rgba(17,52,166,0.42)",  36, "0s"  )}
            {wfog("sm-fog2",  "40%",  "50%",   "68vw", "60vh", "rgba(37,80,212,0.35)",  44, "-4s" )}
            {wfog("sm-fog3",  "55%",  "2%",    "60vw", "54vh", "rgba(13,37,128,0.40)",  40, "-7s" )}
            {wfog("sm-fog4",  "-10%", "38%",   "52vw", "48vh", "rgba(26,69,196,0.30)",  30, "-2s" )}
            {wfog("sm-fog1",  "20%",  "22%",   "55vw", "60vh", "rgba(8,16,60,0.45)",    52, "-10s")}
          </>
        )}
      </div>

      {/* ── Edge vignette — dark only. In light mode the soft blue tint above
              provides enough atmosphere; a vignette would muddy the page. ── */}
      {!isLight && (
        <div style={{position:"absolute",inset:0,pointerEvents:"none",background:"radial-gradient(ellipse at 50% 50%, transparent 35%, rgba(3,3,10,0.80) 100%)"}}/>
      )}

      {/* ── Content ── */}
      <div style={{position:"relative",zIndex:10,maxWidth:380,width:"100%",textAlign:"center"}}>
        <div style={{width:76,height:76,borderRadius:"50%",background:`linear-gradient(135deg,${B.blueLt} 0%,${B.blue} 100%)`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 28px",boxShadow:"0 4px 28px rgba(26,69,196,0.45)"}}>
          <span style={{color:B.onAccent,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:28}}>{initials}</span>
        </div>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:40,color:B.white,letterSpacing:0.5,lineHeight:1.1,marginBottom:12}}>
          {greeting},<br/>{firstName}.
        </div>
        <div style={{fontFamily:"'Barlow',sans-serif",fontWeight:400,fontSize:16,color:B.muted,marginBottom:activeDraftSlots.length > 0 ? 20 : 44}}>
          {isAdmin ? "Ready to dive in?" : "Ready to get started?"}
        </div>
        {activeDraftSlots.length > 0 && (
          <div style={{marginBottom:20,textAlign:"left"}}>
            <div style={{fontSize:10,color:B.orange,letterSpacing:2,textTransform:"uppercase",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,marginBottom:10}}>⬤ In Progress</div>
            {activeDraftSlots.map((s, i) => {
              const typeLabel = installationTypes?.find(t => t.id === s.form.installationTypeId)?.label || "—";
              return (
                <div key={i} onClick={onResumeDraft} className="sm-clickable"
                  style={{background:"rgba(230,126,34,0.08)",border:`1px solid rgba(230,126,34,0.35)`,borderRadius:8,padding:"14px 16px",marginBottom:8,cursor:"pointer",textAlign:"left"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:16,color:B.white,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {s.form.sroNumber || "Untitled Job"}
                      </div>
                      <div style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.muted,marginTop:2}}>
                        {s.form.storeName || "—"} · {typeLabel}
                      </div>
                    </div>
                    <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:11,color:B.orange,letterSpacing:1,textTransform:"uppercase",flexShrink:0,whiteSpace:"nowrap"}}>↺ Resume</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {isAdmin
          ? <button onClick={onDashboard} className="sm-btn" style={primaryBtn}>Go to Dashboard</button>
          : <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <button onClick={onBeginJob}  className="sm-btn" style={primaryBtn}>Begin a Job</button>
              <button onClick={onMyHistory} className="sm-btn" style={ghostBtn}>My Job History</button>
            </div>
        }
      </div>
    </div>
  );
}

// ── LOGOUT CONFIRM MODAL ──────────────────────────────────────────────────────
function LogoutConfirmModal({ hasActiveWork, onConfirm, onCancel }) {
  const primaryBtn = {
    flex:1, background:`linear-gradient(135deg,${B.blueLt} 0%,${B.blue} 100%)`,
    color:B.white, border:"none", borderRadius:6, padding:"13px 20px",
    fontSize:13, fontWeight:800, cursor:"pointer",
    fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1.5,
    textTransform:"uppercase", boxShadow:"0 4px 16px rgba(26,69,196,0.4)",
  };
  const dangerBtn = {
    flex:1, background:"transparent", color:B.red,
    border:`1px solid rgba(192,57,43,0.45)`, borderRadius:6, padding:"13px 20px",
    fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Barlow',sans-serif",
  };
  return (
    <div
      onClick={onCancel}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.72)",backdropFilter:"blur(6px)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div
        onClick={e => e.stopPropagation()}
        style={{background:B.card,border:`1px solid ${hasActiveWork ? B.orange : B.border}`,borderRadius:12,padding:"32px 28px",maxWidth:420,width:"100%",boxShadow:"0 24px 80px rgba(0,0,0,0.7),0 4px 16px rgba(0,0,0,0.5)",textAlign:"center"}}>
        {hasActiveWork ? (
          <>
            <div style={{width:52,height:52,borderRadius:"50%",background:"rgba(230,126,34,0.14)",border:`2px solid ${B.orange}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,margin:"0 auto 20px"}}>⚠</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.orange,letterSpacing:0.5,marginBottom:12}}>Active Job in Progress</div>
            <div style={{fontFamily:"'Barlow',sans-serif",fontSize:14,color:B.light,lineHeight:1.65,marginBottom:28}}>
              You have an active job in progress. If you sign out now your progress will be saved as a draft, but you will remain clocked in to the SRO until the job is marked as Completed, Aborted, or Incomplete on site. Are you sure you want to sign out?
            </div>
          </>
        ) : (
          <>
            <div style={{width:52,height:52,borderRadius:"50%",background:"rgba(192,57,43,0.10)",border:`2px solid rgba(192,57,43,0.35)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,color:B.red,margin:"0 auto 20px",fontWeight:700}}>✕</div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:B.white,letterSpacing:0.5,marginBottom:12}}>Sign Out?</div>
            <div style={{fontFamily:"'Barlow',sans-serif",fontSize:14,color:B.muted,lineHeight:1.65,marginBottom:28}}>
              Are you sure you want to log out?
            </div>
          </>
        )}
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <button onClick={onCancel} className="sm-btn sm-btn-primary" style={primaryBtn}>
            {hasActiveWork ? "Stay on Job" : "Cancel"}
          </button>
          <button onClick={onConfirm} className="sm-btn sm-btn-danger" style={dangerBtn}>
            Yes, Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ROOT ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [appPhase,setAppPhase]                   = useState("splash"); // "splash" | "app"
  const [blurState,setBlurState]                 = useState(null);     // null | "splash-exit" | "login-enter" | "login-exit" | "welcome-enter"
  const [currentUser,setCurrentUser]             = useState(null);
  const [showWelcome,setShowWelcome]             = useState(false);
  const [installerInitView,setInstallerInitView] = useState(null); // null | "list" — passed to InstallerView on mount
  const [jobs,setJobs]                           = useState([]);
  const [installationTypes,setInstallationTypes] = useState(INITIAL_INSTALLATION_TYPES);
  const [services,setServices]                   = useState([]);
  const [users,setUsers]                         = useState(INITIAL_USERS); // replaced by Supabase fetch below
  const [viewKey,setViewKey]                     = useState(0);
  const [activeSessions,setActiveSessions]       = useState([]); // installers clocked in but not submitted
  const [drafts,setDrafts]                       = useState({});        // installer draft auto-save
  const [navState,setNavState]                   = useState({ canGoBack:false, onBack:null });
  const [logoutConfirm,setLogoutConfirm]         = useState(false);
  const [installerInitSlots,setInstallerInitSlots]             = useState(null);
  const [installerInitActiveSlot,setInstallerInitActiveSlot]   = useState(0);
  // Theme — initialised from localStorage via getActiveTheme() so first paint
  // matches what was previously saved.
  const [theme,setTheme]                         = useState(getActiveTheme());
  // CRITICAL: setActiveTheme mutates the `_activeTheme` ref that the B Proxy
  // reads from. It MUST be called BEFORE React renders, otherwise the first
  // render after a toggle reads stale colors (useEffect runs after commit). So
  // we apply it synchronously inside the toggle handler — and also wrap with a
  // useLayoutEffect for safety in case theme is updated by something else.
  useLayoutEffect(() => { setActiveTheme(theme); }, [theme]);
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setActiveTheme(next);            // ← mutate palette BEFORE React renders
    setTheme(next);                   // ← trigger re-render — now sees new B.*
  };

  // ── Supabase: load users on app mount ────────────────────────────────────────
  // Replaces the hardcoded INITIAL_USERS array.  Falls back to INITIAL_USERS if
  // the fetch fails so the app remains usable without a network connection.
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .order("id", { ascending: true });
      if (error) {
        console.error("[Supabase] load users:", error.message);
        return; // keep INITIAL_USERS already in state
      }
      if (data?.length) setUsers(data.map(rowToUser));
    })();
  }, []);

  // ── Supabase: load installation types after login ───────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      const { data, error } = await supabase
        .from("installation_types")
        .select("*")
        .eq("active", true)
        .order("sort_order", { ascending: true });
      if (error) { console.error("[Supabase] load installation_types:", error.message); return; }
      if (data?.length) setInstallationTypes(data.map(rowToInstallationType));
    })();
  }, [currentUser?.id]);

  // ── Supabase: load services after login ──────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      try { const data = await dataService.loadServices(); setServices(data); }
      catch (e) { console.error("[Supabase] load services:", e.message); }
    })();
  }, [currentUser?.id]);

  // ── Supabase: load jobs after login ──────────────────────────────────────────
  // Runs when currentUser is set so the Supabase Auth session is established
  // and RLS policies can correctly identify the user's role.
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select("*")
        .order("submitted_at", { ascending: false });
      if (error) {
        console.error("[Supabase] load jobs:", error.message);
        return;
      }
      if (data) setJobs(data.map(rowToJob));
    })();
  }, [currentUser?.id]);

  const saveDraft   = useCallback((userId, slots, activeSlot) => {
    setDrafts(p => ({...p, [userId]: { slots, activeSlot, savedAt: new Date().toISOString() }}));
  }, []);
  const registerNav = useCallback((canGoBack, onBack) => {
    setNavState({ canGoBack, onBack });
  }, []);
  const goHome = useCallback(() => {
    setShowWelcome(true);
    setNavState({ canGoBack:false, onBack:null });
  }, []);
  const resumeDraft = userId => {
    const draft = drafts[userId];
    if (!draft) return;
    setInstallerInitSlots(draft.slots);
    setInstallerInitActiveSlot(draft.activeSlot || 0);
    setInstallerInitView(null);
    setViewKey(k => k+1);
    setShowWelcome(false);
  };
  // The InstallerView unmount effect saves any active draft automatically when currentUser is cleared.
  const handleSignOut = () => {
    setCurrentUser(null);
    setLogoutConfirm(false);
    setShowWelcome(false);
    setNavState({ canGoBack:false, onBack:null });
  };

  // Auto-escalate rejected jobs to requires_revisit after 48 hours.
  //
  // TODO (backend migration): Replace this entire client-side polling block with
  // a server-side scheduled job (cron / pg_cron / Cloud Scheduler) that calls:
  //   PATCH /api/jobs/escalate-overdue
  // The server updates all qualifying jobs atomically, then the client can poll
  // GET /api/jobs or use a WebSocket push to refresh.  The setJobs updater
  // pattern below is a prototype approximation of that server-side transition.
  useEffect(()=>{
    const check = () => {
      const now = Date.now();
      const escalated = [];
      setJobs(prev => {
        let changed = false;
        const next = prev.map(j => {
          if (j.status !== "rejected" || !j.rejectedAt) return j;
          if ((now - new Date(j.rejectedAt).getTime()) < FORTY_EIGHT_HOURS) return j;
          changed = true;
          // Mirrors dataService.escalateJob() — kept synchronous here because
          // React state updaters cannot be async.  When the backend handles this
          // transition, remove the computation below and replace with a refetch.
          const ts = new Date().toISOString();
          const updated = { ...j, status:"requires_revisit", revisitFlaggedAt:ts,
            statusHistory:[...(j.statusHistory||[]), { status:"requires_revisit", timestamp:ts, actor:"System (auto-escalated after 48h)" }],
            auditLog:[...(j.auditLog||[]), { action:"job_requires_revisit", actor:"System", timestamp:ts, note:"Auto-escalated after 48h without resolution" }] };
          escalated.push(updated);
          return updated;
        });
        return changed ? next : prev;
      });
      // Fire notifications after setJobs (outside the updater to avoid stale closure).
      escalated.forEach(j => notifyJobRevisit(j, users));
    };
    check();
    const id = setInterval(check, 60000); // check every minute
    return () => clearInterval(id);
  },[users]);

  // Blur-dissolve: Splash → Login
  const handleEnterPlatform = () => {
    setBlurState("splash-exit");
    setTimeout(() => {
      setAppPhase("app");
      setBlurState("login-enter");
      setTimeout(() => setBlurState(null), 420);
    }, 340);
  };

  // Blur-dissolve: Login → Welcome
  const handleLogin = user => {
    setBlurState("login-exit");
    setTimeout(() => {
      setCurrentUser(user);
      setShowWelcome(true);
      setBlurState("welcome-enter");
      setTimeout(() => setBlurState(null), 420);
    }, 340);
  };

  if (appPhase === "splash") return <BlurPage isExiting={blurState==="splash-exit"}><SplashScreen onEnter={handleEnterPlatform}/></BlurPage>;
  if (!currentUser) return <BlurPage isEntering={blurState==="login-enter"} isExiting={blurState==="login-exit"}><LoginScreen onLogin={handleLogin} users={users}/></BlurPage>;
  return (
    <div style={{minHeight:"100vh",background:B.black,paddingBottom:!showWelcome?68:0}}>
      <div style={{background:`linear-gradient(180deg, ${B.dark} 0%, ${B.navGradStop} 100%)`,borderBottom:`1px solid ${B.border}`,position:"sticky",top:0,zIndex:100,boxShadow:B.shadowSm}}>
        <div style={{maxWidth:700,margin:"0 auto",padding:"0 16px",display:"flex",alignItems:"center",justifyContent:"space-between",height:56}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {navState.canGoBack && !showWelcome && (
              <button onClick={navState.onBack} className="sm-btn" style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:4,padding:"5px 12px",fontSize:11,fontFamily:"'Barlow',sans-serif",flexShrink:0}}>← Back</button>
            )}
            <div onClick={goHome} style={{cursor:"pointer"}}><LogoDisplay height={22}/></div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {!showWelcome && (
              <button onClick={goHome} className="sm-btn" title="Home" style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:4,padding:"5px 10px",fontSize:14,fontFamily:"'Barlow',sans-serif"}}>⌂</button>
            )}
            <div style={{width:30,height:30,borderRadius:"50%",background:`linear-gradient(135deg, ${B.blueLt} 0%, ${B.blue} 100%)`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 2px 8px rgba(26,69,196,0.4)"}}>
              <span style={{color:B.onAccent,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:11}}>{currentUser.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}</span>
            </div>
            <span style={{fontFamily:"'Barlow',sans-serif",fontSize:12,color:B.light,fontWeight:600,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{currentUser.name}</span>
            <button
              onClick={toggleTheme}
              className="sm-btn"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:4,padding:"5px 10px",cursor:"pointer",fontSize:14,fontFamily:"'Barlow',sans-serif",lineHeight:1}}>
              {theme === "dark" ? "☀" : "☾"}
            </button>
            <button onClick={()=>setLogoutConfirm(true)} className="sm-btn" style={{background:"transparent",color:B.muted,border:`1px solid ${B.border}`,borderRadius:4,padding:"5px 13px",cursor:"pointer",fontSize:11,fontFamily:"'Barlow',sans-serif"}}>Sign Out</button>
          </div>
        </div>
      </div>
      {showWelcome
        ? <BlurPage isEntering={blurState==="welcome-enter"}>
            <WelcomeScreen
              user={currentUser}
              draft={drafts[currentUser.id]}
              installationTypes={installationTypes}
              onResumeDraft={()=>resumeDraft(currentUser.id)}
              onBeginJob={()=>{ setInstallerInitSlots(null); setInstallerInitActiveSlot(0); setInstallerInitView(null); setViewKey(k=>k+1); setShowWelcome(false); }}
              onMyHistory={()=>{ setInstallerInitSlots(null); setInstallerInitActiveSlot(0); setInstallerInitView("list"); setViewKey(k=>k+1); setShowWelcome(false); }}
              onDashboard={()=>setShowWelcome(false)}
            />
          </BlurPage>
        : currentUser.role==="admin" || currentUser.role==="super_admin"
          ?<HeadOfficeView key={viewKey} jobs={jobs} setJobs={setJobs} user={currentUser} installationTypes={installationTypes} setInstallationTypes={setInstallationTypes} services={services} setServices={setServices} users={users} setUsers={setUsers} activeSessions={activeSessions} onRegisterNav={registerNav}/>
          :<InstallerView  key={viewKey} jobs={jobs} setJobs={setJobs} user={currentUser} installationTypes={installationTypes} users={users} services={services} setActiveSessions={setActiveSessions} defaultHistView={installerInitView} initialSlots={installerInitSlots} initialActiveSlot={installerInitActiveSlot} onSaveDraft={saveDraft} onRegisterNav={registerNav}/>
      }
      {!showWelcome && (
        <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:200,background:`linear-gradient(180deg,${B.dark} 0%,#09090f 100%)`,borderTop:`1px solid ${B.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 20px",boxShadow:"0 -4px 20px rgba(0,0,0,0.5)"}}>
          <div style={{flex:1}}>
            {navState.canGoBack && (
              <button onClick={navState.onBack} className="sm-btn" style={{background:"transparent",color:B.light,border:`1px solid ${B.border}`,borderRadius:5,padding:"8px 18px",fontSize:12,fontFamily:"'Barlow',sans-serif",fontWeight:600}}>← Back</button>
            )}
          </div>
          <button onClick={goHome} className="sm-btn" style={{background:"transparent",color:B.light,border:`1px solid ${B.border}`,borderRadius:5,padding:"8px 18px",fontSize:12,fontFamily:"'Barlow',sans-serif",fontWeight:600}}>⌂ Home</button>
        </div>
      )}
      {logoutConfirm && (() => {
        const installerHasActiveWork = currentUser.role === "installer" && (
          activeSessions.some(s => s.installerUserId === currentUser.id) ||
          !!(drafts[currentUser.id]?.slots?.some(s => s.form.startTime && !s.done))
        );
        return (
          <LogoutConfirmModal
            hasActiveWork={installerHasActiveWork}
            onConfirm={handleSignOut}
            onCancel={() => setLogoutConfirm(false)}
          />
        );
      })()}
      <div style={{borderTop:`1px solid ${B.border}`,padding:"16px 20px",textAlign:"center"}}>
        <div style={{fontSize:10,color:B.mid,letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'Barlow',sans-serif"}}>© Somerville Merchandising · Field Operations Platform</div>
      </div>
    </div>
  );
}
